/**
 * My Calendar — tour schedule, custom events, community posts; import tours; PDF export.
 * Themed via ThemeContext (light/dark).
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
  Modal,
  Pressable,
  Image,
  Linking,
  useWindowDimensions,
} from 'react-native';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  pointerWithin,
  DragOverlay,
} from '@dnd-kit/core';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { useUser } from '../components/LoginPersistence';
import { useTheme } from '../contexts/ThemeContext';
import { ScheduleEditor, parseScheduleText, serializeScheduleDoc, formatScheduleAsPlainText } from '../components/ScheduleEditor';
import { parseSetlistText, serializeSetlistDoc, formatSetlistAsPlainText, StructuredDayListEditor } from '../components/SetlistEditor';
import { LodgingDayPanel } from '../components/calendar/LodgingDayPanel';
import { DayNotesEditor } from '../components/calendar/DayNotesEditor';
import { CalendarWriterEditor } from '../components/calendar/CalendarWriterEditor';
import DaySheetToolbar from '../components/calendar/DaySheetToolbar';
import { formatLodgingDaySummary, findTourDayByDate, getLodgingVenueForDay, getInterDayTravelSummary, venueHasLodgingFlag } from '../utils/tourDayLodgingUtils';
import { buildTourDayCard, pickTourDayCard, accentForDayKind, leftBarColorForKind } from '../utils/calendarDayCard';
import { pickShowVenue } from '../utils/tourDayKinds';
import { palette, FONT } from '../styles/tourTheme';
import { loadV2Prefs, saveV2Prefs, resetV2Prefs, weekStartsOnFromPrefs, hexWithAlpha } from '../utils/calendarV2Prefs';
import { applyRiderActColor } from '../utils/calendarRiderLibrary';
import { BUDGET_TAB as V2_BUDGET_TAB, BUDGET_DATE as V2_BUDGET_DATE } from '../utils/calendarTourPnl';
import { v2TabEntryId, V2_CHAT_TAB, V2_CHAT_DATE, V2_MERCH_CATALOG_DATE, V2_RIDER_AREAS_TAB, V2_RIDER_AREAS_DATE, countChatMessages } from '../utils/calendarV2Persist';
import {
  CLAUDE_VIEW_TABS,
  CLAUDE_NAV_TABS,
  MasterTourDesignBar,
  MasterTourWeekBar,
  MasterTourTimeline,
  MasterTourDaySheetWithPhone,
  MasterTourRiders,
  MasterTourContacts,
  MasterTourChat,
  MasterTourMap,
  isNativeColorPickerActive,
} from '../components/calendar/MasterTourViews';
import { SavedContactsCard } from '../components/calendar/SavedContactsCard';
import {
  MasterTourSettlement,
  MasterTourExpenses,
  MasterTourMerch,
  MasterTourFilesPage,
  MasterTourLodgingPage,
  MasterTourPartyPage,
  MasterTourSetlistPage,
  MasterTourGuestListPage,
  MasterTourDaySheetPoster,
} from '../components/calendar/MasterTourOpsViews';
import { MasterTourNotesPage } from '../components/calendar/MasterTourNotesPage';
import { MasterTourSchedulePage } from '../components/calendar/MasterTourSchedulePage';
import { DayCellTourCard, TourDayCellHeader, DayCellChipFooter } from '../components/calendar/TourDayCellHeader';
import { CalendarWeekItinerary } from '../components/calendar/CalendarWeekItinerary';
import {
  STRUCTURED_DAY_LIST_TABS,
  STRUCTURED_DAY_LIST_LABELS,
  STRUCTURED_DAY_LIST_MODAL_COPY,
} from '../components/structuredDayListLabels';
import { DaySheetPaper, buildDaySheetPreviewRows } from '../components/DaySheetPaper';
import DaySheetScheduleEditor from '../components/calendar/DaySheetScheduleEditor';
import { formatSetlistDateLabel, openSetlistPrintWindow } from '../components/SetlistPaper';
import { TourPartyPanel } from '../components/calendar/TourPartyPanel';
import {
  CALENDAR_SIDEBAR_WIDTH,
  CALENDAR_SIDEBAR_WIDTH_WIDE,
} from '../components/calendar/calendarGlassStyles';
import { DayApplyScopePicker } from '../components/DayApplyScopePicker';
import { CalendarTabPublishBar } from '../components/calendar/CalendarTabPublishBar';
import {
  buildTabEntryDraftFields,
  buildTabEntryPublishFields,
  getTabEntryDraftText,
  getTabEntryPublishedText,
  hasUnpublishedTabDraft,
} from '../utils/calendarTabDraftService';
import { getPublishedScheduleItems } from '../utils/scheduleCalendarDisplay';
import {
  isHtmlTabContent,
  isNotesHtml,
} from '../utils/notesFormat';
import { Toast } from '../components/Toast';
import { CalendarWriterPanel } from '../components/calendar/CalendarWriterPanel';
import { CalendarStagePanel } from '../components/calendar/CalendarStagePanel';
import { CalendarDayFilesPanel } from '../components/calendar/CalendarDayFilesPanel';
import { DayFileThumbnail } from '../components/calendar/DayFileThumbnail';
import { CalendarSidebarTabNav, CalendarTabChevrons, CalendarDayChevrons } from '../components/calendar/CalendarSidebarTabNav';
import { isWriterHtmlEmpty } from '../utils/writerFormat';
import { subscribeRiderShares } from '../utils/riderShareService';
import { upsertDayShareLink, SHARE_VISIBILITY, guestListVenueLinkId, contactsDayShareLinkId, buildShareUrl } from '../utils/calendarShareService';
import { sendShareLinkToRecipients } from '../utils/calendarShareSendService';
import { combineRiderHtml } from '../utils/masterTourSend';
import { GUEST_CAP_TAB, serializeGuestCap } from '../utils/calendarGuestList';
import {
  canvasFromSlug,
  isCalendarDateKey,
  parseCalendarPath,
} from '../utils/calendarRoutes';
import { SharedLinksManager } from '../components/calendar/SharedLinksManager';
import { TourSettingsManager, SidebarShareSettingsActions } from '../components/calendar/TourSettingsManager';
import { TourPartyRail } from '../components/calendar/TourPartyRail';
import { DayScheduleSettingsPanel, DayScheduleStatusChip } from '../components/calendar/DayScheduleSettingsPanel';
import { ShareScheduleIndicators } from '../components/calendar/ShareScheduleIndicators';
import {
  buildShareScheduleIndicators,
  processDueShareReminders,
  subscribeTourShareSettings,
} from '../utils/tourShareScheduleService';
import {
  canEditCalendarTab,
  resolveTourPermissionContext,
  canManageTourParty,
  getViewableCalendarTabs,
  resolveV2CanvasAccess,
  canPublishSetlist,
} from '../utils/tourMemberPermissionsService';
import { useMasterTourCalendarUi, useTourPlannerNavVisible, useUpdatedColorScheme, useTourCommunityHub } from '../contexts/FeatureFlagsContext';
import { notifyTourChatMessage } from '../utils/tourChatNotify';
import { getPagePaletteIfEnabled } from '../constants/pageColors';
import { hasCalendarAccess } from '../utils/subscriptionEntitlements';
const DEV_MODE = false
import { useAppLayout } from '../contexts/AppLayoutContext';
import { useTourPartyMembers } from '../hooks/useTourPartyMembers';
import { formatDateLong } from '../utils/calendarContacts';
import { DAY_SHEET_DEFAULT_ITEMS } from '../utils/scheduleCues';
import { BREAKPOINTS, getResponsiveValue } from '../styles/breakpoints';
import {
  buildMonthGrid,
  chunkCalendarWeeks,
  WEEKDAY_LABELS,
  weekdayLabelsForWeekStart,
  startOfWeek,
  monthLabel,
  toDateKey,
  addDaysToKey,
  computeImportDayDateKeys,
  parseDateKey,
  moveDateToMonth,
  getCalendarViewDateRange,
  sortToursForCalendarView,
  filterToursForPicker,
  groupToursForPickerDisplay,
  countToursForPickerFilter,
  formatTourDateRange,
  DAY_APPLY_SCOPES,
  getTourShowDateKeys,
  getTourShowDateKeysFromEvents,
  resolveDayApplyScopeDateKeys,
  countDayApplyScopeDates,
  pickTourIdForDate,
  shouldStackMasterTourHeader,
  tourStartFocus,
} from '../utils/calendarHelpers';
import {
  loadTourInvitesForUser,
  fetchSharedTourSnapshot,
  buildSharedTourFolder,
  enrichSharedInviteRow,
} from '../utils/tourInviteHelpers';

const BRAND = '#2a7d5c';

// Palette for tour color-coding (cycles if more tours than colors)
const TOUR_PALETTE = [
  '#3ba378', '#e06c7c', '#9b7fe8', '#e8a03a',
  '#4ab6d1', '#e87a3a', '#6dbb8a', '#c05090',
  '#5b8dd9', '#b8c044',
];
function getTourColor(index) { return TOUR_PALETTE[index % TOUR_PALETTE.length]; }

const MONTH_SHORT_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function PdfMonthRangeFields({
  styles,
  labelColor,
  fromY,
  fromM,
  toY,
  toM,
  setFromY,
  setFromM,
  setToY,
  setToM,
  onExport,
  onCancel,
  exporting,
}) {
  return (
    <View>
      <Text style={[styles.muted, { marginBottom: 6, fontWeight: '700', color: labelColor }]}>From</Text>
      <View style={styles.monthPickerYearRow}>
        <TouchableOpacity
          style={styles.monthPickerYearBtn}
          onPress={() => setFromY((y) => Math.max(1970, y - 1))}
          accessibilityLabel="From start year previous"
        >
          <Ionicons name="chevron-back" size={22} color={labelColor} />
        </TouchableOpacity>
        <Text style={styles.monthPickerYearText}>{fromY}</Text>
        <TouchableOpacity
          style={styles.monthPickerYearBtn}
          onPress={() => setFromY((y) => Math.min(2100, y + 1))}
          accessibilityLabel="From start year next"
        >
          <Ionicons name="chevron-forward" size={22} color={labelColor} />
        </TouchableOpacity>
      </View>
      <View style={styles.monthGrid}>
        {MONTH_SHORT_LABELS.map((label, i) => (
          <TouchableOpacity
            key={`from-${label}`}
            style={[styles.monthChip, fromM === i && styles.monthChipActive]}
            onPress={() => setFromM(i)}
          >
            <Text style={[styles.monthChipText, fromM === i && styles.monthChipTextActive]}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <Text style={[styles.muted, { marginTop: 14, marginBottom: 6, fontWeight: '700', color: labelColor }]}>To</Text>
      <View style={styles.monthPickerYearRow}>
        <TouchableOpacity
          style={styles.monthPickerYearBtn}
          onPress={() => setToY((y) => Math.max(1970, y - 1))}
          accessibilityLabel="To end year previous"
        >
          <Ionicons name="chevron-back" size={22} color={labelColor} />
        </TouchableOpacity>
        <Text style={styles.monthPickerYearText}>{toY}</Text>
        <TouchableOpacity
          style={styles.monthPickerYearBtn}
          onPress={() => setToY((y) => Math.min(2100, y + 1))}
          accessibilityLabel="To end year next"
        >
          <Ionicons name="chevron-forward" size={22} color={labelColor} />
        </TouchableOpacity>
      </View>
      <View style={styles.monthGrid}>
        {MONTH_SHORT_LABELS.map((label, i) => (
          <TouchableOpacity
            key={`to-${label}`}
            style={[styles.monthChip, toM === i && styles.monthChipActive]}
            onPress={() => setToM(i)}
          >
            <Text style={[styles.monthChipText, toM === i && styles.monthChipTextActive]}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <View style={styles.popupButtonRow}>
        <TouchableOpacity style={styles.popupBtnSecondary} onPress={onCancel}>
          <Text style={styles.popupBtnSecondaryText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.primaryBtn, { marginBottom: 0 }]}
          onPress={onExport}
          disabled={exporting}
        >
          <Text style={styles.primaryBtnText}>{exporting ? '…' : 'Export'}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const TOUR_PICKER_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'inView', label: 'This view' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'past', label: 'Past' },
  { id: 'unscheduled', label: 'No dates' },
  { id: 'hidden', label: 'Hidden' },
];
const CALENDAR_TOP_TABS = [
  'TOUR PARTY',
  'RIDER',
  'STAGE',
  'SETLIST',
  'GUEST LIST',
  'SCHEDULE',
  'FILES',
  'CONTACTS',
  'MERCH',
  'NOTES',
  'DAY SHEET',
  'LODGING',
];
const RIDER_SUBTABS = ['DAILY', 'FULL'];
const DAY_SHARE_OPTIONS = ['VENUES', ...CALENDAR_TOP_TABS];
const DAY_INFO_TAB_KEYS = {
  RIDER: 'RIDER_DAILY',
  'RIDER FULL': 'RIDER_FULL',
};
const TAB_TEXT_COLORS = ['#111111', '#3ba378', '#0a59f0', '#c0392b', '#7c6cdd'];
const TAB_TEXT_ALIGN_OPTIONS = [
  { id: 'left', label: 'Left', icon: 'text-outline' },
  { id: 'center', label: 'Center', icon: 'reorder-three-outline' },
  { id: 'right', label: 'Right', icon: 'menu-outline' },
];
const TAB_TEXT_SIZE_OPTIONS = [11, 13, 15];
const TABS_WITHOUT_DAY_APPLY_SCOPE = new Set(['FILES', 'DAY SHEET', 'TOUR PARTY']);
const DAY_CELL_SKIP_INLINE_TABS = new Set(['FILES', 'SCHEDULE', 'DAY SHEET', 'TOUR PARTY', 'STAGE', 'LODGING']);

function isManagedTourDay(dateKey, tourId, tourDayMetaByDate) {
  if (!dateKey || !tourId) return false;
  return Boolean(tourDayMetaByDate?.[dateKey]?.tours?.some((t) => t.tourId === tourId));
}

function LegacyTourCorner({ cornerMeta, tourDayEvent, styles }) {
  const cornerTourName = String(cornerMeta?.tourName || tourDayEvent?.sourceTourName || '').trim();
  const cornerVenueName = String(cornerMeta?.venueName || tourDayEvent?.dayVenueSummary || '').split(',')[0].trim();
  return (
    <View style={[styles.dayCellTourCorner, styles.dayCellTourInfo]} pointerEvents="none">
      {cornerMeta?.tours?.length > 0 ? (
        cornerMeta.tours.map((t) => (
          <View key={t.tourId} style={styles.dayCellTourCornerRow}>
            <View style={[styles.dayCellTourCornerDot, { backgroundColor: t.tourColor || BRAND }]} />
            <Text style={styles.dayCellTourCornerTour} numberOfLines={1}>{t.tourName}</Text>
          </View>
        ))
      ) : (
        <>
          {cornerTourName ? <Text style={styles.dayCellTourCornerTour} numberOfLines={1}>{cornerTourName}</Text> : null}
          {cornerVenueName ? <Text style={styles.dayCellTourCornerVenue} numberOfLines={1}>{cornerVenueName}</Text> : null}
        </>
      )}
    </View>
  );
}

function buildCardForTourDate(folder, dateKey) {
  const tour = folder?.tour;
  const { day, dayIndex } = findTourDayByDate(tour, dateKey);
  const lodging = day ? getLodgingVenueForDay(day) : null;
  const showVenue = pickShowVenue(day?.venues, lodging);
  const travel = dayIndex >= 0 ? getInterDayTravelSummary(tour, dayIndex) : null;
  const placeVenue = showVenue || lodging || (day?.venues || [])[0] || null;
  return buildTourDayCard({
    inTourRange: true,
    venueName: String(showVenue?.name || '').trim(),
    city: String(placeVenue?.city || '').trim(),
    state: String(placeVenue?.state || '').trim(),
    capacity: showVenue?.capacity,
    hasHotel: Boolean(lodging || (day?.venues || []).some(venueHasLodgingFlag)),
    travel: travel && !travel.same ? travel : null,
  });
}

function shouldShowDayCellExpand(isManagedDay, selectedTourFilterId, activeTopTab) {
  return Boolean(isManagedDay && selectedTourFilterId && activeTopTab);
}

/** Poster month cards hide SHOW/TRAVEL event pills so city/venue/chips can read. */
function hideMasterTourMonthEventPills(masterTourUi, tabModes = {}) {
  if (!masterTourUi) return false;
  return !(
    tabModes.showPlainTabInput ||
    tabModes.showScheduleInCell ||
    tabModes.showStructuredListInCell ||
    tabModes.showFilesInCell ||
    tabModes.showStageFilesInCell
  );
}

function DayTabCellEditor({
  activeTopTab,
  dayTabText,
  dayTabStyle,
  dateKey,
  onChangeDayTabText,
  theme,
  styles,
  fill = false,
  dense = false,
}) {
  if (!dateKey) return null;

  const instanceKey = `cell-${dateKey}-${activeTopTab || 'tab'}`;
  const plainInputStyle = [
    styles.dayCellInlineTabInput,
    {
      color: dayTabStyle?.textColor || theme.input.text,
      textAlign: dayTabStyle?.textAlign || 'left',
      fontSize: Number(dayTabStyle?.fontSize) || (fill ? 13 : dense ? 10 : 11),
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: dense ? 5 : 6,
      backgroundColor: theme.background.card,
      paddingHorizontal: dense ? 5 : 6,
      paddingVertical: dense ? 3 : 4,
      minHeight: fill ? 0 : dense ? 28 : 40,
    },
    fill || dense ? { flex: 1, minHeight: 0 } : null,
  ];
  const richWrapStyle = fill
    ? [styles.dayCellRichEditorWrap, { flex: 1, maxHeight: undefined }]
    : dense
      ? [styles.dayCellRichEditorWrap, { minHeight: 36 }]
      : styles.dayCellRichEditorWrap;

  if (activeTopTab === 'RIDER') {
    return (
      <View style={richWrapStyle}>
        <CalendarWriterEditor
          cellMode
          instanceKey={instanceKey}
          value={String(dayTabText || '')}
          onChange={(t) => onChangeDayTabText?.(dateKey, t)}
          theme={theme}
        />
      </View>
    );
  }

  if (
    activeTopTab === 'NOTES' ||
    isNotesHtml(dayTabText) ||
    isHtmlTabContent(dayTabText)
  ) {
    return (
      <View style={richWrapStyle}>
        <DayNotesEditor
          cellMode
          instanceKey={instanceKey}
          value={String(dayTabText || '')}
          onChange={(t) => onChangeDayTabText?.(dateKey, t)}
          theme={theme}
        />
      </View>
    );
  }

  return (
    <TextInput
      style={plainInputStyle}
      value={String(dayTabText || '')}
      onChangeText={(t) => onChangeDayTabText?.(dateKey, t)}
      multiline
      textAlignVertical="top"
      placeholder={`Add ${String(activeTopTab || 'day').toLowerCase()}…`}
      placeholderTextColor={theme.text.secondary}
    />
  );
}

function dayCellCornerMetaVisible(cell, selectedTourFilterId, sel, allowGridInlineEditAllDays, hasMeta, v2Layout = false) {
  if (!cell?.inMonth || !hasMeta) return false;
  if (v2Layout) return true;
  if (selectedTourFilterId) return true;
  return Boolean(sel || allowGridInlineEditAllDays);
}

function dayCellTabDisplayModes(activeTopTab, cell, sel, gridEditAll, isManagedDay) {
  if (!isManagedDay) {
    return {
      showFilesInCell: false,
      showStageFilesInCell: false,
      showPlainTabInput: false,
      showScheduleInCell: false,
      showStructuredListInCell: false,
    };
  }
  const lockMode = Boolean(activeTopTab);
  const cellEligible = Boolean(sel || gridEditAll);
  const showFilesInCell = Boolean(activeTopTab === 'FILES' && cell?.dateKey && cellEligible);
  const showStageFilesInCell = Boolean(activeTopTab === 'STAGE' && cell?.dateKey && cellEligible);
  const showPlainTabInput = Boolean(
    cell?.dateKey &&
      activeTopTab &&
      !DAY_CELL_SKIP_INLINE_TABS.has(activeTopTab) &&
      !STRUCTURED_DAY_LIST_TABS.has(activeTopTab) &&
      (sel || (lockMode && gridEditAll))
  );
  const showScheduleInCell = Boolean(cell?.dateKey && activeTopTab === 'SCHEDULE' && (sel || (lockMode && gridEditAll)));
  const showStructuredListInCell = Boolean(
    cell?.dateKey && STRUCTURED_DAY_LIST_TABS.has(activeTopTab) && (sel || (lockMode && gridEditAll))
  );
  return { showFilesInCell, showStageFilesInCell, showPlainTabInput, showScheduleInCell, showStructuredListInCell };
}

function serializeStructuredDayListText(raw) {
  return serializeSetlistDoc(parseSetlistText(raw)).trim();
}

function formatStructuredDayListForTab(tabKey, raw) {
  const labels = STRUCTURED_DAY_LIST_LABELS[tabKey];
  return formatSetlistAsPlainText(raw, labels);
}

function defaultDaySheetScheduleRows() {
  return DAY_SHEET_DEFAULT_ITEMS.map((label) => ({
    id: `default_${label.toLowerCase().replace(/[^\w]+/g, '_')}`,
    kind: 'schedule',
    item: label,
    time: '0:00',
  }));
}

/** @returns {number} negative if a<b, 0 if equal, positive if a>b */
function compareYearMonth(y1, m1, y2, m2) {
  if (y1 !== y2) return y1 - y2;
  return m1 - m2;
}

/** Inclusive list of { y, m } from start through end (m = 0–11). */
function enumerateMonthsInclusive(fromY, fromM, toY, toM) {
  const out = [];
  if (compareYearMonth(fromY, fromM, toY, toM) > 0) return out;
  let y = fromY;
  let m = fromM;
  while (compareYearMonth(y, m, toY, toM) <= 0) {
    out.push({ y, m });
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return out;
}

/** Firestore `date` string bounds for events in [fromY/fromM … toY/toM]. */
function pdfRangeToDateBounds(fromY, fromM, toY, toM) {
  const min = `${fromY}-${String(fromM + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(toY, toM + 1, 0).getDate();
  const max = `${toY}-${String(toM + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { min, max };
}

function parseStrictDateKey(dateKey) {
  const s = String(dateKey || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return toDateKey(d) === s ? d : null;
}

function parseStrictTime(value) {
  const s = String(value || '').trim();
  if (!s) return '';
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s);
  if (!m) return null;
  return `${m[1]}:${m[2]}`;
}

function formatDaySheetHeadline(dateKey) {
  const d = parseStrictDateKey(dateKey);
  if (!d) return '';
  const day = d.getDate();
  const mod100 = day % 100;
  const suffix = mod100 >= 11 && mod100 <= 13 ? 'th' : day % 10 === 1 ? 'st' : day % 10 === 2 ? 'nd' : day % 10 === 3 ? 'rd' : 'th';
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  return `${weekday}, ${month} ${day}${suffix} ${d.getFullYear()}`;
}

function enumerateDateKeysInclusive(startKey, endKey) {
  const start = parseStrictDateKey(startKey);
  const end = parseStrictDateKey(endKey);
  if (!start || !end || start > end) return [];
  const out = [];
  const cur = new Date(start);
  while (cur <= end) {
    out.push(toDateKey(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** @param {string} hex e.g. `#3ba378` */
function hexToRgbForPdf(hex) {
  const s = String(hex || '').trim();
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(s);
  if (!m) return [59, 163, 120];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

/** Keep Tour Planner `scheduledDate` in sync when a `tour_day` calendar event moves. */
async function syncTourDayScheduledDate(uid, sourceTourId, sourceDayIndex, newDateKey) {
  if (!uid || !sourceTourId || sourceDayIndex == null || !newDateKey) return;
  try {
    const db = getFirestore();
    const userRef = doc(db, 'users', uid);
    const snap = await getDoc(userRef);
    const folders = { ...(snap.data()?.folders || {}) };
    const folder = folders[sourceTourId];
    if (!folder?.tour?.days || !folder.tour.days[sourceDayIndex]) return;
    const days = [...folder.tour.days];
    days[sourceDayIndex] = { ...days[sourceDayIndex], scheduledDate: newDateKey };
    folders[sourceTourId] = { ...folder, tour: { ...folder.tour, days } };
    await updateDoc(userRef, { folders });
  } catch (e) {
    console.warn('syncTourDayScheduledDate', e);
  }
}

async function persistTourScheduledDates(uid, folder, dateKeys) {
  if (!uid || !folder?.id || !Array.isArray(dateKeys)) return;
  const db = getFirestore();
  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return;
  const folders = { ...(snap.data()?.folders || {}) };
  const current = folders[folder.id];
  if (!current?.tour?.days) return;
  const days = current.tour.days.map((day, i) => ({
    ...day,
    scheduledDate: String(dateKeys[i] || day?.scheduledDate || ''),
  }));
  folders[folder.id] = { ...current, tour: { ...current.tour, days } };
  await updateDoc(userRef, { folders });
}

const FLOAT_ACTION_ICON = 20;

function normalizeDayFileType(raw) {
  const key = String(raw || '').trim();
  if (key === 'files' || key === 'stage' || key === 'riderFull') return key;
  return 'riderFull';
}

function resolveAppBaseUrl() {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin);
  }
  return 'https://vrc.app';
}

function CalendarDayFloatToolbar({
  showEventFloat,
  selectedEv,
  styles,
  theme,
  allowAddEvent = true,
  openNew,
  goTourPlanner,
  openEdit,
  openMoveToMonth,
  deleteEvent,
  deleteTourFromCalendar,
  showTourPlannerNav,
  showTextStyleControls,
  selectedDayTabStyle,
  onChangeSelectedDayTabStyle,
  onOpenDayShare,
  showExpandEditor,
  onOpenExpandedEditor,
  expandEditorIconName = 'expand-outline',
  expandEditorAccessibilityLabel = 'Expand editor',
  showFilesManager,
  onOpenFilesManager,
  filledItems = [],
  anchorBelow = false,
}) {
  return (
    <View style={[styles.dayCellFloatMenu, anchorBelow ? styles.dayCellFloatAnchorBelow : styles.dayCellFloatAnchorAbove]}>
      <View style={styles.dayCellFloatToolbarRow}>
        {showEventFloat ? (
          <>
            {showTourPlannerNav && selectedEv.eventType === 'tour_day' && selectedEv.sourceTourId ? (
              <TouchableOpacity
                style={styles.floatIconBtn}
                onPress={() => goTourPlanner(selectedEv.sourceTourId)}
                accessibilityLabel="Open in Tour Planner"
              >
                <Ionicons name="map-outline" size={FLOAT_ACTION_ICON} color={theme.brand.primary} />
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={styles.floatIconBtn}
              onPress={() => openEdit(selectedEv)}
              accessibilityLabel="Edit and color"
            >
              <Ionicons name="color-palette-outline" size={FLOAT_ACTION_ICON} color={theme.text.primary} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.floatIconBtn}
              onPress={() => openMoveToMonth(selectedEv)}
              accessibilityLabel="Move to another month"
            >
              <Ionicons name="calendar-outline" size={FLOAT_ACTION_ICON} color={theme.brand.accent} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.floatIconBtn}
              onPress={() => deleteEvent(selectedEv.id)}
              accessibilityLabel="Remove event"
            >
              <Ionicons name="trash-outline" size={FLOAT_ACTION_ICON} color={theme.status.error} />
            </TouchableOpacity>
            {selectedEv.eventType === 'tour_day' && selectedEv.sourceTourId ? (
              <TouchableOpacity
                style={styles.floatIconBtn}
                onPress={() => deleteTourFromCalendar(selectedEv.sourceTourId)}
                accessibilityLabel="Remove entire tour from calendar"
              >
                <Ionicons name="albums-outline" size={FLOAT_ACTION_ICON} color={theme.status.error} />
              </TouchableOpacity>
            ) : null}
          </>
        ) : (
          <>
            {allowAddEvent ? (
              <TouchableOpacity style={styles.floatIconBtn} onPress={openNew} activeOpacity={0.85} accessibilityLabel="Add event">
                <Ionicons name="add-circle-outline" size={FLOAT_ACTION_ICON} color={theme.brand.primary} />
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={styles.floatIconBtn}
              onPress={onOpenDayShare}
              activeOpacity={0.85}
              accessibilityLabel="Share day info"
            >
              <Ionicons name="paper-plane-outline" size={FLOAT_ACTION_ICON} color={theme.brand.accent} />
            </TouchableOpacity>
            {showExpandEditor ? (
              <TouchableOpacity
                style={styles.floatIconBtn}
                onPress={onOpenExpandedEditor}
                activeOpacity={0.85}
                accessibilityLabel={expandEditorAccessibilityLabel}
              >
                <Ionicons name={expandEditorIconName} size={FLOAT_ACTION_ICON} color={theme.text.primary} />
              </TouchableOpacity>
            ) : null}
          </>
        )}
      </View>
      {showTextStyleControls ? (
        <View style={styles.floatTextStyleRow}>
          <Text style={styles.floatTextStyleLabel}>Color</Text>
          <View style={styles.floatTextStyleGroup}>
            {TAB_TEXT_COLORS.map((c) => {
              const selected = selectedDayTabStyle?.textColor === c;
              return (
                <TouchableOpacity
                  key={`float-tab-color-${c}`}
                  onPress={() => onChangeSelectedDayTabStyle?.({ textColor: c })}
                  style={[styles.floatTabColorDot, { backgroundColor: c }, selected && styles.floatTabColorDotActive]}
                />
              );
            })}
          </View>
          <Text style={styles.floatTextStyleLabel}>Align</Text>
          <View style={styles.floatTextStyleGroup}>
            {TAB_TEXT_ALIGN_OPTIONS.map((opt) => {
              const selected = selectedDayTabStyle?.textAlign === opt.id;
              return (
                <TouchableOpacity
                  key={`float-tab-align-${opt.id}`}
                  onPress={() => onChangeSelectedDayTabStyle?.({ textAlign: opt.id })}
                  style={[styles.floatTabControlBtn, selected && styles.floatTabControlBtnActive]}
                >
                  <Ionicons
                    name={opt.icon}
                    size={13}
                    color={selected ? theme.button.primaryText : theme.text.primary}
                  />
                  <Text style={[styles.floatTabControlBtnText, selected && styles.floatTabControlBtnTextActive]}>
                    {opt.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <Text style={styles.floatTextStyleLabel}>Size</Text>
          <View style={styles.floatTextStyleGroup}>
            {TAB_TEXT_SIZE_OPTIONS.map((s) => {
              const selected = Number(selectedDayTabStyle?.fontSize) === s;
              return (
                <TouchableOpacity
                  key={`float-tab-size-${s}`}
                  onPress={() => onChangeSelectedDayTabStyle?.({ fontSize: s })}
                  style={[styles.floatTabControlBtn, selected && styles.floatTabControlBtnActive]}
                >
                  <Text style={[styles.floatTabControlBtnText, selected && styles.floatTabControlBtnTextActive]}>
                    {s}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      ) : null}
      {filledItems.length ? (
        <View style={styles.floatFilledItemsRow}>
          {filledItems.map((x) => (
            <View key={`filled-${x}`} style={styles.floatFilledItemChip}>
              <Text style={styles.floatFilledItemText}>{x}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

const EVENT_COLORS = [
  BRAND,
  '#0a59f0',
  '#03bb8d',
  '#e67e22',
  '#8e44ad',
  '#c0392b',
  '#16a085',
];

function safeEventFromDoc(id, data) {
  if (!data) return null;
  return {
    id,
    title: String(data.title || 'Untitled'),
    date: String(data.date || ''),
    startTime: data.startTime != null ? String(data.startTime) : '',
    endTime: data.endTime != null ? String(data.endTime) : '',
    sortOrder: Number(data.sortOrder) || 0,
    eventType: data.eventType || 'custom',
    notes: data.notes != null ? String(data.notes) : '',
    color: data.color || BRAND,
    communityPostId: data.communityPostId || null,
    communityPostTitle: data.communityPostTitle || '',
    sourceTourId: data.sourceTourId || null,
    sourceTourName: data.sourceTourName || '',
    sourceDayIndex: data.sourceDayIndex != null ? Number(data.sourceDayIndex) : null,
    dayVenueSummary: data.dayVenueSummary || '',
  };
}

function initialCalendarDeepLink() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return { canvas: null, dateKey: '' };
  }
  const parsed = parseCalendarPath(`${window.location.pathname}${window.location.search || ''}`);
  if (parsed.kind !== 'canvas') return { canvas: null, dateKey: '' };
  return {
    canvas: parsed.canvas && parsed.canvas !== 'month' ? parsed.canvas : null,
    dateKey: parsed.dateKey || '',
  };
}

export function MyCalendar() {
  const navigation = useNavigation();
  const { user: userData } = useUser();
  const { theme, isDarkMode } = useTheme();
  const updatedColorScheme = useUpdatedColorScheme();
  const masterTourUi = useMasterTourCalendarUi();
  const tourCommunityHub = useTourCommunityHub();
  const showTourPlannerNav = useTourPlannerNavVisible(userData);
  const { headerHeight, setPageSchemeOverride } = useAppLayout();
  const uid = userData?.uid;
  const isAdmin = userData?.userType === 'admin' || userData?.isAdmin === true;
  const canUseCalendar = !userData || isAdmin || DEV_MODE || hasCalendarAccess(userData);

  useEffect(() => {
    if (!userData || canUseCalendar) return;
    navigation.navigate('Plans');
  }, [userData, canUseCalendar, navigation]);

  useEffect(() => {
    if (updatedColorScheme) {
      setPageSchemeOverride('calendar');
    } else {
      setPageSchemeOverride(null);
    }
    return () => setPageSchemeOverride(null);
  }, [updatedColorScheme, setPageSchemeOverride]);

  const pad = getResponsiveValue({ mobile: 12, tablet: 16, laptop: 20, desktop: 24 });
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isCompactMonthLayout = windowWidth <= BREAKPOINTS.tablet;
  const stackMasterTourHeader = masterTourUi && shouldStackMasterTourHeader(windowWidth);
  const allowGridInlineEditAllDays = windowWidth >= 1200;
  const useMonthHorizontalScroll = windowWidth > BREAKPOINTS.tablet && windowWidth < 1200;
  const [v2Prefs, setV2Prefs] = useState(() => loadV2Prefs());
  const deepLink = useRef(initialCalendarDeepLink()).current;
  const [v2Canvas, setV2Canvas] = useState(deepLink.canvas);
  const route = useRoute();
  const [guestVenueLink, setGuestVenueLink] = useState('');
  const [contactsShareUrl, setContactsShareUrl] = useState('');
  const [contactsShareBusy, setContactsShareBusy] = useState(false);
  const calendarPathSyncing = useRef(false);
  const styles = useMemo(
    () => createCalendarStyles(theme, pad, windowWidth, isDarkMode, updatedColorScheme, masterTourUi, v2Prefs.accent, v2Prefs.density),
    [theme, pad, windowWidth, isDarkMode, updatedColorScheme, masterTourUi, v2Prefs.accent, v2Prefs.density],
  );
  const expandedScheduleEditorMaxHeight = useMemo(
    () => Math.max(240, Math.round(windowHeight * 0.44)),
    [windowHeight]
  );
  const placeholderColor = theme.input.placeholder;
  const iconPrimary = theme.text.primary;
  const v2Pal = palette(isDarkMode);
  const chromeIcon = masterTourUi ? v2Pal.ink : theme.text.primary;

  const now = new Date();
  const seedFromLink = deepLink.dateKey ? parseDateKey(deepLink.dateKey) : null;
  const [cursor, setCursor] = useState({
    y: (seedFromLink || now).getFullYear(),
    m: (seedFromLink || now).getMonth(),
  });
  // calView: which calendar view is active
  const [calView, setCalView] = useState(deepLink.canvas === 'week' ? 'week' : 'month');
  // cursorDate: reference date for week/day navigation (independent from cursor month)
  const [cursorDate, setCursorDate] = useState(seedFromLink || now);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDateKey, setSelectedDateKey] = useState(() => deepLink.dateKey || toDateKey(now));
  /** Selected event in grid (web/native) — shows action bar */
  const [selectedEventId, setSelectedEventId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [formStartTime, setFormStartTime] = useState('');
  const [formEndTime, setFormEndTime] = useState('');
  const [datePickerCursor, setDatePickerCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [datePickTarget, setDatePickTarget] = useState('start');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [importDatePickerCursor, setImportDatePickerCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [importDatePickerRowIndex, setImportDatePickerRowIndex] = useState(-1);
  const [formNotes, setFormNotes] = useState('');
  const [formColor, setFormColor] = useState(BRAND);
  const [formCommunityPostId, setFormCommunityPostId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [tourImportDateModal, setTourImportDateModal] = useState({
    open: false,
    folder: null,
    rows: [],
    applyAsSelection: false,
  });
  const [tourFolders, setTourFolders] = useState([]);
  // Set of tour IDs hidden from the calendar grid (empty = all visible)
  const [hiddenTourIds, setHiddenTourIds] = useState(() => {
    try {
      const raw = typeof localStorage !== 'undefined' && localStorage.getItem('cal_hiddenTourIds');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const showCommunityPosts = false;
  const [loadingTours, setLoadingTours] = useState(false);
  const [tourPickerOpen, setTourPickerOpen] = useState(false);
  const [tourPickerFilter, setTourPickerFilter] = useState('all');
  const [tourPickerMainTab, setTourPickerMainTab] = useState('mine');
  const [sharedTourInvites, setSharedTourInvites] = useState([]);
  const [loadingSharedTours, setLoadingSharedTours] = useState(false);
  const [importingSharedTourId, setImportingSharedTourId] = useState(null);
  const [selectedTourFilterId, setSelectedTourFilterId] = useState(null);
  const [activeTopTab, setActiveTopTab] = useState(null);
  const [riderSubtab, setRiderSubtab] = useState('DAILY');
  const [sidebarWide, setSidebarWide] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem('cal_sidebarWide') === 'true';
    } catch {
      return false;
    }
  });
  const [sidebarPanelExpanded, setSidebarPanelExpanded] = useState(false);

  useEffect(() => {
    if (!selectedTourFilterId) setSidebarPanelExpanded(false);
  }, [selectedTourFilterId]);

  const toggleSidebarWide = useCallback(() => {
    setSidebarWide((prev) => {
      const next = !prev;
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('cal_sidebarWide', String(next));
        }
      } catch {}
      return next;
    });
  }, []);

  const sidebarWidth = sidebarWide ? CALENDAR_SIDEBAR_WIDTH_WIDE : CALENDAR_SIDEBAR_WIDTH;
  const [riderShares, setRiderShares] = useState([]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId || !selectedDateKey) {
      setRiderShares([]);
      return undefined;
    }
    const tabKey = riderSubtab === 'FULL' ? 'RIDER_FULL' : 'RIDER_DAILY';
    const db = getFirestore();
    return subscribeRiderShares(db, uid, selectedTourFilterId, selectedDateKey, tabKey, setRiderShares);
  }, [uid, selectedTourFilterId, selectedDateKey, riderSubtab]);

  const [v2HeaderBlockHeight, setV2HeaderBlockHeight] = useState(140);
  const calendarToolbarHeight = masterTourUi
    ? v2HeaderBlockHeight
    : (isCompactMonthLayout ? 88 : 52);
  const lastDayTapRef = useRef({ key: '', ts: 0 });
  const pendingDefaultTourDateRef = useRef(toDateKey(new Date()));
  const tabEntryDirtyRef = useRef({});
  const tabDraftSaveTimerRef = useRef({});
  const tabPublishedBaselineRef = useRef({});
  const v2PersistTimerRef = useRef({});
  const [tabGridDraftByDate, setTabGridDraftByDate] = useState({});
  const [tabPublishedByDate, setTabPublishedByDate] = useState({});
  const [schedulePublishedByDate, setSchedulePublishedByDate] = useState({});
  const [contactsPublishedByDate, setContactsPublishedByDate] = useState({});
  const [setlistsPublishedByDate, setSetlistsPublishedByDate] = useState({});
  const [v2DayTabs, setV2DayTabs] = useState({});
  const [v2ChatDoc, setV2ChatDoc] = useState('');
  const [v2MerchCatalog, setV2MerchCatalog] = useState('');
  const [v2RiderAreas, setV2RiderAreas] = useState('');
  const [v2LegExpenses, setV2LegExpenses] = useState('');
  const [tabGridStyleByDate, setTabGridStyleByDate] = useState({});
  const [tabDraftSaveState, setTabDraftSaveState] = useState('idle');
  const [riderFullDocUrl, setRiderFullDocUrl] = useState('');
  const [riderFullPdfPage, setRiderFullPdfPage] = useState(1);
  const [stagePlotUrl, setStagePlotUrl] = useState('');
  const [stageInputListUrl, setStageInputListUrl] = useState('');
  const [daySheetNotes, setDaySheetNotes] = useState('');
  const [daySheetBannerUrl, setDaySheetBannerUrl] = useState('');
  const [daySheetBgUrl, setDaySheetBgUrl] = useState('');
  const [daySheetAssetSaving, setDaySheetAssetSaving] = useState(false);
  const [daySheetPublicationSaving, setDaySheetPublicationSaving] = useState(false);
  const [daySheetScheduleRows, setDaySheetScheduleRows] = useState([]);
  const [daySheetScheduleDirty, setDaySheetScheduleDirty] = useState(false);
  const [daySheetSeededFromDefaults, setDaySheetSeededFromDefaults] = useState(false);
  const [daySheetPublicationBaselineTick, setDaySheetPublicationBaselineTick] = useState(0);
  const daySheetPublicationBaselineRef = useRef({ notes: '', schedule: '' });
  const daySheetNotesRef = useRef('');
  const daySheetScheduleRowsRef = useRef([]);
  const daySheetLoadGenRef = useRef(0);
  const [daySheetMode, setDaySheetMode] = useState('edit');
  const [dayShareModal, setDayShareModal] = useState({ open: false, dateKey: '' });
  const [dayShareSelections, setDayShareSelections] = useState(['RIDER']);
  const [dayShareAvailableOptions, setDayShareAvailableOptions] = useState([]);
  const [managedDayShareUrl, setManagedDayShareUrl] = useState('');
  const [dayShareSaving, setDayShareSaving] = useState(false);
  const [sharedLinksOpen, setSharedLinksOpen] = useState(false);
  const [tourSettingsOpen, setTourSettingsOpen] = useState(false);
  const [displaySettingsOpen, setDisplaySettingsOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [pdfExportOpen, setPdfExportOpen] = useState(false);
  const [savedContactsOpen, setSavedContactsOpen] = useState(false);
  const [pdfFromY, setPdfFromY] = useState(() => new Date().getFullYear());
  const [pdfFromM, setPdfFromM] = useState(() => new Date().getMonth());
  const [pdfToY, setPdfToY] = useState(() => new Date().getFullYear());
  const [pdfToM, setPdfToM] = useState(() => new Date().getMonth());
  const settingsRailOpen = Boolean(
    masterTourUi && (displaySettingsOpen || pdfExportOpen || savedContactsOpen || (tourSettingsOpen && selectedTourFilterId))
  );
  const openTourSettingsRail = useCallback(() => {
    setDisplaySettingsOpen(false);
    setPdfExportOpen(false);
    setSavedContactsOpen(false);
    setTourSettingsOpen(true);
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined' || !settingsRailOpen) return undefined;
    const onDown = (e) => {
      if (isNativeColorPickerActive()) return;
      const t = e.target;
      if (t && typeof t.closest === 'function') {
        if (t.closest('[data-v2-settings-rail="1"]')) return;
        if (t.closest('[data-v2-settings-chip="1"]')) return;
      }
      setDisplaySettingsOpen(false);
      setTourSettingsOpen(false);
      setSavedContactsOpen(false);
      setPdfExportOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [settingsRailOpen]);
  const [weekBarTodayToken, setWeekBarTodayToken] = useState(0);
  const [tourShareSettings, setTourShareSettings] = useState(null);
  const [expandedNotesModal, setExpandedNotesModal] = useState({ open: false, dateKey: '' });
  const [expandedNotesBaseline, setExpandedNotesBaseline] = useState('');
  const [expandedNotesSaving, setExpandedNotesSaving] = useState(false);
  const [expandedScheduleModal, setExpandedScheduleModal] = useState({ open: false, dateKey: '' });
  const [expandedScheduleBaseline, setExpandedScheduleBaseline] = useState('');
  const [expandedScheduleSaving, setExpandedScheduleSaving] = useState(false);
  const [expandedStructuredListModal, setExpandedStructuredListModal] = useState({ open: false, dateKey: '', tabKey: '' });
  const [expandedStructuredListBaseline, setExpandedStructuredListBaseline] = useState('');
  const [expandedStructuredListSaving, setExpandedStructuredListSaving] = useState(false);
  const [scheduleDaySheetLinkModal, setScheduleDaySheetLinkModal] = useState(null);
  const [scheduleDaySheetLinkBusy, setScheduleDaySheetLinkBusy] = useState(false);
  const scheduleDaySheetLinkPendingRef = useRef(null);
  const [tabApplyScope, setTabApplyScope] = useState(DAY_APPLY_SCOPES.THIS_DAY);
  const [tabApplyScopeSaving, setTabApplyScopeSaving] = useState(false);
  const [dayFilesModal, setDayFilesModal] = useState({ open: false, dateKey: '' });
  const [dayFiles, setDayFiles] = useState([]);
  const [dayFilesByDate, setDayFilesByDate] = useState({});
  const [dayFileNameInput, setDayFileNameInput] = useState('');
  const [dayFileSaving, setDayFileSaving] = useState(false);
  const [dayInfoModal, setDayInfoModal] = useState({ open: false, dateKey: '' });
  const [dayInfoExpanded, setDayInfoExpanded] = useState({
    venues: true,
    rider: true,
    tourParty: false,
    stage: false,
    setlist: false,
    guestList: false,
    schedule: false,
    files: false,
    contacts: false,
    merch: false,
    notes: false,
    daySheet: false,
    lodging: false,
  });
  const [dayInfoTabEntries, setDayInfoTabEntries] = useState({});
  const [communityPosts, setCommunityPosts] = useState([]);
  const [exportingIcal, setExportingIcal] = useState(false);

  /** In-app popups (no Alert — works reliably on web). */
  const [messageModal, setMessageModalRaw] = useState(null);
  const [toast, setToast] = useState(null);
  const [pendingDeleteEventId, setPendingDeleteEventId] = useState(null);
  const [pendingRemoveTourId, setPendingRemoveTourId] = useState(null);

  /** Move event to another month (modal). */
  const [moveMonthEvent, setMoveMonthEvent] = useState(null);
  const [moveMonthYear, setMoveMonthYear] = useState(() => new Date().getFullYear());
  const [moveMonthIndex, setMoveMonthIndex] = useState(() => new Date().getMonth());

  const monthStartKey = useMemo(() => {
    const d = new Date(cursor.y, cursor.m, 1);
    return toDateKey(d);
  }, [cursor.y, cursor.m]);

  const monthEndKey = useMemo(() => {
    const last = new Date(cursor.y, cursor.m + 1, 0);
    return toDateKey(last);
  }, [cursor.y, cursor.m]);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setEvents([]);
      return undefined;
    }
    const db = getFirestore();
    const ref = collection(db, 'users', uid, 'calendarEvents');
    const qy = query(ref, where('date', '>=', monthStartKey), where('date', '<=', monthEndKey), orderBy('date'));

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = [];
        snap.forEach((d) => {
          const ev = safeEventFromDoc(d.id, d.data());
          if (ev) list.push(ev);
        });
        list.sort((a, b) => (a.date !== b.date ? a.date.localeCompare(b.date) : a.sortOrder - b.sortOrder));
        setEvents(list);
        setLoading(false);
      },
      (err) => {
        console.error('calendar snapshot', err);
        setLoading(false);
        if (String(err.code || '').includes('failed-precondition')) {
          setMessageModal({
            variant: 'info',
            title: 'Index required',
            text: 'Create the Firestore index for calendarEvents: date (range) + orderBy date — link appears in the browser console.',
          });
        }
      }
    );
    return () => unsub();
  }, [uid, monthStartKey, monthEndKey]);

  useEffect(() => {
    if (!modalOpen || !uid) return;
    (async () => {
      try {
        const db = getFirestore();
        const pq = query(
          collection(db, 'communityPosts'),
          where('userId', '==', uid)
        );
        const snap = await getDocs(pq);
        const rows = [];
        snap.forEach((d) => {
          const x = d.data();
          const snippet = (x.postContent || '').replace(/\s+/g, ' ').trim().slice(0, 80);
          rows.push({
            id: d.id,
            label: snippet || x.location || 'Post',
          });
        });
        rows.sort((a, b) => b.id.localeCompare(a.id));
        setCommunityPosts(rows.slice(0, 40));
      } catch (e) {
        console.warn('community posts for calendar', e);
      }
    })();
  }, [modalOpen, uid]);

  const weekStartsOn = masterTourUi ? weekStartsOnFromPrefs(v2Prefs) : 0;
  const weekdayLabels = weekdayLabelsForWeekStart(weekStartsOn);

  const grid = useMemo(
    () => buildMonthGrid(cursor.y, cursor.m, { weekStartsOn }),
    [cursor.y, cursor.m, weekStartsOn]
  );

  const weekRows = useMemo(() => chunkCalendarWeeks(grid), [grid]);

  const scheduleCellLabels = useMemo(() => {
    const labels = {};
    weekRows.flat().forEach((cell) => {
      if (!cell?.dateKey) return;
      const plain = formatScheduleAsPlainText(String(tabGridDraftByDate[cell.dateKey] || ''));
      if (plain) labels[cell.dateKey] = plain;
    });
    return labels;
  }, [weekRows, tabGridDraftByDate]);

  const structuredListCellLabels = useMemo(() => {
    if (!activeTopTab || !STRUCTURED_DAY_LIST_TABS.has(activeTopTab)) return {};
    const labels = {};
    weekRows.flat().forEach((cell) => {
      if (!cell?.dateKey) return;
      const plain = formatStructuredDayListForTab(activeTopTab, String(tabGridDraftByDate[cell.dateKey] || ''));
      if (plain) labels[cell.dateKey] = plain;
    });
    return labels;
  }, [weekRows, tabGridDraftByDate, activeTopTab]);

  const datePickerWeekRows = useMemo(
    () => chunkCalendarWeeks(buildMonthGrid(datePickerCursor.y, datePickerCursor.m, { weekStartsOn })),
    [datePickerCursor.y, datePickerCursor.m, weekStartsOn]
  );
  const importDatePickerWeekRows = useMemo(
    () => chunkCalendarWeeks(buildMonthGrid(importDatePickerCursor.y, importDatePickerCursor.m, { weekStartsOn })),
    [importDatePickerCursor.y, importDatePickerCursor.m, weekStartsOn]
  );
  const openDatePickerFor = useCallback(
    (target) => {
      const seed = target === 'end' ? (formEndDate || formDate) : (formDate || formEndDate);
      const d = parseStrictDateKey(seed) || new Date();
      setDatePickTarget(target);
      setDatePickerCursor({ y: d.getFullYear(), m: d.getMonth() });
      setDatePickerOpen(true);
    },
    [formDate, formEndDate]
  );
  const openImportDatePickerForRow = useCallback(
    (rowIndex) => {
      const row = tourImportDateModal.rows[rowIndex];
      if (!row) return;
      const seed = parseStrictDateKey(row.dateKey || row.fallbackDate) || new Date();
      setImportDatePickerRowIndex(rowIndex);
      setImportDatePickerCursor({ y: seed.getFullYear(), m: seed.getMonth() });
    },
    [tourImportDateModal.rows]
  );

  const selectedTourFolder = useMemo(
    () => tourFolders.find((t) => t.id === selectedTourFilterId) || null,
    [selectedTourFilterId, tourFolders]
  );
  const { ownerId: tourOwnerId, tourId: permissionTourId } = useMemo(
    () => resolveTourPermissionContext(selectedTourFolder, uid),
    [selectedTourFolder, uid]
  );
  // Imported (shared) tours keep their worksheets on the owner's account so every party member sees one copy.
  const { dataUid, dataTourId } = useMemo(() => {
    const shared = Boolean(selectedTourFolder?.sharedFrom?.ownerId) && tourOwnerId && permissionTourId && tourOwnerId !== uid;
    return shared
      ? { dataUid: tourOwnerId, dataTourId: permissionTourId }
      : { dataUid: uid, dataTourId: selectedTourFilterId };
  }, [selectedTourFolder, tourOwnerId, permissionTourId, uid, selectedTourFilterId]);
  // Must be defined before tourDayMetaByDate which depends on it
  const tourColorById = useMemo(() => {
    const m = {};
    tourFolders.forEach((t, i) => { m[t.id] = getTourColor(i); });
    return m;
  }, [tourFolders]);

  const tourDayMetaByDate = useMemo(() => {
    const out = {};
    // Build from ALL visible (non-hidden) tours so every tour shows on the grid by default
    const visibleFolders = tourFolders.filter((f) => !hiddenTourIds.has(f.id));
    visibleFolders.forEach((folder) => {
    const tourName = String(folder?.name || folder?.tour?.name || '').trim();
      const tourColor = tourColorById[folder.id];
      const days = folder?.tour?.days || [];
      const datedDays = days
      .map((day) => ({
        date: String(day?.scheduledDate || '').trim(),
        venue: (day?.venues || []).map((v) => String(v?.name || '').trim()).find(Boolean) || '',
      }))
      .filter((x) => parseStrictDateKey(x.date))
      .sort((a, b) => a.date.localeCompare(b.date));
      if (datedDays.length === 0) return;
    const firstDate = datedDays[0].date;
    const lastDate = datedDays[datedDays.length - 1].date;
      // Mark every date in the tour's date range
    enumerateDateKeysInclusive(firstDate, lastDate).forEach((date) => {
        if (!out[date]) out[date] = { tours: [] };
        if (!out[date].tours.some((t) => t.tourId === folder.id)) {
          out[date].tours.push({
            tourName,
            venueName: '',
            tourColor,
            tourId: folder.id,
            card: buildCardForTourDate(folder, date),
          });
        }
    });
      // Overlay venue names on actual scheduled days
    datedDays.forEach((day) => {
      const date = String(day?.date || '').trim();
      const venue = String(day?.venue || '').trim();
      if (!parseStrictDateKey(date)) return;
        if (!out[date]) out[date] = { tours: [] };
        const entry = out[date].tours.find((t) => t.tourId === folder.id);
        const card = buildCardForTourDate(folder, date);
        if (entry) {
          entry.venueName = venue;
          entry.card = card;
        } else {
          out[date].tours.push({ tourName, venueName: venue, tourColor, tourId: folder.id, card });
        }
      });
    });
    // Also mark days from `tour_day` calendar events so days that render on the
    // grid (but may be missing a `scheduledDate` in the folder) still count as
    // managed tour days — this is what enables in-cell editing for those days.
    events.forEach((e) => {
      if (e?.eventType !== 'tour_day' || !e?.sourceTourId) return;
      if (hiddenTourIds.has(e.sourceTourId)) return;
      const date = String(e?.date || '').trim();
      if (!parseStrictDateKey(date)) return;
      if (!out[date]) out[date] = { tours: [] };
      const tourName = String(e?.sourceTourName || '').trim();
      const venueName = String(e?.dayVenueSummary || '').split(',')[0].trim();
      const tourColor = tourColorById[e.sourceTourId];
      const existing = out[date].tours.find((t) => t.tourId === e.sourceTourId);
      if (existing) {
        if (!existing.tourName && tourName) existing.tourName = tourName;
        if (!existing.venueName && venueName) existing.venueName = venueName;
        if (!existing.tourColor && tourColor) existing.tourColor = tourColor;
        if (!existing.card && venueName) {
          existing.card = buildTourDayCard({ inTourRange: true, venueName });
        }
      } else {
        out[date].tours.push({
          tourName,
          venueName,
          tourColor,
          tourId: e.sourceTourId,
          card: buildTourDayCard({ inTourRange: true, venueName }),
        });
      }
    });
    // Keep backward-compat top-level fields from the first (or managed) tour on each date
    Object.keys(out).forEach((date) => {
      const managedEntry = out[date].tours.find((t) => t.tourId === selectedTourFilterId);
      const first = managedEntry || out[date].tours[0];
      if (first) {
        out[date].tourName = first.tourName;
        out[date].venueName = first.venueName;
        out[date].tourColor = first.tourColor;
        out[date].card = first.card || null;
      }
    });
    return out;
  }, [tourFolders, hiddenTourIds, tourColorById, selectedTourFilterId, events]);

  const eventsByDate = useMemo(() => {
    const m = {};
    // Filter by hidden tours and community-post visibility preference
    let eventPool = hiddenTourIds.size > 0
      ? events.filter((e) => !hiddenTourIds.has(e.sourceTourId))
      : events;
    if (!showCommunityPosts) {
      eventPool = eventPool.filter((e) => e.eventType !== 'community_post');
    }
    eventPool.forEach((e) => {
      // Attach tour color to each event so views can use it
      const colored = tourColorById[e.sourceTourId]
        ? { ...e, color: e.color || tourColorById[e.sourceTourId] }
        : e;
      if (!m[e.date]) m[e.date] = [];
      m[e.date].push(colored);
    });
    Object.keys(m).forEach((k) => {
      m[k].sort((a, b) => a.sortOrder - b.sortOrder);
    });
    return m;
  }, [events, hiddenTourIds, showCommunityPosts, tourColorById]);
  const selectedDayEventsRaw = selectedDateKey ? eventsByDate[selectedDateKey] || [] : [];
  const selectedDayEvents = selectedDayEventsRaw.filter((e) => e.eventType !== 'tour_day');
  const selectedDayVenueName = useMemo(() => {
    const fromTourMap = selectedDateKey ? String(tourDayMetaByDate[selectedDateKey]?.venueName || '').trim() : '';
    const raw = fromTourMap || selectedDayEvents.map((e) => String(e.dayVenueSummary || '').trim()).find(Boolean) || '';
    if (!raw) return 'VENUE';
    return raw.split(',')[0].trim() || raw;
  }, [selectedDateKey, selectedDayEvents, tourDayMetaByDate]);
  const nextVenueNameForAftershow = useMemo(() => {
    if (!selectedDateKey) return '';
    const mapDates = Object.keys(tourDayMetaByDate).sort();
    const nextMapDate = mapDates.find((d) => d > selectedDateKey);
    if (nextMapDate) return String(tourDayMetaByDate[nextMapDate]?.venueName || '').trim();
    const pool = (selectedTourFilterId ? events.filter((e) => e.sourceTourId === selectedTourFilterId) : events)
      .filter((e) => String(e.dayVenueSummary || '').trim())
      .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
    const next = pool.find((e) => String(e.date || '') > selectedDateKey);
    if (!next) return '';
    return String(next.dayVenueSummary || '').split(',')[0].trim();
  }, [events, selectedDateKey, selectedTourFilterId, tourDayMetaByDate]);
  const selectedTourFilterName = useMemo(() => {
    if (!selectedTourFilterId) return 'Not selected';
    return tourFolders.find((t) => t.id === selectedTourFilterId)?.name || 'Not selected';
  }, [selectedTourFilterId, tourFolders]);
  const ownerDisplayName = useMemo(() => {
    const first = String(userData?.firstName || '').trim();
    const last = String(userData?.lastName || '').trim();
    const full = `${first} ${last}`.trim();
    return full || String(userData?.username || userData?.email || 'Tour owner').trim();
  }, [userData]);
  const { members: v2PartyMembers } = useTourPartyMembers({
    ownerId: tourOwnerId,
    ownerName: ownerDisplayName,
    ownerEmail: userData?.email,
    ownerPhotoURL: userData?.photoURL,
    tourId: selectedTourFilterId,
  });
  const v2TravelSummary = useMemo(() => {
    const { dayIndex } = findTourDayByDate(selectedTourFolder?.tour, selectedDateKey);
    return getInterDayTravelSummary(selectedTourFolder?.tour, dayIndex);
  }, [selectedTourFolder, selectedDateKey]);
  const v2GroundLine = useMemo(() => {
    const summary = v2TravelSummary;
    if (!summary) return '';
    return `${summary.fromLabel} → ${summary.toLabel}`;
  }, [v2TravelSummary]);
  const v2NotesDiverge = useMemo(() => {
    const notes = String(v2DayTabs.NOTES || daySheetNotes || '');
    const time = String(v2TravelSummary?.timeLabel || '').trim();
    if (!time || !notes.includes('AFTERSHOW')) return false;
    return !notes.includes(time);
  }, [v2DayTabs, daySheetNotes, v2TravelSummary]);
  const v2BusCallTime = useMemo(() => {
    const { day } = findTourDayByDate(selectedTourFolder?.tour, selectedDateKey);
    return String(day?.busCall?.time || '').trim();
  }, [selectedTourFolder, selectedDateKey]);
  const v2ChatCount = useMemo(() => countChatMessages(v2ChatDoc), [v2ChatDoc]);
  const v2GuestCount = useMemo(() => {
    const raw = v2DayTabs['GUEST LIST'] || '';
    const n = parseSetlistText(raw).songs?.filter((s) => String(s.title || '').trim() || (s.lines || []).length).length || 0;
    return n || '';
  }, [v2DayTabs]);

  const selectedTourShowDateKeys = useMemo(() => {
    if (!selectedTourFilterId) return [];
    const folder = tourFolders.find((t) => t.id === selectedTourFilterId);
    const fromFolder = getTourShowDateKeys(folder?.tour?.days);
    if (fromFolder.length) return fromFolder;
    return getTourShowDateKeysFromEvents(events, selectedTourFilterId);
  }, [selectedTourFilterId, tourFolders, events]);

  const selectedDateHasTourDoor = useMemo(
    () =>
      Boolean(
        selectedTourFilterId &&
          selectedDateKey &&
          selectedTourShowDateKeys.includes(selectedDateKey)
      ),
    [selectedTourFilterId, selectedDateKey, selectedTourShowDateKeys]
  );

  const isTourShowDateKey = useCallback(
    (tourId, dateKey) => {
      if (!tourId || !dateKey) return false;
      const folder = tourFolders.find((t) => t.id === tourId);
      const fromFolder = getTourShowDateKeys(folder?.tour?.days);
      if (fromFolder.length) return fromFolder.includes(dateKey);
      return getTourShowDateKeysFromEvents(events, tourId).includes(dateKey);
    },
    [tourFolders, events]
  );

  const syncTabStateForDaySelection = useCallback(
    (dateKey, tourIdOverride) => {
      const tourId = tourIdOverride ?? selectedTourFilterId;
      // Flag-off: keep a content tab active so in-cell editing is available.
      // Master Tour Calendar: stay on the month poster (Claude has no auto-NOTES).
      if (tourId) {
        if (!masterTourUi) setActiveTopTab((prev) => prev || 'NOTES');
        return;
      }
      setActiveTopTab(null);
      setRiderSubtab('DAILY');
    },
    [selectedTourFilterId, masterTourUi]
  );

  // Flag-off only: default to NOTES so in-cell editing turns on without a day click.
  useEffect(() => {
    if (selectedTourFilterId && !masterTourUi) {
      setActiveTopTab((prev) => prev || 'NOTES');
    }
  }, [selectedTourFilterId, masterTourUi]);

  useEffect(() => {
    if (masterTourUi && activeTopTab === 'DAY SHEET') setActiveTopTab(null);
  }, [masterTourUi, activeTopTab]);

  useEffect(() => {
    if (masterTourUi && calView === 'year') setCalView('month');
  }, [masterTourUi, calView]);

  const V2_DOC_NAV_HEIGHT = 48;
  const topTabsBarHeight = selectedTourFilterId || selectedDateHasTourDoor
    ? (masterTourUi ? V2_DOC_NAV_HEIGHT : 38)
    : 0;
  const v2WeekBarHeight = 0;
  const v2ToolsBarHeight = masterTourUi ? 44 : 0;
  const desktopCalendarBodyHeight = Math.max(
    320,
    windowHeight
      - headerHeight
      - calendarToolbarHeight
      - v2WeekBarHeight
      - topTabsBarHeight
      - v2ToolsBarHeight
      - 8
  );

  useEffect(() => {
    if (!uid || !selectedTourFilterId || !tourOwnerId || !permissionTourId) {
      setTourShareSettings(null);
      return undefined;
    }
    const db = getFirestore();
    return subscribeTourShareSettings(db, tourOwnerId, permissionTourId, setTourShareSettings);
  }, [tourOwnerId, permissionTourId]);

  const shareScheduleIndicatorsByDate = useMemo(
    () =>
      tourShareSettings && selectedTourFilterId
        ? buildShareScheduleIndicators({
            settings: tourShareSettings,
            showDateKeys: selectedTourShowDateKeys,
          })
        : {},
    [tourShareSettings, selectedTourFilterId, selectedTourShowDateKeys]
  );

  const tourPanelLabel = useMemo(() => {
    const total = tourFolders.length;
    const hidden = hiddenTourIds.size;
    const visible = total - hidden;
    if (selectedTourFilterId) {
      const name = tourFolders.find((t) => t.id === selectedTourFilterId)?.name || 'Tour';
      return `Managing: ${name}`;
    }
    if (total === 0) return 'Tours';
    if (hidden === 0) return `All ${total} tour${total !== 1 ? 's' : ''}`;
    if (visible === 0) return 'No tours visible';
    return `${visible} of ${total} tours`;
  }, [tourFolders, hiddenTourIds, selectedTourFilterId]);

  const calendarViewRange = useMemo(
    () => getCalendarViewDateRange(calView, cursor, cursorDate),
    [calView, cursor, cursorDate]
  );
  const [todayDateKey, setTodayDateKey] = useState(() => toDateKey(new Date()));
  useEffect(() => {
    // Keep "today" current for a tab left open overnight. A minute poll plus a wake/focus check
    // survives sleep and clock changes that would break a single midnight timer.
    const refresh = () => setTodayDateKey((prev) => {
      const next = toDateKey(new Date());
      return next === prev ? prev : next;
    });
    const timer = setInterval(refresh, 60000);
    const onVisible = () => { if (typeof document === 'undefined' || document.visibilityState !== 'hidden') refresh(); };
    if (typeof document !== 'undefined' && document.addEventListener) document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      if (typeof document !== 'undefined' && document.removeEventListener) document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  const sortedTourPickerTours = useMemo(
    () => sortToursForCalendarView(tourFolders, calendarViewRange, todayDateKey),
    [tourFolders, calendarViewRange, todayDateKey]
  );
  const filteredTourPickerTours = useMemo(
    () => filterToursForPicker(sortedTourPickerTours, tourPickerFilter, hiddenTourIds),
    [sortedTourPickerTours, tourPickerFilter, hiddenTourIds]
  );
  const tourPickerFilterCounts = useMemo(() => {
    const counts = {};
    TOUR_PICKER_FILTERS.forEach((f) => {
      counts[f.id] = countToursForPickerFilter(sortedTourPickerTours, f.id, hiddenTourIds);
    });
    return counts;
  }, [sortedTourPickerTours, hiddenTourIds]);
  const tourPickerDisplayGroups = useMemo(() => {
    if (tourPickerFilter !== 'all') {
      return [{ key: 'flat', title: '', items: filteredTourPickerTours }];
    }
    return groupToursForPickerDisplay(filteredTourPickerTours, calendarViewRange.label);
  }, [filteredTourPickerTours, tourPickerFilter, calendarViewRange.label]);

  const isRiderTab = activeTopTab === 'RIDER';
  const isStageTab = activeTopTab === 'STAGE';
  const isDaySheetTab = activeTopTab === 'DAY SHEET';
  const isRiderFullMode = isRiderTab && riderSubtab === 'FULL';
  const riderFullIsImage = useMemo(
    () => /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(String(riderFullDocUrl || '')),
    [riderFullDocUrl]
  );
  const riderFullIsPdf = useMemo(
    () => /\.pdf(\?|#|$)/i.test(String(riderFullDocUrl || '')),
    [riderFullDocUrl]
  );
  const activeCalendarTabKey = useMemo(() => {
    if (!activeTopTab) return null;
    if (isRiderTab) return riderSubtab === 'DAILY' ? 'RIDER_DAILY' : 'RIDER_FULL';
    if (isStageTab || isDaySheetTab) return null;
    return activeTopTab;
  }, [activeTopTab, isDaySheetTab, isRiderTab, isStageTab, riderSubtab]);
  const calendarTopTabsVisible = useMemo(() => {
    const tabs = getViewableCalendarTabs(uid, tourOwnerId, tourShareSettings, CALENDAR_TOP_TABS);
    if (!masterTourUi) return tabs;
    return [];
  }, [uid, tourOwnerId, tourShareSettings, masterTourUi]);
  // Master Tour pages are canvases (activeTopTab is usually null), so gate by the canvas's permission key.
  const activeCanvasAccess = useMemo(() => {
    if (!masterTourUi || !selectedTourFilterId) return { canView: true, canEdit: true };
    return resolveV2CanvasAccess(uid, tourOwnerId, v2Canvas, tourShareSettings);
  }, [masterTourUi, uid, tourOwnerId, v2Canvas, tourShareSettings, selectedTourFilterId]);
  const canViewV2Canvas = useCallback(
    (canvasId) => !selectedTourFilterId || resolveV2CanvasAccess(uid, tourOwnerId, canvasId, tourShareSettings).canView,
    [uid, tourOwnerId, tourShareSettings, selectedTourFilterId]
  );
  const canEditActiveCalendarTab = useMemo(() => {
    if (masterTourUi && selectedTourFilterId && v2Canvas) return activeCanvasAccess.canEdit;
    if (!activeTopTab || !selectedTourFilterId) return true;
    if (isRiderTab) return canEditCalendarTab(uid, tourOwnerId, 'RIDER', tourShareSettings);
    return canEditCalendarTab(uid, tourOwnerId, activeTopTab, tourShareSettings);
  }, [uid, tourOwnerId, tourShareSettings, activeTopTab, selectedTourFilterId, isRiderTab, masterTourUi, v2Canvas, activeCanvasAccess]);
  const supportsDayApplyScope = Boolean(
    selectedTourFilterId &&
    activeCalendarTabKey &&
    !TABS_WITHOUT_DAY_APPLY_SCOPE.has(activeCalendarTabKey)
  );
  const isCalendarMode = Boolean(activeCalendarTabKey);
  const gridEditEnabled = Boolean(selectedTourFilterId && allowGridInlineEditAllDays && activeTopTab);
  const getAvailableShareSectionsForDate = useCallback(
    (dateKey) => {
      const key = String(dateKey || '').trim();
      if (!key) return [];
      const dayEvents = eventsByDate[key] || [];
      const dayTabText = String(tabGridDraftByDate[key] || '').trim();
      const dayFiles = dayFilesByDate[key] || [];
      const filesByType = {
        files: dayFiles.filter((f) => normalizeDayFileType(f.fileType) === 'files').length,
        stage: dayFiles.filter((f) => normalizeDayFileType(f.fileType) === 'stage').length,
        riderFull: dayFiles.filter((f) => normalizeDayFileType(f.fileType) === 'riderFull').length,
      };
      const available = [];
      if (dayEvents.map((e) => String(e.dayVenueSummary || '').trim()).filter(Boolean).length) available.push('VENUES');
      DAY_SHARE_OPTIONS.forEach((opt) => {
        if (opt === 'VENUES') return;
        if (opt === 'RIDER') {
          if (dayTabText || filesByType.riderFull > 0) available.push(opt);
          return;
        }
        if (opt === 'STAGE') {
          if (filesByType.stage > 0) available.push(opt);
          return;
        }
        if (opt === 'FILES') {
          if (filesByType.files > 0) available.push(opt);
          return;
        }
        if (opt === 'DAY SHEET') {
          const hasDaySheet = Boolean(String(daySheetNotes || '').trim() || daySheetBannerUrl || daySheetBgUrl);
          if (hasDaySheet) available.push(opt);
          return;
        }
        if (dayTabText) available.push(opt);
      });
      return Array.from(new Set(available));
    },
    [dayFilesByDate, daySheetBannerUrl, daySheetBgUrl, daySheetNotes, eventsByDate, tabGridDraftByDate]
  );
  const openDayShareModal = useCallback(
    async (dateKey) => {
      const key = String(dateKey || '').trim();
      if (!key) return;
      const seed = getAvailableShareSectionsForDate(key);
      setDayShareAvailableOptions(seed);
      setDayShareSelections(seed.length ? seed : ['RIDER']);
      setDayShareModal({ open: true, dateKey: key });
      if (!uid || !selectedTourFilterId) return;
      try {
        const db = getFirestore();
        const entriesSnap = await getDocs(
          query(
            collection(db, 'users', dataUid, 'calendarTabEntries'),
            where('tourId', '==', dataTourId),
            where('date', '==', key)
          )
        );
        const available = new Set(seed);
        let hasSchedule = false;
        entriesSnap.forEach((d) => {
          const row = d.data() || {};
          const tabKey = String(row.tabKey || '').trim();
          const text = String(row.text || '').trim();
          if (!text) return;
          if (tabKey === 'RIDER_DAILY' || tabKey === 'RIDER_FULL') available.add('RIDER');
          if (tabKey === 'SCHEDULE') {
            available.add('SCHEDULE');
            hasSchedule = true;
          }
          if (CALENDAR_TOP_TABS.includes(tabKey)) available.add(tabKey);
        });
        const daySheetSnap = await getDoc(doc(db, 'users', uid, 'calendarDaySheets', `${selectedTourFilterId}__${key}`));
        if (daySheetSnap.exists()) {
          const row = daySheetSnap.data() || {};
          if (String(row.aftershowText || '').trim() || String(row.bannerUrl || '').trim() || String(row.backgroundUrl || '').trim()) {
            available.add('DAY SHEET');
          }
        }
        if (hasSchedule) available.add('DAY SHEET');
        const nextOptions = DAY_SHARE_OPTIONS.filter((opt) => available.has(opt));
        setDayShareAvailableOptions(nextOptions);
        setDayShareSelections((prev) => {
          const kept = prev.filter((x) => nextOptions.includes(x));
          if (kept.length) return kept;
          if (!prev.length) return [];
          return nextOptions;
        });
      } catch (e) {
        console.error('share day availability load', e);
      }
    },
    [getAvailableShareSectionsForDate, selectedTourFilterId, dataUid, dataTourId, uid]
  );
  const generatedDayShareLink = useMemo(() => {
    if (!uid || !selectedTourFilterId || !dayShareModal.dateKey || dayShareSelections.length === 0) return '';
    const baseUrl = resolveAppBaseUrl();
    const modules = encodeURIComponent(dayShareSelections.join(','));
    return `${baseUrl}/calendar/day/${encodeURIComponent(dayShareModal.dateKey)}?uid=${encodeURIComponent(dataUid)}&tour=${encodeURIComponent(
      dataTourId
    )}&modules=${modules}`;
  }, [uid, selectedTourFilterId, dataUid, dataTourId, dayShareModal.dateKey, dayShareSelections]);

  useEffect(() => {
    if (!dayShareModal.open || !uid || !selectedTourFilterId || !dayShareModal.dateKey || dayShareSelections.length === 0) {
      setManagedDayShareUrl('');
      return undefined;
    }
    let cancelled = false;
    setDayShareSaving(true);
    upsertDayShareLink(getFirestore(), dataUid, {
      tourId: dataTourId,
      tourName: selectedTourFilterName,
      dateKey: dayShareModal.dateKey,
      modules: dayShareSelections,
      baseUrl: resolveAppBaseUrl(),
    })
      .then(({ url }) => {
        if (!cancelled) setManagedDayShareUrl(url);
      })
      .catch((err) => {
        console.warn('upsertDayShareLink', err);
        if (!cancelled) setManagedDayShareUrl('');
      })
      .finally(() => {
        if (!cancelled) setDayShareSaving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    dayShareModal.open,
    dayShareModal.dateKey,
    uid,
    selectedTourFilterId,
    selectedTourFilterName,
    dayShareSelections,
  ]);

  const activeDayShareLink = managedDayShareUrl || generatedDayShareLink;

  const selectDate = useCallback((dateKey) => {
    setSelectedDateKey(dateKey);
    setSelectedEventId(null);
  }, []);

  const applyTourStartFocus = useCallback((folder) => {
    const focus = tourStartFocus(folder, events);
    if (!focus) return null;
    selectDate(focus.dateKey);
    setCursorDate(focus.date);
    setCursor({ y: focus.year, m: focus.month });
    if (folder?.id) syncTabStateForDaySelection(focus.dateKey, folder.id);
    return focus;
  }, [events, selectDate, syncTabStateForDaySelection]);

  const navigateSelectedDay = useCallback(
    (deltaDays) => {
      if (!selectedDateKey || !deltaDays) return;
      const current = parseStrictDateKey(selectedDateKey);
      if (!current) return;
      const next = new Date(current);
      next.setDate(next.getDate() + deltaDays);
      const nextKey = toDateKey(next);
      selectDate(nextKey);
      setCursorDate(next);
      setCursor({ y: next.getFullYear(), m: next.getMonth() });
      pendingDefaultTourDateRef.current = null;
      const nextTourId = pickTourIdForDate(tourDayMetaByDate[nextKey]?.tours || [], selectedTourFilterId);
      if (nextTourId !== selectedTourFilterId) setSelectedTourFilterId(nextTourId);
      syncTabStateForDaySelection(nextKey, nextTourId);
    },
    [selectDate, selectedDateKey, selectedTourFilterId, tourDayMetaByDate, syncTabStateForDaySelection]
  );

  const showToast = useCallback((message, type = 'success') => {
    setMessageModalRaw(null);
    setToast({ message, type, id: Date.now() });
  }, []);

  useEffect(() => {
    if (!uid || !selectedTourFilterId || !tourShareSettings) return undefined;
    const db = getFirestore();
    processDueShareReminders({
      db,
      ownerId: uid,
      ownerEmail: userData?.email,
      ownerName: ownerDisplayName,
      tourId: selectedTourFilterId,
      tourName: selectedTourFilterName,
      settings: tourShareSettings,
      showDateKeys: selectedTourShowDateKeys,
      onInApp: (message) => showToast(message, 'warning'),
    }).catch((err) => console.warn('processDueShareReminders', err));
    return undefined;
  }, [
    uid,
    selectedTourFilterId,
    tourShareSettings,
    selectedTourShowDateKeys,
    ownerDisplayName,
    selectedTourFilterName,
    userData?.email,
    showToast,
  ]);

  const setMessageModal = useCallback((payload) => {
    if (!payload) {
      setMessageModalRaw(null);
      return;
    }
    const blob = `${payload.title || ''} ${payload.text || ''}`.toLowerCase();
    const useToast =
      payload.variant === 'success' ||
      (payload.variant === 'error' &&
        (/save|saved|attach|upload|invitation|rider|day sheet|file removed|imported|removed/.test(blob)));
    if (useToast) {
      showToast(
        payload.text || payload.title || (payload.variant === 'error' ? 'Something went wrong.' : 'Done.'),
        payload.variant === 'error' ? 'error' : 'success'
      );
      return;
    }
    setMessageModalRaw(payload);
  }, [showToast]);

  const goToToday = useCallback(() => {
    const today = new Date();
    const key = toDateKey(today);
    setSelectedDateKey(key);
    setSelectedEventId(null);
    setCursorDate(today);
    setCursor({ y: today.getFullYear(), m: today.getMonth() });
    pendingDefaultTourDateRef.current = key;
    const nextTourId = pickTourIdForDate(tourDayMetaByDate[key]?.tours || [], selectedTourFilterId);
    if (nextTourId !== selectedTourFilterId) setSelectedTourFilterId(nextTourId);
    syncTabStateForDaySelection(key, nextTourId);
  }, [selectedTourFilterId, syncTabStateForDaySelection, tourDayMetaByDate]);

  const goToTodayRef = useRef(goToToday);
  goToTodayRef.current = goToToday;

  // Folders and events often land after the first "today" jump. Keep pending until
  // both have loaded so a one-tour day (e.g. Hawks today) is selected like a click.
  useEffect(() => {
    const key = pendingDefaultTourDateRef.current;
    if (!key || key !== selectedDateKey) return;
    if (loadingTours || loading) return;
    if (!tourFolders.length) return;
    const toursOnDay = tourDayMetaByDate[key]?.tours || [];
    const nextTourId = pickTourIdForDate(toursOnDay, selectedTourFilterId);
    pendingDefaultTourDateRef.current = null;
    if (nextTourId !== selectedTourFilterId) setSelectedTourFilterId(nextTourId);
    if (nextTourId) syncTabStateForDaySelection(key, nextTourId);
  }, [
    loading,
    loadingTours,
    selectedDateKey,
    selectedTourFilterId,
    syncTabStateForDaySelection,
    tourDayMetaByDate,
    tourFolders.length,
  ]);

  // Only jump to today when the screen gains focus — not when goToToday is recreated
  // (e.g. after month navigation reloads events and rebuilds syncTabStateForDaySelection).
  useFocusEffect(
    useCallback(() => {
      goToTodayRef.current();
    }, [])
  );

  useEffect(() => {
    setSelectedEventId(null);
  }, [cursor.y, cursor.m]);

  const openNew = () => {
    const seed = selectedDateKey || toDateKey(new Date());
    const seedDate = parseStrictDateKey(seed) || new Date();
    setEditingId(null);
    setFormTitle('');
    setFormDate(seed);
    setFormEndDate(seed);
    setFormStartTime('');
    setFormEndTime('');
    setDatePickerCursor({ y: seedDate.getFullYear(), m: seedDate.getMonth() });
    setDatePickTarget('start');
    setFormNotes('');
    setFormColor(BRAND);
    setFormCommunityPostId(null);
    setModalOpen(true);
  };

  const openEdit = (ev) => {
    const seedDate = parseStrictDateKey(ev.date) || new Date();
    setEditingId(ev.id);
    setFormTitle(ev.title);
    setFormDate(ev.date);
    setFormEndDate(ev.date);
    setFormStartTime(ev.startTime || '');
    setFormEndTime(ev.endTime || '');
    setDatePickerCursor({ y: seedDate.getFullYear(), m: seedDate.getMonth() });
    setDatePickTarget('start');
    setFormNotes(ev.notes);
    setFormColor(ev.color || BRAND);
    setFormCommunityPostId(ev.communityPostId || null);
    setModalOpen(true);
  };

  const saveEvent = async () => {
    const startKey = String(formDate || '').trim();
    const endKey = String(formEndDate || '').trim() || startKey;
    const startDate = parseStrictDateKey(startKey);
    const endDate = parseStrictDateKey(endKey);
    if (!uid || !formTitle.trim() || !startDate || !endDate) {
      setMessageModal({ variant: 'info', title: 'Missing fields', text: 'Title and valid start/end dates are required.' });
      return;
    }
    if (startDate > endDate) {
      setMessageModal({ variant: 'info', title: 'Invalid range', text: 'End date must be the same as or after start date.' });
      return;
    }
    const normalizedStartTime = parseStrictTime(formStartTime);
    const normalizedEndTime = parseStrictTime(formEndTime);
    if (normalizedStartTime === null || normalizedEndTime === null) {
      setMessageModal({
        variant: 'info',
        title: 'Invalid time',
        text: 'Time must use 24-hour format HH:MM (example: 09:30).',
      });
      return;
    }
    const rangeKeys = enumerateDateKeysInclusive(startKey, endKey);
    setSaving(true);
    try {
      const db = getFirestore();
      const col = collection(db, 'users', uid, 'calendarEvents');
      let commTitle = '';
      if (formCommunityPostId) {
        const p = communityPosts.find((c) => c.id === formCommunityPostId);
        commTitle = p?.label || '';
      }
      const base = {
        title: formTitle.trim(),
        date: startKey,
        notes: formNotes.trim(),
        color: formColor,
        communityPostId: formCommunityPostId || null,
        communityPostTitle: commTitle,
        startTime: normalizedStartTime || null,
        endTime: normalizedEndTime || null,
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        const existing = events.find((e) => e.id === editingId);
        await updateDoc(doc(db, 'users', uid, 'calendarEvents', editingId), {
          ...base,
          eventType:
            formCommunityPostId
              ? 'community_post'
              : existing?.eventType === 'tour_day'
                ? 'tour_day'
                : 'custom',
        });
        if (
          existing?.eventType === 'tour_day' &&
          existing.sourceTourId &&
          existing.sourceDayIndex != null &&
          startKey !== existing.date
        ) {
          await syncTourDayScheduledDate(uid, existing.sourceTourId, existing.sourceDayIndex, startKey);
        }
        /** Same color for every `tour_day` row imported from this tour (any month). */
        const stillTourDay =
          !formCommunityPostId && existing?.eventType === 'tour_day' && existing?.sourceTourId;
        const colorChanged = formColor !== (existing?.color || BRAND);
        if (stillTourDay && colorChanged) {
          const qTour = query(
            collection(db, 'users', uid, 'calendarEvents'),
            where('sourceTourId', '==', existing.sourceTourId)
          );
          const tourSnap = await getDocs(qTour);
          const colorBatch = writeBatch(db);
          let colorBatchCount = 0;
          tourSnap.forEach((d) => {
            const raw = d.data();
            if (raw.eventType !== 'tour_day') return;
            if (d.id === editingId) return;
            colorBatch.update(d.ref, { color: formColor, updatedAt: serverTimestamp() });
            colorBatchCount += 1;
          });
          if (colorBatchCount > 0) {
            await colorBatch.commit();
          }
        }
      } else {
        for (const dateKey of rangeKeys) {
          const sameDay = events.filter((e) => e.date === dateKey);
          const nextOrder = sameDay.length ? Math.max(...sameDay.map((e) => e.sortOrder)) + 1 : 0;
          await addDoc(col, {
            ...base,
            date: dateKey,
            eventType: formCommunityPostId ? 'community_post' : 'custom',
            sortOrder: nextOrder,
            createdAt: serverTimestamp(),
          });
        }
      }
      setModalOpen(false);
    } catch (e) {
      console.error(e);
      setMessageModal({ variant: 'error', title: 'Save failed', text: e.message || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = (id) => {
    if (!uid) return;
    setPendingDeleteEventId(id);
  };

  const performDeleteEvent = async () => {
    const id = pendingDeleteEventId;
    if (!uid || !id) {
      setPendingDeleteEventId(null);
      return;
    }
    setPendingDeleteEventId(null);
    try {
      await deleteDoc(doc(getFirestore(), 'users', uid, 'calendarEvents', id));
      if (editingId === id) setModalOpen(false);
      setSelectedEventId(null);
      setMessageModal({ variant: 'success', title: 'Removed', text: 'Event removed from your calendar.' });
    } catch (e) {
      setMessageModal({ variant: 'error', title: 'Could not delete', text: e.message || 'Unknown error' });
    }
  };

  const deleteTourFromCalendar = useCallback(
    (tourId) => {
      if (!uid || !tourId) return;
      setPendingRemoveTourId(tourId);
    },
    [uid]
  );

  const performRemoveTourFromCalendar = async () => {
    const tourId = pendingRemoveTourId;
    if (!uid || !tourId) {
      setPendingRemoveTourId(null);
      return;
    }
    setPendingRemoveTourId(null);
    try {
      const db = getFirestore();
      const qy = query(collection(db, 'users', uid, 'calendarEvents'), where('sourceTourId', '==', tourId));
      const snap = await getDocs(qy);
      const batch = writeBatch(db);
      snap.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      setSelectedEventId(null);
      setMessageModal({
        variant: 'success',
        title: 'Removed',
        text: 'All days of this tour were removed from your calendar. Your tour in Tour Planner is unchanged.',
      });
    } catch (e) {
      setMessageModal({ variant: 'error', title: 'Could not remove tour', text: e.message || 'Unknown error' });
    }
  };

  const openMoveToMonth = useCallback(
    (ev) => {
      const p = parseDateKey(ev.date);
      if (p) {
        setMoveMonthYear(p.getFullYear());
        setMoveMonthIndex(p.getMonth());
      } else {
        setMoveMonthYear(cursor.y);
        setMoveMonthIndex(cursor.m);
      }
      setMoveMonthEvent(ev);
    },
    [cursor.y, cursor.m]
  );

  const performMoveToMonth = async () => {
    if (!uid || !moveMonthEvent) {
      setMoveMonthEvent(null);
      return;
    }
    const ev = moveMonthEvent;
    const newDateKey = moveDateToMonth(ev.date, moveMonthYear, moveMonthIndex);
    if (!newDateKey) {
      setMoveMonthEvent(null);
      setMessageModal({ variant: 'error', title: 'Invalid date', text: 'Could not compute the new date.' });
      return;
    }
    if (newDateKey === ev.date) {
      setMessageModal({ variant: 'info', title: 'Same month', text: 'Pick a different month than the event is already in.' });
      return;
    }
    setMoveMonthEvent(null);
    try {
      await updateDoc(doc(getFirestore(), 'users', uid, 'calendarEvents', ev.id), {
        date: newDateKey,
        updatedAt: serverTimestamp(),
      });
      if (ev.eventType === 'tour_day' && ev.sourceTourId != null && ev.sourceDayIndex != null) {
        await syncTourDayScheduledDate(uid, ev.sourceTourId, ev.sourceDayIndex, newDateKey);
      }
      setSelectedEventId(null);
      setMessageModal({
        variant: 'success',
        title: 'Moved',
        text: `Event date is now ${newDateKey}.`,
      });
    } catch (e) {
      setMessageModal({ variant: 'error', title: 'Could not move event', text: e.message || 'Unknown error' });
    }
  };

  const moveToNextDay = async (ev) => {
    if (!uid) return;
    const next = addDaysToKey(ev.date, 1);
    try {
      await updateDoc(doc(getFirestore(), 'users', uid, 'calendarEvents', ev.id), {
        date: next,
        updatedAt: serverTimestamp(),
      });
      if (ev.eventType === 'tour_day' && ev.sourceTourId != null && ev.sourceDayIndex != null) {
        await syncTourDayScheduledDate(uid, ev.sourceTourId, ev.sourceDayIndex, next);
      }
    } catch (e) {
      setMessageModal({ variant: 'error', title: 'Could not move event', text: e.message || 'Unknown error' });
    }
  };

  const loadTourFolders = useCallback(async () => {
    if (!uid) return;
    setLoadingTours(true);
    try {
      const db = getFirestore();
      const snap = await getDoc(doc(db, 'users', uid));
      const folders = (snap.data() && snap.data().folders) || {};
      const rows = Object.entries(folders)
        .filter(([, f]) => f && f.tour && Array.isArray(f.tour.days))
        .map(([id, f]) => ({
          id,
          name: f.tour.name || f.name || 'Tour',
          tour: f.tour,
          demoTour: Boolean(f.demoTour),
          // Keep share provenance so permission checks resolve the real tour owner.
          ownerId: f.ownerId || f.sharedFrom?.ownerId || null,
          sharedFrom: f.sharedFrom || null,
        }));
      setTourFolders(rows);
    } catch (e) {
      console.error(e);
      setMessageModal({ variant: 'error', title: 'Could not load tours', text: e.message || 'Try again.' });
    } finally {
      setLoadingTours(false);
    }
  }, [uid]);

  const loadSharedTourInvites = useCallback(async () => {
    if (!uid) return;
    setLoadingSharedTours(true);
    try {
      const db = getFirestore();
      const rows = await loadTourInvitesForUser(db, uid, userData?.email);
      const enriched = await Promise.all(rows.map(async (invite) => {
        const snapshot = await fetchSharedTourSnapshot(db, invite);
        const folder = buildSharedTourFolder(invite, snapshot);
        return enrichSharedInviteRow(invite, folder);
      }));
      setSharedTourInvites(enriched);
    } catch (e) {
      console.error(e);
      setSharedTourInvites([]);
    } finally {
      setLoadingSharedTours(false);
    }
  }, [uid, userData?.email]);

  const openTourPicker = useCallback(() => {
    if (!tourFolders.length) loadTourFolders();
    loadSharedTourInvites();
    setTourPickerOpen(true);
  }, [loadTourFolders, loadSharedTourInvites, tourFolders.length]);

  const handleSelectTourForDay = useCallback(
    (tourId) => {
      if (!tourId) return;
      setSelectedTourFilterId(tourId);
      if (selectedDateKey) syncTabStateForDaySelection(selectedDateKey, tourId);
    },
    [selectedDateKey, syncTabStateForDaySelection]
  );

  useEffect(() => {
    loadTourFolders();
  }, [loadTourFolders]);

  useEffect(() => {
    if (selectedTourFilterId && !tourFolders.some((t) => t.id === selectedTourFilterId)) {
      setSelectedTourFilterId(null);
      setActiveTopTab(null);
      setRiderSubtab('DAILY');
    }
  }, [selectedTourFilterId, tourFolders]);

  // Persist hidden tours across sessions
  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cal_hiddenTourIds', JSON.stringify([...hiddenTourIds]));
      }
    } catch {}
  }, [hiddenTourIds]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId || !activeCalendarTabKey || !selectedDateKey) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore();
        const entryId = `${dataTourId}__${activeCalendarTabKey}__${selectedDateKey}`;
        const snap = await getDoc(doc(db, 'users', dataUid, 'calendarTabEntries', entryId));
        if (cancelled) return;
        if (tabEntryDirtyRef.current[selectedDateKey]) return;
        const row = snap.exists() ? snap.data() || {} : {};
        const draft = getTabEntryDraftText(row);
        const published = getTabEntryPublishedText(row);
        setTabGridDraftByDate((prev) => ({
          ...prev,
          [selectedDateKey]: draft,
        }));
        setTabPublishedByDate((prev) => ({
          ...prev,
          [selectedDateKey]: published,
        }));
        tabPublishedBaselineRef.current[selectedDateKey] = published;
        setTabGridStyleByDate((prev) => ({
          ...prev,
          [selectedDateKey]: {
            textColor: String(row.textColor || '#111111'),
            textAlign: String(row.textAlign || 'left'),
            fontSize: Number(row.fontSize) || 13,
          },
        }));
      } catch (err) {
        console.error('calendarTabEntries get', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, selectedTourFilterId, dataUid, dataTourId, activeCalendarTabKey, selectedDateKey]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId || !activeCalendarTabKey) return undefined;
    const db = getFirestore();
    const entriesRef = collection(db, 'users', dataUid, 'calendarTabEntries');
    const qy = query(
      entriesRef,
      where('tourId', '==', dataTourId),
      where('tabKey', '==', activeCalendarTabKey),
      where('date', '>=', monthStartKey),
      where('date', '<=', monthEndKey),
      orderBy('date')
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const texts = {};
        const stylesByDate = {};
        const publishedTexts = {};
        snap.forEach((d) => {
          const row = d.data() || {};
          const dateKey = String(row.date || '');
          if (!dateKey) return;
          const draft = getTabEntryDraftText(row);
          const published = getTabEntryPublishedText(row);
          texts[dateKey] = draft;
          publishedTexts[dateKey] = published;
          tabPublishedBaselineRef.current[dateKey] = published;
          stylesByDate[dateKey] = {
            textColor: String(row.textColor || '#111111'),
            textAlign: String(row.textAlign || 'left'),
            fontSize: Number(row.fontSize) || 13,
          };
        });
        setTabGridDraftByDate((prev) => {
          const next = { ...prev };
          Object.keys(prev).forEach((dateKey) => {
            if (!tabEntryDirtyRef.current[dateKey] && !texts[dateKey]) delete next[dateKey];
          });
          Object.entries(texts).forEach(([dateKey, value]) => {
            if (tabEntryDirtyRef.current[dateKey]) return;
            next[dateKey] = value;
          });
          return next;
        });
        setTabPublishedByDate((prev) => {
          const next = { ...prev };
          Object.keys(prev).forEach((dateKey) => {
            if (!tabEntryDirtyRef.current[dateKey] && !publishedTexts[dateKey]) delete next[dateKey];
          });
          Object.entries(publishedTexts).forEach(([dateKey, value]) => {
            if (tabEntryDirtyRef.current[dateKey]) return;
            next[dateKey] = value;
          });
          return next;
        });
        setTabGridStyleByDate((prev) => {
          const next = { ...prev };
          Object.keys(prev).forEach((dateKey) => {
            if (!tabEntryDirtyRef.current[dateKey] && !stylesByDate[dateKey]) delete next[dateKey];
          });
          Object.entries(stylesByDate).forEach(([dateKey, value]) => {
            if (tabEntryDirtyRef.current[dateKey]) return;
            next[dateKey] = value;
          });
          return next;
        });
      },
      (err) => console.error('calendarTabEntries locked snapshot', err)
    );
    return () => unsub();
  }, [uid, selectedTourFilterId, dataUid, dataTourId, activeTopTab, activeCalendarTabKey, monthStartKey, monthEndKey]);

  useEffect(() => {
    if (!activeCalendarTabKey) return;
    tabEntryDirtyRef.current = {};
    tabPublishedBaselineRef.current = {};
    Object.values(tabDraftSaveTimerRef.current).forEach((t) => clearTimeout(t));
    tabDraftSaveTimerRef.current = {};
    setTabGridDraftByDate({});
    setTabPublishedByDate({});
    setTabGridStyleByDate({});
    setTabDraftSaveState('idle');
  }, [selectedTourFilterId, activeCalendarTabKey, monthStartKey, monthEndKey]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId) {
      setSchedulePublishedByDate({});
      setContactsPublishedByDate({});
      setSetlistsPublishedByDate({});
      return undefined;
    }
    const db = getFirestore();
    const qy = query(
      collection(db, 'users', dataUid, 'calendarTabEntries'),
      where('tourId', '==', dataTourId),
      where('tabKey', '==', 'SCHEDULE'),
      where('date', '>=', monthStartKey),
      where('date', '<=', monthEndKey),
      orderBy('date')
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          const row = d.data() || {};
          const dateKey = String(row.date || '');
          if (!dateKey) return;
          next[dateKey] = getTabEntryPublishedText(row);
        });
        setSchedulePublishedByDate(next);
      },
      (err) => console.error('schedule published snapshot', err)
    );
    return () => unsub();
  }, [uid, selectedTourFilterId, dataUid, dataTourId, monthStartKey, monthEndKey]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId) return undefined;
    const db = getFirestore();
    const qy = query(
      collection(db, 'users', dataUid, 'calendarTabEntries'),
      where('tourId', '==', dataTourId),
      where('tabKey', '==', 'CONTACTS'),
      where('date', '>=', monthStartKey),
      where('date', '<=', monthEndKey),
      orderBy('date')
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          const row = d.data() || {};
          const dateKey = String(row.date || '');
          if (!dateKey) return;
          next[dateKey] = getTabEntryDraftText(row) || getTabEntryPublishedText(row);
        });
        setContactsPublishedByDate(next);
      },
      (err) => console.error('contacts published snapshot', err)
    );
    return () => unsub();
  }, [uid, selectedTourFilterId, dataUid, dataTourId, monthStartKey, monthEndKey]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId) return undefined;
    const db = getFirestore();
    const qy = query(
      collection(db, 'users', dataUid, 'calendarTabEntries'),
      where('tourId', '==', dataTourId),
      where('tabKey', '==', 'SETLIST'),
      where('date', '>=', monthStartKey),
      where('date', '<=', monthEndKey),
      orderBy('date')
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          const row = d.data() || {};
          const dateKey = String(row.date || '');
          if (!dateKey) return;
          next[dateKey] = getTabEntryDraftText(row) || getTabEntryPublishedText(row);
        });
        setSetlistsPublishedByDate(next);
      },
      (err) => console.error('setlists published snapshot', err)
    );
    return () => unsub();
  }, [uid, selectedTourFilterId, dataUid, dataTourId, monthStartKey, monthEndKey]);

  useEffect(() => {
    if (!masterTourUi || !uid || !selectedTourFilterId || !selectedDateKey) {
      setV2DayTabs({});
      return undefined;
    }
    const db = getFirestore();
    const qy = query(
      collection(db, 'users', dataUid, 'calendarTabEntries'),
      where('tourId', '==', dataTourId),
      where('date', '==', selectedDateKey)
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          const row = d.data() || {};
          const tabKey = String(row.tabKey || '');
          if (!tabKey) return;
          next[tabKey] = tabKey === GUEST_CAP_TAB
            ? (getTabEntryPublishedText(row) || getTabEntryDraftText(row))
            : (getTabEntryDraftText(row) || getTabEntryPublishedText(row));
        });
        setV2DayTabs(next);
      },
      (err) => console.error('v2 day tabs snapshot', err)
    );
    return () => unsub();
  }, [masterTourUi, uid, selectedTourFilterId, dataUid, dataTourId, selectedDateKey]);

  useEffect(() => {
    if (!masterTourUi || !tourOwnerId || !permissionTourId || !selectedDateKey) return undefined;
    const db = getFirestore();
    const ref = doc(
      db,
      'users',
      tourOwnerId,
      'calendarTabEntries',
      v2TabEntryId(permissionTourId, 'SETLIST', selectedDateKey)
    );
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        const row = snap.data() || {};
        const text = getTabEntryDraftText(row) || getTabEntryPublishedText(row) || '';
        setV2DayTabs((prev) => (prev.SETLIST === text ? prev : { ...prev, SETLIST: text }));
      },
      (err) => console.error('owner setlist snapshot', err)
    );
    return () => unsub();
  }, [masterTourUi, tourOwnerId, permissionTourId, selectedDateKey]);

  useEffect(() => {
    if (!masterTourUi || !uid || !selectedTourFilterId) {
      setV2ChatDoc('');
      setV2MerchCatalog('');
      setV2LegExpenses('');
      setV2RiderAreas('');
      return undefined;
    }
    const db = getFirestore();
    const chatRef = doc(db, 'users', dataUid, 'calendarTabEntries', v2TabEntryId(dataTourId, V2_CHAT_TAB, V2_CHAT_DATE));
    const merchCatRef = doc(db, 'users', dataUid, 'calendarTabEntries', v2TabEntryId(dataTourId, 'MERCH', V2_MERCH_CATALOG_DATE));
    const legRef = doc(db, 'users', dataUid, 'calendarTabEntries', v2TabEntryId(dataTourId, 'EXPENSES_LEG', 'leg'));
    const riderAreasRef = doc(db, 'users', dataUid, 'calendarTabEntries', v2TabEntryId(dataTourId, V2_RIDER_AREAS_TAB, V2_RIDER_AREAS_DATE));
    const unsubChat = onSnapshot(
      chatRef,
      (snap) => {
        const row = snap.exists() ? snap.data() : {};
        setV2ChatDoc(getTabEntryDraftText(row) || getTabEntryPublishedText(row) || '');
      },
      (err) => console.error('v2 chat snapshot', err)
    );
    const unsubMerchCat = onSnapshot(
      merchCatRef,
      (snap) => {
        const row = snap.exists() ? snap.data() : {};
        setV2MerchCatalog(getTabEntryDraftText(row) || getTabEntryPublishedText(row) || '');
      },
      (err) => console.error('v2 merch catalog snapshot', err)
    );
    const unsubLeg = onSnapshot(
      legRef,
      (snap) => {
        const row = snap.exists() ? snap.data() : {};
        setV2LegExpenses(getTabEntryDraftText(row) || getTabEntryPublishedText(row) || '');
      },
      (err) => console.error('v2 leg expenses snapshot', err)
    );
    const unsubRiderAreas = onSnapshot(
      riderAreasRef,
      (snap) => {
        const row = snap.exists() ? snap.data() : {};
        const incoming = getTabEntryDraftText(row) || getTabEntryPublishedText(row) || '';
        const stamp = `${V2_RIDER_AREAS_TAB}::${V2_RIDER_AREAS_DATE}`;
        if (v2PersistTimerRef.current[stamp]) return;
        setV2RiderAreas((prev) => (prev === incoming ? prev : incoming));
      },
      (err) => console.error('v2 rider areas snapshot', err)
    );
    return () => {
      unsubChat();
      unsubMerchCat();
      unsubLeg();
      unsubRiderAreas();
    };
  }, [masterTourUi, uid, selectedTourFilterId, dataUid, dataTourId]);

  // Tour-wide money rows (every show's settlement, every day ledger, the leg ledger, the budget) for the P&L panel and reminders.
  const [tourFinance, setTourFinance] = useState({ settlements: {}, ledgers: {}, leg: '', budget: '' });
  const financeActive = masterTourUi && (v2Canvas === 'settle' || v2Canvas === 'expenses');
  useEffect(() => {
    setTourFinance({ settlements: {}, ledgers: {}, leg: '', budget: '' });
    if (!financeActive || !dataUid || !dataTourId) return undefined;
    const qy = query(
      collection(getFirestore(), 'users', dataUid, 'calendarTabEntries'),
      where('tourId', '==', dataTourId),
      where('tabKey', 'in', ['SETTLEMENT', 'EXPENSES', 'EXPENSES_LEG', V2_BUDGET_TAB])
    );
    return onSnapshot(
      qy,
      (snap) => {
        const next = { settlements: {}, ledgers: {}, leg: '', budget: '' };
        snap.forEach((d) => {
          const row = d.data() || {};
          const text = getTabEntryDraftText(row) || getTabEntryPublishedText(row) || '';
          const date = String(row.date || '');
          if (!text || !date) return;
          if (row.tabKey === 'SETTLEMENT') next.settlements[date] = text;
          else if (row.tabKey === 'EXPENSES') next.ledgers[date] = text;
          else if (row.tabKey === 'EXPENSES_LEG') next.leg = text;
          else if (row.tabKey === V2_BUDGET_TAB) next.budget = text;
        });
        setTourFinance(next);
      },
      (err) => console.error('tour finance snapshot', err)
    );
  }, [financeActive, dataUid, dataTourId]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId || !dayFilesModal.open || !dayFilesModal.dateKey) {
      setDayFiles([]);
      return undefined;
    }
    const db = getFirestore();
    const qy = query(
      collection(db, 'users', uid, 'calendarDayFiles'),
      where('tourId', '==', selectedTourFilterId),
      where('date', '==', dayFilesModal.dateKey),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = [];
        snap.forEach((d) => {
          const row = d.data() || {};
          rows.push({
            id: d.id,
            name: String(row.name || 'File'),
            url: String(row.url || ''),
            fileType: normalizeDayFileType(row.fileType),
          });
        });
        setDayFiles(rows.filter((x) => x.url && normalizeDayFileType(x.fileType) === 'files'));
      },
      (err) => console.error('calendarDayFiles snapshot', err)
    );
    return () => unsub();
  }, [uid, selectedTourFilterId, dayFilesModal.open, dayFilesModal.dateKey]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId || !selectedDateKey) {
      setDaySheetBannerUrl('');
      setDaySheetBgUrl('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore();
        const docId = `${selectedTourFilterId}__${selectedDateKey}`;
        const snap = await getDoc(doc(db, 'users', uid, 'calendarDaySheets', docId));
        if (!snap.exists() || cancelled) {
          if (!cancelled) {
            setDaySheetBannerUrl('');
            setDaySheetBgUrl('');
          }
          return;
        }
        const row = snap.data() || {};
        if (!cancelled) {
          setDaySheetBannerUrl(String(row.bannerUrl || ''));
          setDaySheetBgUrl(String(row.backgroundUrl || ''));
          if (String(row.aftershowText || '').trim()) {
            setDaySheetNotes(String(row.aftershowText || ''));
          }
          setTimeout(() => {
            if (cancelled) return;
            daySheetPublicationBaselineRef.current = {
              notes: String(daySheetNotesRef.current || ''),
              schedule: serializeScheduleDoc({ lines: daySheetScheduleRowsRef.current || [] }),
            };
            setDaySheetPublicationBaselineTick((t) => t + 1);
          }, 0);
        }
      } catch (e) {
        console.error('calendarDaySheets load', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, selectedTourFilterId, selectedDateKey]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId || !selectedDateKey) {
      setDaySheetScheduleRows([]);
      setDaySheetScheduleDirty(false);
      setDaySheetSeededFromDefaults(false);
      return;
    }
    const gen = ++daySheetLoadGenRef.current;
    let cancelled = false;
    const bumpPublicationBaseline = (lines) => {
      queueMicrotask(() => {
        if (cancelled || gen !== daySheetLoadGenRef.current) return;
        daySheetPublicationBaselineRef.current = {
          notes: String(daySheetNotesRef.current || ''),
          schedule: serializeScheduleDoc({ lines: Array.isArray(lines) ? lines : [] }),
        };
        setDaySheetPublicationBaselineTick((t) => t + 1);
      });
    };
    (async () => {
      try {
        const db = getFirestore();
        const entryId = `${dataTourId}__SCHEDULE__${selectedDateKey}`;
        const snap = await getDoc(doc(db, 'users', dataUid, 'calendarTabEntries', entryId));
        if (cancelled || gen !== daySheetLoadGenRef.current) return;
        if (!snap.exists()) {
          const defaults = defaultDaySheetScheduleRows();
          setDaySheetScheduleRows(defaults);
          setDaySheetScheduleDirty(false);
          setDaySheetSeededFromDefaults(true);
          bumpPublicationBaseline(defaults);
          return;
        }
        const raw = String(snap.data()?.text || '');
        const parsed = parseScheduleText(raw);
        const lines = parsed.lines || [];
        const serialized = serializeScheduleDoc({ lines }).trim();
        if (!serialized) {
          setDaySheetScheduleRows([]);
          setDaySheetScheduleDirty(false);
          setDaySheetSeededFromDefaults(false);
          bumpPublicationBaseline([]);
          return;
        }
        setDaySheetScheduleRows(lines);
        setDaySheetScheduleDirty(false);
        setDaySheetSeededFromDefaults(false);
        bumpPublicationBaseline(lines);
      } catch (e) {
        console.error('day sheet schedule load', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, selectedTourFilterId, dataUid, dataTourId, selectedDateKey]);

  useEffect(() => {
    if (activeTopTab !== 'SCHEDULE' || !selectedDateKey) return undefined;
    if (!Object.prototype.hasOwnProperty.call(tabGridDraftByDate, selectedDateKey)) return undefined;
    const raw = tabGridDraftByDate[selectedDateKey];
    const lines = parseScheduleText(String(raw)).lines || [];
    setDaySheetScheduleRows((prev) => {
      try {
        if (JSON.stringify(prev) === JSON.stringify(lines)) return prev;
      } catch {
        /* ignore */
      }
      return lines;
    });
    setDaySheetScheduleDirty(false);
    return undefined;
  }, [activeTopTab, selectedDateKey, tabGridDraftByDate]);

  useEffect(() => {
    if (!selectedDateKey) return;
    if (String(daySheetNotes || '').trim()) return;
    const drive = v2TravelSummary?.timeLabel || '<drive time>';
    const fallback = nextVenueNameForAftershow
      ? `AFTERSHOW, DRIVE ${drive} TO ${nextVenueNameForAftershow.toUpperCase()} FOR A SHOW DAY`
      : 'AFTERSHOW...';
    setDaySheetNotes(fallback);
    const tid = setTimeout(() => {
      daySheetPublicationBaselineRef.current = {
        notes: String(fallback),
        schedule: serializeScheduleDoc({ lines: daySheetScheduleRowsRef.current || [] }),
      };
      setDaySheetPublicationBaselineTick((t) => t + 1);
    }, 0);
    return () => clearTimeout(tid);
  }, [daySheetNotes, nextVenueNameForAftershow, selectedDateKey, v2TravelSummary]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId) {
      setDayFilesByDate({});
      return undefined;
    }
    const db = getFirestore();
    const qy = query(
      collection(db, 'users', uid, 'calendarDayFiles'),
      where('tourId', '==', selectedTourFilterId),
      where('date', '>=', monthStartKey),
      where('date', '<=', monthEndKey),
      orderBy('date')
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          const row = d.data() || {};
          const date = String(row.date || '');
          const url = String(row.url || '');
          if (!date || !url) return;
          if (!next[date]) next[date] = [];
          next[date].push({
            id: d.id,
            name: String(row.name || 'File'),
            url,
            fileType: normalizeDayFileType(row.fileType),
          });
        });
        setDayFilesByDate(next);
      },
      (err) => console.error('calendarDayFiles month snapshot', err)
    );
    return () => unsub();
  }, [uid, selectedTourFilterId, dataUid, dataTourId, monthStartKey, monthEndKey]);

  useEffect(() => {
    if (!dayInfoModal.open || !dayInfoModal.dateKey || !uid || !selectedTourFilterId) {
      setDayInfoTabEntries({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore();
        const qy = query(
          collection(db, 'users', dataUid, 'calendarTabEntries'),
          where('tourId', '==', dataTourId),
          where('date', '==', dayInfoModal.dateKey)
        );
        const snap = await getDocs(qy);
        if (cancelled) return;
        const next = {};
        snap.forEach((d) => {
          const row = d.data() || {};
          const tabKey = String(row.tabKey || '').trim();
          if (!tabKey) return;
          next[tabKey] = String(row.text || '');
        });
        if (activeCalendarTabKey && tabGridDraftByDate[dayInfoModal.dateKey] != null) {
          next[activeCalendarTabKey] = String(tabGridDraftByDate[dayInfoModal.dateKey] || '');
        }
        setDayInfoTabEntries(next);
      } catch (err) {
        console.error('day info tab entries get', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    dayInfoModal.open,
    dayInfoModal.dateKey,
    uid,
    selectedTourFilterId,
    dataUid,
    dataTourId,
    activeCalendarTabKey,
    tabGridDraftByDate,
  ]);

  const commitImportTourWithDates = useCallback(async (folder, dateKeys) => {
    if (!uid || !folder?.tour?.days) return;
    const db = getFirestore();
    const col = collection(db, 'users', uid, 'calendarEvents');
    const existingQ = query(col, where('sourceTourId', '==', folder.id));
    const existingSnap = await getDocs(existingQ);
    if (!existingSnap.empty) {
      setMessageModal({
        variant: 'info',
        title: 'Already on calendar',
        text: 'This tour is already imported. Remove it from the calendar first if you want to re-import.',
      });
      return;
    }
    const { days, name } = folder.tour;
    const batch = writeBatch(db);
    let n = 0;
    days.forEach((day, i) => {
      const dateKey = dateKeys[i];
      const venues = (day.venues || []).map((v) => v.name).filter(Boolean);
      const summary = venues.slice(0, 4).join(', ') + (venues.length > 4 ? '…' : '');
      const ref = doc(col);
      batch.set(ref, {
        title: `${name || 'Tour'} · ${day.name || `Day ${i + 1}`}`,
        date: dateKey,
        sortOrder: i,
        eventType: 'tour_day',
        notes: '',
        color: BRAND,
        sourceTourId: folder.id,
        sourceTourName: name || '',
        sourceDayIndex: i,
        dayVenueSummary: summary,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      n += 1;
    });
    try {
      await batch.commit();
      setImportOpen(false);
      setTourImportDateModal({ open: false, folder: null, rows: [], applyAsSelection: false });
      setMessageModal({
        variant: 'success',
        title: 'Imported',
        text: `${n} day(s) added to your calendar.`,
      });
    } catch (e) {
      console.error(e);
      setMessageModal({
        variant: 'error',
        title: 'Import failed',
        text: e.message || 'Try a smaller tour or check connection.',
      });
    }
  }, [uid]);

  const importTour = async (folder) => {
    if (!uid || !folder?.tour?.days) return;
    const { days } = folder.tour;
    const computedFallbackKeys = computeImportDayDateKeys(days, cursor.y, cursor.m);
    const rows = days.map((day, i) => {
      const scheduled = parseStrictDateKey(day?.scheduledDate || '') ? String(day.scheduledDate) : '';
      const venues = (day?.venues || []).map((v) => String(v?.name || '').trim()).filter(Boolean);
      return {
        dayIndex: i,
        dayName: String(day?.name || `Day ${i + 1}`),
        dateKey: scheduled || '',
        fallbackDate: computedFallbackKeys[i] || '',
        venueLabel: venues[0] || '',
      };
    });
    const missing = rows.some((r) => !r.dateKey);
    if (missing) {
      setTourImportDateModal({ open: true, folder, rows, applyAsSelection: false });
      return;
    }
    await commitImportTourWithDates(folder, rows.map((r) => r.dateKey));
  };

  const importSharedTourInvite = useCallback(async (row) => {
    if (!uid || !row?.folder?.tour?.days?.length) {
      setMessageModal({
        variant: 'info',
        title: 'Tour unavailable',
        text: 'This shared tour could not be loaded yet. Ask the owner to share it again, or use Import tour once it appears in My Tours.',
      });
      return;
    }
    const inviteKey = String(row.invite?.id || row.folder.id);
    setImportingSharedTourId(inviteKey);
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', uid);
      const snap = await getDoc(userRef);
      const folders = { ...(snap.data()?.folders || {}) };
      const folder = row.folder;
      folders[folder.id] = {
        name: folder.name,
        tour: folder.tour,
        sharedFrom: {
          ownerId: folder.ownerId,
          tourId: folder.sourceTourId,
          inviteId: folder.inviteId,
        },
      };
      await updateDoc(userRef, { folders });
      await loadTourFolders();
      await importTour({ id: folder.id, name: folder.name, tour: folder.tour });
      setTourPickerMainTab('mine');
      await loadSharedTourInvites();
    } catch (e) {
      console.error(e);
      setMessageModal({
        variant: 'error',
        title: 'Import failed',
        text: e.message || 'Could not import this shared tour.',
      });
    } finally {
      setImportingSharedTourId(null);
    }
  }, [loadSharedTourInvites, loadTourFolders, uid]);

  const confirmImportTourDates = useCallback(async () => {
    const folder = tourImportDateModal.folder;
    if (!folder) return;
    const normalized = tourImportDateModal.rows.map((r) => {
      const typed = String(r.dateKey || '').trim();
      const fallback = String(r.fallbackDate || '').trim();
      return typed || fallback;
    });
    const invalid = normalized.find((d) => !parseStrictDateKey(d));
    if (invalid) {
      setMessageModal({
        variant: 'info',
        title: 'Invalid day date',
        text: `Use YYYY-MM-DD for each tour day (invalid: ${invalid}).`,
      });
      return;
    }
    if (tourImportDateModal.applyAsSelection) {
      await persistTourScheduledDates(uid, folder, normalized);
      setTourFolders((prev) =>
        prev.map((t) =>
          t.id !== folder.id
            ? t
            : {
                ...t,
                tour: {
                  ...(t.tour || {}),
                  days: (t.tour?.days || []).map((d, i) => ({ ...d, scheduledDate: normalized[i] || d?.scheduledDate || '' })),
                },
              }
        )
      );
      setSelectedTourFilterId(folder.id);
      setActiveTopTab(null);
      const first = normalized.slice().sort()[0] || '';
      const start = parseStrictDateKey(first);
      if (start) {
        selectDate(first);
        setCursorDate(start);
        setCursor({ y: start.getFullYear(), m: start.getMonth() });
        syncTabStateForDaySelection(first, folder.id);
      }
      setTourPickerOpen(false);
      setTourImportDateModal({ open: false, folder: null, rows: [], applyAsSelection: false });
      return;
    }
    await commitImportTourWithDates(folder, normalized);
  }, [commitImportTourWithDates, selectDate, syncTabStateForDaySelection, tourImportDateModal.applyAsSelection, tourImportDateModal.folder, tourImportDateModal.rows, uid]);

  // Simply start managing a tour (enables tabs) without affecting event imports or visibility
  const manageTour = useCallback((folder) => {
    if (!folder?.id) return;
    setSelectedTourFilterId(folder.id);
    setTourPickerOpen(false);
    const focus = applyTourStartFocus(folder);
    if (!focus) {
      if (selectedDateKey) syncTabStateForDaySelection(selectedDateKey, folder.id);
      else setActiveTopTab(null);
    }
  }, [applyTourStartFocus, selectedDateKey, syncTabStateForDaySelection]);

  const handleSelectTourFromDropdown = useCallback(
    async (folder) => {
      if (!folder?.id) return;
      const days = folder?.tour?.days || [];
      const rows = days.map((day, i) => ({
        dayIndex: i,
        dayName: String(day?.name || `Day ${i + 1}`),
        dateKey: parseStrictDateKey(day?.scheduledDate || '') ? String(day.scheduledDate) : '',
        fallbackDate: '',
        venueLabel: (day?.venues || []).map((v) => String(v?.name || '').trim()).find(Boolean) || '',
      }));
      const missing = rows.some((r) => !r.dateKey);
      if (missing) {
        const fallbackKeys = computeImportDayDateKeys(days, cursor.y, cursor.m);
        setTourImportDateModal({
          open: true,
          folder,
          rows: rows.map((r, i) => ({ ...r, fallbackDate: fallbackKeys[i] || '' })),
          applyAsSelection: true,
        });
        return;
      }
      setSelectedTourFilterId(folder.id);
      const focus = applyTourStartFocus(folder);
      if (!focus) {
        if (selectedDateKey) syncTabStateForDaySelection(selectedDateKey, folder.id);
        else setActiveTopTab(null);
      }
      setTourPickerOpen(false);
    },
    [applyTourStartFocus, cursor.m, cursor.y, events, selectedDateKey, syncTabStateForDaySelection]
  );

  const openPdfExportModal = () => {
    setPdfFromY(cursor.y);
    setPdfFromM(cursor.m);
    setPdfToY(cursor.y);
    setPdfToM(cursor.m);
    if (masterTourUi) {
      setDisplaySettingsOpen(false);
      setTourSettingsOpen(false);
      setSavedContactsOpen(false);
    }
    setPdfExportOpen(true);
  };

  const togglePdfExportRail = () => {
    if (isNativeColorPickerActive()) return;
    setDisplaySettingsOpen(false);
    setTourSettingsOpen(false);
    setSavedContactsOpen(false);
    if (pdfExportOpen) {
      setPdfExportOpen(false);
      return;
    }
    setPdfFromY(cursor.y);
    setPdfFromM(cursor.m);
    setPdfToY(cursor.y);
    setPdfToM(cursor.m);
    setPdfExportOpen(true);
  };


  // ── iCal export ──────────────────────────────────────────────────────────────
  const runIcalExport = async () => {
    if (!uid || exportingIcal) return;
    setExportingIcal(true);
    try {
      const db = getFirestore();
      // Export all events from 1 year back to 2 years ahead
      const now = new Date();
      const minDate = `${now.getFullYear() - 1}-01-01`;
      const maxDate = `${now.getFullYear() + 2}-12-31`;
      const snap = await getDocs(
        query(collection(db, 'users', uid, 'calendarEvents'),
          where('date', '>=', minDate),
          where('date', '<=', maxDate),
          orderBy('date'))
      );
      const icsLines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Venue Report Card//MyCalendar//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
      ];
      const formatDt = (dateStr) => dateStr.replace(/-/g, '');
      const escape = (s) => String(s || '').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
      const nowStamp = new Date().toISOString().replace(/[-:.]/g, '').slice(0, 15) + 'Z';
      snap.forEach(docSnap => {
        const ev = docSnap.data();
        if (!ev.date) return;
        const dtStart = formatDt(ev.date);
        // Add 1 day for DTEND (all-day events)
        const d = new Date(ev.date + 'T00:00:00');
        d.setDate(d.getDate() + 1);
        const dtEnd = [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('');
        const desc = [ev.notes, ev.dayVenueSummary].filter(Boolean).join(' | ');
        icsLines.push(
          'BEGIN:VEVENT',
          `UID:${docSnap.id}@venuereportcard.com`,
          `DTSTAMP:${nowStamp}`,
          `DTSTART;VALUE=DATE:${dtStart}`,
          `DTEND;VALUE=DATE:${dtEnd}`,
          `SUMMARY:${escape(ev.title || 'Event')}`,
          ...(desc ? [`DESCRIPTION:${escape(desc)}`] : []),
          'END:VEVENT',
        );
      });
      icsLines.push('END:VCALENDAR');
      const blob = new Blob([icsLines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'my-vrc-calendar.ics'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.warn('iCal export error:', e.message);
    } finally {
      setExportingIcal(false);
    }
  };

  const runPdfExport = async () => {
    if (!uid) return;
    if (compareYearMonth(pdfFromY, pdfFromM, pdfToY, pdfToM) > 0) {
      setMessageModal({
        variant: 'info',
        title: 'Invalid range',
        text: 'End month must be the same as or after the start month.',
      });
      return;
    }
    setPdfExportOpen(false);
    setExportingPdf(true);
    try {
      const { min, max } = pdfRangeToDateBounds(pdfFromY, pdfFromM, pdfToY, pdfToM);
      const db = getFirestore();
      const ref = collection(db, 'users', uid, 'calendarEvents');
      const qy = query(ref, where('date', '>=', min), where('date', '<=', max), orderBy('date'));
      const snap = await getDocs(qy);
      const eventsForPdf = [];
      snap.forEach((d) => {
        const ev = safeEventFromDoc(d.id, d.data());
        if (ev) eventsForPdf.push(ev);
      });
      eventsForPdf.sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.sortOrder - b.sortOrder;
      });

      if (!eventsForPdf.length) {
        setMessageModal({
          variant: 'info',
          title: 'Nothing to export',
          text: 'No events in the selected months.',
        });
        return;
      }

      const eventsByDate = {};
      eventsForPdf.forEach((e) => {
        if (!eventsByDate[e.date]) eventsByDate[e.date] = [];
        eventsByDate[e.date].push(e);
      });
      Object.keys(eventsByDate).forEach((k) => {
        eventsByDate[k].sort((a, b) => a.sortOrder - b.sortOrder);
      });
      const byDate = eventsByDate;
      const dates = Object.keys(byDate).sort();

      let jsPDF;
      try {
        const mod = await import('jspdf');
        jsPDF = mod.jsPDF;
      } catch (e) {
        console.warn('jspdf load', e);
        setMessageModal({
          variant: 'info',
          title: 'PDF unavailable',
          text: 'Install dependency: npm install jspdf (use --legacy-peer-deps if needed).',
        });
        return;
      }

      const monthsList = enumerateMonthsInclusive(pdfFromY, pdfFromM, pdfToY, pdfToM);
      const docPdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pageW = docPdf.internal.pageSize.getWidth();
      const pageH = docPdf.internal.pageSize.getHeight();
      const margin = 14;
      const textW = pageW - margin * 2;
      const bottomSafe = pageH - margin;

      let firstMonthPage = true;
      for (const { y: cy, m: cm } of monthsList) {
        if (!firstMonthPage) {
          docPdf.addPage();
        }
        let yPos = 18;
        if (firstMonthPage) {
          docPdf.setFont('helvetica', 'bold');
          docPdf.setFontSize(18);
          docPdf.setTextColor(40, 40, 40);
          docPdf.text('My Calendar', margin, yPos);
          yPos += 11;
          docPdf.setFontSize(12);
          const rangeLabel =
            pdfFromY === pdfToY && pdfFromM === pdfToM
              ? monthLabel(pdfFromY, pdfFromM)
              : `${monthLabel(pdfFromY, pdfFromM)} – ${monthLabel(pdfToY, pdfToM)}`;
          docPdf.text(`Range: ${rangeLabel}`, margin, yPos);
          yPos += 8;
          docPdf.setFont('helvetica', 'normal');
          docPdf.setFontSize(9);
          docPdf.setTextColor(100, 100, 100);
          docPdf.text(`Exported ${new Date().toLocaleString()} · Venue Report Card`, margin, yPos);
          yPos += 12;
          firstMonthPage = false;
        }
        docPdf.setFont('helvetica', 'bold');
        docPdf.setFontSize(14);
        docPdf.setTextColor(35, 35, 35);
        docPdf.text(monthLabel(cy, cm), margin, yPos);
        yPos += 10;

        const gridTop = yPos;
        const weekdaysHeaderH = 6;
        const weekRowsPdf = chunkCalendarWeeks(buildMonthGrid(cy, cm, { weekStartsOn }));
        const rowH = Math.max(
          22,
          (bottomSafe - gridTop - weekdaysHeaderH) / Math.max(1, weekRowsPdf.length)
        );
        const colW = textW / 7;

        docPdf.setDrawColor(210, 210, 210);
        docPdf.setLineWidth(0.15);

        let gy = gridTop;
        docPdf.setFont('helvetica', 'bold');
        docPdf.setFontSize(7);
        docPdf.setTextColor(90, 90, 90);
        for (let di = 0; di < 7; di += 1) {
          docPdf.rect(margin + di * colW, gy, colW, weekdaysHeaderH);
          docPdf.text(weekdayLabels[di], margin + di * colW + colW / 2, gy + 4.2, { align: 'center' });
        }
        gy += weekdaysHeaderH;

        weekRowsPdf.forEach((week) => {
          week.forEach((cell, di) => {
            const x = margin + di * colW;
            const cyCell = gy;
            docPdf.setDrawColor(220, 220, 220);
            docPdf.rect(x, cyCell, colW, rowH);

            const dayEvents = cell.dateKey ? eventsByDate[cell.dateKey] || [] : [];
            docPdf.setFont('helvetica', 'bold');
            docPdf.setFontSize(8);
            if (cell.inMonth) {
              docPdf.setTextColor(45, 45, 45);
            } else {
              docPdf.setTextColor(175, 175, 175);
            }
            docPdf.text(String(cell.jsDate.getDate()), x + 1.2, cyCell + 4);

            const linePitch = 3.2;
            const innerTop = cyCell + 6.5;
            const maxH = rowH - 7;
            let maxLines = Math.max(1, Math.floor(maxH / linePitch));
            if (maxLines > 6) maxLines = 6;

            docPdf.setFont('helvetica', 'normal');
            docPdf.setFontSize(5.5);
            docPdf.setTextColor(32, 32, 32);
            let ty = innerTop;
            const shown = dayEvents.slice(0, maxLines);
            shown.forEach((ev) => {
              const [r, g, b] = hexToRgbForPdf(ev.color || BRAND);
              docPdf.setFillColor(r, g, b);
              docPdf.rect(x + 0.9, ty - 1.1, 1.1, 1.1, 'F');
              const raw = (ev.title || 'Untitled').trim();
              const short = raw.length > 22 ? `${raw.slice(0, 20)}…` : raw;
              docPdf.text(short, x + 2.8, ty);
              ty += linePitch;
            });
            if (dayEvents.length > maxLines) {
              docPdf.setFontSize(4.5);
              docPdf.setTextColor(110, 110, 110);
              docPdf.text(`+${dayEvents.length - maxLines} more`, x + 1.2, cyCell + rowH - 1.5);
            }
          });
          gy += rowH;
        });
      }

      docPdf.addPage();
      let listY = 18;
      docPdf.setFont('helvetica', 'bold');
      docPdf.setFontSize(12);
      docPdf.setTextColor(40, 40, 40);
      docPdf.text('Event details', margin, listY);
      listY += 9;

      docPdf.setFontSize(10);
      for (const d of dates) {
        if (listY > bottomSafe - 8) {
          docPdf.addPage();
          listY = 18;
        }
        docPdf.setTextColor(50, 130, 100);
        docPdf.setFont('helvetica', 'bold');
        docPdf.text(d, margin, listY);
        listY += 7;
        docPdf.setFont('helvetica', 'normal');
        docPdf.setTextColor(30, 30, 30);
        for (const ev of byDate[d]) {
          const line = `• ${ev.title}${ev.notes ? ` — ${ev.notes.slice(0, 120)}` : ''}`;
          const lines = docPdf.splitTextToSize(line, textW);
          lines.forEach((ln) => {
            if (listY > bottomSafe - 4) {
              docPdf.addPage();
              listY = 18;
            }
            docPdf.text(ln, margin + 2, listY);
            listY += 5;
          });
          if (ev.eventType === 'tour_day' && String(ev.dayVenueSummary || '').trim()) {
            const venueLine = `  Venues: ${String(ev.dayVenueSummary).trim()}`;
            const vlines = docPdf.splitTextToSize(venueLine, textW - 4);
            vlines.forEach((ln) => {
              if (listY > bottomSafe - 4) {
                docPdf.addPage();
                listY = 18;
              }
              docPdf.text(ln, margin + 2, listY);
              listY += 5;
            });
          }
          listY += 2;
        }
        listY += 4;
      }

      const fnFrom = `${pdfFromY}-${String(pdfFromM + 1).padStart(2, '0')}`;
      const fnTo = `${pdfToY}-${String(pdfToM + 1).padStart(2, '0')}`;
      docPdf.save(`my-calendar_${fnFrom}_to_${fnTo}.pdf`);
    } catch (e) {
      console.error(e);
      setMessageModal({ variant: 'error', title: 'Export failed', text: e.message || 'Unknown error' });
    } finally {
      setExportingPdf(false);
    }
  };

  const goTourPlanner = (tourId) => {
    navigation.navigate('TourPlanner', { loadTourId: tourId });
  };

  const handleTopTabSingleClick = useCallback((tabKey) => {
    setV2Canvas(null);
    if (masterTourUi && tabKey === 'DAY SHEET') {
      setActiveTopTab(null);
      setCalView('day');
      return;
    }
    if (masterTourUi && activeTopTab === tabKey) {
      setActiveTopTab(null);
      return;
    }
    setActiveTopTab(tabKey);
    if (tabKey === 'RIDER') setRiderSubtab('DAILY');
  }, [masterTourUi, activeTopTab]);

  const handleRiderSubtabPress = useCallback((sub) => {
      setRiderSubtab(sub);
  }, []);

  const handleDayCellPress = useCallback(
    (dateKey, opts) => {
      if (!dateKey) return;
      const nowTs = Date.now();
      const { key, ts } = lastDayTapRef.current;
      if (!opts?.skipDoubleTap && key === dateKey && nowTs - ts <= 360) {
        lastDayTapRef.current = { key: '', ts: 0 };
        navigation.navigate('CalendarDayDetails', {
          dateKey,
          tourId: selectedTourFilterId || null,
        });
        return;
      }
      lastDayTapRef.current = opts?.skipDoubleTap ? { key: '', ts: 0 } : { key: dateKey, ts: nowTs };
      pendingDefaultTourDateRef.current = null;
      if (dateKey !== selectedDateKey) {
        selectDate(dateKey);
        const parts = dateKey.split('-').map(Number);
        if (parts.length === 3) setCursorDate(new Date(parts[0], parts[1] - 1, parts[2]));
      }
      const nextTourId = pickTourIdForDate(tourDayMetaByDate[dateKey]?.tours || [], selectedTourFilterId);
      if (nextTourId !== selectedTourFilterId) setSelectedTourFilterId(nextTourId);
      syncTabStateForDaySelection(dateKey, nextTourId);
    },
    [navigation, selectDate, selectedTourFilterId, selectedDateKey, setCursorDate, tourDayMetaByDate, syncTabStateForDaySelection]
  );

  const serializeTabValueForStorage = useCallback((tabKey, raw) => {
    if (tabKey === 'SCHEDULE') {
      return serializeScheduleDoc(parseScheduleText(String(raw || ''))).trim();
    }
    if (STRUCTURED_DAY_LIST_TABS.has(tabKey)) {
      return serializeStructuredDayListText(raw);
    }
    return String(raw || '');
  }, []);

  const persistTabDraft = useCallback(
    async (dateKey, draftOverride) => {
      if (!uid || !selectedTourFilterId || !dateKey || !activeCalendarTabKey) return;
      if (!canEditActiveCalendarTab) return;
      const raw = draftOverride != null ? draftOverride : tabGridDraftByDate[dateKey];
      const value = serializeTabValueForStorage(activeCalendarTabKey, raw);
      const styleForDate = tabGridStyleByDate[dateKey] || { textColor: '#111111', textAlign: 'left', fontSize: 13 };
      const db = getFirestore();
      const entryId = `${dataTourId}__${activeCalendarTabKey}__${dateKey}`;
      const ref = doc(db, 'users', dataUid, 'calendarTabEntries', entryId);
      if (!value) {
        try {
          await deleteDoc(ref);
        } catch (e) {
          if (!String(e?.code || '').includes('not-found')) throw e;
        }
        tabEntryDirtyRef.current[dateKey] = false;
        setTabDraftSaveState('saved');
        return;
      }
      await setDoc(
        ref,
        {
          ...buildTabEntryDraftFields({
            tourId: dataTourId,
            tabKey: activeCalendarTabKey,
            date: dateKey,
            draftText: value,
            ...styleForDate,
          }),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      tabEntryDirtyRef.current[dateKey] = false;
      setTabDraftSaveState('saved');
    },
    [
      uid,
      selectedTourFilterId,
      dataUid,
      dataTourId,
      activeCalendarTabKey,
      tabGridDraftByDate,
      tabGridStyleByDate,
      canEditActiveCalendarTab,
      serializeTabValueForStorage,
    ]
  );

  const updateTabGridCellDraft = useCallback(
    (dateKey, text) => {
      if (!dateKey) return;
    tabEntryDirtyRef.current[dateKey] = true;
    setTabGridDraftByDate((prev) => ({ ...prev, [dateKey]: text }));
      if (tabDraftSaveTimerRef.current[dateKey]) {
        clearTimeout(tabDraftSaveTimerRef.current[dateKey]);
      }
      setTabDraftSaveState('saving');
      tabDraftSaveTimerRef.current[dateKey] = setTimeout(() => {
        persistTabDraft(dateKey, text).catch((err) => {
          console.error('persist tab draft', err);
          setTabDraftSaveState('error');
        });
      }, 500);
    },
    [persistTabDraft]
  );

  const openExpandedTabEditor = useCallback(
    (dateKey) => {
      if (!dateKey || !activeTopTab || !selectedTourFilterId) {
        setMessageModal({
          variant: 'info',
          title: 'Select a tab first',
          text: 'Choose a calendar tab, then open the expanded editor for that day.',
        });
        return;
      }
      selectDate(dateKey);
      if (!isCompactMonthLayout && !masterTourUi) {
        setSidebarPanelExpanded(true);
        return;
      }
      if (!activeCalendarTabKey) {
        return;
      }
      if (activeTopTab === 'SCHEDULE') {
        const raw = String(tabGridDraftByDate[dateKey] || '');
        setExpandedScheduleBaseline(serializeScheduleDoc(parseScheduleText(raw)));
        setExpandedScheduleModal({ open: true, dateKey });
        return;
      }
      if (STRUCTURED_DAY_LIST_TABS.has(activeTopTab)) {
        const raw = String(tabGridDraftByDate[dateKey] || '');
        setExpandedStructuredListBaseline(serializeStructuredDayListText(raw));
        setExpandedStructuredListModal({ open: true, dateKey, tabKey: activeTopTab });
        return;
      }
      setExpandedNotesBaseline(String(tabGridDraftByDate[dateKey] || ''));
      setExpandedNotesModal({ open: true, dateKey });
    },
    [activeCalendarTabKey, activeTopTab, selectedTourFilterId, selectDate, tabGridDraftByDate, isCompactMonthLayout, masterTourUi]
  );

  const openDayFilesManager = useCallback(
    (dateKey) => {
      if (!dateKey || !selectedTourFilterId || activeTopTab !== 'FILES') {
        setMessageModal({
          variant: 'info',
          title: 'Open FILES tab first',
          text: 'Select FILES tab, then open file manager for that day.',
        });
        return;
      }
      selectDate(dateKey);
      setDayFileNameInput('');
      setDayFilesModal({ open: true, dateKey });
    },
    [activeTopTab, selectedTourFilterId, selectDate]
  );

  const updateTabGridCellStyle = useCallback((dateKey, patch) => {
    if (!dateKey) return;
    tabEntryDirtyRef.current[dateKey] = true;
    setTabGridStyleByDate((prev) => {
      const current = prev[dateKey] || { textColor: '#111111', textAlign: 'left', fontSize: 13 };
      return { ...prev, [dateKey]: { ...current, ...patch } };
    });
  }, []);

  const persistTabGridCell = useCallback(
    async (dateKey, options = {}) => {
      if (!uid || !selectedTourFilterId || !dateKey) return;
      if (!canEditActiveCalendarTab) {
        showToast('You do not have permission to edit this section.', 'warning');
        return;
      }
      const applyScope = options.applyScope || DAY_APPLY_SCOPES.THIS_DAY;
      const value = serializeTabValueForStorage(activeCalendarTabKey, tabGridDraftByDate[dateKey]);
      const styleForDate = tabGridStyleByDate[dateKey] || { textColor: '#111111', textAlign: 'left', fontSize: 13 };
      const tourFolder = tourFolders.find((t) => t.id === selectedTourFilterId);
      const fromFolder = getTourShowDateKeys(tourFolder?.tour?.days);
      const fromEvents = getTourShowDateKeysFromEvents(events, selectedTourFilterId);
      const tourShowDates = fromFolder.length ? fromFolder : fromEvents;
      const targetDates = resolveDayApplyScopeDateKeys(dateKey, applyScope, tourShowDates);
      const db = getFirestore();
      const isWriterTab = activeCalendarTabKey === 'RIDER_DAILY' || activeCalendarTabKey === 'RIDER_FULL';
      const hasValue =
        activeCalendarTabKey === 'SCHEDULE' || STRUCTURED_DAY_LIST_TABS.has(activeCalendarTabKey)
          ? Boolean(value)
          : isWriterTab
            ? !isWriterHtmlEmpty(value)
            : value.length > 0;
      const batch = writeBatch(db);
      targetDates.forEach((targetDate) => {
        const entryId = `${dataTourId}__${activeCalendarTabKey}__${targetDate}`;
      const ref = doc(db, 'users', dataUid, 'calendarTabEntries', entryId);
      if (!hasValue) {
          batch.delete(ref);
        } else {
          batch.set(
        ref,
        {
              ...buildTabEntryPublishFields({
          tourId: dataTourId,
          tabKey: activeCalendarTabKey,
                date: targetDate,
                draftText: value,
          textColor: styleForDate.textColor || '#111111',
          textAlign: styleForDate.textAlign || 'left',
          fontSize: Number(styleForDate.fontSize) || 13,
              }),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
        }
      });
      await batch.commit();
      setTabGridDraftByDate((prev) => {
        const next = { ...prev };
        targetDates.forEach((targetDate) => {
          if (hasValue) next[targetDate] = value;
          else delete next[targetDate];
        });
        return next;
      });
      setTabGridStyleByDate((prev) => {
        const next = { ...prev };
        targetDates.forEach((targetDate) => {
          if (hasValue) next[targetDate] = { ...styleForDate };
          else if (!tabEntryDirtyRef.current[targetDate]) delete next[targetDate];
        });
        return next;
      });
      setTabPublishedByDate((prev) => {
        const next = { ...prev };
        targetDates.forEach((targetDate) => {
          if (hasValue) {
            next[targetDate] = value;
            tabPublishedBaselineRef.current[targetDate] = value;
          } else {
            delete next[targetDate];
            delete tabPublishedBaselineRef.current[targetDate];
          }
        });
        return next;
      });
      if (activeCalendarTabKey === 'SCHEDULE') {
        setSchedulePublishedByDate((prev) => {
          const next = { ...prev };
          targetDates.forEach((targetDate) => {
            if (hasValue) next[targetDate] = value;
            else delete next[targetDate];
          });
          return next;
        });
      }
      targetDates.forEach((targetDate) => {
        tabEntryDirtyRef.current[targetDate] = false;
      });
    },
    [
      uid,
      selectedTourFilterId,
      dataUid,
      dataTourId,
      activeCalendarTabKey,
      tabGridDraftByDate,
      tabGridStyleByDate,
      tourFolders,
      events,
      canEditActiveCalendarTab,
      showToast,
      serializeTabValueForStorage,
    ]
  );

  const persistV2Tab = useCallback(
    (tabKey, dateKey, text, { immediate, ownerScope } = {}) => {
      if (!uid || !selectedTourFilterId || !tabKey || !dateKey) return Promise.resolve();
      const value = String(text ?? '');
      if (tabKey === V2_CHAT_TAB) setV2ChatDoc(value);
      else if (tabKey === V2_RIDER_AREAS_TAB) setV2RiderAreas(value);
      else if (tabKey === 'MERCH' && dateKey === V2_MERCH_CATALOG_DATE) setV2MerchCatalog(value);
      else if (tabKey === 'EXPENSES_LEG') setV2LegExpenses(value);
      else if (dateKey === selectedDateKey) {
        setV2DayTabs((prev) => ({ ...prev, [tabKey]: value }));
      }
      const persistUid = ownerScope && tourOwnerId ? tourOwnerId : dataUid;
      const persistTourId = ownerScope && permissionTourId ? permissionTourId : dataTourId;
      const run = async () => {
        const db = getFirestore();
        const entryId = v2TabEntryId(persistTourId, tabKey, dateKey);
        const ref = doc(db, 'users', persistUid, 'calendarTabEntries', entryId);
        if (!value.trim()) {
          try {
            await deleteDoc(ref);
          } catch (e) {
            if (!String(e?.code || '').includes('not-found')) console.error('v2 tab delete', e);
          }
          return;
        }
        try {
          await setDoc(
            ref,
            {
              ...buildTabEntryPublishFields({
                tourId: persistTourId,
                tabKey,
                date: dateKey,
                draftText: value,
              }),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        } catch (e) {
          console.error('v2 tab persist', e);
          showToast(e?.message || 'Could not save.', 'error');
        }
      };
      const stamp = `${tabKey}::${dateKey}`;
      if (v2PersistTimerRef.current[stamp]) clearTimeout(v2PersistTimerRef.current[stamp]);
      if (immediate) return run();
      v2PersistTimerRef.current[stamp] = setTimeout(() => {
        v2PersistTimerRef.current[stamp] = null;
        run();
      }, 450);
      return Promise.resolve();
    },
    [uid, selectedTourFilterId, selectedDateKey, showToast, tourOwnerId, permissionTourId, dataUid, dataTourId]
  );

  const loadMerchForDate = useCallback(async (dateKey) => {
    if (!uid || !selectedTourFilterId || !dateKey) return '';
    try {
      const snap = await getDoc(doc(
        getFirestore(),
        'users',
        dataUid,
        'calendarTabEntries',
        v2TabEntryId(dataTourId, 'MERCH', dateKey)
      ));
      const row = snap.exists() ? snap.data() : {};
      return getTabEntryDraftText(row) || getTabEntryPublishedText(row) || '';
    } catch (e) {
      console.error('load merch for date', e);
      return '';
    }
  }, [uid, selectedTourFilterId, dataUid, dataTourId]);

  // Saved EXPENSES ledger text for another day: '' when that day has no doc, null when the read failed.
  const loadLedgerForDate = useCallback(async (dateKey) => {
    if (!uid || !selectedTourFilterId || !dateKey) return null;
    try {
      const snap = await getDoc(doc(
        getFirestore(),
        'users',
        dataUid,
        'calendarTabEntries',
        v2TabEntryId(dataTourId, 'EXPENSES', dateKey)
      ));
      const row = snap.exists() ? snap.data() : {};
      return getTabEntryDraftText(row) || getTabEntryPublishedText(row) || '';
    } catch (e) {
      console.error('load ledger for date', e);
      return null;
    }
  }, [uid, selectedTourFilterId, dataUid, dataTourId]);

  const ensureGuestVenueLink = useCallback(async () => {
    if (!uid || !selectedTourFilterId || !selectedDateKey) return;
    try {
      const result = await upsertDayShareLink(getFirestore(), dataUid, {
        tourId: dataTourId,
        tourName: selectedTourFilterName,
        dateKey: selectedDateKey,
        modules: ['GUEST LIST'],
        baseUrl: resolveAppBaseUrl(),
        visibility: SHARE_VISIBILITY.LINK,
        label: `Guest list · ${selectedDateKey}`,
      });
      const url = String(result?.url || '').trim();
      setGuestVenueLink(url);
      if (url && Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
      showToast(url ? 'Venue guest-list link copied.' : 'Venue link created.', 'success');
    } catch (e) {
      console.error('guest venue link', e);
      showToast(e?.message || 'Could not create venue link.', 'error');
    }
  }, [uid, selectedTourFilterId, selectedDateKey, selectedTourFilterName, showToast]);

  const persistScheduleTabEntry = useCallback(
    async (dateKey, scheduleSerializedOverride) => {
      if (!uid || !selectedTourFilterId || !dateKey) return;
      if (!canEditCalendarTab(uid, tourOwnerId, 'SCHEDULE', tourShareSettings)) return;
      const raw =
        scheduleSerializedOverride != null ? String(scheduleSerializedOverride) : String(tabGridDraftByDate[dateKey] || '');
      const value = raw ? serializeScheduleDoc(parseScheduleText(raw)).trim() : '';
      const styleForDate = tabGridStyleByDate[dateKey] || { textColor: '#111111', textAlign: 'left', fontSize: 13 };
      const db = getFirestore();
      const entryId = `${dataTourId}__SCHEDULE__${dateKey}`;
      const ref = doc(db, 'users', dataUid, 'calendarTabEntries', entryId);
      const hasValue = Boolean(value);
      if (!hasValue) {
        try {
          await deleteDoc(ref);
        } catch (e) {
          if (String(e?.code || '').includes('not-found')) return;
          throw e;
        }
        tabEntryDirtyRef.current[dateKey] = false;
        return;
      }
      await setDoc(
        ref,
        {
          ...buildTabEntryDraftFields({
          tourId: dataTourId,
          tabKey: 'SCHEDULE',
          date: dateKey,
            draftText: value,
          textColor: styleForDate.textColor || '#111111',
          textAlign: styleForDate.textAlign || 'left',
          fontSize: Number(styleForDate.fontSize) || 13,
          }),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      tabEntryDirtyRef.current[dateKey] = false;
    },
    [uid, selectedTourFilterId, dataUid, dataTourId, tabGridDraftByDate, tabGridStyleByDate, tourOwnerId, tourShareSettings]
  );

  const flushDaySheetScheduleIfDirty = useCallback(async () => {
    if (!daySheetScheduleDirty || !uid || !selectedTourFilterId || !selectedDateKey) return;
    const serialized = serializeScheduleDoc({ lines: daySheetScheduleRows });
    try {
      await persistScheduleTabEntry(selectedDateKey, serialized);
      setDaySheetScheduleDirty(false);
    } catch (e) {
      console.error('flush day sheet schedule', e);
    }
  }, [
    daySheetScheduleDirty,
    daySheetScheduleRows,
    uid,
    selectedTourFilterId,
    selectedDateKey,
    persistScheduleTabEntry,
  ]);

  const syncPublishedScheduleToDaySheet = useCallback(
    async (dateKey, serialized) => {
      if (!uid || !selectedTourFilterId || !dateKey) return;
      await setDoc(
        doc(getFirestore(), 'users', uid, 'calendarDaySheets', `${selectedTourFilterId}__${dateKey}`),
        {
          tourId: selectedTourFilterId,
          date: dateKey,
          publishedScheduleText: String(serialized || ''),
          schedulePublished: true,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      if (dateKey === selectedDateKey) {
        const lines = parseScheduleText(String(serialized || '')).lines || [];
        setDaySheetScheduleRows(lines);
        setDaySheetScheduleDirty(false);
        daySheetPublicationBaselineRef.current = {
          notes: String(daySheetNotesRef.current || ''),
          schedule: String(serialized || ''),
        };
        setDaySheetPublicationBaselineTick((t) => t + 1);
      }
    },
    [uid, selectedTourFilterId, selectedDateKey]
  );

  const executeDaySheetSave = useCallback(
    async ({ alsoPublishSchedule = false } = {}) => {
    if (!uid || !selectedTourFilterId || !selectedDateKey) return;
    if (!canEditCalendarTab(uid, tourOwnerId, 'DAY SHEET', tourShareSettings)) return;
    const serialized = serializeScheduleDoc({ lines: daySheetScheduleRows });
    const b = daySheetPublicationBaselineRef.current;
    if (
      String(daySheetNotes || '') === String(b.notes || '') &&
      String(serialized || '') === String(b.schedule || '')
    ) {
      return;
    }
    try {
      setDaySheetPublicationSaving(true);
      await persistScheduleTabEntry(selectedDateKey, serialized);
        if (alsoPublishSchedule) {
          const styleForDate = tabGridStyleByDate[selectedDateKey] || {
            textColor: '#111111',
            textAlign: 'left',
            fontSize: 13,
          };
          await setDoc(
            doc(getFirestore(), 'users', dataUid, 'calendarTabEntries', `${dataTourId}__SCHEDULE__${selectedDateKey}`),
            {
              ...buildTabEntryPublishFields({
                tourId: dataTourId,
                tabKey: 'SCHEDULE',
                date: selectedDateKey,
                draftText: serialized,
                textColor: styleForDate.textColor || '#111111',
                textAlign: styleForDate.textAlign || 'left',
                fontSize: Number(styleForDate.fontSize) || 13,
              }),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          setTabPublishedByDate((prev) => ({ ...prev, [selectedDateKey]: serialized }));
          setSchedulePublishedByDate((prev) => ({ ...prev, [selectedDateKey]: serialized }));
          tabPublishedBaselineRef.current[selectedDateKey] = serialized;
        }
      setDaySheetScheduleDirty(false);
      await setDoc(
        doc(getFirestore(), 'users', uid, 'calendarDaySheets', `${selectedTourFilterId}__${selectedDateKey}`),
        {
          tourId: selectedTourFilterId,
          date: selectedDateKey,
          aftershowText: String(daySheetNotes || ''),
          publishedScheduleText: serialized,
          schedulePublished: true,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      daySheetPublicationBaselineRef.current = {
        notes: String(daySheetNotes || ''),
        schedule: serialized,
      };
      setDaySheetPublicationBaselineTick((t) => t + 1);
      setDaySheetSeededFromDefaults(false);
        showToast(
          alsoPublishSchedule
            ? 'Day sheet saved and live schedule updated.'
            : 'Day sheet saved.',
          'success'
        );
    } catch (e) {
      console.error('save day sheet publication', e);
        showToast(e?.message || 'Could not save day sheet.', 'error');
    } finally {
      setDaySheetPublicationSaving(false);
    }
    },
    [
    uid,
    selectedTourFilterId,
    dataUid,
    dataTourId,
    selectedDateKey,
    daySheetScheduleRows,
    daySheetNotes,
    persistScheduleTabEntry,
      tabGridStyleByDate,
      showToast,
      tourOwnerId,
      tourShareSettings,
    ]
  );

  const saveDaySheetPublication = useCallback(() => {
    if (!uid || !selectedTourFilterId || !selectedDateKey) return;
    const serialized = serializeScheduleDoc({ lines: daySheetScheduleRows });
    const b = daySheetPublicationBaselineRef.current;
    if (
      String(daySheetNotes || '') === String(b.notes || '') &&
      String(serialized || '') === String(b.schedule || '')
    ) {
      return;
    }
    scheduleDaySheetLinkPendingRef.current = (alsoPublishSchedule) =>
      executeDaySheetSave({ alsoPublishSchedule });
    setScheduleDaySheetLinkModal({ direction: 'daysheet-to-schedule' });
  }, [uid, selectedTourFilterId, selectedDateKey, daySheetScheduleRows, daySheetNotes, executeDaySheetSave]);

  const executeSchedulePublish = useCallback(
    async (dateKey, { alsoUpdateDaySheet = false, applyScope, savingContext = 'tab', onSuccess } = {}) => {
      if (!dateKey) return;
      const setSaving = savingContext === 'expanded' ? setExpandedScheduleSaving : setTabApplyScopeSaving;
      try {
        setSaving(true);
        await persistTabGridCell(dateKey, { applyScope: applyScope || tabApplyScope });
        if (alsoUpdateDaySheet) {
          const serialized = serializeTabValueForStorage('SCHEDULE', tabGridDraftByDate[dateKey]);
          await syncPublishedScheduleToDaySheet(dateKey, serialized);
        }
        onSuccess?.();
        const count = countDayApplyScopeDates(applyScope || tabApplyScope, dateKey, selectedTourShowDateKeys);
        showToast(
          count > 1
            ? alsoUpdateDaySheet
              ? `Published schedule to ${count} show days and updated the day sheet.`
              : `Published schedule to ${count} show days.`
            : alsoUpdateDaySheet
              ? 'Published schedule and updated the day sheet.'
              : 'Published schedule — now live for your tour.',
          'success'
        );
      } catch (e) {
        console.error('publish schedule', e);
        showToast(e?.message || 'Could not publish schedule.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [
      persistTabGridCell,
      tabApplyScope,
      tabGridDraftByDate,
      serializeTabValueForStorage,
      syncPublishedScheduleToDaySheet,
      selectedTourShowDateKeys,
      showToast,
    ]
  );

  const requestSchedulePublish = useCallback(
    (dateKey, { applyScope, savingContext = 'tab', onSuccess } = {}) => {
      if (!dateKey) return;
      scheduleDaySheetLinkPendingRef.current = (alsoUpdateDaySheet) =>
        executeSchedulePublish(dateKey, { alsoUpdateDaySheet, applyScope, savingContext, onSuccess });
      setScheduleDaySheetLinkModal({ direction: 'schedule-to-daysheet' });
    },
    [executeSchedulePublish]
  );

  const closeScheduleDaySheetLinkModal = useCallback(() => {
    if (scheduleDaySheetLinkBusy) return;
    setScheduleDaySheetLinkModal(null);
    scheduleDaySheetLinkPendingRef.current = null;
  }, [scheduleDaySheetLinkBusy]);

  const resolveScheduleDaySheetLink = useCallback(
    async (syncOther) => {
      const fn = scheduleDaySheetLinkPendingRef.current;
      if (!fn) {
        closeScheduleDaySheetLinkModal();
        return;
      }
      try {
        setScheduleDaySheetLinkBusy(true);
        await fn(syncOther);
      } finally {
        setScheduleDaySheetLinkBusy(false);
        setScheduleDaySheetLinkModal(null);
        scheduleDaySheetLinkPendingRef.current = null;
      }
    },
    [closeScheduleDaySheetLinkModal]
  );

  const handleTopTabPress = useCallback(
    async (tabKey) => {
      if (activeTopTab === 'DAY SHEET' && tabKey !== 'DAY SHEET') {
        await flushDaySheetScheduleIfDirty();
      }
      const requiresSelectedDay = ['STAGE', 'DAY SHEET'].includes(tabKey);
      if (requiresSelectedDay && !selectedDateKey) {
        setMessageModal({
          variant: 'info',
          title: 'Select a day first',
          text: 'Pick a day in the calendar, then open a tab for that day.',
        });
        return;
      }
      handleTopTabSingleClick(tabKey);
    },
    [activeTopTab, flushDaySheetScheduleIfDirty, handleTopTabSingleClick, selectedDateKey]
  );

  const addDayFile = useCallback(async () => {
    const dateKey = dayFilesModal.dateKey;
    if (!uid || !selectedTourFilterId || !dateKey) return;
    try {
      setDayFileSaving(true);
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const asset = result.assets[0];
      const fileName = String(dayFileNameInput || asset.name || 'File').trim() || 'File';
      const uri = asset.uri;
      if (!uri) return;
      const response = await fetch(uri);
      const blob = await response.blob();
      const safeName = String(asset.name || `file-${Date.now()}`).replace(/[^\w.\-]+/g, '_');
      const path = `calendarDayFiles/${uid}/${selectedTourFilterId}/${dateKey}/${Date.now()}_${safeName}`;
      const storage = getStorage();
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob);
      const url = await getDownloadURL(ref);
      const db = getFirestore();
      await addDoc(collection(db, 'users', uid, 'calendarDayFiles'), {
        tourId: selectedTourFilterId,
        date: dateKey,
        name: fileName,
        url,
        fileType: 'files',
        storagePath: path,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setDayFileNameInput('');
      showToast(`${fileName} attached to this day.`, 'success');
    } catch (e) {
      console.error('add day file', e);
      showToast(e?.message || 'Could not add file.', 'error');
    } finally {
      setDayFileSaving(false);
    }
  }, [dayFileNameInput, dayFilesModal.dateKey, selectedTourFilterId, uid, showToast]);

  const uploadDayFileFromAsset = useCallback(
    async (fileType = 'riderFull', assetOverride = null, dateKeyOverride = null) => {
    const dateKey = dateKeyOverride || selectedDateKey;
    const fileLabel = fileType === 'stage' ? 'STAGE' : 'RIDER FULL';
    if (!uid || !selectedTourFilterId || !dateKey) {
      setMessageModal({
        variant: 'info',
        title: 'Select a day first',
        text: `Choose a day in calendar, then upload from ${fileLabel}.`,
      });
      return;
    }
    try {
      setDayFileSaving(true);
        let asset = assetOverride;
        if (!asset) {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (result.canceled || !result.assets?.[0]) return;
          asset = result.assets[0];
        }
      const fileName = String(asset.name || 'File').trim() || 'File';
      const uri = asset.uri;
      if (!uri) return;
      const response = await fetch(uri);
      const blob = await response.blob();
      const safeName = String(asset.name || `file-${Date.now()}`).replace(/[^\w.\-]+/g, '_');
      const path = `calendarDayFiles/${uid}/${selectedTourFilterId}/${dateKey}/${Date.now()}_${safeName}`;
      const storage = getStorage();
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob);
      const url = await getDownloadURL(ref);
      const db = getFirestore();
      await addDoc(collection(db, 'users', uid, 'calendarDayFiles'), {
        tourId: selectedTourFilterId,
        date: dateKey,
        name: fileName,
        url,
        fileType,
        storagePath: path,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
        showToast(`${fileName} attached to this day.`, 'success');
    } catch (e) {
        console.error('add day file', e);
        showToast(e?.message || 'Could not add file.', 'error');
        throw e;
    } finally {
      setDayFileSaving(false);
    }
    },
    [selectedDateKey, selectedTourFilterId, uid, showToast]
  );

  const addRiderFullFileToSelectedDay = useCallback(
    (fileType = 'riderFull') => uploadDayFileFromAsset(fileType),
    [uploadDayFileFromAsset]
  );

  const uploadStageFileFromAsset = useCallback(
    (asset) => uploadDayFileFromAsset('stage', asset),
    [uploadDayFileFromAsset]
  );

  const openStageFilesManager = useCallback(
    (dateKey) => {
      if (!dateKey || !selectedTourFilterId) return;
      selectDate(dateKey);
      uploadDayFileFromAsset('stage', null, dateKey);
    },
    [selectedTourFilterId, selectDate, uploadDayFileFromAsset]
  );

  const uploadFilesTabFromAsset = useCallback(
    (asset) => uploadDayFileFromAsset('files', asset),
    [uploadDayFileFromAsset]
  );

  const persistStageFilesForScope = useCallback(
    async (dateKey, applyScope) => {
      if (!uid || !selectedTourFilterId || !dateKey) return;
      const sourceFiles = (dayFilesByDate[dateKey] || []).filter(
        (f) => normalizeDayFileType(f.fileType) === 'stage'
      );
      const targetDates = resolveDayApplyScopeDateKeys(dateKey, applyScope, selectedTourShowDateKeys);
      const db = getFirestore();
      const batch = writeBatch(db);
      let hasOps = false;

      targetDates.forEach((targetDate) => {
        const existing = (dayFilesByDate[targetDate] || []).filter(
          (f) => normalizeDayFileType(f.fileType) === 'stage'
        );
        existing.forEach((f) => {
          batch.delete(doc(db, 'users', uid, 'calendarDayFiles', f.id));
          hasOps = true;
        });
        sourceFiles.forEach((src) => {
          const newRef = doc(collection(db, 'users', uid, 'calendarDayFiles'));
          batch.set(newRef, {
            tourId: selectedTourFilterId,
            date: targetDate,
            name: src.name,
            url: src.url,
            fileType: 'stage',
            storagePath: src.storagePath || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          hasOps = true;
        });
      });

      if (!hasOps) return;
      await batch.commit();
    },
    [uid, selectedTourFilterId, dayFilesByDate, selectedTourShowDateKeys]
  );

  const saveStageFilesForSelectedDay = useCallback(async () => {
    if (!selectedDateKey) return;
    try {
      setTabApplyScopeSaving(true);
      await persistStageFilesForScope(selectedDateKey, tabApplyScope);
      const count = countDayApplyScopeDates(tabApplyScope, selectedDateKey, selectedTourShowDateKeys);
      const hasFiles = (dayFilesByDate[selectedDateKey] || []).some(
        (f) => normalizeDayFileType(f.fileType) === 'stage'
      );
      showToast(
        count > 1
          ? `Stage files copied to ${count} show days.`
          : hasFiles
            ? 'Stage files saved for this day.'
            : 'Stage files cleared for this day.',
        'success'
      );
    } catch (e) {
      console.error('save stage files', e);
      showToast(e?.message || 'Could not save stage files.', 'error');
    } finally {
      setTabApplyScopeSaving(false);
    }
  }, [
    selectedDateKey,
    tabApplyScope,
    persistStageFilesForScope,
    selectedTourShowDateKeys,
    dayFilesByDate,
    showToast,
  ]);

  const uploadDaySheetAsset = useCallback(
    async (kind) => {
      const dateKey = selectedDateKey;
      if (!uid || !selectedTourFilterId || !dateKey) {
        setMessageModal({
          variant: 'info',
          title: 'Select a day first',
          text: 'Choose a day in calendar, then upload day sheet assets.',
        });
        return;
      }
      try {
        setDaySheetAssetSaving(true);
        const result = await DocumentPicker.getDocumentAsync({
          type: ['image/*'],
          copyToCacheDirectory: true,
          multiple: false,
        });
        if (result.canceled || !result.assets?.[0]) return;
        const asset = result.assets[0];
        const uri = asset.uri;
        if (!uri) return;
        const response = await fetch(uri);
        const blob = await response.blob();
        const safeName = String(asset.name || `${kind}-${Date.now()}`).replace(/[^\w.\-]+/g, '_');
        const path = `calendarDaySheetAssets/${uid}/${selectedTourFilterId}/${dateKey}/${kind}_${Date.now()}_${safeName}`;
        const storage = getStorage();
        const ref = storageRef(storage, path);
        await uploadBytes(ref, blob);
        const url = await getDownloadURL(ref);
        const db = getFirestore();
        const docId = `${selectedTourFilterId}__${dateKey}`;
        const patch = kind === 'banner' ? { bannerUrl: url } : { backgroundUrl: url };
        await setDoc(
          doc(db, 'users', uid, 'calendarDaySheets', docId),
          {
            tourId: selectedTourFilterId,
            date: dateKey,
            ...patch,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        if (kind === 'banner') setDaySheetBannerUrl(url);
        else setDaySheetBgUrl(url);
        showToast(`${kind === 'banner' ? 'Banner' : 'Background'} uploaded.`, 'success');
      } catch (e) {
        console.error('upload day sheet asset', e);
        showToast(e?.message || 'Could not upload image.', 'error');
      } finally {
        setDaySheetAssetSaving(false);
      }
    },
    [selectedDateKey, selectedTourFilterId, uid, showToast]
  );

  const removeDayFile = useCallback(
    async (id) => {
      if (!uid || !id) return;
      try {
        await deleteDoc(doc(getFirestore(), 'users', uid, 'calendarDayFiles', id));
        showToast('File removed.', 'success');
      } catch (e) {
        console.error('remove day file', e);
        showToast(e?.message || 'Could not remove file.', 'error');
      }
    },
    [uid, showToast]
  );

  const saveExpandedNotes = useCallback(async () => {
    const dateKey = expandedNotesModal.dateKey;
    if (!dateKey) return;
    try {
      setExpandedNotesSaving(true);
      await persistTabGridCell(dateKey, { applyScope: tabApplyScope });
      setExpandedNotesBaseline(String(tabGridDraftByDate[dateKey] || ''));
      const count = countDayApplyScopeDates(tabApplyScope, dateKey, selectedTourShowDateKeys);
      showToast(
        count > 1
          ? `Saved ${activeTopTab || 'tab'} to ${count} show days.`
          : `Saved ${activeTopTab || 'tab'} for this day.`,
        'success'
      );
    } catch (e) {
      console.error('save expanded notes', e);
      showToast(e?.message || 'Could not save notes.', 'error');
    } finally {
      setExpandedNotesSaving(false);
    }
  }, [
    expandedNotesModal.dateKey,
    persistTabGridCell,
    tabGridDraftByDate,
    tabApplyScope,
    selectedTourShowDateKeys,
    activeTopTab,
    showToast,
  ]);

  const saveExpandedSchedule = useCallback(() => {
    const dateKey = expandedScheduleModal.dateKey;
    if (!dateKey) return;
    requestSchedulePublish(dateKey, {
      applyScope: tabApplyScope,
      savingContext: 'expanded',
      onSuccess: () => {
      const raw = String(tabGridDraftByDate[dateKey] || '');
      setExpandedScheduleBaseline(serializeScheduleDoc(parseScheduleText(raw)));
      },
    });
  }, [expandedScheduleModal.dateKey, requestSchedulePublish, tabApplyScope, tabGridDraftByDate]);

  const saveExpandedStructuredList = useCallback(async () => {
    const dateKey = expandedStructuredListModal.dateKey;
    const tabKey = expandedStructuredListModal.tabKey;
    if (!dateKey || !tabKey) return;
    const copy = STRUCTURED_DAY_LIST_MODAL_COPY[tabKey] || STRUCTURED_DAY_LIST_MODAL_COPY.SETLIST;
    try {
      setExpandedStructuredListSaving(true);
      await persistTabGridCell(dateKey, { applyScope: tabApplyScope });
      const raw = String(tabGridDraftByDate[dateKey] || '');
      setExpandedStructuredListBaseline(serializeStructuredDayListText(raw));
      const count = countDayApplyScopeDates(tabApplyScope, dateKey, selectedTourShowDateKeys);
      showToast(count > 1 ? copy.savedMany(count) : copy.savedOne, 'success');
    } catch (e) {
      console.error('save expanded structured list', e);
      showToast(e?.message || copy.saveError, 'error');
    } finally {
      setExpandedStructuredListSaving(false);
    }
  }, [
    expandedStructuredListModal.dateKey,
    expandedStructuredListModal.tabKey,
    persistTabGridCell,
    tabGridDraftByDate,
    tabApplyScope,
    selectedTourShowDateKeys,
    showToast,
  ]);

  const publishTabContentForSelectedDay = useCallback(async () => {
    if (!selectedDateKey) return;
    if (activeCalendarTabKey === 'SCHEDULE') {
      requestSchedulePublish(selectedDateKey, { applyScope: tabApplyScope, savingContext: 'tab' });
      return;
    }
    try {
      setTabApplyScopeSaving(true);
      await persistTabGridCell(selectedDateKey, { applyScope: tabApplyScope });
      const count = countDayApplyScopeDates(tabApplyScope, selectedDateKey, selectedTourShowDateKeys);
      const tabLabel = activeTopTab || 'tab';
      const isStructuredList = STRUCTURED_DAY_LIST_TABS.has(activeCalendarTabKey);
      const isNotes = activeCalendarTabKey === 'NOTES';
      showToast(
        count > 1
          ? `Saved ${tabLabel} to ${count} show days.`
          : isStructuredList || isNotes
            ? `Saved ${tabLabel} for this day.`
            : `Published ${tabLabel} — now live for your tour.`,
        'success'
      );
    } catch (e) {
      console.error('publish tab content', e);
      showToast(e?.message || 'Could not publish changes.', 'error');
    } finally {
      setTabApplyScopeSaving(false);
    }
  }, [
    selectedDateKey,
    activeCalendarTabKey,
    tabApplyScope,
    persistTabGridCell,
    selectedTourShowDateKeys,
    activeTopTab,
    showToast,
    requestSchedulePublish,
  ]);

  const selectedTabHasUnpublishedDraft = useMemo(() => {
    if (!selectedDateKey || !activeCalendarTabKey) return false;
    const draft = serializeTabValueForStorage(activeCalendarTabKey, tabGridDraftByDate[selectedDateKey]);
    const published = serializeTabValueForStorage(
      activeCalendarTabKey,
      tabPublishedByDate[selectedDateKey] ?? tabPublishedBaselineRef.current[selectedDateKey] ?? ''
    );
    return hasUnpublishedTabDraft(draft, published);
  }, [selectedDateKey, activeCalendarTabKey, tabGridDraftByDate, tabPublishedByDate, serializeTabValueForStorage]);

  const selectedTabDayText = selectedDateKey ? tabGridDraftByDate[selectedDateKey] || '' : '';
  const selectedTabDayStyle = selectedDateKey
    ? tabGridStyleByDate[selectedDateKey] || { textColor: '#111111', textAlign: 'left', fontSize: 13 }
    : { textColor: '#111111', textAlign: 'left', fontSize: 13 };
  const daySheetScheduleSerialized = useMemo(
    () => serializeScheduleDoc({ lines: daySheetScheduleRows }),
    [daySheetScheduleRows]
  );
  useLayoutEffect(() => {
    daySheetNotesRef.current = daySheetNotes;
    daySheetScheduleRowsRef.current = daySheetScheduleRows;
  }, [daySheetNotes, daySheetScheduleRows]);
  const hasUnsavedDaySheetPublication = useMemo(() => {
    if (daySheetPublicationBaselineTick < 1) return false;
    const b = daySheetPublicationBaselineRef.current;
    return (
      String(daySheetNotes || '') !== String(b.notes || '') ||
      String(daySheetScheduleSerialized || '') !== String(b.schedule || '')
    );
  }, [daySheetPublicationBaselineTick, daySheetNotes, daySheetScheduleSerialized]);
  const daySheetPreviewRows = useMemo(
    () => buildDaySheetPreviewRows(daySheetScheduleSerialized, { useDefaults: false }),
    [daySheetScheduleSerialized]
  );
  const handleDaySheetScheduleChange = useCallback(
    (nextRaw) => {
      const parsed = parseScheduleText(String(nextRaw || ''));
      const lines = parsed.lines || [];
      setDaySheetScheduleRows(lines);
      setDaySheetScheduleDirty(true);
      if (selectedDateKey) {
        const serialized = serializeScheduleDoc({ lines });
        tabEntryDirtyRef.current[selectedDateKey] = true;
        setTabGridDraftByDate((prev) => ({ ...prev, [selectedDateKey]: serialized }));
      }
    },
    [selectedDateKey]
  );
  const selectedDateFiles = selectedDateKey ? dayFilesByDate[selectedDateKey] || [] : [];
  const selectedDayRiderFullFiles = selectedDateFiles.filter((f) => normalizeDayFileType(f.fileType) === 'riderFull');
  const selectedDayStageFiles = selectedDateFiles.filter((f) => normalizeDayFileType(f.fileType) === 'stage');
  const renderSelectedDayFilesPanel = useCallback((items, emptyText) => {
    if (!selectedDateKey) return null;
    return (
      <View style={styles.dayUploadsPanel}>
        <Text style={styles.label}>Files attached to {selectedDateKey}</Text>
        {items.length === 0 ? (
          <Text style={styles.muted}>{emptyText || 'No files uploaded yet for this day.'}</Text>
        ) : (
          <View style={styles.dayUploadsGrid}>
            {items.map((f) => (
                <View key={`day-upload-${f.id}`} style={styles.dayUploadCard}>
                <DayFileThumbnail
                  url={f.url}
                  name={f.name}
                  theme={theme}
                  variant="card"
                      onPress={() => Linking.openURL(f.url).catch(() => {})}
                  accessibilityLabel={`Open ${f.name}`}
                />
                  <Text style={styles.dayUploadFileName} numberOfLines={2}>
                    {f.name}
                  </Text>
                  <View style={styles.dayUploadActions}>
                    <TouchableOpacity
                      style={styles.smallBtn}
                      onPress={() => Linking.openURL(f.url).catch(() => {})}
                      accessibilityLabel="Open uploaded file"
                    >
                      <Text style={styles.smallBtnText}>Open</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.dayUploadDeleteBtn}
                      onPress={() => removeDayFile(f.id)}
                      accessibilityLabel="Delete uploaded file"
                    >
                      <Ionicons name="trash-outline" size={14} color={theme.status.error} />
                    </TouchableOpacity>
                  </View>
                </View>
            ))}
          </View>
        )}
      </View>
    );
  }, [removeDayFile, selectedDateKey, styles, theme]);
  useEffect(() => {
    if (!uid || !selectedTourFilterId || !selectedDateKey) return undefined;
    if (!daySheetScheduleDirty) return undefined;
    const serialized = serializeScheduleDoc({ lines: daySheetScheduleRows });
    const timer = setTimeout(() => {
      persistScheduleTabEntry(selectedDateKey, serialized)
        .then(() => setDaySheetScheduleDirty(false))
        .catch((e) => console.error('save day sheet schedule', e));
    }, 450);
    return () => clearTimeout(timer);
  }, [daySheetScheduleDirty, daySheetScheduleRows, selectedDateKey, selectedTourFilterId, uid, persistScheduleTabEntry]);
  const expandedNotesCurrentText = expandedNotesModal.dateKey ? String(tabGridDraftByDate[expandedNotesModal.dateKey] || '') : '';
  const expandedNotesHasChanges = expandedNotesModal.open && expandedNotesCurrentText !== expandedNotesBaseline;
  const expandedNotesDayStyle = useMemo(() => {
    const dk = expandedNotesModal.dateKey;
    if (!dk) return { textColor: '#111111', textAlign: 'left', fontSize: 13 };
    return tabGridStyleByDate[dk] || { textColor: '#111111', textAlign: 'left', fontSize: 13 };
  }, [expandedNotesModal.dateKey, tabGridStyleByDate]);
  const expandedScheduleCurrentSerialized = expandedScheduleModal.dateKey
    ? serializeScheduleDoc(parseScheduleText(String(tabGridDraftByDate[expandedScheduleModal.dateKey] || '')))
    : '';
  const expandedScheduleHasChanges =
    expandedScheduleModal.open && expandedScheduleCurrentSerialized !== expandedScheduleBaseline;
  const expandedNotesSaveDayCount =
    expandedNotesModal.dateKey && supportsDayApplyScope
      ? countDayApplyScopeDates(tabApplyScope, expandedNotesModal.dateKey, selectedTourShowDateKeys)
      : 1;
  const expandedScheduleSaveDayCount =
    expandedScheduleModal.dateKey && supportsDayApplyScope
      ? countDayApplyScopeDates(tabApplyScope, expandedScheduleModal.dateKey, selectedTourShowDateKeys)
      : 1;
  const expandedStructuredListCurrentSerialized = expandedStructuredListModal.dateKey
    ? serializeStructuredDayListText(String(tabGridDraftByDate[expandedStructuredListModal.dateKey] || ''))
    : '';
  const expandedStructuredListHasChanges =
    expandedStructuredListModal.open &&
    expandedStructuredListCurrentSerialized !== expandedStructuredListBaseline;
  const expandedStructuredListSaveDayCount =
    expandedStructuredListModal.dateKey && supportsDayApplyScope
      ? countDayApplyScopeDates(tabApplyScope, expandedStructuredListModal.dateKey, selectedTourShowDateKeys)
      : 1;

  useEffect(() => {
    if (!selectedDateKey || !activeCalendarTabKey || !selectedTourFilterId) return undefined;
    if (isRiderTab) return undefined;
    if (tabApplyScope !== DAY_APPLY_SCOPES.THIS_DAY) return undefined;
    if (expandedNotesModal.open && expandedNotesModal.dateKey === selectedDateKey) return undefined;
    if (expandedScheduleModal.open && expandedScheduleModal.dateKey === selectedDateKey) return undefined;
    if (expandedStructuredListModal.open && expandedStructuredListModal.dateKey === selectedDateKey) return undefined;
    if (!tabEntryDirtyRef.current[selectedDateKey]) return undefined;
    const timer = setTimeout(() => {
      persistTabGridCell(selectedDateKey, { applyScope: DAY_APPLY_SCOPES.THIS_DAY }).catch((e) => {
        console.error('autosave tab day entry', e);
      });
    }, 450);
    return () => clearTimeout(timer);
  }, [
    selectedDateKey,
    activeCalendarTabKey,
    selectedTourFilterId,
    selectedTabDayText,
    tabApplyScope,
    expandedNotesModal.open,
    expandedNotesModal.dateKey,
    expandedScheduleModal.open,
    expandedScheduleModal.dateKey,
    expandedStructuredListModal.open,
    expandedStructuredListModal.dateKey,
    persistTabGridCell,
    isRiderTab,
  ]);

  useEffect(() => {
    setTabApplyScope(DAY_APPLY_SCOPES.THIS_DAY);
  }, [selectedTourFilterId, activeCalendarTabKey, riderSubtab, activeTopTab]);

  useEffect(() => {
    if (calView !== 'day') return;
    const key = toDateKey(cursorDate);
    setSelectedDateKey(key);
    setSelectedEventId(null);
  }, [calView, cursorDate]);

  const useDesktopSidebar = !isCompactMonthLayout && !masterTourUi;
  const useMasterTourFullCanvas = Boolean(masterTourUi);

  const sidebarTabProps = useMemo(
    () => ({
      isRiderTab,
      isRiderFullMode,
      isStageTab,
      isDaySheetTab,
      addRiderFullFileToSelectedDay,
      uploadStageFileFromAsset,
      uploadFilesTabFromAsset,
      dayFileSaving,
      saveStageFilesForSelectedDay,
      renderSelectedDayFilesPanel,
      selectedDayRiderFullFiles,
      selectedDayStageFiles,
      riderFullDocUrl,
      riderFullIsImage,
      riderFullIsPdf,
      riderFullPdfPage,
      setRiderFullPdfPage,
      daySheetMode,
      setDaySheetMode,
      uploadDaySheetAsset,
      daySheetAssetSaving,
      saveDaySheetPublication,
      hasUnsavedDaySheetPublication,
      daySheetPublicationSaving,
      daySheetSeededFromDefaults,
      selectedDayVenueName,
      daySheetBannerUrl,
      daySheetBgUrl,
      daySheetPreviewRows,
      daySheetNotes,
      setDaySheetNotes,
      daySheetScheduleSerialized,
      handleDaySheetScheduleChange,
      placeholderColor,
      updateTabGridCellDraft,
      supportsDayApplyScope,
      tabApplyScope,
      setTabApplyScope,
      selectedTourShowDateKeys,
      publishTabContentForSelectedDay,
      tabApplyScopeSaving,
      selectedTabHasUnpublishedDraft,
      tabDraftSaveState,
      ownerId: uid,
      currentUserId: uid,
      ownerName: ownerDisplayName,
      ownerEmail: userData?.email,
      ownerPhotoURL: userData?.photoURL,
      tourId: selectedTourFilterId,
      tourName: selectedTourFilterName,
      tourShareSettings,
      riderSubtab,
      onRiderSubtabChange: handleRiderSubtabPress,
      onWriterError: (text) => showToast(text || 'Something went wrong.', 'error'),
      onWriterSuccess: (text) => showToast(text || 'Rider saved.', 'success'),
      onTourPartyError: (text) => showToast(text || 'Could not send invite.', 'error'),
      onTourPartySuccess: (text) => showToast(text || 'Invitation sent.', 'success'),
      sidebarWide,
      riderShares,
      onOpenTourSettings: openTourSettingsRail,
      selectedTourFolder,
      loadTourFolders,
      goTourPlanner,
      canEditActiveCalendarTab,
    }),
    [
      isRiderTab,
      isRiderFullMode,
      isStageTab,
      isDaySheetTab,
      addRiderFullFileToSelectedDay,
      uploadStageFileFromAsset,
      uploadFilesTabFromAsset,
      dayFileSaving,
      saveStageFilesForSelectedDay,
      renderSelectedDayFilesPanel,
      selectedDayRiderFullFiles,
      selectedDayStageFiles,
      riderFullDocUrl,
      riderFullIsImage,
      riderFullIsPdf,
      riderFullPdfPage,
      daySheetMode,
      uploadDaySheetAsset,
      daySheetAssetSaving,
      saveDaySheetPublication,
      hasUnsavedDaySheetPublication,
      daySheetPublicationSaving,
      daySheetSeededFromDefaults,
      selectedDayVenueName,
      daySheetBannerUrl,
      daySheetBgUrl,
      daySheetPreviewRows,
      daySheetNotes,
      daySheetScheduleSerialized,
      handleDaySheetScheduleChange,
      placeholderColor,
      updateTabGridCellDraft,
      supportsDayApplyScope,
      tabApplyScope,
      selectedTourShowDateKeys,
      publishTabContentForSelectedDay,
      tabApplyScopeSaving,
      selectedTabHasUnpublishedDraft,
      tabDraftSaveState,
      uid,
      ownerDisplayName,
      userData?.email,
      userData?.photoURL,
      selectedTourFilterId,
      selectedTourFilterName,
      riderSubtab,
      handleRiderSubtabPress,
      tourShareSettings,
      showToast,
      sidebarWide,
      riderShares,
      selectedTourFolder,
      loadTourFolders,
      goTourPlanner,
      canEditActiveCalendarTab,
    ]
  );

  if (!uid) {
    return (
      <View style={styles.centered}>
        <Text style={styles.muted}>Sign in to use My Calendar.</Text>
      </View>
    );
  }

  // Shared helpers for toolbar
  const calNavTitle = calView === 'year' ? String(cursor.y)
    : calView === 'week' ? (() => {
        const start = startOfWeek(cursorDate, weekStartsOn);
        const end = new Date(start); end.setDate(end.getDate() + 6);
        const sm = MONTH_SHORT_LABELS[start.getMonth()]; const em = MONTH_SHORT_LABELS[end.getMonth()];
        return sm === em
          ? `${sm} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`
          : `${sm} ${start.getDate()} – ${em} ${end.getDate()}`;
      })()
    : calView === 'day'
      ? `${MONTH_SHORT_LABELS[cursorDate.getMonth()]} ${cursorDate.getDate()}, ${cursorDate.getFullYear()}`
      : monthLabel(cursor.y, cursor.m);

  const handleCalPrev = () => {
    if (calView === 'year') { setCursor((c) => ({ ...c, y: c.y - 1 })); return; }
    if (calView === 'week') { setCursorDate((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; }); return; }
    if (calView === 'day') { setCursorDate((d) => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; }); return; }
    setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  };
  const handleCalNext = () => {
    if (calView === 'year') { setCursor((c) => ({ ...c, y: c.y + 1 })); return; }
    if (calView === 'week') { setCursorDate((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; }); return; }
    if (calView === 'day') { setCursorDate((d) => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; }); return; }
    setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));
  };
  const handleCalViewChange = (v) => {
    if (masterTourUi) {
      setActiveTopTab(null);
      setV2Canvas(null);
    }
    setCalView(v);
    if (v === 'day' || v === 'week') {
      if (selectedDateKey) {
        const parts = selectedDateKey.split('-').map(Number);
        if (parts.length === 3) setCursorDate(new Date(parts[0], parts[1] - 1, parts[2]));
      }
    }
    if (v === 'month' || v === 'year') {
      setCursor({ y: cursorDate.getFullYear(), m: cursorDate.getMonth() });
    }
  };

  const handleClaudeView = (id) => {
    if (id === 'stage') {
      setActiveTopTab(null);
      setV2Canvas('riders');
      return;
    }
    if (id === 'notes') {
      setActiveTopTab('NOTES');
      setV2Canvas('notes');
      return;
    }
    setActiveTopTab(null);
    if (id === 'month' || id === 'week') {
      setV2Canvas(id);
      handleCalViewChange(id);
      return;
    }
    if (id === 'year') {
      setV2Canvas(null);
      handleCalViewChange(id);
      return;
    }
    setV2Canvas(id);
  };

  const openLodgingForDate = (dateKey) => {
    if (!dateKey) return;
    // Nested in the day cell — do not count this as a second tap (that opens day details).
    handleDayCellPress(dateKey, { skipDoubleTap: true });
    handleClaudeView('lodging');
  };

  const jumpV2Date = (key) => {
    if (!key) return;
    selectDate(key);
    const parts = String(key).split('-').map(Number);
    if (parts.length === 3) {
      setCursorDate(new Date(parts[0], parts[1] - 1, parts[2]));
      setCursor({ y: parts[0], m: parts[1] - 1 });
    }
  };

  useEffect(() => {
    if (Platform.OS !== 'web' || !masterTourUi) return;
    const paramCanvas = canvasFromSlug(route?.params?.canvas);
    const paramDate = isCalendarDateKey(route?.params?.dateKey) ? route.params.dateKey : '';
    const paramTourId = String(route?.params?.tourId || '').trim();
    if (calendarPathSyncing.current) {
      calendarPathSyncing.current = false;
      return;
    }
    if (paramDate && paramDate !== selectedDateKey) {
      jumpV2Date(paramDate);
    }
    const currentCanvas = v2Canvas || (calView === 'week' ? 'week' : 'month');
    if (paramCanvas && paramCanvas !== currentCanvas) {
      handleClaudeView(paramCanvas);
    }
    if (paramTourId && paramTourId !== selectedTourFilterId) {
      setSelectedTourFilterId(paramTourId);
    }
  }, [route?.params?.canvas, route?.params?.dateKey, route?.params?.tourId]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !masterTourUi || !navigation?.setParams) return;
    const canvas = v2Canvas || calView || 'month';
    const dateKey = selectedDateKey || undefined;
    const routeCanvas = canvasFromSlug(route?.params?.canvas) || 'month';
    if (routeCanvas === canvas && route?.params?.dateKey === dateKey) return;
    calendarPathSyncing.current = true;
    navigation.setParams({ canvas, dateKey });
  }, [v2Canvas, selectedDateKey, calView, masterTourUi]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId || !selectedDateKey) {
      setGuestVenueLink('');
      return undefined;
    }
    const db = getFirestore();
    const linkId = guestListVenueLinkId(dataTourId, selectedDateKey);
    const ref = doc(db, 'users', dataUid, 'calendarShareLinks', linkId);
    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setGuestVenueLink('');
          return;
        }
        const data = snap.data() || {};
        const token = String(data.token || '').trim();
        setGuestVenueLink(token ? buildShareUrl(resolveAppBaseUrl(), token) : '');
        if (Object.prototype.hasOwnProperty.call(data, 'guestSpots')) {
          const serialized = serializeGuestCap(data.guestSpots);
          setV2DayTabs((prev) => (prev[GUEST_CAP_TAB] === serialized ? prev : { ...prev, [GUEST_CAP_TAB]: serialized }));
        }
      },
      () => setGuestVenueLink('')
    );
  }, [uid, selectedTourFilterId, selectedDateKey]);

  useEffect(() => {
    if (!uid || !selectedTourFilterId || !selectedDateKey) {
      setContactsShareUrl('');
      return undefined;
    }
    const db = getFirestore();
    const linkId = contactsDayShareLinkId(selectedTourFilterId, selectedDateKey);
    const ref = doc(db, 'users', uid, 'calendarShareLinks', linkId);
    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setContactsShareUrl('');
          return;
        }
        const token = String(snap.data()?.token || '').trim();
        setContactsShareUrl(token ? buildShareUrl(resolveAppBaseUrl(), token) : '');
      },
      () => setContactsShareUrl('')
    );
  }, [uid, selectedTourFilterId, selectedDateKey]);

  const ensureContactsShareLink = useCallback(async () => {
    if (!uid || !selectedTourFilterId || !selectedDateKey) {
      throw new Error('Pick a tour and a show day first.');
    }
    setContactsShareBusy(true);
    try {
      const result = await upsertDayShareLink(getFirestore(), dataUid, {
        tourId: dataTourId,
        tourName: selectedTourFilterName,
        dateKey: selectedDateKey,
        modules: ['CONTACTS'],
        baseUrl: resolveAppBaseUrl(),
        visibility: SHARE_VISIBILITY.LINK,
        label: `Contacts · ${selectedDateKey}`,
        venueName: selectedDayVenueName,
        cityLine: pickTourDayCard(tourDayMetaByDate?.[selectedDateKey], selectedTourFilterId)?.cityLine,
      });
      const url = String(result?.url || '').trim();
      if (url) setContactsShareUrl(url);
      return result;
    } catch (e) {
      console.error('contacts share link', e);
      showToast(e?.message || 'Could not create contacts share link.', 'error');
      throw e;
    } finally {
      setContactsShareBusy(false);
    }
  }, [uid, selectedTourFilterId, selectedDateKey, selectedTourFilterName, selectedDayVenueName, tourDayMetaByDate, showToast]);

  useEffect(() => {
    if (v2Canvas !== 'contacts' || !uid || !selectedTourFilterId || !selectedDateKey) return undefined;
    let cancelled = false;
    ensureContactsShareLink().catch(() => {
      if (!cancelled) setContactsShareUrl('');
    });
    return () => {
      cancelled = true;
    };
  }, [v2Canvas, uid, selectedTourFilterId, selectedDateKey, ensureContactsShareLink]);

  const emailContactsToParty = useCallback(async () => {
    const result = await ensureContactsShareLink();
    const url = String(result?.url || contactsShareUrl || '').trim();
    if (!url) throw new Error('Could not create a share link.');
    const recipients = (v2PartyMembers || [])
      .filter((m) => String(m?.id || '') !== uid)
      .map((m) => ({
        email: String(m?.email || '').trim(),
        userId: String(m?.id || '').trim(),
        name: String(m?.name || '').trim(),
      }))
      .filter((m) => m.email);
    if (!recipients.length) {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
      showToast('No tour party emails yet. Share link copied instead.', 'error');
      throw new Error('Add tour party members with emails, or send the copied link yourself.');
    }
    await sendShareLinkToRecipients({
      db: getFirestore(),
      ownerId: uid,
      ownerName: ownerDisplayName,
      linkId: result.id,
      shareToken: result.token,
      shareUrl: url,
      tourName: selectedTourFilterName,
      dateKey: selectedDateKey,
      dateLabel: formatDateLong(selectedDateKey) || selectedDateKey,
      visibility: SHARE_VISIBILITY.LINK,
      recipients,
      message: `Venue contact sheet${selectedDayVenueName && selectedDayVenueName !== 'VENUE' ? ` for ${selectedDayVenueName}` : ''}. Anyone with the link can view — no login required.`,
    });
    showToast(`Emailed contacts to ${recipients.length} ${recipients.length === 1 ? 'person' : 'people'}.`, 'success');
    return url;
  }, [
    ensureContactsShareLink,
    contactsShareUrl,
    v2PartyMembers,
    uid,
    ownerDisplayName,
    selectedTourFilterName,
    selectedDateKey,
    selectedDayVenueName,
    showToast,
  ]);

  const renderSelectedDayDetailPanel = () => (
    <SelectedDayDetailPanel
      selectedDateKey={selectedDateKey}
      selectedDayEvents={selectedDayEventsRaw}
      tourDayMetaByDate={tourDayMetaByDate}
      selectedTourFilterId={selectedTourFilterId}
      onSelectTour={handleSelectTourForDay}
      tourColorById={tourColorById}
      activeCalendarTabKey={activeCalendarTabKey}
      activeTopTab={activeTopTab}
      isStageTab={isStageTab}
      isDaySheetTab={isDaySheetTab}
      selectedTabDayText={selectedTabDayText}
      selectedTabDayStyle={selectedTabDayStyle}
      dayFilesByDate={dayFilesByDate}
      theme={theme}
      styles={styles}
      allowAddEvent={!selectedTourFilterId}
      openNew={openNew}
      openExpandedTabEditor={openExpandedTabEditor}
      openDayFilesManager={openDayFilesManager}
      openDayShareModal={openDayShareModal}
      openEdit={openEdit}
      goTourPlanner={goTourPlanner}
      showTourPlannerNav={showTourPlannerNav}
      moveToNextDay={moveToNextDay}
      onChangeSelectedDayTabText={(t) => selectedDateKey && updateTabGridCellDraft(selectedDateKey, t)}
      updateTabGridCellDraft={updateTabGridCellDraft}
      supportsDayApplyScope={supportsDayApplyScope}
      tabApplyScope={tabApplyScope}
      setTabApplyScope={setTabApplyScope}
      selectedTourShowDateKeys={selectedTourShowDateKeys}
      publishTabContentForSelectedDay={publishTabContentForSelectedDay}
      selectedTabHasUnpublishedDraft={selectedTabHasUnpublishedDraft}
      tabDraftSaveState={tabDraftSaveState}
      tabApplyScopeSaving={tabApplyScopeSaving}
      uploadStageFileFromAsset={uploadStageFileFromAsset}
      uploadFilesTabFromAsset={uploadFilesTabFromAsset}
      dayFileSaving={dayFileSaving}
      saveStageFilesForSelectedDay={saveStageFilesForSelectedDay}
      selectedDayStageFiles={selectedDayStageFiles}
      renderSelectedDayFilesPanel={renderSelectedDayFilesPanel}
      showMobileSharedLinks={isCompactMonthLayout}
      onOpenSharedLinks={() => setSharedLinksOpen(true)}
      onOpenTourSettings={openTourSettingsRail}
      tourShareSettings={tourShareSettings}
      onScheduleToast={showToast}
      onTopTabPress={handleTopTabPress}
      onPrevDay={() => navigateSelectedDay(-1)}
      onNextDay={() => navigateSelectedDay(1)}
      ownerId={uid}
      ownerName={ownerDisplayName}
      ownerEmail={userData?.email}
      ownerPhotoURL={userData?.photoURL}
      tourId={selectedTourFilterId}
      tourName={selectedTourFilterName}
      riderSubtab={riderSubtab}
      onRiderSubtabChange={handleRiderSubtabPress}
      onWriterError={(text) => showToast(text || 'Something went wrong.', 'error')}
      onWriterSuccess={(text) => showToast(text || 'Rider saved.', 'success')}
      onTourPartyError={(text) => showToast(text || 'Could not send invite.', 'error')}
      onTourPartySuccess={(text) => showToast(text || 'Invitation sent.', 'success')}
      currentUserId={uid}
      selectedTourFolder={selectedTourFolder}
      loadTourFolders={loadTourFolders}
      canEditActiveCalendarTab={canEditActiveCalendarTab}
    />
  );

  const tourPartyRailEl = selectedTourFilterId ? (
    <TourPartyRail
      ownerId={uid}
      ownerName={ownerDisplayName}
      ownerEmail={userData?.email}
      ownerPhotoURL={userData?.photoURL}
      tourId={selectedTourFilterId}
      tourName={selectedTourFilterName}
      currentUserId={uid}
      tourShareSettings={tourShareSettings}
      theme={theme}
      layout="vertical"
      visible={selectedDateHasTourDoor}
      doorDateKey={selectedDateKey}
      onSuccess={(text) => showToast(text || 'Done.', 'success')}
      onError={(text) => showToast(text || 'Something went wrong.', 'error')}
    />
  ) : null;

  const calendarDayPanelEl = (
    <CalendarDayPanel
      selectedDateKey={selectedDateKey}
      selectedEventId={selectedEventId}
      setSelectedEventId={setSelectedEventId}
      eventsByDate={eventsByDate}
      tourDayMetaByDate={tourDayMetaByDate}
      selectedTourFilterId={selectedTourFilterId}
      onSelectTour={handleSelectTourForDay}
      tourColorById={tourColorById}
      openNew={openNew}
      openEdit={openEdit}
      deleteEvent={deleteEvent}
      deleteTourFromCalendar={deleteTourFromCalendar}
      openMoveToMonth={openMoveToMonth}
      goTourPlanner={goTourPlanner}
      showTourPlannerNav={showTourPlannerNav}
      activeTopTab={activeTopTab}
      activeCalendarTabKey={activeCalendarTabKey}
      selectedDayTabText={selectedTabDayText}
      selectedDayTabStyle={selectedTabDayStyle}
      onChangeSelectedDayTabText={(t) => selectedDateKey && updateTabGridCellDraft(selectedDateKey, t)}
      onChangeSelectedDayTabStyle={(patch) => selectedDateKey && updateTabGridCellStyle(selectedDateKey, patch)}
      dayFilesByDate={dayFilesByDate}
      onOpenExpandedEditor={openExpandedTabEditor}
      onOpenFilesManager={openDayFilesManager}
      onOpenDayShare={openDayShareModal}
      onOpenTourSettings={openTourSettingsRail}
      onOpenSharedLinks={() => setSharedLinksOpen(true)}
      tourShareSettings={tourShareSettings}
      selectedTourShowDateKeys={selectedTourShowDateKeys}
      onScheduleToast={showToast}
      ownerId={uid}
      tourId={selectedTourFilterId}
      allowAddEvent={!selectedTourFilterId}
      sidebarTabProps={sidebarTabProps}
      sidebarWidth={sidebarWidth}
      sidebarWide={sidebarWide}
      fillMain={useMasterTourFullCanvas}
      onToggleSidebarWide={toggleSidebarWide}
      onCollapsePanel={useMasterTourFullCanvas ? undefined : () => setSidebarPanelExpanded(false)}
      onTopTabPress={handleTopTabPress}
      onPrevDay={() => navigateSelectedDay(-1)}
      onNextDay={() => navigateSelectedDay(1)}
      theme={theme}
      styles={styles}
    />
  );

  const timeGridTabProps = selectedTourFilterId && activeTopTab
    ? {
        activeTopTab,
        tabTextByDate: tabGridDraftByDate,
        tabStyleByDate: tabGridStyleByDate,
        selectedDayTabStyle: selectedTabDayStyle,
        dayFilesByDate,
        scheduleCellLabels,
        structuredListCellLabels,
        allowGridInlineEditAllDays: gridEditEnabled,
        selectedTourFilterId,
        tourDayMetaByDate,
        onChangeDayTabText: updateTabGridCellDraft,
        onOpenExpandedEditor: openExpandedTabEditor,
        onOpenFilesManager: openDayFilesManager,
        onOpenStageFiles: openStageFilesManager,
        styles,
      }
    : null;

  const renderCompactCalendarGrid = () => {
    if (calView === 'year') {
              return (
        <CalendarYearView
          year={cursor.y}
          eventsByDate={eventsByDate}
          tourDayMetaByDate={tourDayMetaByDate}
          selectedDateKey={selectedDateKey}
          theme={theme}
          onMonthPress={(m) => { setCursor({ y: cursor.y, m }); setCalView('month'); }}
          onDayPress={handleDayCellPress}
          weekStartsOn={weekStartsOn}
        />
      );
    }
    if (calView === 'week') {
                return (
        <CalendarWeekView
          cursorDate={cursorDate}
          eventsByDate={eventsByDate}
          tourDayMetaByDate={tourDayMetaByDate}
          selectedDateKey={selectedDateKey}
          theme={theme}
          onDayCellPress={handleDayCellPress}
          openNew={openNew}
          timeGridTabProps={timeGridTabProps}
          selectedTourFilterId={selectedTourFilterId}
          weekStartsOn={weekStartsOn}
          tour={selectedTourFolder?.tour}
          scheduleByDate={{
            ...schedulePublishedByDate,
            ...(v2DayTabs.SCHEDULE && selectedDateKey ? { [selectedDateKey]: v2DayTabs.SCHEDULE } : {}),
          }}
          showTravelDetail={v2Prefs.showTravelDetail}
          accent={v2Prefs.accent}
          timeFormat={v2Prefs.timeFormat}
          onHotelPress={openLodgingForDate}
          contactsByDate={{
            ...contactsPublishedByDate,
            ...(v2DayTabs.CONTACTS && selectedDateKey ? { [selectedDateKey]: v2DayTabs.CONTACTS } : {}),
          }}
        />
      );
    }
    if (calView === 'day') {
      return (
        <CalendarDayView
          cursorDate={cursorDate}
          selectedDateKey={selectedDateKey}
          eventsByDate={eventsByDate}
          tourDayMetaByDate={tourDayMetaByDate}
          theme={theme}
          onDayCellPress={handleDayCellPress}
          openNew={openNew}
          openEdit={openEdit}
          deleteEvent={deleteEvent}
          timeGridTabProps={timeGridTabProps}
          selectedTourFilterId={selectedTourFilterId}
          lodgingSummary={formatLodgingDaySummary(selectedTourFolder?.tour, selectedDateKey)}
          groundLine={v2GroundLine}
          notesText={v2DayTabs.NOTES || daySheetNotes || ''}
          guestCount={v2GuestCount}
          partyMembers={v2PartyMembers}
          v2Prefs={v2Prefs}
          scheduleText={v2DayTabs.SCHEDULE ?? schedulePublishedByDate[selectedDateKey] ?? ''}
        />
      );
    }
    return (
      <CalendarCompactMonthView
        weekRows={weekRows}
        eventsByDate={eventsByDate}
        selectedDateKey={selectedDateKey}
        onDayCellPress={handleDayCellPress}
        tourDayMetaByDate={tourDayMetaByDate}
        tourColorById={tourColorById}
        selectedTourFilterId={selectedTourFilterId}
        shareScheduleIndicatorsByDate={shareScheduleIndicatorsByDate}
        activeTopTab={activeTopTab}
        tabTextByDate={tabGridDraftByDate}
        tabStyleByDate={tabGridStyleByDate}
        selectedDayTabStyle={selectedTabDayStyle}
        dayFilesByDate={dayFilesByDate}
        allowGridInlineEditAllDays={Boolean(selectedTourFilterId && activeTopTab)}
        onChangeDayTabText={(dateKey, t) => updateTabGridCellDraft(dateKey, t)}
        onOpenExpandedEditor={openExpandedTabEditor}
        onOpenFilesManager={openDayFilesManager}
        onOpenStageFiles={openStageFilesManager}
                  theme={theme}
        styles={styles}
        weekStartsOn={weekStartsOn}
      />
    );
  };

  const renderDesktopCalendarMain = () => {
    if (calView === 'year') {
      return (
        <CalendarYearView
          year={cursor.y}
          eventsByDate={eventsByDate}
          tourDayMetaByDate={tourDayMetaByDate}
          selectedDateKey={selectedDateKey}
                        theme={theme}
          onMonthPress={(m) => { setCursor({ y: cursor.y, m }); setCalView('month'); }}
          onDayPress={handleDayCellPress}
          weekStartsOn={weekStartsOn}
        />
      );
    }
    if (calView === 'week') {
      return (
        <CalendarWeekView
          cursorDate={cursorDate}
          eventsByDate={eventsByDate}
          tourDayMetaByDate={tourDayMetaByDate}
          selectedDateKey={selectedDateKey}
          theme={theme}
          onDayCellPress={handleDayCellPress}
          openNew={openNew}
          timeGridTabProps={timeGridTabProps}
          selectedTourFilterId={selectedTourFilterId}
          weekStartsOn={weekStartsOn}
          tour={selectedTourFolder?.tour}
          scheduleByDate={{
            ...schedulePublishedByDate,
            ...(v2DayTabs.SCHEDULE && selectedDateKey ? { [selectedDateKey]: v2DayTabs.SCHEDULE } : {}),
          }}
          showTravelDetail={v2Prefs.showTravelDetail}
          accent={v2Prefs.accent}
          timeFormat={v2Prefs.timeFormat}
          onHotelPress={openLodgingForDate}
          contactsByDate={{
            ...contactsPublishedByDate,
            ...(v2DayTabs.CONTACTS && selectedDateKey ? { [selectedDateKey]: v2DayTabs.CONTACTS } : {}),
          }}
        />
      );
    }
    if (calView === 'day') {
      return (
        <CalendarDayView
          cursorDate={cursorDate}
          selectedDateKey={selectedDateKey}
          eventsByDate={eventsByDate}
          tourDayMetaByDate={tourDayMetaByDate}
          theme={theme}
          onDayCellPress={handleDayCellPress}
          openNew={openNew}
          openEdit={openEdit}
          deleteEvent={deleteEvent}
          timeGridTabProps={timeGridTabProps}
          selectedTourFilterId={selectedTourFilterId}
          lodgingSummary={formatLodgingDaySummary(selectedTourFolder?.tour, selectedDateKey)}
          groundLine={v2GroundLine}
          notesText={v2DayTabs.NOTES || daySheetNotes || ''}
          guestCount={v2GuestCount}
          partyMembers={v2PartyMembers}
          v2Prefs={v2Prefs}
          scheduleText={v2DayTabs.SCHEDULE ?? schedulePublishedByDate[selectedDateKey] ?? ''}
        />
      );
    }
    if (Platform.OS === 'web') {
      return (
            <CalendarWebLargeDnD
              weekRows={weekRows}
              weekStartsOn={weekStartsOn}
              eventsByDate={eventsByDate}
              events={events}
              selectedDateKey={selectedDateKey}
              selectDate={selectDate}
              onDayCellPress={handleDayCellPress}
              selectedEventId={selectedEventId}
              setSelectedEventId={setSelectedEventId}
              styles={styles}
              theme={theme}
              uid={uid}
              v2Dense={v2Prefs.density === 'compact'}
              openNew={openNew}
              goTourPlanner={goTourPlanner}
              showTourPlannerNav={showTourPlannerNav}
              openEdit={openEdit}
              deleteEvent={deleteEvent}
              deleteTourFromCalendar={deleteTourFromCalendar}
              openMoveToMonth={openMoveToMonth}
              onDragError={(msg) =>
                setMessageModal({ variant: 'error', title: 'Could not move event', text: msg || 'Unknown error' })
              }
          activeTopTab={activeTopTab}
          activeCalendarTabKey={activeCalendarTabKey}
              selectedDayTabText={selectedTabDayText}
              selectedDayTabStyle={selectedTabDayStyle}
              tabTextByDate={tabGridDraftByDate}
              tabStyleByDate={tabGridStyleByDate}
              dayFilesByDate={dayFilesByDate}
              tourDayMetaByDate={tourDayMetaByDate}
          selectedTourFilterId={selectedTourFilterId}
          onSelectTour={handleSelectTourForDay}
          tourColorById={tourColorById}
              onChangeSelectedDayTabText={(t) => selectedDateKey && updateTabGridCellDraft(selectedDateKey, t)}
              onChangeSelectedDayTabStyle={(patch) => selectedDateKey && updateTabGridCellStyle(selectedDateKey, patch)}
              onChangeDayTabText={(dateKey, t) => updateTabGridCellDraft(dateKey, t)}
              allowAddEvent={!selectedTourFilterId}
              onOpenExpandedEditor={openExpandedTabEditor}
              onOpenFilesManager={openDayFilesManager}
              onOpenStageFiles={openStageFilesManager}
              onOpenDayShare={openDayShareModal}
          shareScheduleIndicatorsByDate={shareScheduleIndicatorsByDate}
          allowGridInlineEditAllDays={gridEditEnabled}
          scheduleCellLabels={scheduleCellLabels}
          structuredListCellLabels={structuredListCellLabels}
          useMonthHorizontalScroll={useMonthHorizontalScroll}
              onHotelChipPress={openLodgingForDate}
            />
      );
    }
    return (
            <CalendarNativeLarge
              weekRows={weekRows}
              weekStartsOn={weekStartsOn}
              eventsByDate={eventsByDate}
              selectedDateKey={selectedDateKey}
              selectDate={selectDate}
              onDayCellPress={handleDayCellPress}
              selectedEventId={selectedEventId}
              setSelectedEventId={setSelectedEventId}
              styles={styles}
              theme={theme}
              v2Dense={v2Prefs.density === 'compact'}
              openNew={openNew}
              goTourPlanner={goTourPlanner}
              showTourPlannerNav={showTourPlannerNav}
              openEdit={openEdit}
              openMoveToMonth={openMoveToMonth}
              deleteEvent={deleteEvent}
              deleteTourFromCalendar={deleteTourFromCalendar}
              activeTopTab={activeCalendarTabKey}
              selectedDayTabText={selectedTabDayText}
              selectedDayTabStyle={selectedTabDayStyle}
              tabTextByDate={tabGridDraftByDate}
              tabStyleByDate={tabGridStyleByDate}
              dayFilesByDate={dayFilesByDate}
              tourDayMetaByDate={tourDayMetaByDate}
              onChangeSelectedDayTabText={(t) => selectedDateKey && updateTabGridCellDraft(selectedDateKey, t)}
              onChangeSelectedDayTabStyle={(patch) => selectedDateKey && updateTabGridCellStyle(selectedDateKey, patch)}
              onChangeDayTabText={(dateKey, t) => updateTabGridCellDraft(dateKey, t)}
              allowAddEvent={!selectedTourFilterId}
              onOpenExpandedEditor={openExpandedTabEditor}
              onOpenFilesManager={openDayFilesManager}
              onOpenStageFiles={openStageFilesManager}
              onOpenDayShare={openDayShareModal}
        shareScheduleIndicatorsByDate={shareScheduleIndicatorsByDate}
        allowGridInlineEditAllDays={gridEditEnabled}
        scheduleCellLabels={scheduleCellLabels}
        structuredListCellLabels={structuredListCellLabels}
        selectedTourFilterId={selectedTourFilterId}
        useMonthHorizontalScroll={useMonthHorizontalScroll}
        onHotelChipPress={openLodgingForDate}
      />
    );
  };

  const isTodaySelected = selectedDateKey === todayDateKey;

  const tourPickerModalMaxHeight = Math.max(380, Math.round(windowHeight * 0.58));

  const renderTourPickerTourRow = (t) => {
    const tourColor = tourColorById[t.id] || BRAND;
    const isHidden = hiddenTourIds.has(t.id);
    const isManaged = selectedTourFilterId === t.id;
    const meta = t.meta;
    const statusLabel = t.inView
      ? `In ${calendarViewRange.label}`
      : t.status === 'active'
        ? 'In progress'
        : t.status === 'upcoming'
          ? 'Upcoming'
          : t.status === 'past'
            ? 'Past'
            : 'No dates';
    const statusColor = t.inView
      ? tourColor
      : t.status === 'upcoming' || t.status === 'active'
        ? '#4ab6d1'
        : t.status === 'past'
          ? theme.text.secondary
          : '#e8a03a';
    const dateLabel = formatTourDateRange(meta?.firstDate, meta?.lastDate);
    const detailParts = [
      `${meta?.dayCount || 0} day${meta?.dayCount === 1 ? '' : 's'}`,
      `${meta?.venueCount || 0} venue${meta?.venueCount === 1 ? '' : 's'}`,
    ];
    if (meta?.scheduledDayCount && meta.scheduledDayCount !== meta.dayCount) {
      detailParts.push(`${meta.scheduledDayCount} dated`);
    }

    return (
      <TouchableOpacity
        key={t.id}
        activeOpacity={0.75}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: 13,
          paddingHorizontal: 12,
          borderRadius: 12,
          marginBottom: 8,
          borderWidth: isManaged ? 2 : 1,
          borderColor: isManaged ? tourColor : theme.ui.border,
          backgroundColor: isManaged ? (tourColor + '18') : theme.background.card,
          opacity: isHidden && !isManaged ? 0.55 : 1,
        }}
        onPress={() => {
          if (isManaged) {
            setSelectedTourFilterId(null);
            setActiveTopTab(null);
            setTourPickerOpen(false);
          } else {
            manageTour(t);
          }
        }}
      >
        <View style={{
          width: 4, height: 52, borderRadius: 2,
          backgroundColor: tourColor,
          marginRight: 12,
          alignSelf: 'stretch',
        }} />

        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{
              fontSize: 15, fontWeight: '800',
              color: theme.text.primary,
              flexShrink: 1,
            }} numberOfLines={1}>
              {t.name}
              </Text>
            <View style={{
              paddingHorizontal: 7,
              paddingVertical: 2,
              borderRadius: 999,
              backgroundColor: statusColor + '22',
              borderWidth: 1,
              borderColor: statusColor + '55',
            }}>
              <Text style={{ fontSize: 10, fontWeight: '700', color: statusColor }} numberOfLines={1}>
                {statusLabel}
              </Text>
            </View>
          </View>
          <Text style={{ fontSize: 12, fontWeight: '600', color: theme.text.primary, marginBottom: 2 }} numberOfLines={1}>
            {dateLabel}
          </Text>
          <Text style={{ fontSize: 11, color: theme.text.secondary }} numberOfLines={1}>
            {detailParts.join(' · ')}
            {meta?.firstVenueLabel ? ` · ${meta.firstVenueLabel}` : ''}
            {isManaged ? ' · Managing' : ''}
          </Text>
        </View>

        {isManaged ? (
          <Ionicons name="checkmark-circle" size={20} color={tourColor} style={{ marginRight: 8 }} />
        ) : null}

        <TouchableOpacity
          hitSlop={12}
          style={{ padding: 6 }}
          onPress={(e) => {
            e.stopPropagation?.();
            setHiddenTourIds((prev) => {
              const next = new Set(prev);
              next.has(t.id) ? next.delete(t.id) : next.add(t.id);
              return next;
            });
          }}
        >
          <Ionicons
            name={isHidden ? 'eye-off-outline' : 'eye-outline'}
            size={20}
            color={isHidden ? theme.text.secondary : tourColor}
          />
              </TouchableOpacity>
      </TouchableOpacity>
    );
  };

  const renderSharedTourRow = (row) => {
    const inviteKey = String(row.invite?.id || row.folder?.id || '');
    const isImporting = importingSharedTourId === inviteKey;
    const alreadyOwned = tourFolders.some((t) => t.id === row.folder?.id);
    const statusColor = row.statusLabel === 'Accepted' ? '#3ba378' : '#4ab6d1';

    return (
      <View
        key={inviteKey}
        style={{
          borderWidth: 1,
          borderColor: theme.ui.border,
          borderRadius: 12,
          padding: 12,
          marginBottom: 10,
          backgroundColor: theme.background.card,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
          <View style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: statusColor + '22',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Ionicons name="people-outline" size={20} color={statusColor} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Text style={{ fontSize: 15, fontWeight: '800', color: theme.text.primary, flex: 1 }} numberOfLines={1}>
                {row.folder?.name || row.invite?.tourName || 'Shared tour'}
              </Text>
              <View style={{
                paddingHorizontal: 7,
                paddingVertical: 2,
                borderRadius: 999,
                backgroundColor: statusColor + '22',
                borderWidth: 1,
                borderColor: statusColor + '55',
              }}>
                <Text style={{ fontSize: 10, fontWeight: '700', color: statusColor }}>{row.statusLabel}</Text>
              </View>
            </View>
            <Text style={{ fontSize: 12, color: theme.text.secondary, marginBottom: 2 }} numberOfLines={1}>
              From {row.ownerName}
            </Text>
            <Text style={{ fontSize: 12, fontWeight: '600', color: theme.text.primary, marginBottom: 2 }} numberOfLines={1}>
              {row.dateLabel}
            </Text>
            <Text style={{ fontSize: 11, color: theme.text.secondary }} numberOfLines={2}>
              {row.meta?.dayCount || 0} days · {row.meta?.venueCount || 0} venues
              {row.meta?.firstVenueLabel ? ` · ${row.meta.firstVenueLabel}` : ''}
            </Text>
            {row.invite?.message ? (
              <Text style={{ fontSize: 11, color: theme.text.secondary, marginTop: 6, fontStyle: 'italic' }} numberOfLines={2}>
                “{row.invite.message}”
                        </Text>
                      ) : null}
          </View>
        </View>
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
          <TouchableOpacity
            style={[styles.toolbarBtn, { flex: 1, justifyContent: 'center' }]}
            onPress={() => importSharedTourInvite(row)}
            disabled={isImporting || alreadyOwned || !(row.folder?.tour?.days?.length)}
          >
            {isImporting ? (
              <ActivityIndicator color={theme.button.primaryText} size="small" />
            ) : (
              <>
                <Ionicons name="download-outline" size={15} color={theme.button.primaryText} />
                <Text style={styles.toolbarBtnText}>{alreadyOwned ? 'Already imported' : 'Import to calendar'}</Text>
              </>
            )}
          </TouchableOpacity>
          {row.folder?.sourceTourId ? (
            <TouchableOpacity
              style={[styles.toolbarBtn, styles.toolbarBtnSecondary, { paddingHorizontal: 14 }]}
              onPress={() => {
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  window.open(`/tour/${row.folder.sourceTourId}`, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              <Ionicons name="open-outline" size={15} color={theme.text.primary} />
                        </TouchableOpacity>
                      ) : null}
                    </View>
      </View>
    );
  };

  const v2ViewTabsEl = (
    <View style={styles.calViewToggle}>
      {CLAUDE_VIEW_TABS.filter((t) => t.id !== 'chat').filter((t) => canViewV2Canvas(t.id)).map((t) => {
        const active = v2Canvas ? v2Canvas === t.id : (!v2Canvas && calView === t.id);
        return (
          <TouchableOpacity
            key={t.id}
            style={[styles.calViewBtn, active && styles.calViewBtnActive]}
            onPress={() => handleClaudeView(t.id)}
          >
            <Text style={[styles.calViewBtnText, active && styles.calViewBtnTextActive]}>
              {t.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  if (userData && !canUseCalendar) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="large" color={theme.brand.accent} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {masterTourUi ? (
        <View
          style={styles.v2HeaderBlock}
          onLayout={(e) => {
            const next = Math.round(e.nativeEvent.layout.height || 0);
            if (next > 0) setV2HeaderBlockHeight((prev) => (prev === next ? prev : next));
          }}
        >
        <View style={styles.v2Header}>
          <View style={styles.v2HeaderTitleRow}>
            <View style={styles.v2HeaderLeft}>
              <TouchableOpacity onPress={handleCalPrev} style={styles.v2PagerBtn} accessibilityLabel="Previous">
                <Text style={styles.v2PagerGlyph}>‹</Text>
              </TouchableOpacity>
              <Text style={styles.v2MonthLabel} numberOfLines={1}>{calNavTitle}</Text>
              <TouchableOpacity onPress={handleCalNext} style={styles.v2PagerBtn} accessibilityLabel="Next">
                <Text style={styles.v2PagerGlyph}>›</Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity style={styles.v2HeaderCenter} onPress={openTourPicker} accessibilityLabel="Select tour">
              <Text style={styles.v2TourPickerKicker}>Select tour</Text>
              <View style={styles.v2TourPickerNameRow}>
                <Text style={styles.v2TourName} numberOfLines={1}>
                  {String(selectedTourFilterName || 'All tours').toUpperCase()}
                </Text>
                <Ionicons name="chevron-down" size={16} color={isDarkMode ? '#9aa3ad' : '#66707a'} />
              </View>
              <Text style={styles.v2TourMeta} numberOfLines={1}>{tourPanelLabel}</Text>
            </TouchableOpacity>
            {stackMasterTourHeader ? null : (
              <View style={styles.v2HeaderRight}>{v2ViewTabsEl}</View>
            )}
          </View>
          {stackMasterTourHeader ? (
            <View style={styles.v2HeaderRight}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.v2HeaderTabsScrollContent}
                style={styles.v2HeaderTabsScroll}
              >
                {v2ViewTabsEl}
              </ScrollView>
            </View>
          ) : null}
        </View>
        <MasterTourWeekBar
          flush
          cursorDate={cursorDate}
          selectedDateKey={selectedDateKey}
          weekStartsOn={weekStartsOn}
          dateKeys={selectedTourShowDateKeys}
          tourDayMetaByDate={tourDayMetaByDate}
          selectedTourFilterId={selectedTourFilterId}
          isDarkMode={isDarkMode}
          accent={v2Prefs.accent}
          laneColors={v2Prefs.laneColors}
          showTodayButton={false}
          scrollToTodayToken={weekBarTodayToken}
          onBackToToday={goToToday}
          onDayPress={(dateKey) => {
            if (!dateKey) return;
            handleDayCellPress(dateKey);
            const parts = dateKey.split('-').map(Number);
            if (parts.length === 3) {
              const next = new Date(parts[0], parts[1] - 1, parts[2]);
              setCursorDate(next);
              setCursor({ y: parts[0], m: parts[1] - 1 });
            }
          }}
        />
        </View>
      ) : isCompactMonthLayout ? (
        /* ── Mobile toolbar: 2 compact rows ── */
        <View style={styles.toolbar}>
          {/* Row 1: nav arrows + title + view toggle (single-letter) */}
          <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', gap: 4 }}>
            <TouchableOpacity onPress={handleCalPrev} style={styles.iconBtn}>
              <Ionicons name="chevron-back" size={22} color={chromeIcon} />
                      </TouchableOpacity>
            <Text style={[styles.monthTitle, { flex: 1, fontSize: 16 }]} numberOfLines={1}>{calNavTitle}</Text>
            <TouchableOpacity onPress={handleCalNext} style={styles.iconBtn}>
              <Ionicons name="chevron-forward" size={22} color={chromeIcon} />
                      </TouchableOpacity>
            <View style={[styles.calViewToggle, { flexShrink: 0 }]}>
              {(['day', 'week', 'month', 'year']).map((v) => (
                <TouchableOpacity
                  key={v}
                  style={[styles.calViewBtn, calView === v && styles.calViewBtnActive, { paddingHorizontal: 9 }]}
                  onPress={() => handleCalViewChange(v)}
                >
                  <Text style={[styles.calViewBtnText, calView === v && styles.calViewBtnTextActive]}>
                    {v[0].toUpperCase()}
                  </Text>
                </TouchableOpacity>
              ))}
                    </View>
            <TouchableOpacity
              style={[styles.todayBtn, isTodaySelected && styles.todayBtnActive]}
              onPress={goToToday}
              accessibilityLabel="Go to today"
            >
              <Text style={[styles.todayBtnText, isTodaySelected && styles.todayBtnTextActive]}>Today</Text>
            </TouchableOpacity>
                  </View>
          {/* Row 2: centered tours dropdown + icon-only actions */}
          <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', gap: 8, marginTop: 4 }}>
            <View style={{ flex: 1, alignItems: 'center' }}>
              <TouchableOpacity
                style={[styles.tourDropdownBtn, { maxWidth: 300, width: '100%' }]}
                onPress={openTourPicker}
              >
                {selectedTourFilterId ? (
                  <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: tourColorById[selectedTourFilterId] || BRAND, marginRight: 4 }} />
                ) : (
                  <Ionicons name="layers-outline" size={13} color={chromeIcon} style={{ marginRight: 4 }} />
                )}
                <Text style={styles.tourDropdownText} numberOfLines={1}>{tourPanelLabel}</Text>
                <Ionicons name="chevron-down" size={14} color={chromeIcon} />
              </TouchableOpacity>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <TouchableOpacity style={styles.mobileIconActionBtn} onPress={openPdfExportModal} disabled={exportingPdf}>
              {exportingPdf
                ? <ActivityIndicator color={theme.brand.primary} size="small" />
                : <Ionicons name="document-text-outline" size={18} color={theme.text.primary} />}
            </TouchableOpacity>
            <TouchableOpacity style={styles.mobileIconActionBtn} onPress={runIcalExport} disabled={exportingIcal}>
              {exportingIcal
                ? <ActivityIndicator color={theme.brand.primary} size="small" />
                : <Ionicons name="calendar-outline" size={18} color={theme.text.primary} />}
            </TouchableOpacity>
            </View>
          </View>
        </View>
      ) : (
        /* ── Desktop toolbar: left / center / right clusters ── */
        <View style={styles.toolbar}>
          <View style={styles.toolbarLeftCluster}>
            <TouchableOpacity onPress={handleCalPrev} style={styles.iconBtn}>
              <Ionicons name="chevron-back" size={22} color={chromeIcon} />
            </TouchableOpacity>
            <Text style={styles.monthTitle}>{calNavTitle}</Text>
            <TouchableOpacity onPress={handleCalNext} style={styles.iconBtn}>
              <Ionicons name="chevron-forward" size={22} color={chromeIcon} />
            </TouchableOpacity>
            <View style={styles.calViewToggle}>
              {(['day', 'week', 'month', 'year']).map((v) => (
                <TouchableOpacity
                  key={v}
                  style={[styles.calViewBtn, calView === v && styles.calViewBtnActive]}
                  onPress={() => handleCalViewChange(v)}
                >
                  <Text style={[styles.calViewBtnText, calView === v && styles.calViewBtnTextActive]}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity
              style={[styles.todayBtn, isTodaySelected && styles.todayBtnActive]}
              onPress={goToToday}
              accessibilityLabel="Go to today"
            >
              <Text style={[styles.todayBtnText, isTodaySelected && styles.todayBtnTextActive]}>Today</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.toolbarCenterCluster}>
            <TouchableOpacity
              style={styles.tourDropdownBtn}
              onPress={openTourPicker}
            >
              {selectedTourFilterId ? (
                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: tourColorById[selectedTourFilterId] || BRAND, marginRight: 4 }} />
              ) : (
                <Ionicons name="layers-outline" size={13} color={chromeIcon} style={{ marginRight: 4 }} />
              )}
              <Text style={styles.tourDropdownText} numberOfLines={1}>{tourPanelLabel}</Text>
              <Ionicons name="chevron-down" size={14} color={chromeIcon} />
            </TouchableOpacity>
          </View>
          <View style={styles.toolbarRightCluster}>
          <TouchableOpacity style={[styles.toolbarBtn, styles.toolbarBtnSecondary]} onPress={openPdfExportModal} disabled={exportingPdf}>
            {exportingPdf ? <ActivityIndicator color={theme.brand.primary} size="small" /> : <Ionicons name="document-text-outline" size={16} color={theme.brand.primary} />}
            <Text style={[styles.toolbarBtnText, styles.toolbarBtnTextDark]}>PDF</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.toolbarBtn, styles.toolbarBtnSecondary]} onPress={runIcalExport} disabled={exportingIcal}>
            {exportingIcal ? <ActivityIndicator color={theme.brand.primary} size="small" /> : <Ionicons name="calendar-outline" size={16} color={theme.brand.primary} />}
            <Text style={[styles.toolbarBtnText, styles.toolbarBtnTextDark]}>iCal</Text>
          </TouchableOpacity>
          </View>
        </View>
      )}
      {(selectedTourFilterId || selectedDateHasTourDoor) ? (
        <View style={[styles.topTabsBarWrap, masterTourUi && styles.v2TopTabsBarWrap]}>
          {masterTourUi ? (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.v2TopTabsBarContent}
              style={styles.v2TopTabsBarScroll}
            >
              {CLAUDE_NAV_TABS.map((t) => {
                if (!canViewV2Canvas(t.id)) return null;
                const isActive = v2Canvas === t.id || (!v2Canvas && t.id === 'day' && calView === 'day');
                return (
                  <TouchableOpacity
                    key={t.id}
                    style={styles.v2ClaudeNavBtn}
                    onPress={() => handleClaudeView(t.id)}
                  >
                    <Text style={[styles.v2ClaudeNavText, isActive && styles.v2ClaudeNavTextActive]}>
                      {t.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.topTabsBarContent}
            style={styles.topTabsBarScroll}
          >
            {calendarTopTabsVisible.map((tabKey) => {
              const isActive = tabKey === activeTopTab;
              return (
                <TouchableOpacity
                  key={tabKey}
                  style={styles.topTabPlainBtn}
                  onPress={() => handleTopTabPress(tabKey)}
                >
                  <Text
                    style={[
                      styles.topTabPlainText,
                      isActive && styles.topTabPlainTextActive,
                    ]}
                  >
                    {tabKey}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
          )}
          {!masterTourUi && !isCompactMonthLayout && !useDesktopSidebar ? (
            <View style={styles.topBarShareSettingsRow}>
              <TouchableOpacity
                style={styles.sharedLinksBarBtn}
                onPress={() => setSharedLinksOpen(true)}
                accessibilityLabel="Manage shared links"
              >
                <Ionicons name="link-outline" size={14} color={theme.brand.accent} />
                <Text style={styles.sharedLinksBarBtnText}>Shared links</Text>
              </TouchableOpacity>
              {selectedTourFilterId ? (
                <TouchableOpacity
                  style={styles.tourSettingsBarBtn}
                  onPress={() => setTourSettingsOpen(true)}
                  accessibilityLabel="Tour settings"
                >
                  <Ionicons name="settings-outline" size={14} color={theme.brand.primary} />
                  <Text style={styles.tourSettingsBarBtnText}>Tour settings</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}
        </View>
      ) : null}
      {masterTourUi ? (
        <View style={styles.v2ToolsBar}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.v2ToolsBarScroll}
            style={styles.v2ToolsBarScroller}
          >
            <TouchableOpacity
              onPress={() => {
                if (isNativeColorPickerActive()) return;
                setTourSettingsOpen(false);
                setPdfExportOpen(false);
                setSavedContactsOpen(false);
                setDisplaySettingsOpen((open) => !open);
              }}
              style={[styles.v2ToolsChip, displaySettingsOpen && styles.v2ToolsChipActive]}
              accessibilityLabel="Calendar display settings"
              {...(Platform.OS === 'web' ? { dataSet: { v2SettingsChip: '1' } } : {})}
            >
              <Ionicons name="options-outline" size={13} color={displaySettingsOpen ? v2Pal.page : v2Pal.ink} />
              <Text style={[styles.v2ToolsChipText, displaySettingsOpen && styles.v2ToolsChipTextOnActive]}>Display</Text>
            </TouchableOpacity>
            <View style={styles.v2ToolsExportGroup}>
              <TouchableOpacity
                onPress={togglePdfExportRail}
                disabled={exportingPdf}
                style={[styles.v2ToolsExportBtn, pdfExportOpen && styles.v2ToolsChipActive]}
                accessibilityLabel="Export PDF"
                {...(Platform.OS === 'web' ? { dataSet: { v2SettingsChip: '1' } } : {})}
              >
                {exportingPdf ? (
                  <ActivityIndicator color={pdfExportOpen ? v2Pal.page : v2Pal.ink} size="small" />
                ) : (
                  <Text style={[styles.v2ToolsChipText, pdfExportOpen && styles.v2ToolsChipTextOnActive]}>PDF</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                onPress={runIcalExport}
                disabled={exportingIcal}
                style={[styles.v2ToolsExportBtn, { borderRightWidth: 0 }]}
                accessibilityLabel="Export iCal"
              >
                {exportingIcal ? <ActivityIndicator color={v2Pal.ink} size="small" /> : <Text style={styles.v2ToolsChipText}>iCal</Text>}
              </TouchableOpacity>
            </View>
            {selectedTourFilterId ? (
              <TouchableOpacity
                style={[styles.v2ToolsChip, styles.v2ToolsChipAccent]}
                onPress={() => setSharedLinksOpen(true)}
                accessibilityLabel="Manage shared links"
              >
                <Ionicons name="link-outline" size={13} color={theme.brand.accent} />
                <Text style={[styles.v2ToolsChipText, styles.v2ToolsChipTextAccent]}>Shared links</Text>
              </TouchableOpacity>
            ) : null}
            {selectedTourFilterId ? (
              <TouchableOpacity
                style={[styles.v2ToolsChip, tourSettingsOpen && styles.v2ToolsChipActive]}
                onPress={() => {
                  if (isNativeColorPickerActive()) return;
                  setDisplaySettingsOpen(false);
                  setPdfExportOpen(false);
                  setSavedContactsOpen(false);
                  setTourSettingsOpen((open) => !open);
                }}
                accessibilityLabel="Tour settings"
                {...(Platform.OS === 'web' ? { dataSet: { v2SettingsChip: '1' } } : {})}
              >
                <Ionicons name="settings-outline" size={13} color={tourSettingsOpen ? v2Pal.page : v2Pal.ink} />
                <Text style={[styles.v2ToolsChipText, tourSettingsOpen && styles.v2ToolsChipTextOnActive]}>Tour settings</Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={[styles.v2ToolsChip, savedContactsOpen && styles.v2ToolsChipActive]}
              onPress={() => {
                if (isNativeColorPickerActive()) return;
                setDisplaySettingsOpen(false);
                setPdfExportOpen(false);
                setTourSettingsOpen(false);
                setSavedContactsOpen((open) => !open);
              }}
              accessibilityLabel="Saved contacts"
              {...(Platform.OS === 'web' ? { dataSet: { v2SettingsChip: '1' } } : {})}
            >
              <Ionicons name="people-outline" size={13} color={savedContactsOpen ? v2Pal.page : v2Pal.ink} />
              <Text style={[styles.v2ToolsChipText, savedContactsOpen && styles.v2ToolsChipTextOnActive]}>Saved contacts</Text>
            </TouchableOpacity>
            {selectedTourFilterId ? (
              <TouchableOpacity
                onPress={() => handleClaudeView('chat')}
                style={[styles.v2ToolsChip, styles.v2ToolsChatBtn, v2Canvas === 'chat' && styles.v2ToolsChipActive]}
                accessibilityLabel={v2ChatCount ? `Tour chat, ${v2ChatCount} messages` : 'Tour chat'}
              >
                <Ionicons name="chatbubbles-outline" size={13} color={v2Canvas === 'chat' ? v2Pal.page : v2Pal.ink} />
                <Text style={[styles.v2ToolsChipText, v2Canvas === 'chat' && styles.v2ToolsChipTextOnActive]}>Chat</Text>
                {v2ChatCount > 0 ? (
                  <View style={styles.v2ToolsChatBadge}>
                    <Text style={styles.v2ToolsChatBadgeText}>
                      {v2ChatCount > 99 ? '99+' : v2ChatCount}
                    </Text>
                  </View>
                ) : null}
              </TouchableOpacity>
            ) : null}
          </ScrollView>
          <TouchableOpacity
            onPress={() => {
              goToToday();
              setWeekBarTodayToken((n) => n + 1);
            }}
            style={[styles.v2ToolsTodayBtn, isTodaySelected && styles.v2ToolsTodayBtnActive]}
            accessibilityLabel="Back to today"
          >
            <Text style={[styles.v2ToolsTodayBtnText, isTodaySelected && styles.v2ToolsTodayBtnTextActive]}>
              Back to Today
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.brand.accent} />
        </View>
      ) : useMasterTourFullCanvas ? (
        <View style={{ flexDirection: 'row', minHeight: desktopCalendarBodyHeight, alignItems: 'stretch' }}>
          {masterTourUi && settingsRailOpen ? (
            <View
              style={[styles.v2DisplaySidebar, pdfExportOpen && styles.v2PdfSidebar]}
              {...(Platform.OS === 'web' ? { dataSet: { v2SettingsRail: '1' } } : {})}
            >
              <View style={styles.v2DisplaySidebarHeader}>
                <View style={{ flex: 1, paddingRight: 8 }}>
                  <Text style={styles.v2DisplaySidebarTitle}>
                    {savedContactsOpen ? 'Saved contacts' : displaySettingsOpen ? 'Calendar display' : pdfExportOpen ? 'Export PDF' : 'Tour settings'}
                  </Text>
                  <Text style={styles.v2DisplaySidebarLead}>
                    {savedContactsOpen
                      ? 'Nearest to this show when you open it. Search looks through every contact you have saved.'
                      : displaySettingsOpen
                      ? 'Changes apply live on the calendar.'
                      : pdfExportOpen
                        ? 'Choose the first and last month to include. Each month gets a calendar page; event details are listed at the end.'
                        : `${selectedTourFilterName || 'Tour'} · global defaults for every show day`}
                  </Text>
                </View>
                <TouchableOpacity
                  onPress={() => {
                    setDisplaySettingsOpen(false);
                    setTourSettingsOpen(false);
                    setPdfExportOpen(false);
                    setSavedContactsOpen(false);
                  }}
                  accessibilityLabel={
                    savedContactsOpen
                      ? 'Close saved contacts'
                      : displaySettingsOpen
                      ? 'Close display settings'
                      : pdfExportOpen
                        ? 'Close PDF export'
                        : 'Close tour settings'
                  }
                  hitSlop={8}
                >
                  <Ionicons name="close" size={20} color={v2Pal.ink} />
                </TouchableOpacity>
              </View>
              <ScrollView
                style={styles.v2DisplaySidebarScroll}
                contentContainerStyle={styles.v2DisplaySidebarScrollContent}
                showsVerticalScrollIndicator
                keyboardShouldPersistTaps="handled"
              >
                {savedContactsOpen ? (
                  <SavedContactsCard
                    pal={v2Pal}
                    isDarkMode={isDarkMode}
                    userId={uid}
                    userName={ownerDisplayName}
                    tour={selectedTourFolder?.tour}
                    dateKey={selectedDateKey}
                    canEdit={Boolean(uid)}
                    previewLimit={5}
                  />
                ) : displaySettingsOpen ? (
                  <MasterTourDesignBar
                    sheet
                    prefs={v2Prefs}
                    isDarkMode={isDarkMode}
                    onChange={(patch) => setV2Prefs((p) => saveV2Prefs({ ...p, ...patch }))}
                    onReset={() => setV2Prefs(resetV2Prefs())}
                    onSave={() => setV2Prefs(saveV2Prefs(v2Prefs))}
                  />
                ) : pdfExportOpen ? (
                  <PdfMonthRangeFields
                    styles={styles}
                    labelColor={v2Pal.ink}
                    fromY={pdfFromY}
                    fromM={pdfFromM}
                    toY={pdfToY}
                    toM={pdfToM}
                    setFromY={setPdfFromY}
                    setFromM={setPdfFromM}
                    setToY={setPdfToY}
                    setToM={setPdfToM}
                    onExport={runPdfExport}
                    onCancel={() => setPdfExportOpen(false)}
                    exporting={exportingPdf}
                  />
                ) : (
                  <TourSettingsManager
                    variant="sidebar"
                    visible={Boolean(tourSettingsOpen && selectedTourFilterId)}
                    onClose={() => setTourSettingsOpen(false)}
                    ownerId={uid}
                    tourId={selectedTourFilterId}
                    tourName={selectedTourFilterName}
                    showDateKeys={selectedTourShowDateKeys}
                    onToast={showToast}
                    currentUserId={uid}
                    acceptedMembers={v2PartyMembers}
                    tourShareSettings={tourShareSettings}
                  />
                )}
              </ScrollView>
            </View>
          ) : null}
          <View style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {v2Canvas && !activeCanvasAccess.canView ? (
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 }}>
              <Text style={{ color: v2Pal.mute, fontSize: 15, textAlign: 'center' }}>
                The tour owner has hidden this page from your access.
              </Text>
            </View>
          ) : v2Canvas === 'timeline' ? (
            <MasterTourTimeline
              year={cursor.y}
              month={cursor.m}
              tourDayMetaByDate={tourDayMetaByDate}
              selectedTourFilterId={selectedTourFilterId}
              selectedDateKey={selectedDateKey}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              laneColors={v2Prefs.laneColors}
              tour={selectedTourFolder?.tour}
              partyMembers={v2PartyMembers}
              onDayPress={(key) => {
                if (typeof key === 'string') handleDayCellPress(key);
              }}
            />
          ) : v2Canvas === 'map' ? (
            <MasterTourMap
              tourId={selectedTourFilterId}
              tour={selectedTourFolder?.tour}
              tourName={selectedTourFilterName}
              year={cursor.y}
              month={cursor.m}
              selectedDateKey={selectedDateKey}
              navigation={navigation}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              tourAccent={selectedTourFilterId ? tourColorById[selectedTourFilterId] : null}
              onOpenPlanner={() => goTourPlanner(selectedTourFilterId)}
            />
          ) : v2Canvas === 'riders' ? (
            <MasterTourRiders
              tourName={selectedTourFilterName}
              tour={selectedTourFolder?.tour}
              folderId={selectedTourFilterId}
              demoTour={Boolean(selectedTourFolder?.demoTour)}
              riderFiles={Object.values(dayFilesByDate || {}).flat().filter((f) => normalizeDayFileType(f.fileType) === 'riderFull')}
              isDarkMode={isDarkMode}
              actColors={(v2Prefs.riderActColors || {})[selectedTourFilterId] || {}}
              onChangeActColor={(actId, hex) => {
                if (!selectedTourFilterId) return;
                setV2Prefs((p) => {
                  const tourColors = applyRiderActColor((p.riderActColors || {})[selectedTourFilterId], actId, hex);
                  return saveV2Prefs({
                    ...p,
                    riderActColors: { ...(p.riderActColors || {}), [selectedTourFilterId]: tourColors },
                  });
                });
              }}
              ownerId={uid}
              ownerName={ownerDisplayName}
              dateKey={selectedDateKey}
              partyMembers={v2PartyMembers}
              riderHtml={combineRiderHtml(v2DayTabs.RIDER_DAILY, v2DayTabs.RIDER_FULL)}
              areasText={v2RiderAreas}
              onPersistAreas={canEditActiveCalendarTab ? (text) => persistV2Tab(V2_RIDER_AREAS_TAB, V2_RIDER_AREAS_DATE, text, { immediate: true }) : undefined}
              onToast={showToast}
              onUpload={canEditActiveCalendarTab ? () => uploadDayFileFromAsset('riderFull') : undefined}
            />
          ) : v2Canvas === 'contacts' ? (
            <MasterTourContacts
              venueName={selectedDayVenueName}
              cityLine={pickTourDayCard(tourDayMetaByDate?.[selectedDateKey], selectedTourFilterId)?.cityLine}
              contactsText={v2DayTabs.CONTACTS || ''}
              partyMembers={v2PartyMembers}
              isDarkMode={isDarkMode}
              userId={uid}
              userName={ownerDisplayName}
              tour={selectedTourFolder?.tour}
              dateKey={selectedDateKey}
              accent={v2Prefs.accent}
              sharePath={contactsShareUrl}
              shareLinkBusy={contactsShareBusy}
              onEnsureShareLink={async () => {
                const result = await ensureContactsShareLink();
                return String(result?.url || '').trim();
              }}
              onEmailParty={emailContactsToParty}
              onPersist={canEditActiveCalendarTab ? (text) => persistV2Tab('CONTACTS', selectedDateKey, text) : undefined}
              viewerMode={!canEditActiveCalendarTab}
            />
          ) : v2Canvas === 'chat' ? (
            <MasterTourChat
              partyMembers={v2PartyMembers}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              chatColors={v2Prefs.chatColors}
              tourName={selectedTourFilterName}
              chatText={v2ChatDoc}
              currentUserName={ownerDisplayName}
              currentUserId={uid}
              currentUserPhotoURL={userData?.photoURL}
              onPersist={(text) => persistV2Tab(V2_CHAT_TAB, V2_CHAT_DATE, text, { immediate: true })}
              onInvite={openTourSettingsRail}
              onNotifyNewMessage={tourCommunityHub ? (payload) => {
                notifyTourChatMessage({
                  tourId: selectedTourFilterId,
                  tourName: selectedTourFilterName,
                  senderId: uid,
                  senderName: ownerDisplayName,
                  senderEmail: userData?.email,
                  ...payload,
                }).catch((err) => console.warn('tour chat email/inbox', err));
              } : undefined}
              canManageParty={canManageTourParty(uid, tourOwnerId, tourShareSettings)}
            />
          ) : v2Canvas === 'settle' ? (
            <MasterTourSettlement
              tourId={selectedTourFilterId}
              dateKey={selectedDateKey}
              showDateKeys={selectedTourShowDateKeys}
              tourDayMetaByDate={tourDayMetaByDate}
              selectedTourFilterId={selectedTourFilterId}
              tour={selectedTourFolder?.tour}
              seedText={v2DayTabs.SETTLEMENT || ''}
              merchSeedText={v2DayTabs.MERCH || ''}
              merchCatalogText={v2MerchCatalog}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              onSelectDate={jumpV2Date}
              onPersist={(json, dateKey) => persistV2Tab('SETTLEMENT', dateKey || selectedDateKey, json)}
              canEdit={canEditActiveCalendarTab}
              finance={tourFinance}
              todayKey={todayDateKey}
            />
          ) : v2Canvas === 'expenses' ? (
            <MasterTourExpenses
              tourId={selectedTourFilterId}
              tourName={selectedTourFilterName}
              dateKey={selectedDateKey}
              showDateKeys={selectedTourShowDateKeys}
              tourDayMetaByDate={tourDayMetaByDate}
              selectedTourFilterId={selectedTourFilterId}
              seedText={v2DayTabs.EXPENSES || ''}
              seedLegText={v2LegExpenses || v2DayTabs.EXPENSES_LEG || ''}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              onSelectDate={jumpV2Date}
              onPersist={(json, scope) => persistV2Tab(
                scope === 'leg' ? 'EXPENSES_LEG' : 'EXPENSES',
                scope === 'leg' ? 'leg' : (scope === 'day' ? selectedDateKey : scope),
                json
              )}
              onLoadLedgerDate={loadLedgerForDate}
              canEdit={canEditActiveCalendarTab}
              finance={canViewV2Canvas('settle') ? tourFinance : { ...tourFinance, settlements: {} }}
              onSaveBudget={(json) => persistV2Tab(V2_BUDGET_TAB, V2_BUDGET_DATE, json)}
            />
          ) : v2Canvas === 'merch' ? (
            <MasterTourMerch
              tourId={selectedTourFilterId}
              dateKey={selectedDateKey}
              merchText={v2DayTabs.MERCH || ''}
              catalogText={v2MerchCatalog}
              showDateKeys={selectedTourShowDateKeys}
              venueName={selectedDayVenueName}
              cityLine={pickTourDayCard(tourDayMetaByDate?.[selectedDateKey], selectedTourFilterId)?.cityLine}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              onPersist={(text, dateKey) => persistV2Tab('MERCH', dateKey || selectedDateKey, text)}
              onPersistCatalog={(text) => persistV2Tab('MERCH', V2_MERCH_CATALOG_DATE, text)}
              onLoadMerchDate={loadMerchForDate}
              canEdit={canEditActiveCalendarTab}
            />
          ) : v2Canvas === 'files' ? (
            <MasterTourFilesPage
              files={Object.values(dayFilesByDate || {}).flat()}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              onUpload={canEditActiveCalendarTab ? () => uploadDayFileFromAsset('files') : undefined}
            />
          ) : v2Canvas === 'lodging' ? (
            <MasterTourLodgingPage
              tour={selectedTourFolder?.tour}
              tourDayMetaByDate={tourDayMetaByDate}
              selectedTourFilterId={selectedTourFilterId}
              selectedDateKey={selectedDateKey}
              lodgingText={v2DayTabs.LODGING || ''}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              onSelectDate={jumpV2Date}
              onOpenPlanner={() => goTourPlanner(selectedTourFilterId)}
              onPersist={(text) => persistV2Tab('LODGING', selectedDateKey, text)}
              canEdit={canEditActiveCalendarTab}
            />
          ) : v2Canvas === 'tourparty' ? (
            <MasterTourPartyPage
              partyMembers={v2PartyMembers}
              tourName={selectedTourFilterName}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              onInvite={openTourSettingsRail}
              ownerId={tourOwnerId}
              currentUserId={uid}
              tourId={permissionTourId}
              tourShareSettings={tourShareSettings}
              theme={theme}
              onError={(text) => showToast(text || 'Could not save permissions.', 'error')}
              onSuccess={(text) => showToast(text || 'Permissions updated.', 'success')}
            />
          ) : v2Canvas === 'setlist' ? (
            <MasterTourSetlistPage
              setlistText={v2DayTabs.SETLIST || ''}
              venueName={selectedDayVenueName}
              cityLine={pickTourDayCard(tourDayMetaByDate?.[selectedDateKey], selectedTourFilterId)?.cityLine}
              dateKey={selectedDateKey}
              tourName={selectedTourFilterName}
              tourId={permissionTourId || selectedTourFilterId}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              theme={theme}
              setlistsByDate={setlistsPublishedByDate}
              venueByDate={Object.fromEntries(Object.keys(setlistsPublishedByDate || {}).map((k) => [k, pickTourDayCard(tourDayMetaByDate?.[k], selectedTourFilterId)?.venueLine || '']))}
              onPersist={(text) => persistV2Tab('SETLIST', selectedDateKey, text, { ownerScope: true })}
              onPrintError={(message) => showToast(message, 'error')}
              partyMembers={v2PartyMembers}
              ownerId={tourOwnerId}
              currentUserId={uid}
              currentUserName={ownerDisplayName}
              tourShareSettings={tourShareSettings}
              canEdit={canEditActiveCalendarTab || canPublishSetlist(uid, tourOwnerId, tourShareSettings)}
            />
          ) : v2Canvas === 'guestlist' ? (
            <MasterTourGuestListPage
              guestText={v2DayTabs['GUEST LIST'] || ''}
              venueName={selectedDayVenueName}
              dateKey={selectedDateKey}
              showDateKeys={selectedTourShowDateKeys}
              tourDayMetaByDate={tourDayMetaByDate}
              selectedTourFilterId={selectedTourFilterId}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              onSelectDate={jumpV2Date}
              theme={theme}
              onPersist={canEditActiveCalendarTab ? (text) => persistV2Tab('GUEST LIST', selectedDateKey, text) : undefined}
              spotsRaw={v2DayTabs[GUEST_CAP_TAB] || ''}
              onPersistSpots={canEditActiveCalendarTab ? (spots) => persistV2Tab(GUEST_CAP_TAB, selectedDateKey, serializeGuestCap(spots), { immediate: true }) : undefined}
              guestAsMe={ownerDisplayName}
              venueLinkUrl={guestVenueLink}
              onEnsureVenueLink={canEditActiveCalendarTab ? ensureGuestVenueLink : undefined}
            />
          ) : v2Canvas === 'day' ? (
            <MasterTourSchedulePage
              scheduleText={v2DayTabs.SCHEDULE ?? schedulePublishedByDate[selectedDateKey] ?? ''}
              venueName={selectedDayVenueName}
              cityLine={pickTourDayCard(tourDayMetaByDate?.[selectedDateKey], selectedTourFilterId)?.cityLine}
              dateKey={selectedDateKey}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              onPersist={canEditActiveCalendarTab ? (text) => persistV2Tab('SCHEDULE', selectedDateKey, text) : undefined}
              canEdit={canEditActiveCalendarTab}
              busCallTime={v2BusCallTime}
              timeFormat={v2Prefs.timeFormat || '12-hour'}
            />
          ) : v2Canvas === 'daysheet' ? (
            <MasterTourDaySheetPoster
              tourName={selectedTourFilterName}
              dateKey={selectedDateKey}
              venueName={selectedDayVenueName}
              cityLine={pickTourDayCard(tourDayMetaByDate?.[selectedDateKey], selectedTourFilterId)?.cityLine}
              scheduleText={v2DayTabs.SCHEDULE ?? schedulePublishedByDate[selectedDateKey] ?? ''}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              onPersist={canEditActiveCalendarTab ? (text) => persistV2Tab('SCHEDULE', selectedDateKey, text) : undefined}
              bannerUrl={daySheetBannerUrl}
              backgroundUrl={daySheetBgUrl}
              onUploadBanner={canEditActiveCalendarTab ? () => uploadDaySheetAsset('banner') : undefined}
              onUploadBackground={canEditActiveCalendarTab ? () => uploadDaySheetAsset('background') : undefined}
              assetSaving={daySheetAssetSaving}
            />
          ) : v2Canvas === 'notes' ? (
            <MasterTourNotesPage
              notesText={v2DayTabs.NOTES || ''}
              venueName={selectedDayVenueName}
              cityLine={pickTourDayCard(tourDayMetaByDate?.[selectedDateKey], selectedTourFilterId)?.cityLine}
              dateKey={selectedDateKey}
              isDarkMode={isDarkMode}
              accent={v2Prefs.accent}
              theme={theme}
              onPersist={canEditActiveCalendarTab ? (text) => persistV2Tab('NOTES', selectedDateKey, text) : undefined}
              canEdit={canEditActiveCalendarTab}
            />
          ) : v2Canvas === 'stage' ? (
            calendarDayPanelEl
          ) : (activeTopTab && activeTopTab !== 'DAY SHEET') ? (
            calendarDayPanelEl
          ) : (
            renderDesktopCalendarMain()
          )}
          </View>
        </View>
      ) : useDesktopSidebar ? (
        <CalendarDesktopSidebarLayout
          height={desktopCalendarBodyHeight}
          rail={sidebarPanelExpanded ? tourPartyRailEl : undefined}
          sidebar={
            sidebarPanelExpanded ? (
              calendarDayPanelEl
            ) : (
              <View
                style={{
                  width: 36,
                  flexShrink: 0,
                  alignSelf: 'stretch',
                  borderLeftWidth: 1,
                  borderLeftColor: theme.ui.border,
                  backgroundColor: theme.background.elevated,
                  alignItems: 'center',
                  paddingTop: 12,
                }}
              >
                <TouchableOpacity
                  onPress={() => setSidebarPanelExpanded(true)}
                  accessibilityLabel="Expand day panel"
                  style={{ padding: 8 }}
                >
                  <Ionicons name="chevron-back" size={20} color={theme.text.secondary} />
                </TouchableOpacity>
              </View>
            )
          }
        >
          {renderDesktopCalendarMain()}
        </CalendarDesktopSidebarLayout>
      ) : (
        <>
          {selectedTourFilterId ? (
            <TourPartyRail
              ownerId={uid}
              ownerName={ownerDisplayName}
              ownerEmail={userData?.email}
              ownerPhotoURL={userData?.photoURL}
              tourId={selectedTourFilterId}
              tourName={selectedTourFilterName}
              currentUserId={uid}
              tourShareSettings={tourShareSettings}
              theme={theme}
              layout="horizontal"
              visible={selectedDateHasTourDoor}
              doorDateKey={selectedDateKey}
              onSuccess={(text) => showToast(text || 'Done.', 'success')}
              onError={(text) => showToast(text || 'Something went wrong.', 'error')}
            />
          ) : null}
        <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
          {isDaySheetTab ? (
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>DAY SHEET</Text>
              <DaySheetToolbar
                theme={theme}
                styles={styles}
                daySheetMode={daySheetMode}
                onModeChange={setDaySheetMode}
                onUploadBanner={() => uploadDaySheetAsset('banner')}
                onUploadBackground={() => uploadDaySheetAsset('background')}
                onSave={() => void saveDaySheetPublication()}
                daySheetAssetSaving={daySheetAssetSaving}
                daySheetPublicationSaving={daySheetPublicationSaving}
                hasUnsavedDaySheetPublication={hasUnsavedDaySheetPublication}
                daySheetSeededFromDefaults={daySheetSeededFromDefaults}
                daySheetBannerUrl={daySheetBannerUrl}
                daySheetBgUrl={daySheetBgUrl}
              />
              <View style={[styles.daySheetPaperWrap, styles.daySheetPaperWrapSidebar]}>
                <DaySheetPaper
                  theme={theme}
                  tourName={selectedTourFilterName}
                  headline={formatDaySheetHeadline(selectedDateKey)}
                  venueName={selectedDayVenueName}
                  bannerUrl={daySheetBannerUrl}
                  backgroundUrl={daySheetBgUrl}
                  scheduleRows={daySheetPreviewRows}
                  aftershowText={daySheetNotes}
                  editMode={daySheetMode === 'edit'}
                  compact
                  aftershowPlaceholder="AFTERSHOW..."
                  onAftershowChange={daySheetMode === 'edit' ? setDaySheetNotes : undefined}
                  renderSchedule={() =>
                    daySheetMode === 'edit' ? (
                      <DaySheetScheduleEditor
                        value={daySheetScheduleSerialized}
                        onChange={handleDaySheetScheduleChange}
                        compact
                      />
                    ) : null
                  }
                />
              </View>
            </View>
          ) : (
            <>
              {renderCompactCalendarGrid()}
              {renderSelectedDayDetailPanel()}
            </>
          )}
        </ScrollView>
        </>
      )}

      <Modal visible={modalOpen} transparent animationType="fade" onRequestClose={() => setModalOpen(false)}>
        <Pressable style={styles.modalOverlay} onPress={() => setModalOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>{editingId ? 'Edit event' : 'New event'}</Text>
            <Text style={styles.label}>Title</Text>
            <TextInput
              style={styles.input}
              value={formTitle}
              onChangeText={setFormTitle}
              placeholder="Event title"
              placeholderTextColor={placeholderColor}
            />
            <Text style={styles.label}>Start date (YYYY-MM-DD)</Text>
            <View style={styles.dateInputRow}>
              <TextInput
                style={[styles.input, styles.dateInput]}
                value={formDate}
                onChangeText={setFormDate}
                onFocus={() => openDatePickerFor('start')}
                placeholder="2025-06-01"
                placeholderTextColor={placeholderColor}
              />
              <TouchableOpacity style={styles.datePickBtn} onPress={() => openDatePickerFor('start')}>
                <Ionicons name="calendar-outline" size={16} color={theme.text.primary} />
              </TouchableOpacity>
              <TextInput
                style={[styles.input, styles.timeInputInline]}
                value={formStartTime}
                onChangeText={setFormStartTime}
                placeholder="Start HH:MM"
                placeholderTextColor={placeholderColor}
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>
            <Text style={styles.label}>End date (YYYY-MM-DD)</Text>
            <View style={styles.dateInputRow}>
              <TextInput
                style={[styles.input, styles.dateInput]}
                value={formEndDate}
                onChangeText={setFormEndDate}
                onFocus={() => openDatePickerFor('end')}
                placeholder="2025-06-03"
                placeholderTextColor={placeholderColor}
              />
              <TouchableOpacity style={styles.datePickBtn} onPress={() => openDatePickerFor('end')}>
                <Ionicons name="calendar-outline" size={16} color={theme.text.primary} />
              </TouchableOpacity>
              <TextInput
                style={[styles.input, styles.timeInputInline]}
                value={formEndTime}
                onChangeText={setFormEndTime}
                placeholder="End HH:MM"
                placeholderTextColor={placeholderColor}
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.inputMultiline]}
              value={formNotes}
              onChangeText={setFormNotes}
              placeholder="Optional"
              placeholderTextColor={placeholderColor}
              multiline
            />
            <Text style={styles.label}>Color</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {EVENT_COLORS.map((c) => (
                <TouchableOpacity
                  key={c}
                  onPress={() => setFormColor(c)}
                  style={[styles.colorSwatch, { backgroundColor: c }, formColor === c && styles.colorSwatchActive]}
                />
              ))}
            </View>
            <Text style={styles.label}>Link Community post (optional)</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ maxHeight: 44 }}>
              <TouchableOpacity
                style={[styles.chip, !formCommunityPostId && styles.chipActive]}
                onPress={() => setFormCommunityPostId(null)}
              >
                <Text style={styles.chipText}>None</Text>
              </TouchableOpacity>
              {communityPosts.map((p) => (
                <TouchableOpacity
                  key={p.id}
                  style={[styles.chip, formCommunityPostId === p.id && styles.chipActive]}
                  onPress={() => setFormCommunityPostId(p.id)}
                >
                  <Text style={styles.chipText} numberOfLines={1}>
                    {p.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <View style={styles.modalActions}>
              {editingId ? (
                <TouchableOpacity onPress={() => deleteEvent(editingId)} style={styles.dangerBtn}>
                  <Text style={styles.dangerBtnText}>Delete</Text>
                </TouchableOpacity>
              ) : (
                <View />
              )}
              <TouchableOpacity onPress={() => setModalOpen(false)} style={styles.secondaryBtn}>
                <Text style={styles.secondaryBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={saveEvent} style={styles.primaryBtn} disabled={saving}>
                {saving ? <ActivityIndicator color={theme.button.primaryText} /> : <Text style={styles.primaryBtnText}>Save</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal visible={modalOpen && datePickerOpen} transparent animationType="fade" onRequestClose={() => setDatePickerOpen(false)}>
        <Pressable style={styles.modalOverlay} onPress={() => setDatePickerOpen(false)}>
          <Pressable style={styles.datePickerPopupCard} onPress={(e) => e.stopPropagation()}>
            <View style={styles.rangeChipRow}>
              <TouchableOpacity
                style={[styles.rangeChip, datePickTarget === 'start' && styles.rangeChipActive]}
                onPress={() => setDatePickTarget('start')}
              >
                <Text style={styles.rangeChipText}>Picking start</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.rangeChip, datePickTarget === 'end' && styles.rangeChipActive]}
                onPress={() => setDatePickTarget('end')}
              >
                <Text style={styles.rangeChipText}>Picking end</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.monthPickerYearRow}>
              <TouchableOpacity
                style={styles.monthPickerYearBtn}
                onPress={() =>
                  setDatePickerCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }))
                }
                accessibilityLabel="Previous month"
              >
                <Ionicons name="chevron-back" size={20} color={theme.text.primary} />
              </TouchableOpacity>
              <Text style={styles.monthPickerYearText}>{monthLabel(datePickerCursor.y, datePickerCursor.m)}</Text>
              <TouchableOpacity
                style={styles.monthPickerYearBtn}
                onPress={() =>
                  setDatePickerCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }))
                }
                accessibilityLabel="Next month"
              >
                <Ionicons name="chevron-forward" size={20} color={theme.text.primary} />
              </TouchableOpacity>
            </View>
            <View style={styles.pickerWeekdayRow}>
              {weekdayLabels.map((w) => (
                <Text key={`picker-wd-${w}`} style={styles.pickerWeekdayCell}>
                  {w}
                </Text>
              ))}
            </View>
            {datePickerWeekRows.map((week, wi) => (
              <View key={`picker-week-${wi}`} style={styles.pickerWeekRow}>
                {week.map((cell) => {
                  const key = cell.dateKey || '';
                  const inRange = key && formDate && formEndDate && key >= formDate && key <= formEndDate;
                  const isStart = key && key === formDate;
                  const isEnd = key && key === formEndDate;
                  return (
                    <TouchableOpacity
                      key={key || `${wi}-${cell.jsDate.toISOString()}`}
                      style={[
                        styles.pickerDayCell,
                        !cell.inMonth && styles.pickerDayCellMuted,
                        inRange && styles.pickerDayCellInRange,
                        (isStart || isEnd) && styles.pickerDayCellEdge,
                      ]}
                      onPress={() => {
                        if (!key) return;
                        if (datePickTarget === 'start') {
                          setFormDate(key);
                          if (!formEndDate || key > formEndDate) setFormEndDate(key);
                        } else {
                          setFormEndDate(key);
                          if (!formDate || key < formDate) setFormDate(key);
                        }
                      }}
                    >
                      <Text
                        style={[
                          styles.pickerDayText,
                          !cell.inMonth && styles.pickerDayTextMuted,
                          (isStart || isEnd) && styles.pickerDayTextEdge,
                        ]}
                      >
                        {cell.jsDate.getDate()}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            ))}
            <View style={styles.popupButtonRow}>
              <TouchableOpacity style={styles.popupBtnSecondary} onPress={() => setDatePickerOpen(false)}>
                <Text style={styles.popupBtnSecondaryText}>Done</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal visible={tourPickerOpen} transparent animationType="fade" onRequestClose={() => setTourPickerOpen(false)}>
        <Pressable style={styles.modalOverlay} onPress={() => setTourPickerOpen(false)}>
          <Pressable
            style={[
              styles.modalCard,
              styles.tourPickerModalCard,
              { maxHeight: Math.round(windowHeight * 0.9) },
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={styles.tourPickerModalHeader}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.modalTitle, { marginBottom: 2 }]}>Tours</Text>
                <Text style={[styles.muted, { marginBottom: 0, fontSize: 12 }]}>
                  {tourPickerMainTab === 'mine'
                    ? `Your tours ordered for ${calendarViewRange.label}`
                    : 'Tours you’ve been invited to — import to add them to your calendar'}
                </Text>
              </View>
              <TouchableOpacity onPress={() => setTourPickerOpen(false)} hitSlop={10} style={{ padding: 4 }}>
                <Ionicons name="close" size={22} color={theme.text.secondary} />
              </TouchableOpacity>
            </View>

            <View style={styles.tourPickerMainTabs}>
            <TouchableOpacity
                style={[styles.tourPickerMainTab, tourPickerMainTab === 'mine' && styles.tourPickerMainTabActive]}
                onPress={() => setTourPickerMainTab('mine')}
              >
                <Text style={[
                  styles.tourPickerMainTabText,
                  tourPickerMainTab === 'mine' && styles.tourPickerMainTabTextActive,
                ]}>
                  My Tours{tourFolders.length ? ` (${tourFolders.length})` : ''}
                </Text>
            </TouchableOpacity>
              <TouchableOpacity
                style={[styles.tourPickerMainTab, tourPickerMainTab === 'shared' && styles.tourPickerMainTabActive]}
                onPress={() => setTourPickerMainTab('shared')}
              >
                <Text style={[
                  styles.tourPickerMainTabText,
                  tourPickerMainTab === 'shared' && styles.tourPickerMainTabTextActive,
                ]}>
                  Shared{sharedTourInvites.length ? ` (${sharedTourInvites.length})` : ''}
                </Text>
              </TouchableOpacity>
            </View>

            {tourPickerMainTab === 'mine' ? (
              <>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  style={{ marginBottom: 12, flexGrow: 0 }}
                  contentContainerStyle={{ gap: 8, paddingRight: 4 }}
                >
                  {TOUR_PICKER_FILTERS.map((f) => {
                    const active = tourPickerFilter === f.id;
                    const count = tourPickerFilterCounts[f.id] || 0;
                    return (
                      <TouchableOpacity
                        key={f.id}
                        onPress={() => setTourPickerFilter(f.id)}
                        style={[
                          styles.tourPickerFilterChip,
                          active && styles.tourPickerFilterChipActive,
                        ]}
                      >
                        <Text style={[
                          styles.tourPickerFilterChipText,
                          active && styles.tourPickerFilterChipTextActive,
                        ]}>
                          {f.label}{count ? ` (${count})` : ''}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>

            {loadingTours ? (
                  <View style={{ paddingVertical: 32, alignItems: 'center' }}>
                    <ActivityIndicator color={theme.brand.accent} />
              </View>
            ) : tourFolders.length === 0 ? (
                  <View style={styles.tourPickerEmptyState}>
                    <Ionicons name="map-outline" size={36} color={theme.text.secondary} />
                    <Text style={styles.muted}>No tours yet. Import one or check the Shared tab for invites.</Text>
                    <TouchableOpacity
                      style={[styles.toolbarBtn, { marginTop: 8 }]}
                      onPress={() => { setTourPickerOpen(false); loadTourFolders(); setImportOpen(true); }}
                    >
                      <Ionicons name="download-outline" size={16} color={theme.button.primaryText} />
                      <Text style={styles.toolbarBtnText}>Import tour</Text>
                    </TouchableOpacity>
                  </View>
                ) : filteredTourPickerTours.length === 0 ? (
                  <View style={styles.tourPickerEmptyState}>
                    <Ionicons name="filter-outline" size={32} color={theme.text.secondary} />
                    <Text style={styles.muted}>No tours match this filter.</Text>
                  </View>
                ) : (
                  <ScrollView style={{ maxHeight: tourPickerModalMaxHeight }} showsVerticalScrollIndicator>
                    {tourPickerDisplayGroups.map((group) => (
                      <View key={group.key}>
                        {group.title ? (
                          <Text style={styles.tourPickerSectionTitle}>{group.title}</Text>
                        ) : null}
                        {group.items.map((t) => renderTourPickerTourRow(t))}
                      </View>
                    ))}
                  </ScrollView>
                )}

                <View style={styles.tourPickerModalFooter}>
                  <Text style={[styles.muted, { flex: 1, marginBottom: 0, fontSize: 11 }]}>
                    Tap a tour to manage · eye toggles calendar visibility
                  </Text>
                  <TouchableOpacity
                    style={[styles.toolbarBtn, styles.toolbarBtnSecondary]}
                    onPress={() => { setTourPickerOpen(false); loadTourFolders(); setImportOpen(true); }}
                  >
                    <Ionicons name="download-outline" size={15} color={theme.brand.primary} />
                    <Text style={[styles.toolbarBtnText, styles.toolbarBtnTextDark]}>Import tour</Text>
                  </TouchableOpacity>
                </View>
              </>
            ) : (
              <>
                {loadingSharedTours ? (
                  <View style={{ paddingVertical: 32, alignItems: 'center' }}>
                    <ActivityIndicator color={theme.brand.accent} />
                  </View>
                ) : sharedTourInvites.length === 0 ? (
                  <View style={styles.tourPickerEmptyState}>
                    <Ionicons name="people-outline" size={36} color={theme.text.secondary} />
                    <Text style={[styles.muted, { textAlign: 'center' }]}>
                      No shared tour invites yet. When someone invites you to a tour, it will appear here.
                    </Text>
                    <Text style={[styles.muted, { textAlign: 'center', fontSize: 11 }]}>
                      You can also import any of your own saved tours from the My Tours tab.
                    </Text>
                    <TouchableOpacity
                      style={[styles.toolbarBtn, { marginTop: 8 }]}
                      onPress={() => { setTourPickerMainTab('mine'); setTourPickerOpen(false); loadTourFolders(); setImportOpen(true); }}
                    >
                      <Ionicons name="download-outline" size={16} color={theme.button.primaryText} />
                      <Text style={styles.toolbarBtnText}>Import tour</Text>
                  </TouchableOpacity>
                  </View>
                ) : (
                  <ScrollView style={{ maxHeight: tourPickerModalMaxHeight }} showsVerticalScrollIndicator>
                    {sharedTourInvites.map((row) => renderSharedTourRow(row))}
              </ScrollView>
                )}

                <View style={styles.tourPickerModalFooter}>
                  <Text style={[styles.muted, { flex: 1, marginBottom: 0, fontSize: 11 }]}>
                    Import copies the tour into My Tours and adds it to your calendar
                  </Text>
                  <TouchableOpacity
                    style={[styles.toolbarBtn, styles.toolbarBtnSecondary]}
                    onPress={loadSharedTourInvites}
                    disabled={loadingSharedTours}
                  >
                    {loadingSharedTours ? (
                      <ActivityIndicator color={theme.brand.primary} size="small" />
                    ) : (
                      <>
                        <Ionicons name="refresh-outline" size={15} color={theme.brand.primary} />
                        <Text style={[styles.toolbarBtnText, styles.toolbarBtnTextDark]}>Refresh</Text>
                      </>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>

      <Modal visible={importOpen} transparent animationType="fade" onRequestClose={() => setImportOpen(false)}>
        <Pressable style={styles.modalOverlay} onPress={() => setImportOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Import tour</Text>
            <Text style={styles.muted}>
              Tours are not on your calendar until you import them here. Each day becomes one calendar event. Moving a tour day on
              the calendar updates its day date in Tour Planner.
            </Text>
            {loadingTours ? (
              <ActivityIndicator color={theme.brand.accent} style={{ marginVertical: 20 }} />
            ) : tourFolders.length === 0 ? (
              <Text style={styles.muted}>No saved tours found. Save a tour in My Tours first.</Text>
            ) : (
              sortedTourPickerTours.map((t) => (
                <TouchableOpacity key={t.id} style={styles.tourPick} onPress={() => importTour(t)}>
                  <Text style={styles.tourPickTitle}>{t.name}</Text>
                  <Text style={styles.tourPickSub}>
                    {formatTourDateRange(t.meta?.firstDate, t.meta?.lastDate)}
                    {' · '}{t.meta?.dayCount || 0} days
                  </Text>
                </TouchableOpacity>
              ))
            )}
            <TouchableOpacity style={styles.secondaryBtn} onPress={() => setImportOpen(false)}>
              <Text style={styles.secondaryBtnText}>Close</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={tourImportDateModal.open}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setImportDatePickerRowIndex(-1);
          setTourImportDateModal({ open: false, folder: null, rows: [], applyAsSelection: false });
        }}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => {
            setImportDatePickerRowIndex(-1);
            setTourImportDateModal({ open: false, folder: null, rows: [], applyAsSelection: false });
          }}
        >
          <Pressable style={[styles.modalCard, { maxWidth: 700 }]} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Assign tour day dates</Text>
            <Text style={styles.muted}>
              Some tour days have no scheduled date. Set a date (YYYY-MM-DD) for each day before importing.
            </Text>
            <ScrollView style={styles.importDatesList}>
              {tourImportDateModal.rows.map((row, idx) => (
                <View key={`import-date-row-${row.dayIndex}`} style={styles.importDateRowBlock}>
                  <View style={styles.importDateRow}>
                    <View style={styles.importDateMeta}>
                      <Text style={styles.importDateDayTitle}>
                        Day {row.dayIndex + 1}: {row.dayName}
                      </Text>
                      {row.venueLabel ? (
                        <Text style={styles.importDateVenue} numberOfLines={1}>
                          {row.venueLabel}
                        </Text>
                      ) : null}
                    </View>
                    <TouchableOpacity style={styles.importDatePickBtn} onPress={() => openImportDatePickerForRow(idx)}>
                      <Text style={[styles.importDatePickBtnText, !row.dateKey && styles.importDatePickBtnTextPlaceholder]}>
                        {row.dateKey || row.fallbackDate || 'YYYY-MM-DD'}
                      </Text>
                      <Ionicons name="calendar-outline" size={15} color={theme.text.primary} />
                    </TouchableOpacity>
                  </View>
                  {importDatePickerRowIndex === idx ? (
                    <View style={styles.importDatePickerInline}>
                      <View style={styles.importPickerHeaderRow}>
                        <TouchableOpacity
                          style={styles.importPickerNavBtn}
                          onPress={() =>
                            setImportDatePickerCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }))
                          }
                          accessibilityLabel="Previous month"
                        >
                          <Ionicons name="chevron-back" size={16} color={theme.text.primary} />
                        </TouchableOpacity>
                        <Text style={styles.importPickerMonthText}>
                          {monthLabel(importDatePickerCursor.y, importDatePickerCursor.m)}
                        </Text>
                        <TouchableOpacity
                          style={styles.importPickerNavBtn}
                          onPress={() =>
                            setImportDatePickerCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }))
                          }
                          accessibilityLabel="Next month"
                        >
                          <Ionicons name="chevron-forward" size={16} color={theme.text.primary} />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.importPickerWeekdayRow}>
                        {weekdayLabels.map((w) => (
                          <Text key={`import-picker-wd-${w}`} style={styles.importPickerWeekdayCell}>
                            {w}
                          </Text>
                        ))}
                      </View>
                      {importDatePickerWeekRows.map((week, wi) => (
                        <View key={`import-picker-week-${wi}`} style={styles.importPickerWeekRow}>
                          {week.map((cell) => (
                            <TouchableOpacity
                              key={cell.dateKey || `${wi}-${cell.jsDate.toISOString()}`}
                              style={[styles.importPickerDayCell, !cell.inMonth && styles.importPickerDayCellMuted]}
                              onPress={() => {
                                if (!cell.dateKey) return;
                                setTourImportDateModal((prev) => ({
                                  ...prev,
                                  rows: prev.rows.map((r, i) => (i === idx ? { ...r, dateKey: cell.dateKey } : r)),
                                }));
                                setImportDatePickerRowIndex(-1);
                              }}
                            >
                              <Text style={[styles.importPickerDayText, !cell.inMonth && styles.importPickerDayTextMuted]}>
                                {cell.jsDate.getDate()}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      ))}
                    </View>
                  ) : null}
                </View>
              ))}
            </ScrollView>
            <View style={styles.popupButtonRow}>
              <TouchableOpacity
                style={styles.popupBtnSecondary}
                onPress={() => {
                  setImportDatePickerRowIndex(-1);
                  setTourImportDateModal({ open: false, folder: null, rows: [], applyAsSelection: false });
                }}
              >
                <Text style={styles.popupBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.primaryBtn, { marginBottom: 0 }]} onPress={confirmImportTourDates}>
                <Text style={styles.primaryBtnText}>Import</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Message (success / error / info) — MediaManager-style, no Alert */}
      <Modal visible={messageModal != null} transparent animationType="fade" onRequestClose={() => setMessageModal(null)}>
        <Pressable style={styles.modalOverlay} onPress={() => setMessageModal(null)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <View style={{ alignItems: 'center', marginBottom: 8 }}>
              <Ionicons
                name={
                  messageModal?.variant === 'success'
                    ? 'checkmark-circle'
                    : messageModal?.variant === 'error'
                      ? 'alert-circle'
                      : 'information-circle'
                }
                size={44}
                color={
                  messageModal?.variant === 'success'
                    ? theme.brand.primary
                    : messageModal?.variant === 'error'
                      ? theme.status.error
                      : theme.brand.accent
                }
              />
            </View>
            <Text style={styles.modalTitle}>{messageModal?.title || ''}</Text>
            <Text style={[styles.muted, { marginBottom: 16 }]}>{messageModal?.text || ''}</Text>
            <TouchableOpacity style={styles.primaryBtn} onPress={() => setMessageModal(null)}>
              <Text style={styles.primaryBtnText}>OK</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={scheduleDaySheetLinkModal != null}
        transparent
        animationType="fade"
        onRequestClose={closeScheduleDaySheetLinkModal}
      >
        <Pressable style={styles.modalOverlay} onPress={closeScheduleDaySheetLinkModal}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <View style={{ alignItems: 'center', marginBottom: 8 }}>
              <Ionicons
                name={
                  scheduleDaySheetLinkModal?.direction === 'schedule-to-daysheet'
                    ? 'document-text-outline'
                    : 'calendar-outline'
                }
                size={40}
                color={theme.brand.accent}
              />
            </View>
            <Text style={styles.modalTitle}>
              {scheduleDaySheetLinkModal?.direction === 'schedule-to-daysheet'
                ? 'Update day sheet too?'
                : 'Update live schedule too?'}
            </Text>
            <Text style={[styles.muted, { marginBottom: 16 }]}>
              {scheduleDaySheetLinkModal?.direction === 'schedule-to-daysheet'
                ? 'You are publishing the schedule. Copy this schedule to the day sheet for this show date as well?'
                : 'You are saving the day sheet. Publish this schedule to the live Schedule tab (calendar views and shared links) as well?'}
            </Text>
            <View style={styles.popupButtonRow}>
              <TouchableOpacity
                style={styles.popupBtnSecondary}
                onPress={() => void resolveScheduleDaySheetLink(false)}
                disabled={scheduleDaySheetLinkBusy}
              >
                <Text style={styles.popupBtnSecondaryText}>
                  {scheduleDaySheetLinkModal?.direction === 'schedule-to-daysheet'
                    ? 'Schedule only'
                    : 'Day sheet only'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.primaryBtn, scheduleDaySheetLinkBusy && { opacity: 0.55 }]}
                onPress={() => void resolveScheduleDaySheetLink(true)}
                disabled={scheduleDaySheetLinkBusy}
              >
                {scheduleDaySheetLinkBusy ? (
                  <ActivityIndicator size="small" color={theme.button?.primaryText || '#fff'} />
                ) : (
                  <Text style={styles.primaryBtnText}>
                    {scheduleDaySheetLinkModal?.direction === 'schedule-to-daysheet'
                      ? 'Update day sheet'
                      : 'Update schedule'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Delete single event */}
      <Modal visible={pendingDeleteEventId != null} transparent animationType="fade" onRequestClose={() => setPendingDeleteEventId(null)}>
        <Pressable style={styles.modalOverlay} onPress={() => setPendingDeleteEventId(null)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <View style={{ alignItems: 'center', marginBottom: 8 }}>
              <Ionicons name="trash-outline" size={40} color={theme.status.error} />
            </View>
            <Text style={styles.modalTitle}>Delete event</Text>
            <Text style={[styles.muted, { marginBottom: 16 }]}>Remove this from your calendar? This cannot be undone.</Text>
            <View style={styles.popupButtonRow}>
              <TouchableOpacity style={styles.popupBtnSecondary} onPress={() => setPendingDeleteEventId(null)}>
                <Text style={styles.popupBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.popupBtnDanger} onPress={performDeleteEvent}>
                <Text style={styles.popupBtnDangerText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Remove entire tour from calendar */}
      <Modal visible={pendingRemoveTourId != null} transparent animationType="fade" onRequestClose={() => setPendingRemoveTourId(null)}>
        <Pressable style={styles.modalOverlay} onPress={() => setPendingRemoveTourId(null)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <View style={{ alignItems: 'center', marginBottom: 8 }}>
              <Ionicons name="albums-outline" size={40} color={theme.status.error} />
            </View>
            <Text style={styles.modalTitle}>Remove tour from calendar</Text>
            <Text style={[styles.muted, { marginBottom: 16 }]}>
              Remove all days of this tour from your calendar? Your saved tour in Tour Planner is not deleted.
            </Text>
            <View style={styles.popupButtonRow}>
              <TouchableOpacity style={styles.popupBtnSecondary} onPress={() => setPendingRemoveTourId(null)}>
                <Text style={styles.popupBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.popupBtnDanger} onPress={performRemoveTourFromCalendar}>
                <Text style={styles.popupBtnDangerText}>Remove all</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal
        visible={expandedNotesModal.open}
        transparent
        animationType="fade"
        onRequestClose={() => setExpandedNotesModal({ open: false, dateKey: '' })}
      >
        <View style={styles.modalRoot}>
          <Pressable style={styles.modalBackdrop} onPress={() => setExpandedNotesModal({ open: false, dateKey: '' })} />
          <View
            style={[
              styles.modalCard,
              styles.expandedNotesModalCard,
              { height: Math.round(windowHeight * 0.9), maxHeight: Math.round(windowHeight * 0.9) },
            ]}
          >
            <ScrollView
              style={styles.expandedNotesModalScroll}
              contentContainerStyle={styles.expandedNotesScrollContent}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
              nestedScrollEnabled
            >
            <Text style={styles.modalTitle}>Expanded notes editor</Text>
            <Text style={styles.muted}>
              {expandedNotesModal.dateKey
                ? `${activeTopTab || 'TAB'} · ${expandedNotesModal.dateKey}`
                : 'Select a day and tab first.'}
            </Text>
            {expandedNotesModal.dateKey ? (
              <DayNotesEditor
              value={String(tabGridDraftByDate[expandedNotesModal.dateKey] || '')}
                onChange={(t) => updateTabGridCellDraft(expandedNotesModal.dateKey, t)}
                theme={theme}
              placeholder="Start writing notes for this day..."
                inputMinHeight={Math.round(Math.max(160, windowHeight * 0.28))}
            />
            ) : null}
            </ScrollView>
            {supportsDayApplyScope && expandedNotesModal.dateKey ? (
              <View style={{ marginTop: 8 }}>
                <DayApplyScopePicker
                  value={tabApplyScope}
                  onChange={setTabApplyScope}
                  anchorDateKey={expandedNotesModal.dateKey}
                  tourShowDateKeys={selectedTourShowDateKeys}
                  theme={theme}
                />
              </View>
            ) : null}
            <View style={styles.expandedNotesModalFooter}>
              <View style={styles.expandedNotesFooterRow}>
                <TouchableOpacity
                  style={[
                    styles.expandedNotesFooterBtn,
                    styles.expandedNotesFooterBtnPrimary,
                    (!expandedNotesHasChanges || expandedNotesSaving) && { opacity: 0.55 },
                  ]}
                  disabled={!expandedNotesHasChanges || expandedNotesSaving}
                  onPress={saveExpandedNotes}
                >
                  <Text style={styles.expandedNotesFooterBtnPrimaryText}>
                    {expandedNotesSaving
                      ? 'Saving...'
                      : expandedNotesSaveDayCount > 1
                        ? `Save to ${expandedNotesSaveDayCount} days`
                        : 'Save'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.expandedNotesFooterBtn, styles.expandedNotesFooterBtnSecondary]}
                  onPress={() => {
                    setExpandedNotesModal({ open: false, dateKey: '' });
                    setExpandedNotesSaving(false);
                  }}
                >
                  <Text style={styles.expandedNotesFooterBtnSecondaryText}>Done</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>
      <Modal
        visible={expandedScheduleModal.open}
        transparent
        animationType="fade"
        onRequestClose={() => setExpandedScheduleModal({ open: false, dateKey: '' })}
      >
        <View style={styles.modalRoot}>
          <Pressable style={styles.modalBackdrop} onPress={() => setExpandedScheduleModal({ open: false, dateKey: '' })} />
          <View
            style={[
              styles.modalCard,
              styles.expandedNotesModalCard,
              styles.expandedTabEditorModalCard,
              { maxHeight: Math.min(Math.round(windowHeight * 0.92), 720), height: Math.min(Math.round(windowHeight * 0.92), 720) },
            ]}
          >
            <ScrollView
              style={styles.expandedNotesModalScroll}
              contentContainerStyle={styles.expandedNotesScrollContent}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
              nestedScrollEnabled
          >
            <Text style={styles.modalTitle}>Schedule editor</Text>
            <Text style={styles.muted}>
              {expandedScheduleModal.dateKey ? `SCHEDULE · ${expandedScheduleModal.dateKey}` : 'Select a day first.'}
            </Text>
            <Text style={[styles.muted, { marginBottom: 8 }]}>
              Time + item lines, or text lines. Reorder with arrows; + adds between rows or at the end.
            </Text>
            {expandedScheduleModal.dateKey ? (
              <ScheduleEditor
                mode="full"
                theme={theme}
                  fullUnscroll
                value={String(tabGridDraftByDate[expandedScheduleModal.dateKey] || '')}
                  onChange={(next) =>
                    expandedScheduleModal.dateKey && updateTabGridCellDraft(expandedScheduleModal.dateKey, next)
                  }
              />
            ) : null}
            </ScrollView>
            {supportsDayApplyScope && expandedScheduleModal.dateKey ? (
              <View style={{ marginTop: 8, flexShrink: 0 }}>
                <DayApplyScopePicker
                  value={tabApplyScope}
                  onChange={setTabApplyScope}
                  anchorDateKey={expandedScheduleModal.dateKey}
                  tourShowDateKeys={selectedTourShowDateKeys}
                  theme={theme}
                />
              </View>
            ) : null}
            <View style={styles.expandedNotesModalFooter}>
              <View style={styles.expandedNotesFooterRow}>
              <TouchableOpacity
                style={[
                    styles.expandedNotesFooterBtn,
                    styles.expandedNotesFooterBtnPrimary,
                  (!expandedScheduleHasChanges || expandedScheduleSaving) && { opacity: 0.52 },
                ]}
                disabled={!expandedScheduleHasChanges || expandedScheduleSaving}
                onPress={saveExpandedSchedule}
              >
                  <Text style={styles.expandedNotesFooterBtnPrimaryText}>
                    {expandedScheduleSaving
                      ? 'Saving...'
                      : expandedScheduleSaveDayCount > 1
                        ? `Save to ${expandedScheduleSaveDayCount} days`
                        : 'Save'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                  style={[styles.expandedNotesFooterBtn, styles.expandedNotesFooterBtnSecondary]}
                onPress={() => {
                  setExpandedScheduleModal({ open: false, dateKey: '' });
                  setExpandedScheduleSaving(false);
                }}
              >
                  <Text style={styles.expandedNotesFooterBtnSecondaryText}>Done</Text>
              </TouchableOpacity>
            </View>
            </View>
          </View>
        </View>
      </Modal>
      <Modal
        visible={expandedStructuredListModal.open}
        transparent
        animationType="fade"
        onRequestClose={() => setExpandedStructuredListModal({ open: false, dateKey: '', tabKey: '' })}
      >
        <View style={styles.modalRoot}>
          <Pressable
            style={styles.modalBackdrop}
            onPress={() => setExpandedStructuredListModal({ open: false, dateKey: '', tabKey: '' })}
          />
          <View
            style={[
              styles.modalCard,
              styles.expandedNotesModalCard,
              styles.expandedTabEditorModalCard,
              { maxHeight: Math.min(Math.round(windowHeight * 0.92), 720), height: Math.min(Math.round(windowHeight * 0.92), 720) },
            ]}
          >
            <ScrollView
              style={styles.expandedNotesModalScroll}
              contentContainerStyle={styles.expandedNotesScrollContent}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
              nestedScrollEnabled
            >
              <Text style={styles.modalTitle}>
                {STRUCTURED_DAY_LIST_MODAL_COPY[expandedStructuredListModal.tabKey]?.title || 'List editor'}
              </Text>
              <Text style={styles.muted}>
                {expandedStructuredListModal.dateKey
                  ? `${expandedStructuredListModal.tabKey} · ${expandedStructuredListModal.dateKey}`
                  : 'Select a day first.'}
              </Text>
              <Text style={[styles.muted, { marginBottom: 8 }]}>
                {STRUCTURED_DAY_LIST_MODAL_COPY[expandedStructuredListModal.tabKey]?.hint || ''}
              </Text>
              {expandedStructuredListModal.dateKey ? (
                <StructuredDayListEditor
                  tabKey={expandedStructuredListModal.tabKey}
                  mode="full"
                  theme={theme}
                  fullUnscroll
                  value={String(tabGridDraftByDate[expandedStructuredListModal.dateKey] || '')}
                  onChange={(next) =>
                    expandedStructuredListModal.dateKey && updateTabGridCellDraft(expandedStructuredListModal.dateKey, next)
                  }
                />
              ) : null}
            </ScrollView>
            {supportsDayApplyScope && expandedStructuredListModal.dateKey ? (
              <View style={{ marginTop: 8, flexShrink: 0 }}>
                <DayApplyScopePicker
                  value={tabApplyScope}
                  onChange={setTabApplyScope}
                  anchorDateKey={expandedStructuredListModal.dateKey}
                  tourShowDateKeys={selectedTourShowDateKeys}
                  theme={theme}
                />
              </View>
            ) : null}
            <View style={styles.expandedNotesModalFooter}>
              <View style={styles.expandedNotesFooterRow}>
                <TouchableOpacity
                  style={[
                    styles.expandedNotesFooterBtn,
                    styles.expandedNotesFooterBtnPrimary,
                    (!expandedStructuredListHasChanges || expandedStructuredListSaving) && { opacity: 0.52 },
                  ]}
                  disabled={!expandedStructuredListHasChanges || expandedStructuredListSaving}
                  onPress={saveExpandedStructuredList}
                >
                  <Text style={styles.expandedNotesFooterBtnPrimaryText}>
                    {expandedStructuredListSaving
                      ? 'Saving...'
                      : expandedStructuredListSaveDayCount > 1
                        ? `Save to ${expandedStructuredListSaveDayCount} days`
                        : 'Save'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.expandedNotesFooterBtn, styles.expandedNotesFooterBtnSecondary]}
                  onPress={() => {
                    setExpandedStructuredListModal({ open: false, dateKey: '', tabKey: '' });
                    setExpandedStructuredListSaving(false);
                  }}
                >
                  <Text style={styles.expandedNotesFooterBtnSecondaryText}>Done</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>
      <Modal
        visible={dayFilesModal.open}
        transparent
        animationType="fade"
        onRequestClose={() => setDayFilesModal({ open: false, dateKey: '' })}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setDayFilesModal({ open: false, dateKey: '' })}>
          <Pressable style={[styles.modalCard, { maxWidth: 560 }]} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Day files</Text>
            <Text style={styles.muted}>Add downloadable files for {dayFilesModal.dateKey || 'this day'}.</Text>
            <Text style={styles.label}>File name</Text>
            <TextInput
              style={styles.input}
              value={dayFileNameInput}
              onChangeText={setDayFileNameInput}
              placeholder="Setlist PDF"
              placeholderTextColor={placeholderColor}
            />
            <View style={styles.popupButtonRow}>
              <TouchableOpacity style={styles.primaryBtn} onPress={addDayFile} disabled={dayFileSaving}>
                <Text style={styles.primaryBtnText}>{dayFileSaving ? 'Uploading...' : 'Upload file'}</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.dayFilesListWrap}>
              {dayFiles.length === 0 ? (
                <Text style={styles.muted}>No files added yet.</Text>
              ) : (
                dayFiles.map((f) => (
                  <View key={f.id} style={styles.dayFileRow}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => Linking.openURL(f.url).catch(() => {})}>
                      <Text style={styles.link} numberOfLines={1}>
                        {f.name}
                      </Text>
                      <Text style={styles.eventSub} numberOfLines={1}>
                        {f.url}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.floatIconBtn} onPress={() => removeDayFile(f.id)}>
                      <Ionicons name="trash-outline" size={16} color={theme.status.error} />
                    </TouchableOpacity>
                  </View>
                ))
              )}
            </View>
            <View style={styles.popupButtonRow}>
              <TouchableOpacity style={styles.popupBtnSecondary} onPress={() => setDayFilesModal({ open: false, dateKey: '' })}>
                <Text style={styles.popupBtnSecondaryText}>Done</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal
        visible={dayShareModal.open}
        transparent
        animationType="fade"
        onRequestClose={() => setDayShareModal({ open: false, dateKey: '' })}
      >
        <View style={styles.modalRoot}>
          <Pressable style={styles.modalBackdrop} onPress={() => setDayShareModal({ open: false, dateKey: '' })} />
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Share day info</Text>
            <Text style={styles.muted}>Select sections to share for {dayShareModal.dateKey || 'this day'}.</Text>
            <View style={{ marginTop: 8, marginBottom: 8 }}>
              {dayShareAvailableOptions.map((opt) => {
                const checked = dayShareSelections.includes(opt);
                return (
                  <TouchableOpacity
                    key={`share-opt-${opt}`}
                    style={styles.dayShareOptionRow}
                    onPress={() =>
                      setDayShareSelections((prev) =>
                        checked ? prev.filter((x) => x !== opt) : [...prev, opt]
                      )
                    }
                  >
                    <Ionicons name={checked ? 'checkbox' : 'square-outline'} size={18} color={theme.brand.accent} />
                    <Text style={styles.dayShareOptionText}>{opt}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <Text style={styles.label}>Share link</Text>
            {dayShareSaving ? (
              <ActivityIndicator color={theme.brand.accent} style={{ marginBottom: 8 }} />
            ) : null}
            <TextInput
              style={styles.input}
              value={activeDayShareLink}
              editable={false}
              selectTextOnFocus
              placeholder="Select sections to generate link"
              placeholderTextColor={placeholderColor}
            />
            <Text style={[styles.muted, { marginTop: 6, marginBottom: 4 }]}>
              Manage all links, access, and previews from Shared links in the tab bar.
            </Text>
            <View style={styles.popupButtonRow}>
              <TouchableOpacity
                style={styles.popupBtnSecondary}
                onPress={() => {
                  if (activeDayShareLink && Platform.OS === 'web' && navigator?.clipboard) {
                    navigator.clipboard.writeText(activeDayShareLink).catch(() => {});
                    showToast('Link copied.', 'success');
                  }
                }}
                disabled={!activeDayShareLink}
              >
                <Text style={styles.popupBtnSecondaryText}>Copy link</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.popupBtnSecondary} onPress={() => setDayShareModal({ open: false, dateKey: '' })}>
                <Text style={styles.popupBtnSecondaryText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      <Modal
        visible={dayInfoModal.open}
        transparent
        animationType="fade"
        onRequestClose={() => setDayInfoModal({ open: false, dateKey: '' })}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setDayInfoModal({ open: false, dateKey: '' })}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Day details · {dayInfoModal.dateKey}</Text>
            {[
              {
                id: 'venues',
                title: 'VENUES',
                body:
                  (eventsByDate[dayInfoModal.dateKey] || [])
                    .map((e) => String(e.dayVenueSummary || '').trim())
                    .filter(Boolean)
                    .join('\n') || 'No venues for this day.',
              },
              ...CALENDAR_TOP_TABS.map((tabLabel) => {
                const tabKey = DAY_INFO_TAB_KEYS[tabLabel] || tabLabel;
                const bodyFromTab = String(dayInfoTabEntries[tabKey] || '').trim();
                if (tabLabel === 'STAGE') {
                  const stageBody = [bodyFromTab, stagePlotUrl, stageInputListUrl].filter((x) => String(x || '').trim()).join('\n');
                  return { id: 'stage', title: 'STAGE', body: stageBody || 'No stage info.' };
                }
                if (tabLabel === 'DAY SHEET') {
                  const daySheetBody = [bodyFromTab, daySheetNotes].filter((x) => String(x || '').trim()).join('\n');
                  return { id: 'daySheet', title: 'DAY SHEET', body: daySheetBody || 'No day sheet info.' };
                }
                if (tabLabel === 'SCHEDULE') {
                  return {
                    id: 'schedule',
                    title: 'SCHEDULE',
                    body: formatScheduleAsPlainText(bodyFromTab) || 'No schedule info.',
                  };
                }
                if (tabLabel === 'SETLIST') {
                  return {
                    id: 'setlist',
                    title: 'SETLIST',
                    body: formatStructuredDayListForTab('SETLIST', bodyFromTab) || 'No setlist info.',
                  };
                }
                if (tabLabel === 'GUEST LIST') {
                  return {
                    id: 'guestList',
                    title: 'GUEST LIST',
                    body: formatStructuredDayListForTab('GUEST LIST', bodyFromTab) || 'No guest list info.',
                  };
                }
                if (tabLabel === 'CONTACTS') {
                  return {
                    id: 'contacts',
                    title: 'CONTACTS',
                    body: formatStructuredDayListForTab('CONTACTS', bodyFromTab) || 'No contacts info.',
                  };
                }
                if (tabLabel === 'MERCH') {
                  return {
                    id: 'merch',
                    title: 'MERCH',
                    body: formatStructuredDayListForTab('MERCH', bodyFromTab) || 'No merch info.',
                  };
                }
                if (tabLabel === 'LODGING') {
                  const plannerBody = formatLodgingDaySummary(selectedTourFolder?.tour, dayInfoModal.dateKey);
                  const combined = [plannerBody, bodyFromTab].filter(Boolean).join('\n\n');
                  return {
                    id: 'lodging',
                    title: 'LODGING',
                    body: combined || 'No lodging info.',
                  };
                }
                const sectionIdMap = {
                  'TOUR PARTY': 'tourParty',
                  RIDER: 'rider',
                  SETLIST: 'setlist',
                  'GUEST LIST': 'guestList',
                  SCHEDULE: 'schedule',
                  FILES: 'files',
                  CONTACTS: 'contacts',
                  MERCH: 'merch',
                  NOTES: 'notes',
                  LODGING: 'lodging',
                };
                const id = sectionIdMap[tabLabel] || tabLabel.toLowerCase().replace(/\s+/g, '');
                return {
                  id,
                  title: tabLabel,
                  body: bodyFromTab || `No ${tabLabel.toLowerCase()} info.`,
                };
              }),
            ].map((sec) => {
              const expanded = !!dayInfoExpanded[sec.id];
              return (
                <View key={`day-info-${sec.id}`} style={styles.dayInfoSection}>
                  <TouchableOpacity
                    style={styles.dayInfoSectionHead}
                    onPress={() => setDayInfoExpanded((prev) => ({ ...prev, [sec.id]: !expanded }))}
                  >
                    <Ionicons name={expanded ? 'chevron-down' : 'chevron-forward'} size={16} color={theme.text.primary} />
                    <Text style={styles.dayInfoSectionTitle}>{sec.title}</Text>
                  </TouchableOpacity>
                  {expanded ? <Text style={styles.dayInfoSectionBody}>{sec.body}</Text> : null}
                </View>
              );
            })}
            <View style={styles.popupButtonRow}>
              <TouchableOpacity style={styles.popupBtnSecondary} onPress={() => setDayInfoModal({ open: false, dateKey: '' })}>
                <Text style={styles.popupBtnSecondaryText}>Close</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* PDF export — month range (legacy calendar). Master Tour uses the left rail. */}
      <Modal visible={pdfExportOpen && !masterTourUi} transparent animationType="fade" onRequestClose={() => setPdfExportOpen(false)}>
        <Pressable style={styles.modalOverlay} onPress={() => setPdfExportOpen(false)}>
          <Pressable style={[styles.modalCard, { maxWidth: 420 }]} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Export PDF</Text>
            <Text style={[styles.muted, { marginBottom: 12 }]}>
              Choose the first and last month to include. Each month gets a calendar page; event details are listed at the end.
            </Text>
            <PdfMonthRangeFields
              styles={styles}
              labelColor={theme.text.primary}
              fromY={pdfFromY}
              fromM={pdfFromM}
              toY={pdfToY}
              toM={pdfToM}
              setFromY={setPdfFromY}
              setFromM={setPdfFromM}
              setToY={setPdfToY}
              setToM={setPdfToM}
              onExport={runPdfExport}
              onCancel={() => setPdfExportOpen(false)}
              exporting={exportingPdf}
            />
          </Pressable>
        </Pressable>
      </Modal>

      {/* Move event to another month */}
      <Modal visible={moveMonthEvent != null} transparent animationType="fade" onRequestClose={() => setMoveMonthEvent(null)}>
        <Pressable style={styles.modalOverlay} onPress={() => setMoveMonthEvent(null)}>
          <Pressable style={[styles.modalCard, { maxWidth: 400 }]} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Move to month</Text>
            <Text style={[styles.muted, { marginBottom: 12 }]}>
              Choose the year and month. The day of month stays the same when possible (e.g. Jan 31 → Feb 28).
            </Text>
            <View style={styles.monthPickerYearRow}>
              <TouchableOpacity
                style={styles.monthPickerYearBtn}
                onPress={() => setMoveMonthYear((y) => Math.max(1970, y - 1))}
                accessibilityLabel="Previous year"
              >
                <Ionicons name="chevron-back" size={22} color={theme.text.primary} />
              </TouchableOpacity>
              <Text style={styles.monthPickerYearText}>{moveMonthYear}</Text>
              <TouchableOpacity
                style={styles.monthPickerYearBtn}
                onPress={() => setMoveMonthYear((y) => Math.min(2100, y + 1))}
                accessibilityLabel="Next year"
              >
                <Ionicons name="chevron-forward" size={22} color={theme.text.primary} />
              </TouchableOpacity>
            </View>
            <View style={styles.monthGrid}>
              {MONTH_SHORT_LABELS.map((label, i) => (
                <TouchableOpacity
                  key={label}
                  style={[styles.monthChip, moveMonthIndex === i && styles.monthChipActive]}
                  onPress={() => setMoveMonthIndex(i)}
                >
                  <Text style={[styles.monthChipText, moveMonthIndex === i && styles.monthChipTextActive]}>{label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            {moveMonthEvent ? (
              <Text style={[styles.muted, { marginTop: 12, marginBottom: 8 }]}>
                New date:{' '}
                <Text style={{ fontWeight: '800', color: theme.text.primary }}>
                  {moveDateToMonth(moveMonthEvent.date, moveMonthYear, moveMonthIndex) || '—'}
                </Text>
              </Text>
            ) : null}
            <View style={styles.popupButtonRow}>
              <TouchableOpacity style={styles.popupBtnSecondary} onPress={() => setMoveMonthEvent(null)}>
                <Text style={styles.popupBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.primaryBtn, { marginBottom: 0 }]} onPress={performMoveToMonth}>
                <Text style={styles.primaryBtnText}>Move</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {toast ? (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onHide={() => setToast(null)}
        />
      ) : null}

      <SharedLinksManager
        visible={sharedLinksOpen && Boolean(selectedTourFilterId)}
        onClose={() => setSharedLinksOpen(false)}
        ownerId={uid}
        ownerName={ownerDisplayName}
        tourId={selectedTourFilterId}
        tourName={selectedTourFilterName}
        userId={uid}
        userEmail={userData?.email}
        onToast={showToast}
        showDateKeys={selectedTourShowDateKeys}
      />
      {!masterTourUi ? (
        <TourSettingsManager
          visible={tourSettingsOpen && Boolean(selectedTourFilterId)}
          onClose={() => setTourSettingsOpen(false)}
          ownerId={uid}
          tourId={selectedTourFilterId}
          tourName={selectedTourFilterName}
          showDateKeys={selectedTourShowDateKeys}
          onToast={showToast}
          currentUserId={uid}
          acceptedMembers={v2PartyMembers}
          tourShareSettings={tourShareSettings}
        />
      ) : null}
    </View>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────
const HOUR_HEIGHT = 64; // px per hour in time-grid views
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function parseTimeToMinutes(t) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h)) return null;
  return h * 60 + (m || 0);
}

function formatHour(h) {
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

// ─── Compact month grid (mobile / tablet): dots + tap-to-select ───────────────
function CalendarPublishedScheduleList({
  dateKey,
  rawPublished,
  theme,
  styles,
  compact = false,
  maxItems = 4,
  onItemPress,
}) {
  const items = useMemo(() => getPublishedScheduleItems(rawPublished), [rawPublished]);
  if (!items.length) return null;
  return (
    <View style={[styles.calendarScheduleList, compact && styles.calendarScheduleListCompact]} pointerEvents="box-none">
      {items.slice(0, maxItems).map((it) => (
        <TouchableOpacity
          key={it.id}
          style={styles.calendarScheduleChip}
          onPress={() => onItemPress?.(dateKey, it)}
          disabled={!onItemPress}
          activeOpacity={onItemPress ? 0.75 : 1}
        >
          <Text style={styles.calendarScheduleChipText} numberOfLines={1}>
            {it.timeLabel ? `${it.timeLabel} ` : ''}
            {it.item || '—'}
          </Text>
        </TouchableOpacity>
      ))}
      {items.length > maxItems ? (
        <Text style={styles.calendarScheduleMore}>+{items.length - maxItems} more</Text>
      ) : null}
    </View>
  );
}

function CalendarCompactMonthView({
  weekRows,
  eventsByDate,
  selectedDateKey,
  onDayCellPress,
  tourDayMetaByDate,
  tourColorById,
  selectedTourFilterId,
  schedulePublishedByDate = {},
  onScheduleItemPress,
  shareScheduleIndicatorsByDate = {},
  activeTopTab = null,
  tabTextByDate = {},
  tabStyleByDate = {},
  selectedDayTabStyle = null,
  dayFilesByDate = {},
  allowGridInlineEditAllDays = false,
  onChangeDayTabText,
  onOpenExpandedEditor,
  onOpenFilesManager,
  onOpenStageFiles,
  theme,
  styles,
  weekStartsOn = 0,
}) {
  const masterTourUi = useMasterTourCalendarUi();
  const weekdayLabels = weekdayLabelsForWeekStart(masterTourUi ? weekStartsOn : 0);
  const todayKey = toDateKey(new Date());
  return (
    <View style={[styles.card, styles.calendarCard]}>
      <View style={styles.compactMonthTable}>
        <View style={styles.compactWeekRow}>
          {weekdayLabels.map((w) => (
            <View key={w} style={styles.compactColumnHeader}>
              <Text style={styles.compactWeekday}>{w.slice(0, 1)}</Text>
            </View>
          ))}
        </View>
        {weekRows.map((week, wi) => (
          <View key={`compact-week-${wi}`} style={styles.compactWeekRow}>
            {week.map((cell, di) => {
              const dk = cell.dateKey;
              const dayEvents = dk ? eventsByDate[dk] || [] : [];
              const visibleEvents = dayEvents.filter((e) => e.eventType !== 'tour_day');
              const tourDay = dayEvents.find((e) => e.eventType === 'tour_day');
              const meta = dk ? tourDayMetaByDate?.[dk] : null;
              const dayCard = pickTourDayCard(meta, selectedTourFilterId);
              const isSel = dk === selectedDateKey;
              const isToday = dk === todayKey;
              const hasTourDay = Boolean(tourDay || meta);
              const tourColor = masterTourUi
                ? (accentForDayKind(dayCard?.kind) ||
                  tourDay?.color ||
                  meta?.tourColor ||
                  (selectedTourFilterId ? tourColorById?.[selectedTourFilterId] : null) ||
                  BRAND)
                : (tourDay?.color ||
                  meta?.tourColor ||
                  (selectedTourFilterId ? tourColorById?.[selectedTourFilterId] : null) ||
                  BRAND);
              const dotCount = Math.min(visibleEvents.length + (hasTourDay ? 1 : 0), 3);
              const gridEditAll = Boolean(allowGridInlineEditAllDays && activeTopTab);
              const isManagedDay = isManagedTourDay(dk, selectedTourFilterId, tourDayMetaByDate);
              const {
                showFilesInCell,
                showStageFilesInCell,
                showPlainTabInput,
                showScheduleInCell,
                showStructuredListInCell,
              } = dayCellTabDisplayModes(activeTopTab, cell, isSel, gridEditAll, isManagedDay);
              const showCellExpand = shouldShowDayCellExpand(isManagedDay, selectedTourFilterId, activeTopTab);
              const hasInlineEditor =
                showFilesInCell || showStageFilesInCell || showPlainTabInput || showScheduleInCell || showStructuredListInCell;
              const dayTabText = dk ? tabTextByDate?.[dk] || '' : '';
              const dayTabStyle = dk ? tabStyleByDate?.[dk] || selectedDayTabStyle : selectedDayTabStyle;
              const fileItems = dk
                ? (dayFilesByDate?.[dk] || []).filter((f) => normalizeDayFileType(f.fileType) === 'files')
                : [];
              const stageFileItems = dk
                ? (dayFilesByDate?.[dk] || []).filter((f) => normalizeDayFileType(f.fileType) === 'stage')
                : [];
              return (
                <TouchableOpacity
                  key={`${dk}-${wi}-${di}`}
                  style={[
                    styles.compactDayCell,
                    cell.inMonth && masterTourUi && !dayCard?.kind && styles.dayCellEmpty,
                    !cell.inMonth && styles.compactDayCellMuted,
                    isSel && styles.compactDayCellSelected,
                    isToday && !isSel && styles.compactDayCellToday,
                    hasTourDay && { borderLeftWidth: 3, borderLeftColor: tourColor },
                    hasInlineEditor && styles.compactDayCellEditing,
                  ]}
                  onPress={() => dk && onDayCellPress?.(dk)}
                  activeOpacity={0.75}
                  accessibilityLabel={dk ? `Day ${cell.jsDate.getDate()}` : undefined}
                >
                  {showCellExpand ? (
                    <Pressable
                      style={[styles.dayCellExpandScheduleBtn, styles.dayCellExpandScheduleBtnBottom]}
                      onPress={() => dk && onOpenExpandedEditor?.(dk)}
                      hitSlop={6}
                      accessibilityLabel="Expand day panel"
                    >
                      <Ionicons name="expand-outline" size={11} color={theme.text.primary} />
                    </Pressable>
                  ) : null}
                  <Text
                    style={[
                      styles.compactDayNum,
                      !cell.inMonth && styles.cellDayMuted,
                      isSel && styles.compactDayNumSelected,
                      isToday && !isSel && styles.compactDayNumToday,
                    ]}
                  >
                    {cell.jsDate.getDate()}
                  </Text>
                  {hasTourDay && !hasInlineEditor && masterTourUi ? (
                    <TourDayCellHeader
                      card={pickTourDayCard(meta, selectedTourFilterId)}
                      isDarkMode={theme.name === 'dark'}
                      compact
                      muted={!cell.inMonth}
                    />
                  ) : null}
                  {dotCount > 0 ? (
                    <View style={styles.compactDayDots}>
                      {visibleEvents.slice(0, 3).map((ev, i) => (
                        <View
                          key={`dot-${ev.id || i}`}
                          style={[styles.compactDayDot, { backgroundColor: ev.color || BRAND }]}
                        />
                      ))}
                      {hasTourDay && visibleEvents.length < 3 ? (
                        <View style={[styles.compactDayDot, { backgroundColor: tourColor }]} />
                      ) : null}
                    </View>
                  ) : (
                    <View style={styles.compactDayDotsSpacer} />
                  )}
                  {selectedTourFilterId && shareScheduleIndicatorsByDate[dk]?.length ? (
                    <ShareScheduleIndicators
                      indicators={shareScheduleIndicatorsByDate[dk]}
                      theme={theme}
                      compact
                    />
                  ) : null}
                  {selectedTourFilterId && dk && !hasInlineEditor ? (
                    <CalendarPublishedScheduleList
                      dateKey={dk}
                      rawPublished={schedulePublishedByDate[dk]}
                      theme={theme}
                      styles={styles}
                      compact
                      maxItems={2}
                      onItemPress={onScheduleItemPress}
                    />
                  ) : null}
                  {hasInlineEditor ? (
                    <View
                      style={styles.compactDayEditorWrap}
                      onStartShouldSetResponder={() => true}
                    >
                      {showPlainTabInput ? (
                        <DayTabCellEditor
                          activeTopTab={activeTopTab}
                          dayTabText={dayTabText}
                          dayTabStyle={dayTabStyle}
                          dateKey={dk}
                          onChangeDayTabText={onChangeDayTabText}
                          theme={theme}
                          styles={styles}
                          dense
                        />
                      ) : null}
                      {showScheduleInCell ? (
                        <ScheduleEditor
                          mode="compact"
                          dense
                          theme={theme}
                          value={dayTabText}
                          onChange={(next) => dk && onChangeDayTabText?.(dk, next)}
                        />
                      ) : null}
                      {showStructuredListInCell ? (
                        <StructuredDayListEditor
                          tabKey={activeTopTab}
                          mode="compact"
                          dense
                          theme={theme}
                          value={dayTabText}
                          onChange={(next) => dk && onChangeDayTabText?.(dk, next)}
                        />
                      ) : null}
                      {showFilesInCell ? (
                        <View style={styles.dayFilesInlineList}>
                          {fileItems.slice(0, 2).map((f) => (
                            <TouchableOpacity
                              key={`compact-file-${f.id}`}
                              style={styles.dayFileInlineChip}
                              onPress={() => Linking.openURL(f.url).catch(() => {})}
                            >
                              <Ionicons name="document-outline" size={10} color={theme.brand.accent} />
                              <Text style={styles.dayFileInlineChipText} numberOfLines={1}>{f.name}</Text>
                            </TouchableOpacity>
                          ))}
                          <TouchableOpacity
                            style={styles.dayFilesAttachBtn}
                            onPress={() => dk && onOpenFilesManager?.(dk)}
                            accessibilityLabel="Attach file to day"
                          >
                            <Ionicons name="attach-outline" size={13} color={theme.brand.primary} />
                          </TouchableOpacity>
                        </View>
                      ) : null}
                      {showStageFilesInCell ? (
                        <View style={styles.dayFilesInlineList}>
                          {stageFileItems.slice(0, 2).map((f) => (
                            <TouchableOpacity
                              key={`compact-stage-${f.id}`}
                              style={styles.dayFileInlineChip}
                              onPress={() => Linking.openURL(f.url).catch(() => {})}
                            >
                              <Ionicons name="document-outline" size={10} color={theme.brand.accent} />
                              <Text style={styles.dayFileInlineChipText} numberOfLines={1}>{f.name}</Text>
                            </TouchableOpacity>
                          ))}
                          <TouchableOpacity
                            style={styles.dayFilesAttachBtn}
                            onPress={() => dk && onOpenStageFiles?.(dk)}
                            accessibilityLabel="Attach stage file to day"
                          >
                            <Ionicons name="attach-outline" size={13} color={theme.brand.primary} />
                          </TouchableOpacity>
                        </View>
                      ) : null}
                    </View>
                  ) : null}
                </TouchableOpacity>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
}

function CalendarDayTabContent({
  selectedDateKey,
  selectedTourFilterId,
  activeTopTab,
  activeCalendarTabKey,
  isRiderTab = false,
  isRiderFullMode,
  isStageTab,
  isDaySheetTab,
  selectedTabDayText,
  selectedTabDayStyle,
  onChangeSelectedDayTabText,
  onChangeSelectedDayTabStyle,
  updateTabGridCellDraft,
  dayFilesByDate,
  openExpandedTabEditor,
  openDayFilesManager,
  openDayShareModal,
  onOpenTourSettings,
  addRiderFullFileToSelectedDay,
  uploadStageFileFromAsset,
  uploadFilesTabFromAsset,
  dayFileSaving,
  saveStageFilesForSelectedDay,
  renderSelectedDayFilesPanel,
  selectedDayRiderFullFiles,
  selectedDayStageFiles,
  riderFullDocUrl,
  riderFullIsImage,
  riderFullIsPdf,
  riderFullPdfPage,
  setRiderFullPdfPage,
  daySheetMode,
  setDaySheetMode,
  uploadDaySheetAsset,
  daySheetAssetSaving,
  saveDaySheetPublication,
  hasUnsavedDaySheetPublication,
  daySheetPublicationSaving,
  daySheetSeededFromDefaults,
  selectedDayVenueName,
  daySheetBannerUrl,
  daySheetBgUrl,
  daySheetPreviewRows,
  daySheetNotes,
  setDaySheetNotes,
  daySheetScheduleSerialized,
  handleDaySheetScheduleChange,
  placeholderColor,
  supportsDayApplyScope = false,
  tabApplyScope,
  setTabApplyScope,
  selectedTourShowDateKeys = [],
  publishTabContentForSelectedDay,
  tabApplyScopeSaving = false,
  selectedTabHasUnpublishedDraft = false,
  tabDraftSaveState = 'idle',
  ownerId,
  ownerName,
  ownerEmail,
  ownerPhotoURL,
  tourId,
  tourName,
  riderSubtab,
  onRiderSubtabChange,
  onWriterError,
  onWriterSuccess,
  onTourPartyError,
  onTourPartySuccess,
  currentUserId,
  tourShareSettings = null,
  sidebarWide = false,
  riderShares = [],
  sidebarFillHeight = false,
  selectedTourFolder = null,
  loadTourFolders,
  goTourPlanner,
  canEditActiveCalendarTab = true,
  onScheduleToast,
  theme,
  styles,
}) {
  if (!selectedDateKey || !selectedTourFilterId || !activeTopTab) return null;

  const daySheetCompact = !sidebarWide;

  const isTourPartyTab = activeCalendarTabKey === 'TOUR PARTY';
  const isLodgingTab = activeCalendarTabKey === 'LODGING';
  const isNotesTab = activeCalendarTabKey === 'NOTES';
  const isScheduleTab = activeCalendarTabKey === 'SCHEDULE';
  const isStructuredListTab = STRUCTURED_DAY_LIST_TABS.has(activeCalendarTabKey);
  /** Schedule uses parent sidebar scroll; structured lists use compactUnscroll the same way. */
  const allowSidebarFill = sidebarFillHeight && !isScheduleTab && !isStructuredListTab && !isNotesTab;
  const structuredListUsesParentScroll = sidebarFillHeight && isStructuredListTab;
  const editorBlockStyle = [styles.selectedDayEditorBlock, allowSidebarFill && styles.selectedDayEditorBlockFill];
  const editorZoneStyle = [styles.selectedDayTabEditorZone, allowSidebarFill && styles.selectedDayTabEditorZoneFill];
  const tabInputStyle = [styles.selectedDayTabInput, allowSidebarFill && styles.selectedDayTabInputFill];

  if (isTourPartyTab) {
    return (
      <View style={editorBlockStyle}>
        <View style={styles.selectedDayEditorHeader}>
          <Text style={styles.selectedDayEditorLabel}>TOUR PARTY</Text>
          <SidebarShareSettingsActions
            theme={theme}
            styles={styles}
            onShare={() => openDayShareModal(selectedDateKey)}
            onSettings={() => onOpenTourSettings?.()}
          />
        </View>
        <TourPartyPanel
          ownerId={ownerId}
          ownerName={ownerName}
          ownerEmail={ownerEmail}
          tourId={tourId || selectedTourFilterId}
          tourName={tourName}
          currentUserId={currentUserId}
          tourShareSettings={tourShareSettings}
          theme={theme}
          onError={onTourPartyError}
          onSuccess={onTourPartySuccess}
        />
      </View>
    );
  }

  if (isRiderTab) {
    return (
      <View style={editorBlockStyle}>
        <View style={styles.selectedDayEditorHeader}>
          <Text style={styles.selectedDayEditorLabel}>RIDER</Text>
          <View style={styles.selectedDayEditorActions}>
            <CalendarTabPublishBar
              placement="header"
              theme={theme}
              hasUnpublishedDraft={selectedTabHasUnpublishedDraft}
              draftSaveState={tabDraftSaveState}
              publishing={tabApplyScopeSaving}
              onPublish={publishTabContentForSelectedDay}
            />
            <SidebarShareSettingsActions
              theme={theme}
              styles={styles}
              onShare={() => openDayShareModal(selectedDateKey)}
              onSettings={() => onOpenTourSettings?.()}
            />
          </View>
        </View>
        <CalendarWriterPanel
          selectedDateKey={selectedDateKey}
          value={selectedTabDayText}
          onChange={(next) => onChangeSelectedDayTabText(next)}
          riderMode={riderSubtab}
          onRiderModeChange={onRiderSubtabChange}
          tabApplyScope={tabApplyScope}
          setTabApplyScope={setTabApplyScope}
          selectedTourShowDateKeys={selectedTourShowDateKeys}
          onSave={publishTabContentForSelectedDay}
          saving={tabApplyScopeSaving}
          ownerId={ownerId}
          ownerName={ownerName}
          ownerEmail={ownerEmail}
          ownerPhotoURL={ownerPhotoURL}
          tourId={tourId || selectedTourFilterId}
          tourName={tourName}
          theme={theme}
          onError={onWriterError}
          onSuccess={onWriterSuccess}
          sidebarWide={sidebarWide}
          riderShares={riderShares}
        />
      </View>
    );
  }

  if (isStageTab) {
    return (
      <View style={editorBlockStyle}>
        <View style={styles.selectedDayEditorHeader}>
          <Text style={styles.selectedDayEditorLabel}>STAGE</Text>
          <SidebarShareSettingsActions
            theme={theme}
            styles={styles}
            onShare={() => openDayShareModal(selectedDateKey)}
            onSettings={() => onOpenTourSettings?.()}
          />
        </View>
        <CalendarStagePanel
          selectedDateKey={selectedDateKey}
          onUploadFile={uploadStageFileFromAsset}
          uploading={dayFileSaving}
          tabApplyScope={tabApplyScope}
          setTabApplyScope={setTabApplyScope}
          selectedTourShowDateKeys={selectedTourShowDateKeys}
          onSave={saveStageFilesForSelectedDay}
          saving={tabApplyScopeSaving}
          theme={theme}
          compact
          onError={onWriterError}
          onSuccess={onWriterSuccess}
        >
          {renderSelectedDayFilesPanel(selectedDayStageFiles, 'No stage files uploaded yet for this day.')}
        </CalendarStagePanel>
      </View>
    );
  }

  if (activeCalendarTabKey === 'FILES') {
    const selectedDayGeneralFiles = (dayFilesByDate?.[selectedDateKey] || []).filter(
      (f) => normalizeDayFileType(f.fileType) === 'files'
    );
    return (
      <View style={editorBlockStyle}>
        <View style={styles.selectedDayEditorHeader}>
          <Text style={styles.selectedDayEditorLabel}>FILES</Text>
          <SidebarShareSettingsActions
            theme={theme}
            styles={styles}
            onShare={() => openDayShareModal(selectedDateKey)}
            onSettings={() => onOpenTourSettings?.()}
          />
        </View>
        <CalendarDayFilesPanel
          selectedDateKey={selectedDateKey}
          onUploadFile={uploadFilesTabFromAsset}
          uploading={dayFileSaving}
          theme={theme}
          compact
          onError={onWriterError}
          onSuccess={onWriterSuccess}
        >
          {renderSelectedDayFilesPanel(selectedDayGeneralFiles, 'No files attached to this day yet.')}
        </CalendarDayFilesPanel>
      </View>
    );
  }

  if (isDaySheetTab) {
    return (
      <View style={styles.selectedDayEditorBlock}>
        <View style={[styles.selectedDayEditorHeader, styles.daySheetEditorHeader]}>
          <Text style={styles.selectedDayEditorLabel}>DAY SHEET</Text>
          <SidebarShareSettingsActions
            theme={theme}
            styles={styles}
            onShare={() => openDayShareModal(selectedDateKey)}
            onSettings={() => onOpenTourSettings?.()}
          />
        </View>
        <DaySheetToolbar
          theme={theme}
          styles={styles}
          daySheetMode={daySheetMode}
          onModeChange={setDaySheetMode}
          onUploadBanner={() => uploadDaySheetAsset('banner')}
          onUploadBackground={() => uploadDaySheetAsset('background')}
          onSave={() => void saveDaySheetPublication()}
          daySheetAssetSaving={daySheetAssetSaving}
          daySheetPublicationSaving={daySheetPublicationSaving}
          hasUnsavedDaySheetPublication={hasUnsavedDaySheetPublication}
          daySheetSeededFromDefaults={daySheetSeededFromDefaults}
          daySheetBannerUrl={daySheetBannerUrl}
          daySheetBgUrl={daySheetBgUrl}
          compact
        />
        <View style={[styles.daySheetPaperWrap, styles.daySheetPaperWrapSidebar]}>
          <DaySheetPaper
            theme={theme}
            tourName={tourName}
            headline={formatDaySheetHeadline(selectedDateKey)}
            venueName={selectedDayVenueName}
            bannerUrl={daySheetBannerUrl}
            backgroundUrl={daySheetBgUrl}
            scheduleRows={daySheetPreviewRows}
            aftershowText={daySheetNotes}
            editMode={daySheetMode === 'edit'}
            compact={daySheetCompact}
            aftershowPlaceholder="AFTERSHOW..."
            onAftershowChange={daySheetMode === 'edit' ? setDaySheetNotes : undefined}
            renderSchedule={() =>
              daySheetMode === 'edit' ? (
                <DaySheetScheduleEditor
                  value={daySheetScheduleSerialized}
                  onChange={handleDaySheetScheduleChange}
                  compact={daySheetCompact}
                />
              ) : null
            }
          />
        </View>
      </View>
    );
  }

  if (!activeCalendarTabKey) return null;

  const isSetlistTab = activeCalendarTabKey === 'SETLIST';
  const isFilesTab = activeCalendarTabKey === 'FILES';
  const fileItems = (dayFilesByDate?.[selectedDateKey] || []).filter((f) => normalizeDayFileType(f.fileType) === 'files');

  const handlePrintSetlist = () => {
    if (Platform.OS !== 'web') return;
    try {
      openSetlistPrintWindow({
        dateLabel: formatSetlistDateLabel(selectedDateKey),
        setlistRaw: selectedTabDayText,
        tourName: tourName || 'Untitled tour',
      });
    } catch (err) {
      onWriterError?.(err?.message || 'Could not open setlist print view.');
    }
  };

  const showTabPublishControls =
    activeCalendarTabKey &&
    !isFilesTab &&
    !isTourPartyTab &&
    !isRiderTab &&
    !isStageTab &&
    !isDaySheetTab &&
    !isLodgingTab;

  return (
    <View style={editorBlockStyle}>
      <View style={styles.selectedDayEditorHeader}>
        <Text style={styles.selectedDayEditorLabel}>{activeTopTab || activeCalendarTabKey}</Text>
        <View style={styles.selectedDayEditorActions}>
          {showTabPublishControls ? (
            <CalendarTabPublishBar
              placement="header"
              theme={theme}
              hasUnpublishedDraft={selectedTabHasUnpublishedDraft}
              draftSaveState={tabDraftSaveState}
              publishing={tabApplyScopeSaving}
              onPublish={publishTabContentForSelectedDay}
            />
          ) : null}
          {!isFilesTab && !isTourPartyTab && !isLodgingTab ? (
            <TouchableOpacity
              style={styles.selectedDayIconActionBtn}
              onPress={() => openExpandedTabEditor(selectedDateKey)}
              accessibilityLabel="Expand editor"
            >
              <Ionicons name="expand-outline" size={16} color={theme.brand.primary} />
            </TouchableOpacity>
          ) : null}
          <SidebarShareSettingsActions
            theme={theme}
            styles={styles}
            onShare={() => openDayShareModal(selectedDateKey)}
            onSettings={() => onOpenTourSettings?.()}
            onPrint={isSetlistTab && Platform.OS === 'web' ? handlePrintSetlist : undefined}
          />
        </View>
      </View>
      <View style={editorZoneStyle}>
        {isLodgingTab ? (
          <LodgingDayPanel
            ownerId={ownerId}
            tourId={tourId || selectedTourFilterId}
            tour={selectedTourFolder?.tour}
            dateKey={selectedDateKey}
            currentUserId={currentUserId}
            canEdit={canEditActiveCalendarTab}
            onTourUpdated={loadTourFolders}
            onOpenTourPlanner={goTourPlanner}
            onToast={onScheduleToast}
            theme={theme}
            ownerName={ownerName}
            ownerEmail={ownerEmail}
            ownerPhotoURL={ownerPhotoURL}
          />
        ) : isScheduleTab ? (
          <ScheduleEditor
            mode="compact"
            theme={theme}
            value={selectedTabDayText}
            onChange={(next) => updateTabGridCellDraft(selectedDateKey, next)}
            compactUnscroll={sidebarFillHeight}
          />
        ) : isStructuredListTab ? (
          <StructuredDayListEditor
            tabKey={activeCalendarTabKey}
            mode="compact"
            theme={theme}
            value={selectedTabDayText}
            onChange={(next) => updateTabGridCellDraft(selectedDateKey, next)}
            compactUnscroll={structuredListUsesParentScroll}
            compactFill={allowSidebarFill}
          />
        ) : isNotesTab ? (
          <DayNotesEditor
            value={selectedTabDayText}
            onChange={(t) => onChangeSelectedDayTabText(t)}
            theme={theme}
            compact
            fill={allowSidebarFill}
          />
        ) : (
          <TextInput
            style={[tabInputStyle, styles.selectedDayTabInputBordered]}
            value={selectedTabDayText}
            onChangeText={(t) => onChangeSelectedDayTabText(t)}
            placeholder={`Add ${activeTopTab || 'notes'} for this day…`}
            placeholderTextColor={theme.input.placeholder}
            multiline
            textAlignVertical="top"
          />
        )}
      </View>
      {showTabPublishControls && supportsDayApplyScope ? (
        <View style={styles.selectedDayCopyToCard}>
          <DayApplyScopePicker
            compact
            contained
            showSaveButton
            value={tabApplyScope}
            onChange={setTabApplyScope}
            anchorDateKey={selectedDateKey}
            tourShowDateKeys={selectedTourShowDateKeys}
            theme={theme}
            onSave={publishTabContentForSelectedDay}
            saving={tabApplyScopeSaving}
          />
        </View>
      ) : null}
    </View>
  );
}

function SelectedDayDetailPanel({
  selectedDateKey,
  selectedDayEvents,
  tourDayMetaByDate,
  selectedTourFilterId,
  onSelectTour,
  tourColorById,
  activeCalendarTabKey,
  activeTopTab,
  isStageTab = false,
  isDaySheetTab = false,
  selectedTabDayText,
  selectedTabDayStyle,
  dayFilesByDate,
  theme,
  styles,
  allowAddEvent,
  openNew,
  openExpandedTabEditor,
  openDayFilesManager,
  openDayShareModal,
  openEdit,
  goTourPlanner,
  showTourPlannerNav,
  moveToNextDay,
  onChangeSelectedDayTabText,
  updateTabGridCellDraft,
  supportsDayApplyScope = false,
  tabApplyScope,
  setTabApplyScope,
  selectedTourShowDateKeys = [],
  publishTabContentForSelectedDay,
  tabApplyScopeSaving = false,
  selectedTabHasUnpublishedDraft = false,
  tabDraftSaveState = 'idle',
  uploadStageFileFromAsset,
  uploadFilesTabFromAsset,
  dayFileSaving = false,
  saveStageFilesForSelectedDay,
  selectedDayStageFiles = [],
  renderSelectedDayFilesPanel,
  showMobileSharedLinks = false,
  onOpenSharedLinks,
  onOpenTourSettings,
  tourShareSettings = null,
  onScheduleToast,
  onTopTabPress,
  onPrevDay,
  onNextDay,
  ownerId,
  ownerName,
  ownerEmail,
  ownerPhotoURL,
  tourId,
  tourName,
  riderSubtab,
  onRiderSubtabChange,
  onWriterError,
  onWriterSuccess,
  onTourPartyError,
  onTourPartySuccess,
  currentUserId,
  selectedTourFolder = null,
  loadTourFolders,
  canEditActiveCalendarTab = true,
}) {
  const meta = selectedDateKey ? tourDayMetaByDate?.[selectedDateKey] : null;
  const toursOnDay = meta?.tours || [];
  const activeTourEntry = toursOnDay.find((t) => t.tourId === selectedTourFilterId) || toursOnDay[0] || null;
  const resolvedTourName = String(tourName || activeTourEntry?.tourName || meta?.tourName || '').trim();
  const venueName = String(activeTourEntry?.venueName || meta?.venueName || '').trim();
  const visibleEvents = selectedDayEvents.filter((e) => e.eventType !== 'tour_day');
  const fileItems = selectedDateKey
    ? (dayFilesByDate?.[selectedDateKey] || []).filter((f) => normalizeDayFileType(f.fileType) === 'files')
    : [];
  const showTabEditor = Boolean(
    selectedTourFilterId &&
      selectedDateKey &&
      isScheduleShowDay &&
      (activeCalendarTabKey || isStageTab)
  );
  const isScheduleTab = activeCalendarTabKey === 'SCHEDULE';
  const isStructuredListTab = STRUCTURED_DAY_LIST_TABS.has(activeCalendarTabKey);
  const isLodgingTab = activeCalendarTabKey === 'LODGING';
  const isNotesTab = activeCalendarTabKey === 'NOTES';
  const isFilesTab = activeCalendarTabKey === 'FILES';
  const isTourPartyTab = activeCalendarTabKey === 'TOUR PARTY';
  const isRiderTab = activeTopTab === 'RIDER';
  const isScheduleShowDay = Boolean(
    selectedDateKey && selectedTourShowDateKeys.includes(selectedDateKey)
  );
  const showTabPublishControls =
    activeCalendarTabKey &&
    !isFilesTab &&
    !isTourPartyTab &&
    !isStageTab &&
    !isDaySheetTab &&
    !isLodgingTab;
  const [dayScheduleExpanded, setDayScheduleExpanded] = useState(false);

  useEffect(() => {
    setDayScheduleExpanded(false);
  }, [selectedDateKey, selectedTourFilterId]);

  if (!selectedDateKey) {
    return (
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Select a day</Text>
        <Text style={styles.muted}>Tap any date in the calendar above to view events and edit tour details.</Text>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <View style={styles.selectedDayPanelHeaderBlock}>
        <View style={styles.selectedDayPanelHeaderRow}>
          <CalendarDayChevrons onPrevDay={onPrevDay} onNextDay={onNextDay} theme={theme} size="sm" />
          <Text style={[styles.sectionTitle, styles.selectedDayPanelHeaderTitle]} numberOfLines={2}>
            {formatDaySheetHeadline(selectedDateKey)}
          </Text>
        </View>
        {selectedTourFilterId && tourId ? (
          <DayScheduleStatusChip
            dateKey={selectedDateKey}
            tourShareSettings={tourShareSettings}
            isShowDay={isScheduleShowDay}
            expanded={dayScheduleExpanded}
            onPress={() => setDayScheduleExpanded((v) => !v)}
          />
        ) : null}
        {showMobileSharedLinks && selectedTourFilterId ? (
          <View style={styles.mobilePanelLinksRow}>
            <TouchableOpacity
              style={[styles.sharedLinksBarBtn, styles.sharedLinksPanelBtn]}
              onPress={onOpenSharedLinks}
              accessibilityLabel="Manage shared links"
            >
              <Ionicons name="link-outline" size={14} color={theme.brand.accent} />
              <Text style={styles.sharedLinksBarBtnText}>Shared links</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tourSettingsBarBtn, styles.sharedLinksPanelBtn]}
              onPress={onOpenTourSettings}
              accessibilityLabel="Tour settings"
            >
              <Ionicons name="settings-outline" size={14} color={theme.brand.primary} />
              <Text style={styles.tourSettingsBarBtnText}>Tour settings</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>

      {selectedTourFilterId && tourId ? (
        <DayScheduleSettingsPanel
          dateKey={selectedDateKey}
          ownerId={ownerId}
          tourId={tourId}
          tourShareSettings={tourShareSettings}
          isShowDay={isScheduleShowDay}
          onToast={onScheduleToast}
          onOpenTourSettings={onOpenTourSettings}
          compact
          expanded={dayScheduleExpanded}
          onExpandedChange={setDayScheduleExpanded}
        />
      ) : null}

      {isScheduleShowDay && onTopTabPress ? (
        <CalendarSidebarTabNav
          tabs={CALENDAR_TOP_TABS}
          activeTab={activeTopTab}
          onTabPress={onTopTabPress}
          theme={theme}
          compact
        />
      ) : null}

      {selectedTourFilterId && selectedDateKey && !isScheduleShowDay ? (
        <View style={styles.selectedDayOffTourHint}>
          <Text style={styles.selectedDayOffTourHintText}>
            This day isn&apos;t on your tour schedule. Select a show day to open tour party, rider, schedule, and other tour tools.
          </Text>
        </View>
      ) : null}

      {toursOnDay.length > 1 ? (
        <View style={styles.selectedDayTourPickerBlock}>
          <Text style={styles.selectedDayTourPickerLabel}>Tours on this day</Text>
          {toursOnDay.map((t) => {
            const isSelected = selectedTourFilterId === t.tourId;
            const color = t.tourColor || tourColorById?.[t.tourId] || BRAND;
            return (
              <TouchableOpacity
                key={t.tourId}
                style={[
                  styles.selectedDayTourPickerItem,
                  isSelected && { borderColor: color, backgroundColor: color + '16' },
                ]}
                onPress={() => onSelectTour?.(t.tourId)}
              >
                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.selectedDayTourPickerName} numberOfLines={1}>{t.tourName}</Text>
                  {t.venueName ? (
                    <Text style={styles.selectedDayTourPickerVenue} numberOfLines={1}>{t.venueName}</Text>
                  ) : null}
                </View>
                {isSelected ? <Ionicons name="checkmark-circle" size={18} color={color} /> : null}
              </TouchableOpacity>
            );
          })}
        </View>
      ) : (resolvedTourName || venueName) ? (
        <View style={styles.selectedDayMetaRow}>
          {resolvedTourName ? <Text style={styles.selectedDayMetaTour} numberOfLines={1}>{resolvedTourName}</Text> : null}
          {venueName ? <Text style={styles.selectedDayMetaVenue} numberOfLines={2}>{venueName}</Text> : null}
        </View>
      ) : null}

      {showTabEditor ? (
        <View style={styles.selectedDayEditorBlock}>
          <View style={styles.selectedDayEditorHeader}>
            <Text style={styles.selectedDayEditorLabel}>{activeTopTab || activeCalendarTabKey}</Text>
            <View style={styles.selectedDayEditorActions}>
              {showTabPublishControls ? (
                <CalendarTabPublishBar
                  placement="header"
                  theme={theme}
                  hasUnpublishedDraft={selectedTabHasUnpublishedDraft}
                  draftSaveState={tabDraftSaveState}
                  publishing={tabApplyScopeSaving}
                  onPublish={publishTabContentForSelectedDay}
                />
              ) : null}
              {!isFilesTab && !isTourPartyTab && !isRiderTab && !isStageTab && !isLodgingTab ? (
                <TouchableOpacity
                  style={styles.selectedDayIconActionBtn}
                  onPress={() => openExpandedTabEditor(selectedDateKey)}
                  accessibilityLabel="Expand editor"
                >
                  <Ionicons name="expand-outline" size={16} color={theme.brand.primary} />
                </TouchableOpacity>
              ) : null}
              <SidebarShareSettingsActions
                theme={theme}
                styles={styles}
                onShare={() => openDayShareModal(selectedDateKey)}
                onSettings={() => onOpenTourSettings?.()}
              />
            </View>
          </View>
          {isTourPartyTab ? (
            <TourPartyPanel
              ownerId={ownerId}
              ownerName={ownerName}
              ownerEmail={ownerEmail}
              tourId={tourId || selectedTourFilterId}
              tourName={resolvedTourName}
              currentUserId={currentUserId}
              tourShareSettings={tourShareSettings}
              theme={theme}
              onError={onTourPartyError}
              onSuccess={onTourPartySuccess}
            />
          ) : isRiderTab ? (
            <CalendarWriterPanel
              selectedDateKey={selectedDateKey}
              value={selectedTabDayText}
              onChange={(next) => onChangeSelectedDayTabText(next)}
              riderMode={riderSubtab}
              onRiderModeChange={onRiderSubtabChange}
              tabApplyScope={tabApplyScope}
              setTabApplyScope={setTabApplyScope}
              selectedTourShowDateKeys={selectedTourShowDateKeys}
              onSave={publishTabContentForSelectedDay}
              saving={tabApplyScopeSaving}
              ownerId={ownerId}
              ownerName={ownerName}
              ownerEmail={ownerEmail}
              ownerPhotoURL={ownerPhotoURL}
              tourId={tourId || selectedTourFilterId}
              theme={theme}
              onError={onWriterError}
              onSuccess={onWriterSuccess}
            />
          ) : isStageTab ? (
            <CalendarStagePanel
              selectedDateKey={selectedDateKey}
              onUploadFile={uploadStageFileFromAsset}
              uploading={dayFileSaving}
              tabApplyScope={tabApplyScope}
              setTabApplyScope={setTabApplyScope}
              selectedTourShowDateKeys={selectedTourShowDateKeys}
              onSave={saveStageFilesForSelectedDay}
              saving={tabApplyScopeSaving}
              theme={theme}
              compact
              onError={onWriterError}
              onSuccess={onWriterSuccess}
            >
              {renderSelectedDayFilesPanel?.(
                selectedDayStageFiles,
                'No stage files uploaded yet for this day.'
              )}
            </CalendarStagePanel>
          ) : isFilesTab ? (
            <CalendarDayFilesPanel
              selectedDateKey={selectedDateKey}
              onUploadFile={uploadFilesTabFromAsset}
              uploading={dayFileSaving}
              theme={theme}
              compact
              onError={onWriterError}
              onSuccess={onWriterSuccess}
            >
              {renderSelectedDayFilesPanel?.(fileItems, 'No files attached to this day yet.')}
            </CalendarDayFilesPanel>
          ) : (
            <View style={styles.selectedDayTabEditorZone}>
              {isLodgingTab ? (
                <LodgingDayPanel
                  ownerId={ownerId}
                  tourId={tourId || selectedTourFilterId}
                  tour={selectedTourFolder?.tour}
                  dateKey={selectedDateKey}
                  currentUserId={currentUserId}
                  canEdit={canEditActiveCalendarTab}
                  onTourUpdated={loadTourFolders}
                  onOpenTourPlanner={goTourPlanner}
                  onToast={onScheduleToast}
                  theme={theme}
                  ownerName={ownerName}
                  ownerEmail={ownerEmail}
                  ownerPhotoURL={ownerPhotoURL}
                />
              ) : isScheduleTab ? (
                <ScheduleEditor
                  mode="compact"
                  theme={theme}
                  value={selectedTabDayText}
                  onChange={(next) => updateTabGridCellDraft(selectedDateKey, next)}
                />
              ) : isStructuredListTab ? (
                <StructuredDayListEditor
                  tabKey={activeCalendarTabKey}
                  mode="compact"
                  theme={theme}
                  value={selectedTabDayText}
                  onChange={(next) => updateTabGridCellDraft(selectedDateKey, next)}
                />
              ) : isNotesTab ? (
                <DayNotesEditor
                  value={selectedTabDayText}
                  onChange={(t) => onChangeSelectedDayTabText(t)}
                  theme={theme}
                  compact
                />
              ) : (
                <TextInput
                  style={[styles.selectedDayTabInput, styles.selectedDayTabInputBordered]}
                  value={selectedTabDayText}
                  onChangeText={(t) => onChangeSelectedDayTabText(t)}
                  placeholder={`Add ${activeTopTab || 'notes'} for this day…`}
                  placeholderTextColor={theme.input.placeholder}
                  multiline
                  textAlignVertical="top"
                />
              )}
            </View>
          )}
          {showTabPublishControls && supportsDayApplyScope && !isRiderTab ? (
            <View style={styles.selectedDayCopyToCard}>
              <DayApplyScopePicker
                compact
                contained
                showSaveButton
                value={tabApplyScope}
                onChange={setTabApplyScope}
                anchorDateKey={selectedDateKey}
                tourShowDateKeys={selectedTourShowDateKeys}
                theme={theme}
                onSave={publishTabContentForSelectedDay}
                saving={tabApplyScopeSaving}
              />
            </View>
          ) : null}
        </View>
      ) : null}

      <View style={styles.selectedDayEventsHeader}>
        <Text style={styles.selectedDayEventsTitle}>Events</Text>
        {allowAddEvent ? (
          <TouchableOpacity style={styles.selectedDayActionBtn} onPress={openNew}>
            <Ionicons name="add-circle-outline" size={16} color={theme.brand.primary} />
            <Text style={styles.selectedDayActionBtnText}>Add</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      {visibleEvents.length === 0 ? (
        <Text style={styles.muted}>No events this day.</Text>
      ) : (
        visibleEvents.map((ev) => (
          <View key={ev.id} style={[styles.eventRow, { borderLeftColor: ev.color || BRAND }]}>
            <View style={{ flex: 1 }}>
              <Text style={styles.eventTitle}>{ev.title}</Text>
              {ev.startTime || ev.endTime ? (
                <Text style={styles.eventSub}>
                  {ev.startTime || '--:--'} - {ev.endTime || '--:--'}
                </Text>
              ) : null}
              {ev.dayVenueSummary ? <Text style={styles.eventSub}>{ev.dayVenueSummary}</Text> : null}
              {ev.notes ? <Text style={styles.eventNotes}>{ev.notes}</Text> : null}
              {showTourPlannerNav && ev.eventType === 'tour_day' && ev.sourceTourId ? (
                <TouchableOpacity onPress={() => goTourPlanner(ev.sourceTourId)}>
                  <Text style={styles.link}>Open in Tour Planner</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            <View style={styles.eventActions}>
              <TouchableOpacity onPress={() => openEdit(ev)} style={styles.smallBtn}>
                <Text style={styles.smallBtnText}>Edit</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => moveToNextDay(ev)} style={styles.smallBtn}>
                <Text style={styles.smallBtnText}>Next day</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))
      )}
    </View>
  );
}

// ─── Year view: responsive grid of mini-months ───────────────────────────────
function CalendarYearView({ year, eventsByDate, tourDayMetaByDate, selectedDateKey, theme, onMonthPress, onDayPress, weekStartsOn = 0 }) {
  const masterTourUi = useMasterTourCalendarUi();
  const start = masterTourUi ? weekStartsOn : 0;
  const weekdayLabels = weekdayLabelsForWeekStart(start);
  const todayKey = toDateKey(new Date());
  const { width } = useWindowDimensions();
  const GAP = 12;
  const PAD = 12;
  // Determine columns based on viewport width
  const cols = width >= 1200 ? 4 : width >= 800 ? 3 : width >= 500 ? 2 : 1;
  const monthCardStyle = {
    flex: 1,
    minWidth: 0,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: theme.ui.border,
    backgroundColor: theme.background.card,
    padding: 12,
    paddingBottom: 10,
    ...(Platform.OS === 'web' ? { boxShadow: `0 1px 6px ${theme.ui.shadow}` } : {}),
  };

  const months = Array.from({ length: 12 }, (_, i) => i);
  // Group months into rows
  const rows = [];
  for (let r = 0; r < months.length; r += cols) rows.push(months.slice(r, r + cols));

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: PAD, paddingBottom: 24 }}>
      {rows.map((rowMonths, ri) => (
        <View key={ri} style={{ flexDirection: 'row', gap: GAP, marginBottom: GAP }}>
          {rowMonths.map((m) => {
            const grid = buildMonthGrid(year, m, { weekStartsOn });
            const weekRows = chunkCalendarWeeks(grid);
            return (
              <View key={m} style={monthCardStyle}>
                <TouchableOpacity onPress={() => onMonthPress(m)} activeOpacity={0.85}>
                  <Text style={{ fontSize: 13, fontWeight: '800', color: theme.brand.accent, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {MONTH_SHORT_LABELS[m]}
                  </Text>
                </TouchableOpacity>
                <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                  {weekdayLabels.map((w) => (
                    <Text key={w} style={{ flex: 1, fontSize: 9, textAlign: 'center', color: theme.text.secondary, fontWeight: '700', textTransform: 'uppercase' }}>
                      {w[0]}
                    </Text>
                  ))}
                </View>
                {weekRows.map((week, wi) => (
                  <View key={wi} style={{ flexDirection: 'row', marginBottom: 3 }}>
                    {week.map((cell, di) => {
                      const dk = cell.dateKey;
                      // Include ALL event types (tour_day too) for dots, plus tourDayMetaByDate fallback
                      const dayEvs = dk ? (eventsByDate[dk] || []) : [];
                      const meta = dk ? tourDayMetaByDate?.[dk] : null;
                      // Synthesise a virtual dot for tour range days that haven't been imported
                      const tourMetaDot = meta && !dayEvs.some(e => e.eventType === 'tour_day')
                        ? [{ color: meta.tourColor || BRAND }]
                        : [];
                      const allDots = [...dayEvs, ...tourMetaDot];
                      const isSel = dk === selectedDateKey;
                      const isToday = dk === todayKey;
                      return (
                        <TouchableOpacity
                          key={di}
                          style={{ flex: 1, alignItems: 'center', paddingVertical: 1 }}
                          onPress={() => dk && cell.inMonth && onDayPress?.(dk)}
                          disabled={!dk || !cell.inMonth}
                          activeOpacity={0.7}
                        >
                          <View style={{
                            width: 22, height: 22, borderRadius: 11,
                            backgroundColor: isSel ? theme.brand.accent : isToday ? (theme.brand.accent + '28') : 'transparent',
                            alignItems: 'center', justifyContent: 'center',
                            borderWidth: isToday && !isSel ? 1.5 : 0,
                            borderColor: theme.brand.accent,
                          }}>
                            <Text style={{
                              fontSize: 10, fontWeight: isSel || isToday ? '800' : '500',
                              color: isSel ? '#fff' : (cell.inMonth ? theme.text.primary : theme.text.secondary),
                              opacity: cell.inMonth ? 1 : 0.3,
                            }}>
                              {cell.jsDate.getDate()}
                            </Text>
                          </View>
                          {allDots.length > 0 && cell.inMonth ? (
                            <View style={{ flexDirection: 'row', gap: 2, marginTop: 1 }}>
                              {allDots.slice(0, 3).map((ev, ei) => (
                                <View key={ei} style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: ev.color || BRAND }} />
                              ))}
                            </View>
                          ) : <View style={{ height: 5 }} />}
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                ))}
              </View>
            );
          })}
          {/* Fill empty slots in last row */}
          {rowMonths.length < cols ? Array.from({ length: cols - rowMonths.length }).map((_, i) => (
            <View key={`empty-${i}`} style={{ flex: 1, minWidth: 0 }} />
          )) : null}
        </View>
      ))}
    </ScrollView>
  );
}

// ─── Time grid used by both Week and Day views ────────────────────────────────
function TimeGridDayTabCell({ dateKey, sel, columnIndex, timeGridTabProps, theme, fill = false }) {
  if (!timeGridTabProps || !dateKey) return null;
  const {
    activeTopTab,
    tabTextByDate,
    tabStyleByDate,
    selectedDayTabStyle,
    dayFilesByDate,
    scheduleCellLabels,
    structuredListCellLabels,
    allowGridInlineEditAllDays,
    selectedTourFilterId,
    tourDayMetaByDate,
    onChangeDayTabText,
    onOpenExpandedEditor,
    onOpenFilesManager,
    onOpenStageFiles,
    styles,
  } = timeGridTabProps;
  const cell = { dateKey, inMonth: true };
  const gridEditAll = Boolean(allowGridInlineEditAllDays);
  const isManagedDay = isManagedTourDay(dateKey, selectedTourFilterId, tourDayMetaByDate);
  const {
    showFilesInCell,
    showStageFilesInCell,
    showPlainTabInput,
    showScheduleInCell,
    showStructuredListInCell,
  } = dayCellTabDisplayModes(activeTopTab, cell, sel, gridEditAll, isManagedDay);
  const showCellExpand = shouldShowDayCellExpand(isManagedDay, selectedTourFilterId, activeTopTab);
  if (
    !showCellExpand &&
    !showFilesInCell &&
    !showStageFilesInCell &&
    !showPlainTabInput &&
    !showScheduleInCell &&
    !showStructuredListInCell
  ) {
    return <View style={{ flex: 1, minHeight: 4 }} />;
  }
  const dayTabText = tabTextByDate?.[dateKey] || '';
  const dayTabStyle = tabStyleByDate?.[dateKey] || selectedDayTabStyle;
  const fileItems = (dayFilesByDate?.[dateKey] || []).filter((f) => normalizeDayFileType(f.fileType) === 'files');
  const stageFileItems = (dayFilesByDate?.[dateKey] || []).filter((f) => normalizeDayFileType(f.fileType) === 'stage');
  const containerStyle = fill
    ? { flex: 1, padding: 8, gap: 6 }
    : { flex: 1, padding: 4, gap: 4, minHeight: 56 };
  return (
    <View style={containerStyle}>
      {showCellExpand ? (
        <Pressable
          style={[styles.dayCellExpandScheduleBtn, { top: 2, right: 2 }]}
          onPress={() => onOpenExpandedEditor?.(dateKey)}
          hitSlop={6}
          accessibilityLabel="Expand day panel"
        >
          <Ionicons name="expand-outline" size={12} color={theme.text.primary} />
        </Pressable>
      ) : null}
      {showPlainTabInput ? (
        <DayTabCellEditor
          activeTopTab={activeTopTab}
          dayTabText={dayTabText}
          dayTabStyle={dayTabStyle}
          dateKey={dateKey}
          onChangeDayTabText={onChangeDayTabText}
          theme={theme}
          styles={styles}
          fill={fill}
        />
      ) : null}
      {showScheduleInCell ? (
        <View style={fill ? { flex: 1 } : null}>
          <ScheduleEditor
            mode="compact"
            compactFloat={!fill}
            compactFloatSide={columnIndex >= 4 ? 'left' : 'right'}
            theme={theme}
            value={dayTabText}
            onChange={(next) => onChangeDayTabText?.(dateKey, next)}
          />
        </View>
      ) : null}
      {showStructuredListInCell ? (
        <View style={fill ? { flex: 1 } : null}>
          <StructuredDayListEditor
            tabKey={activeTopTab}
            mode="compact"
            compactFloat={!fill}
            compactFloatSide={columnIndex >= 4 ? 'left' : 'right'}
            theme={theme}
            value={dayTabText}
            onChange={(next) => onChangeDayTabText?.(dateKey, next)}
          />
        </View>
      ) : null}
      {showFilesInCell ? (
        <View style={styles.dayFilesInlineList}>
          {fileItems.slice(0, fill ? 6 : 2).map((f) => (
            <TouchableOpacity
              key={`tg-file-${f.id}`}
              style={styles.dayFileInlineChip}
              onPress={() => Linking.openURL(f.url).catch(() => {})}
            >
              <Ionicons name="document-outline" size={10} color={theme.brand.accent} />
              <Text style={styles.dayFileInlineChipText} numberOfLines={1}>{f.name}</Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={styles.dayFilesAttachBtn}
            onPress={() => onOpenFilesManager?.(dateKey)}
            accessibilityLabel="Attach file to day"
          >
            <Ionicons name="attach-outline" size={13} color={theme.brand.primary} />
          </TouchableOpacity>
        </View>
      ) : null}
      {showStageFilesInCell ? (
        <View style={styles.dayFilesInlineList}>
          {stageFileItems.slice(0, fill ? 6 : 2).map((f) => (
            <TouchableOpacity
              key={`tg-stage-${f.id}`}
              style={styles.dayFileInlineChip}
              onPress={() => Linking.openURL(f.url).catch(() => {})}
            >
              <Ionicons name="document-outline" size={10} color={theme.brand.accent} />
              <Text style={styles.dayFileInlineChipText} numberOfLines={1}>{f.name}</Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={styles.dayFilesAttachBtn}
            onPress={() => onOpenStageFiles?.(dateKey)}
            accessibilityLabel="Attach stage file to day"
          >
            <Ionicons name="attach-outline" size={13} color={theme.brand.primary} />
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
}

function TimeGrid({
  days,
  eventsByDate,
  tourDayMetaByDate,
  selectedDateKey,
  theme,
  onDayCellPress,
  openNew,
  isDay = false,
  timeGridTabProps = null,
  selectedTourFilterId = null,
}) {
  const masterTourUi = useMasterTourCalendarUi();
  const todayKey = toDateKey(new Date());
  const scrollRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollRef.current?.scrollTo({ y: 7 * HOUR_HEIGHT - 20, animated: false });
    }, 80);
    return () => clearTimeout(timer);
  }, []);

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  return (
    <View style={{ flex: 1, flexDirection: 'column' }}>
      {/* ── Day header row ── */}
      <View style={{
        flexDirection: 'row',
        borderBottomWidth: 1, borderBottomColor: theme.ui.border,
        backgroundColor: theme.background.card,
      }}>
        <View style={{ width: 52 }} />
        {days.map(({ date, dateKey }) => {
          const isToday = dateKey === todayKey;
          const isSel = dateKey === selectedDateKey;
          const dayEvs = eventsByDate[dateKey] || [];
          const tourDayEv = dayEvs.find((e) => e.eventType === 'tour_day');
          const meta = tourDayMetaByDate?.[dateKey];
          const dayCard = pickTourDayCard(meta, selectedTourFilterId);
          const cornerTourName = String(meta?.tourName || tourDayEv?.sourceTourName || '').trim();
          return (
            <TouchableOpacity
              key={dateKey}
              style={{ flex: 1, alignItems: 'center', paddingVertical: 8, position: 'relative' }}
              onPress={() => onDayCellPress(dateKey)}
              activeOpacity={0.7}
            >
              <Text style={{ fontSize: 11, fontWeight: '700', color: theme.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {isDay ? date.toLocaleDateString('en-US', { weekday: 'long' }) : WEEKDAY_LABELS[date.getDay()]}
              </Text>
              <View style={{
                width: 34, height: 34, borderRadius: 17, marginTop: 3,
                backgroundColor: (isSel || isToday) ? theme.brand.accent : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <Text style={{ fontSize: 18, fontWeight: '800', color: (isSel || isToday) ? '#fff' : theme.text.primary }}>
                  {date.getDate()}
                </Text>
              </View>
              {masterTourUi && dayCard?.kind ? (
                <TourDayCellHeader
                  card={dayCard}
                  isDarkMode={theme.name === 'dark'}
                  compact
                />
              ) : cornerTourName ? (
                <Text style={{ fontSize: 9, color: theme.text.secondary, textAlign: 'center', marginTop: 2 }} numberOfLines={1}>
                  {cornerTourName}
                </Text>
              ) : null}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* ── All-day strip (tour days + events without times) ── */}
      {(() => {
        const hasTourDays = days.some(({ dateKey }) =>
          (eventsByDate[dateKey] || []).some(e => e.eventType === 'tour_day') ||
          Boolean(tourDayMetaByDate?.[dateKey])
        );
        const hasAllDay = days.some(({ dateKey }) =>
          (eventsByDate[dateKey] || []).some(e => e.eventType !== 'tour_day' && !e.startTime)
        );
        if (!hasTourDays && !hasAllDay) return null;
        return (
          <View style={{
            flexDirection: 'row',
            borderBottomWidth: 1, borderBottomColor: theme.ui.border,
            backgroundColor: theme.background.primary,
            minHeight: 28,
          }}>
            <View style={{ width: 52, justifyContent: 'center', alignItems: 'flex-end', paddingRight: 6 }}>
              <Text style={{ fontSize: 9, color: theme.text.secondary, fontWeight: '600', textTransform: 'uppercase' }}>All day</Text>
            </View>
            {days.map(({ dateKey }) => {
              const dayEvs = eventsByDate[dateKey] || [];
              const tourDayEv = dayEvs.find(e => e.eventType === 'tour_day');
              const meta = tourDayMetaByDate?.[dateKey];
              const otherAllDay = dayEvs.filter(e => e.eventType !== 'tour_day' && !e.startTime);
              // Show either an imported tour_day event or a fallback from tourDayMetaByDate
              const showTourBadge = tourDayEv || meta;
              const tourBadgeColor = tourDayEv?.color || meta?.tourColor || BRAND;
              const tourBadgeLabel = tourDayEv
                ? String(tourDayEv.dayVenueSummary || tourDayEv.title || 'Tour day').split(',')[0]
                : String(meta?.venueName || meta?.tourName || 'Tour day');
              return (
                <View key={dateKey} style={{
                  flex: 1, borderLeftWidth: 1, borderLeftColor: theme.ui.border,
                  padding: 2, gap: 2,
                }}>
                  {showTourBadge ? (
                    <View style={{
                      borderRadius: 4, paddingHorizontal: 4, paddingVertical: 2,
                      backgroundColor: tourBadgeColor + 'CC',
                    }}>
                      <Text style={{ fontSize: 9, fontWeight: '800', color: '#fff' }} numberOfLines={1}>
                        {tourBadgeLabel}
                      </Text>
                    </View>
                  ) : null}
                  {otherAllDay.slice(0, 2).map((ev) => (
                    <View key={ev.id} style={{
                      borderRadius: 4, paddingHorizontal: 4, paddingVertical: 2,
                      backgroundColor: (ev.color || BRAND) + 'AA',
                    }}>
                      <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff' }} numberOfLines={1}>{ev.title}</Text>
                    </View>
                  ))}
                </View>
              );
            })}
          </View>
        );
      })()}

      {timeGridTabProps?.activeTopTab ? (
        /* ── Full-box tour-day editor mode: the whole day box is an input ── */
        <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
          <View style={{ flex: 1, flexDirection: 'row', minHeight: isDay ? 420 : 320, backgroundColor: theme.background.primary }}>
            <View style={{ width: 52, paddingTop: 8, alignItems: 'flex-end', paddingRight: 6 }}>
              <Text style={{ fontSize: 9, color: theme.text.secondary, fontWeight: '600', textTransform: 'uppercase' }}>
                {timeGridTabProps.activeTopTab}
              </Text>
            </View>
            {days.map(({ dateKey }, columnIndex) => (
              <View
                key={`tab-${dateKey}`}
                style={{
                  flex: 1,
                  borderLeftWidth: 1,
                  borderLeftColor: theme.ui.border,
                  backgroundColor: dateKey === selectedDateKey ? (theme.brand.accent + '08') : 'transparent',
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                <TimeGridDayTabCell
                  dateKey={dateKey}
                  sel={dateKey === selectedDateKey}
                  columnIndex={columnIndex}
                  timeGridTabProps={timeGridTabProps}
                  theme={theme}
                  fill
                />
              </View>
            ))}
          </View>
        </ScrollView>
      ) : (
      /* ── Scrollable time body ── */
      <ScrollView ref={scrollRef} style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
        <View style={{ flexDirection: 'row' }}>
          {/* Hour labels */}
          <View style={{ width: 52 }}>
            {HOURS.map((h) => (
              <View key={h} style={{ height: HOUR_HEIGHT, justifyContent: 'flex-start', paddingTop: 2, paddingRight: 8, alignItems: 'flex-end' }}>
                {h > 0 ? (
                  <Text style={{ fontSize: 10, color: theme.text.secondary, fontWeight: '600' }}>
                    {formatHour(h)}
                  </Text>
                ) : null}
              </View>
            ))}
          </View>

          {/* Day columns */}
          {days.map(({ dateKey }) => {
            const dayEvs = eventsByDate[dateKey] || [];
            // Only show timed regular events in the time grid
            const timedEvs = dayEvs.filter(e => e.eventType !== 'tour_day' && parseTimeToMinutes(e.startTime) !== null);
            const isToday = dateKey === todayKey;
            return (
              <View
                key={dateKey}
                style={{
                  flex: 1, position: 'relative',
                  borderLeftWidth: 1, borderLeftColor: theme.ui.border,
                  backgroundColor: isToday ? (theme.brand.accent + '06') : 'transparent',
                  overflow: 'hidden',
                }}
              >
                {/* Hour lines */}
                {HOURS.map((h) => (
                  <View key={h} style={{
                    position: 'absolute', left: 0, right: 0, top: h * HOUR_HEIGHT,
                    height: 1, backgroundColor: theme.ui.border, opacity: 0.5,
                  }} />
                ))}
                {/* Half-hour lines */}
                {HOURS.map((h) => (
                  <View key={`half-${h}`} style={{
                    position: 'absolute', left: 0, right: 0,
                    top: h * HOUR_HEIGHT + HOUR_HEIGHT / 2,
                    height: 1, backgroundColor: theme.ui.border, opacity: 0.18,
                  }} />
                ))}

                {/* Timed events */}
                {timedEvs.map((ev) => {
                  const startMin = parseTimeToMinutes(ev.startTime);
                  const endMin = ev.endTime ? parseTimeToMinutes(ev.endTime) : startMin + 60;
                  const durationMin = Math.max(endMin - startMin, 30);
                  const top = (startMin / 60) * HOUR_HEIGHT;
                  const height = (durationMin / 60) * HOUR_HEIGHT;
                  const col = ev.color || BRAND;
                  return (
                    <View key={ev.id} style={{
                      position: 'absolute',
                      top: top + 1, left: 2, right: 2,
                      height: Math.max(height - 2, 22),
                      borderRadius: 6,
                      backgroundColor: col + 'CC',
                      borderLeftWidth: 3, borderLeftColor: col,
                      paddingHorizontal: 4, paddingVertical: 3,
                      overflow: 'hidden', zIndex: 2,
                    }}>
                      <Text style={{ fontSize: 10, fontWeight: '800', color: '#fff', lineHeight: 13 }} numberOfLines={1}>
                        {ev.title}
                      </Text>
                      {height > 32 ? (
                        <Text style={{ fontSize: 9, color: 'rgba(255,255,255,0.85)', lineHeight: 12 }}>
                          {ev.startTime}{ev.endTime ? ` – ${ev.endTime}` : ''}
                        </Text>
                      ) : null}
                    </View>
                  );
                })}

                {/* Now indicator — clipped to column */}
                {isToday ? (
                  <View style={{
                    position: 'absolute', left: 0, right: 0,
                    top: (nowMinutes / 60) * HOUR_HEIGHT - 1,
                    height: 2, backgroundColor: '#e74c3c', zIndex: 10,
                  }}>
                    <View style={{
                      width: 8, height: 8, borderRadius: 4,
                      backgroundColor: '#e74c3c',
                      position: 'absolute', left: 0, top: -3,
                    }} />
                  </View>
                ) : null}

                {/* Height spacer so the column fills 24 hours */}
                <View style={{ height: 24 * HOUR_HEIGHT }} />
              </View>
            );
          })}
        </View>
      </ScrollView>
      )}
    </View>
  );
}

// ─── Week view ────────────────────────────────────────────────────────────────
function CalendarWeekView({ cursorDate, eventsByDate, tourDayMetaByDate, selectedDateKey, theme, onDayCellPress, openNew, timeGridTabProps = null, selectedTourFilterId = null, weekStartsOn = 0, tour = null, scheduleByDate = {}, contactsByDate = {}, showTravelDetail = true, accent = null, timeFormat = '12-hour', onHotelPress }) {
  const masterTourUi = useMasterTourCalendarUi();
  const weekStart = startOfWeek(cursorDate, masterTourUi ? weekStartsOn : 0);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(weekStart.getDate() + i);
    return { date: d, dateKey: toDateKey(d) };
  });
  const useItinerary = masterTourUi && !timeGridTabProps?.activeTopTab;
  return (
    <View style={{ flex: 1, backgroundColor: 'transparent' }}>
      {useItinerary ? (
        <CalendarWeekItinerary
          days={days}
          eventsByDate={eventsByDate}
          tourDayMetaByDate={tourDayMetaByDate}
          selectedDateKey={selectedDateKey}
          selectedTourFilterId={selectedTourFilterId}
          theme={theme}
          accent={accent}
          onDayCellPress={onDayCellPress}
          tour={tour}
          scheduleByDate={scheduleByDate}
          contactsByDate={contactsByDate}
          showTravelDetail={showTravelDetail}
          timeFormat={timeFormat}
          onHotelPress={onHotelPress}
        />
      ) : (
        <TimeGrid
          days={days}
          eventsByDate={eventsByDate}
          tourDayMetaByDate={tourDayMetaByDate}
          selectedDateKey={selectedDateKey}
          theme={theme}
          onDayCellPress={onDayCellPress}
          openNew={openNew}
          timeGridTabProps={timeGridTabProps}
          selectedTourFilterId={selectedTourFilterId}
          weekStartsOn={weekStartsOn}
        />
      )}
    </View>
  );
}

// ─── Day view ─────────────────────────────────────────────────────────────────
function CalendarDayView({ cursorDate, selectedDateKey, eventsByDate, tourDayMetaByDate, theme, onDayCellPress, openNew, openEdit, deleteEvent, timeGridTabProps = null, selectedTourFilterId = null, lodgingSummary = '', groundLine = '', notesText = '', guestCount = '', partyMembers = [], v2Prefs = null, scheduleText = '' }) {
  const masterTourUi = useMasterTourCalendarUi();
  const dateKey = selectedDateKey || toDateKey(cursorDate);
  if (masterTourUi) {
    return (
      <MasterTourDaySheetWithPhone
        dateKey={dateKey}
        tourDayMetaByDate={tourDayMetaByDate}
        selectedTourFilterId={selectedTourFilterId}
        scheduleText={scheduleText || timeGridTabProps?.tabTextByDate?.[dateKey] || ''}
        lodgingSummary={lodgingSummary}
        groundLine={groundLine}
        notesText={notesText}
        guestCount={guestCount}
        partyMembers={partyMembers}
        prefs={v2Prefs || loadV2Prefs()}
        isDarkMode={theme?.name === 'dark'}
        accent={(v2Prefs || loadV2Prefs()).accent}
      />
    );
  }
  const days = [{ date: cursorDate, dateKey: toDateKey(cursorDate) }];
  return (
    <View style={{ flex: 1 }}>
      <TimeGrid
        days={days}
        eventsByDate={eventsByDate}
        tourDayMetaByDate={tourDayMetaByDate}
        selectedDateKey={dateKey}
        theme={theme}
        onDayCellPress={onDayCellPress || (() => {})}
        openNew={openNew}
        isDay
        timeGridTabProps={timeGridTabProps}
        selectedTourFilterId={selectedTourFilterId}
      />
    </View>
  );
}

function CalendarNativeLarge({
  weekRows,
  eventsByDate,
  selectedDateKey,
  selectDate,
  onDayCellPress,
  selectedEventId,
  setSelectedEventId,
  styles,
  theme,
  openNew,
  goTourPlanner,
  showTourPlannerNav,
  openEdit,
  openMoveToMonth,
  deleteEvent,
  deleteTourFromCalendar,
  activeTopTab,
  selectedDayTabText,
  selectedDayTabStyle,
  tabTextByDate,
  tabStyleByDate,
  dayFilesByDate,
  tourDayMetaByDate,
  onChangeSelectedDayTabText,
  onChangeSelectedDayTabStyle,
  onChangeDayTabText,
  allowAddEvent = true,
  onOpenExpandedEditor,
  onOpenFilesManager,
  onOpenStageFiles,
  onOpenDayShare,
  allowGridInlineEditAllDays = true,
  selectedTourFilterId = null,
  scheduleCellLabels = {},
  structuredListCellLabels = {},
  shareScheduleIndicatorsByDate = {},
  weekStartsOn = 0,
  v2Dense = false,
  onHotelChipPress,
}) {
  const masterTourUi = useMasterTourCalendarUi();
  const weekdayLabels = weekdayLabelsForWeekStart(masterTourUi ? weekStartsOn : 0);
  return (
    <View style={[styles.card, styles.calendarCard, { flex: 1, minHeight: 0, height: '100%', overflow: 'visible' }]}>
      <View style={[styles.calendarTable, { flex: 1, minHeight: 0, height: '100%' }]}>
        <View style={[styles.weekRow, { flexGrow: 0, flexShrink: 0, minHeight: 28 }]}>
          {weekdayLabels.map((w) => (
            <View key={w} style={styles.columnHeader}>
              <Text style={styles.weekday}>{w}</Text>
            </View>
          ))}
        </View>
        <ScrollView
          style={{ flex: 1, minHeight: 0 }}
          nestedScrollEnabled
          scrollEnabled={Boolean(masterTourUi)}
          contentContainerStyle={masterTourUi ? { flexGrow: 1, gap: 6, paddingBottom: 12 } : { flexGrow: 1, flex: 1 }}
        >
        {weekRows.map((week, wi) => (
          <View key={`week-${wi}`} style={[styles.weekRow, masterTourUi ? { flexGrow: 1, flexShrink: 0, minHeight: v2Dense ? 120 : 136 } : { flex: 1, minHeight: 0 }]}>
            {week.map((cell, di) => {
              const dayEvents = cell.dateKey ? eventsByDate[cell.dateKey] || [] : [];
              const tourDayEvents = dayEvents.filter((e) => e.eventType === 'tour_day');
              const visibleDayEvents = dayEvents.filter((e) => e.eventType !== 'tour_day');
              const sel = cell.dateKey === selectedDateKey;
              const lockMode = Boolean(activeTopTab);
              const gridEditAll = Boolean(allowGridInlineEditAllDays && lockMode);
              const isManagedDay = isManagedTourDay(cell.dateKey, selectedTourFilterId, tourDayMetaByDate);
              const fileItems = cell.dateKey
                ? (dayFilesByDate?.[cell.dateKey] || []).filter((f) => normalizeDayFileType(f.fileType) === 'files')
                : [];
              const stageFileItems = cell.dateKey
                ? (dayFilesByDate?.[cell.dateKey] || []).filter((f) => normalizeDayFileType(f.fileType) === 'stage')
                : [];
              const {
                showFilesInCell,
                showStageFilesInCell,
                showPlainTabInput,
                showScheduleInCell,
                showStructuredListInCell,
              } = dayCellTabDisplayModes(activeTopTab, cell, sel, gridEditAll, isManagedDay);
              const dayTabText = cell.dateKey ? tabTextByDate?.[cell.dateKey] || '' : '';
              const dayTabStyle = cell.dateKey ? tabStyleByDate?.[cell.dateKey] || selectedDayTabStyle : selectedDayTabStyle;
              const tourDayEvent = dayEvents.find((e) => e.eventType === 'tour_day');
              const cornerMeta = (cell.dateKey && tourDayMetaByDate?.[cell.dateKey]) || null;
              const dayCard = pickTourDayCard(cornerMeta, selectedTourFilterId);
              const cornerHasMeta = Boolean(dayCard?.kind || cornerMeta?.tours?.length > 0);
              const showCornerMeta = dayCellCornerMetaVisible(cell, selectedTourFilterId, sel, allowGridInlineEditAllDays, cornerHasMeta, masterTourUi);
              const showCellExpand = shouldShowDayCellExpand(isManagedDay, selectedTourFilterId, activeTopTab);
              const hideEventPills = hideMasterTourMonthEventPills(masterTourUi, {
                showFilesInCell,
                showStageFilesInCell,
                showPlainTabInput,
                showScheduleInCell,
                showStructuredListInCell,
              });
              const filledItems = [];
              if (fileItems.length) filledItems.push(`Files (${fileItems.length})`);
              const selectedEv = selectedEventId ? visibleDayEvents.find((e) => e.id === selectedEventId) : null;
              const showEventFloat = Boolean(selectedEv);
              const showDayFloat = cell.dateKey === selectedDateKey && !selectedEventId && cell.inMonth;
              const showFloat = showEventFloat || showDayFloat;
              const tourAccentColor = masterTourUi
                ? leftBarColorForKind(dayCard?.kind)
                : (tourDayEvent?.color || cornerMeta?.tourColor || null);
              return (
                <View
                  key={`${cell.dateKey}-${wi}-${di}`}
                  style={[styles.calendarCellWrap, showFloat && styles.calendarCellWrapFloat]}
                >
                  <View
                    style={[
                      styles.dayCellLarge,
                      cell.inMonth && masterTourUi && !dayCard?.kind && styles.dayCellEmpty,
                      !cell.inMonth && styles.dayCellMuted,
                      sel && styles.dayCellSelected,
                      tourAccentColor && cell.inMonth && { borderLeftWidth: 3, borderLeftColor: tourAccentColor },
                    ]}
                  >
                    {showCellExpand ? (
                      <Pressable
                        style={[styles.dayCellExpandScheduleBtn, styles.dayCellExpandScheduleBtnBottom]}
                        onPress={() => cell.dateKey && onOpenExpandedEditor?.(cell.dateKey)}
                        hitSlop={6}
                        accessibilityLabel={
                          activeTopTab === 'SCHEDULE'
                            ? 'Expand schedule editor'
                            : STRUCTURED_DAY_LIST_TABS.has(activeTopTab)
                              ? `Expand ${String(activeTopTab || 'list').toLowerCase()} editor`
                              : activeTopTab === 'FILES'
                                ? 'Expand files panel'
                                : 'Expand day panel'
                        }
                      >
                        <Ionicons name="expand-outline" size={13} color={theme.text.primary} />
                      </Pressable>
                    ) : null}
                    <TouchableOpacity
                      activeOpacity={0.88}
                      style={styles.dayCellTouchableFill}
                      onPress={() => cell.dateKey && onDayCellPress?.(cell.dateKey)}
                    >
                      <View style={styles.dayCellTopRow} pointerEvents="none">
                        <Text style={[styles.dayCellNum, !cell.inMonth && styles.cellDayMuted]}>
                          {cell.jsDate.getDate()}
                        </Text>
                        {masterTourUi && dayCard?.kindLabel ? (
                          <Text style={styles.dayCellKind}>{dayCard.kindLabel}</Text>
                        ) : null}
                      </View>
                      <View style={styles.dayCellBody}>
                        {showCornerMeta ? (
                          masterTourUi ? (
                          <View style={styles.dayCellTourInfo} pointerEvents="none">
                            <DayCellTourCard
                              meta={cornerMeta}
                              selectedTourFilterId={selectedTourFilterId}
                              isDarkMode={theme.name === 'dark'}
                              compact={v2Dense}
                              muted={!cell.inMonth}
                              hideKind
                              hideChips
                            />
                          </View>
                          ) : (
                            <LegacyTourCorner cornerMeta={cornerMeta} tourDayEvent={tourDayEvent} styles={styles} />
                          )
                        ) : null}
                        {showPlainTabInput ? (
                          <DayTabCellEditor
                            activeTopTab={activeTopTab}
                            dayTabText={dayTabText}
                            dayTabStyle={dayTabStyle}
                            dateKey={cell.dateKey}
                            onChangeDayTabText={onChangeDayTabText}
                            theme={theme}
                            styles={styles}
                            dense
                          />
                        ) : null}
                        {showScheduleInCell ? (
                          <View style={styles.dayCellScheduleWrap} onStartShouldSetResponder={() => true}>
                            <ScheduleEditor
                              mode="compact"
                              dense
                              theme={theme}
                              value={dayTabText}
                              onChange={(next) => cell.dateKey && onChangeDayTabText?.(cell.dateKey, next)}
                            />
                          </View>
                        ) : null}
                        {showStructuredListInCell ? (
                          <View style={styles.dayCellScheduleWrap} onStartShouldSetResponder={() => true}>
                            <StructuredDayListEditor
                              tabKey={activeTopTab}
                              mode="compact"
                              dense
                              theme={theme}
                              value={dayTabText}
                              onChange={(next) => cell.dateKey && onChangeDayTabText?.(cell.dateKey, next)}
                            />
                          </View>
                        ) : null}
                        {showFilesInCell ? (
                          <View style={styles.dayFilesInlineList}>
                            {fileItems.slice(0, 3).map((f) => (
                              <TouchableOpacity
                                key={`file-inline-${f.id}`}
                                style={styles.dayFileInlineChip}
                                onPress={() => Linking.openURL(f.url).catch(() => {})}
                              >
                                <Ionicons name="document-outline" size={11} color={theme.brand.accent} />
                                <Text style={styles.dayFileInlineChipText} numberOfLines={1}>
                                  {f.name}
                                </Text>
                              </TouchableOpacity>
                            ))}
                            {fileItems.length > 3 ? (
                              <Text style={styles.dayFileInlineMore}>+{fileItems.length - 3} more</Text>
                            ) : null}
                              <TouchableOpacity
                                style={styles.dayFilesAttachBtn}
                                onPress={() => cell.dateKey && onOpenFilesManager?.(cell.dateKey)}
                                accessibilityLabel="Attach file to day"
                              >
                                <Ionicons name="attach-outline" size={14} color={theme.brand.primary} />
                              </TouchableOpacity>
                          </View>
                        ) : null}
                        {showStageFilesInCell ? (
                          <View style={styles.dayFilesInlineList}>
                            {stageFileItems.slice(0, 3).map((f) => (
                              <TouchableOpacity
                                key={`stage-inline-${f.id}`}
                                style={styles.dayFileInlineChip}
                                onPress={() => Linking.openURL(f.url).catch(() => {})}
                              >
                                <Ionicons name="document-outline" size={11} color={theme.brand.accent} />
                                <Text style={styles.dayFileInlineChipText} numberOfLines={1}>
                                  {f.name}
                                </Text>
                              </TouchableOpacity>
                            ))}
                            {stageFileItems.length > 3 ? (
                              <Text style={styles.dayFileInlineMore}>+{stageFileItems.length - 3} more</Text>
                            ) : null}
                              <TouchableOpacity
                                style={styles.dayFilesAttachBtn}
                                onPress={() => cell.dateKey && onOpenStageFiles?.(cell.dateKey)}
                                accessibilityLabel="Attach stage file to day"
                              >
                                <Ionicons name="attach-outline" size={14} color={theme.brand.primary} />
                              </TouchableOpacity>
                          </View>
                        ) : null}
                        {hideEventPills ? null : (
                        <ScrollView
                          style={styles.dayCellEventScroll}
                          nestedScrollEnabled
                          showsVerticalScrollIndicator={false}
                          keyboardShouldPersistTaps="handled"
                        >
                          {tourDayEvents.map((ev) => (
                            <View key={`td-${ev.id}`} style={[styles.tourDayPill, { backgroundColor: ev.color || BRAND }]}>
                              <Text style={styles.tourDayPillText} numberOfLines={1}>
                                {ev.sourceTourName || ev.title || '●'}
                              </Text>
                            </View>
                          ))}
                          {visibleDayEvents.map((ev) => {
                            const active = selectedEventId === ev.id;
                            return (
                              <TouchableOpacity
                                key={ev.id}
                                onPress={() =>
                                  setSelectedEventId((prev) => (prev === ev.id ? null : ev.id))
                                }
                                style={[
                                  styles.dayCellEventChip,
                                  { borderLeftColor: ev.color || BRAND },
                                  active && styles.dayCellEventChipActive,
                                ]}
                              >
                                <Text style={styles.dayCellEventChipText} numberOfLines={3}>
                                  {ev.title}
                                </Text>
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                        )}
                        {shareScheduleIndicatorsByDate[cell.dateKey]?.length ? (
                          <ShareScheduleIndicators
                            indicators={shareScheduleIndicatorsByDate[cell.dateKey]}
                            theme={theme}
                          />
                        ) : null}
                        {hideEventPills ? <View style={styles.dayCellPosterSpacer} /> : null}
                        {masterTourUi && dayCard?.kind ? (
                          <DayCellChipFooter
                            chips={dayCard.chips}
                            isDarkMode={theme.name === 'dark'}
                            onChipAction={(action) => {
                              if (action === 'lodging' && cell.dateKey) onHotelChipPress?.(cell.dateKey);
                            }}
                          />
                        ) : null}
                      </View>
                    </TouchableOpacity>
                  </View>
                  {showFloat ? (
                    <CalendarDayFloatToolbar
                      showEventFloat={showEventFloat}
                      selectedEv={selectedEv}
                      styles={styles}
                      theme={theme}
                      allowAddEvent={allowAddEvent}
                      openNew={openNew}
                      goTourPlanner={goTourPlanner}
                      showTourPlannerNav={showTourPlannerNav}
                      openEdit={openEdit}
                      openMoveToMonth={openMoveToMonth}
                      deleteEvent={deleteEvent}
                      deleteTourFromCalendar={deleteTourFromCalendar}
                      showTextStyleControls={false}
                      showExpandEditor={false}
                      expandEditorIconName="expand-outline"
                      expandEditorAccessibilityLabel="Expand notes editor"
                      showFilesManager={sel && activeTopTab === 'FILES'}
                      selectedDayTabStyle={selectedDayTabStyle}
                      onChangeSelectedDayTabStyle={onChangeSelectedDayTabStyle}
                      filledItems={filledItems}
                      onOpenExpandedEditor={() => cell.dateKey && onOpenExpandedEditor?.(cell.dateKey)}
                      onOpenFilesManager={() => cell.dateKey && onOpenFilesManager?.(cell.dateKey)}
                      onOpenDayShare={() => {
                        if (!cell.dateKey) return;
                        onOpenDayShare?.(cell.dateKey);
                      }}
                      anchorBelow={wi <= 1}
                    />
                  ) : null}
                </View>
              );
            })}
          </View>
        ))}
        </ScrollView>
      </View>
    </View>
  );
}

function DraggableEventChipWeb({ ev, styles, theme, isSelected, onToggleSelect }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `cal-evt-${ev.id}`,
  });
  return (
    <View
      ref={setNodeRef}
      style={[
        styles.dayCellEventChip,
        styles.dayCellEventChipRow,
        { borderLeftColor: ev.color || BRAND },
        isDragging && { opacity: 0.25 },
        isSelected && styles.dayCellEventChipActive,
      ]}
    >
      <Pressable style={styles.dayCellDragHandle} {...listeners} {...attributes}>
        <Ionicons name="reorder-three" size={16} color={theme.text.tertiary} />
      </Pressable>
      <Pressable
        style={{ flex: 1, minWidth: 0 }}
        onPress={() => onToggleSelect(ev.id)}
      >
        <Text style={styles.dayCellEventChipText} numberOfLines={3}>
          {ev.title}
        </Text>
      </Pressable>
    </View>
  );
}

function DroppableDayCellWeb({
  cell,
  columnIndex = 0,
  rowIndex = 0,
  eventsByDate,
  selectedDateKey,
  selectDate,
  onDayCellPress,
  selectedEventId,
  setSelectedEventId,
  styles,
  theme,
  draggingActive,
  openNew,
  goTourPlanner,
  showTourPlannerNav,
  openEdit,
  openMoveToMonth,
  deleteEvent,
  deleteTourFromCalendar,
  activeTopTab,
  selectedDayTabText,
  selectedDayTabStyle,
  tabTextByDate,
  tabStyleByDate,
  dayFilesByDate,
  tourDayMetaByDate,
  onChangeSelectedDayTabText,
  onChangeSelectedDayTabStyle,
  onChangeDayTabText,
  allowAddEvent = true,
  onOpenExpandedEditor,
  onOpenFilesManager,
  onOpenStageFiles,
  onOpenDayShare,
  allowGridInlineEditAllDays = true,
  selectedTourFilterId = null,
  scheduleCellLabels = {},
  structuredListCellLabels = {},
  hideFloat = false,
  v2Dense = false,
  shareScheduleIndicatorsByDate = {},
  onHotelChipPress,
}) {
  const masterTourUi = useMasterTourCalendarUi();
  const { setNodeRef, isOver } = useDroppable({ id: `cal-cell-${cell.dateKey}` });
  const dayEvents = cell.dateKey ? eventsByDate[cell.dateKey] || [] : [];
  const tourDayEvents = dayEvents.filter((e) => e.eventType === 'tour_day');
  const visibleDayEvents = dayEvents.filter((e) => e.eventType !== 'tour_day');
  const sel = cell.dateKey === selectedDateKey;
  const lockMode = Boolean(activeTopTab);
  const gridEditAll = Boolean(allowGridInlineEditAllDays && lockMode);
  const isManagedDay = isManagedTourDay(cell.dateKey, selectedTourFilterId, tourDayMetaByDate);
  const fileItems = cell.dateKey
    ? (dayFilesByDate?.[cell.dateKey] || []).filter((f) => normalizeDayFileType(f.fileType) === 'files')
    : [];
  const stageFileItems = cell.dateKey
    ? (dayFilesByDate?.[cell.dateKey] || []).filter((f) => normalizeDayFileType(f.fileType) === 'stage')
    : [];
  const {
    showFilesInCell,
    showStageFilesInCell,
    showPlainTabInput,
    showScheduleInCell,
    showStructuredListInCell,
  } = dayCellTabDisplayModes(activeTopTab, cell, sel, gridEditAll, isManagedDay);
  const dayTabText = cell.dateKey ? tabTextByDate?.[cell.dateKey] || '' : '';
  const dayTabStyle = cell.dateKey ? tabStyleByDate?.[cell.dateKey] || selectedDayTabStyle : selectedDayTabStyle;
  const tourDayEvent = dayEvents.find((e) => e.eventType === 'tour_day');
  const cornerMeta = (cell.dateKey && tourDayMetaByDate?.[cell.dateKey]) || null;
  const dayCard = pickTourDayCard(cornerMeta, selectedTourFilterId);
  const cornerHasMeta = Boolean(dayCard?.kind || cornerMeta?.tours?.length > 0);
  const showCornerMeta = dayCellCornerMetaVisible(cell, selectedTourFilterId, sel, allowGridInlineEditAllDays, cornerHasMeta, masterTourUi);
  const showCellExpand = shouldShowDayCellExpand(isManagedDay, selectedTourFilterId, activeTopTab);
  const hideEventPills = hideMasterTourMonthEventPills(masterTourUi, {
    showFilesInCell,
    showStageFilesInCell,
    showPlainTabInput,
    showScheduleInCell,
    showStructuredListInCell,
  });
  const filledItems = [];
  if (fileItems.length) filledItems.push(`Files (${fileItems.length})`);
  const selectedEv = selectedEventId ? visibleDayEvents.find((e) => e.id === selectedEventId) : null;
  const showEventFloat = Boolean(selectedEv);
  const showDayFloat = cell.dateKey === selectedDateKey && !selectedEventId && cell.inMonth;
  const showFloat = showEventFloat || showDayFloat;
  // Visual left accent: 3px teal on show days only (prototype month cards).
  const tourAccentColor = masterTourUi
    ? leftBarColorForKind(dayCard?.kind)
    : (tourDayEvent?.color || cornerMeta?.tourColor || null);

  return (
    <View style={[styles.calendarCellWrap, showFloat && styles.calendarCellWrapFloat]}>
      <View
        ref={setNodeRef}
        style={[
          styles.dayCellLarge,
          cell.inMonth && masterTourUi && !dayCard?.kind && styles.dayCellEmpty,
          !cell.inMonth && styles.dayCellMuted,
          sel && styles.dayCellSelected,
          isOver && styles.dayCellDropOver,
          draggingActive && isOver && styles.dayCellDropOverDragging,
          tourAccentColor && cell.inMonth && { borderLeftWidth: 3, borderLeftColor: tourAccentColor },
        ]}
      >
        {showCellExpand ? (
          <Pressable
            style={[styles.dayCellExpandScheduleBtn, styles.dayCellExpandScheduleBtnBottom]}
            onPress={() => cell.dateKey && onOpenExpandedEditor?.(cell.dateKey)}
            hitSlop={6}
            accessibilityLabel={
              activeTopTab === 'SCHEDULE'
                ? 'Expand schedule editor'
                : STRUCTURED_DAY_LIST_TABS.has(activeTopTab)
                  ? `Expand ${String(activeTopTab || 'list').toLowerCase()} editor`
                  : activeTopTab === 'FILES'
                    ? 'Expand files panel'
                    : 'Expand day panel'
            }
          >
            <Ionicons name="expand-outline" size={13} color={theme.text.primary} />
          </Pressable>
        ) : null}
        <TouchableOpacity
          activeOpacity={0.9}
          onPress={() => cell.dateKey && onDayCellPress?.(cell.dateKey)}
          style={styles.dayCellInner}
        >
          <View style={styles.dayCellTopRow} pointerEvents="none">
            <Text style={[styles.dayCellNum, !cell.inMonth && styles.cellDayMuted]}>
              {cell.jsDate.getDate()}
            </Text>
            {masterTourUi && dayCard?.kindLabel ? (
              <Text style={styles.dayCellKind}>{dayCard.kindLabel}</Text>
            ) : null}
          </View>
          <View style={styles.dayCellBody}>
            {showCornerMeta ? (
              masterTourUi ? (
              <View style={styles.dayCellTourInfo} pointerEvents="none">
                <DayCellTourCard
                  meta={cornerMeta}
                  selectedTourFilterId={selectedTourFilterId}
                  isDarkMode={theme.name === 'dark'}
                  compact={v2Dense}
                  muted={!cell.inMonth}
                  hideKind
                  hideChips
                />
              </View>
              ) : (
                <LegacyTourCorner cornerMeta={cornerMeta} tourDayEvent={tourDayEvent} styles={styles} />
              )
            ) : null}
            {showPlainTabInput ? (
              <DayTabCellEditor
                activeTopTab={activeTopTab}
                dayTabText={dayTabText}
                dayTabStyle={dayTabStyle}
                dateKey={cell.dateKey}
                onChangeDayTabText={onChangeDayTabText}
                theme={theme}
                styles={styles}
                dense
              />
            ) : null}
            {showScheduleInCell ? (
              <View style={styles.dayCellScheduleWrap} onStartShouldSetResponder={() => true}>
                <ScheduleEditor
                  mode="compact"
                  dense
                  theme={theme}
                  value={dayTabText}
                  onChange={(next) => cell.dateKey && onChangeDayTabText?.(cell.dateKey, next)}
                />
              </View>
            ) : null}
            {showStructuredListInCell ? (
              <View style={styles.dayCellScheduleWrap} onStartShouldSetResponder={() => true}>
                <StructuredDayListEditor
                  tabKey={activeTopTab}
                  mode="compact"
                  dense
                  theme={theme}
                  value={dayTabText}
                  onChange={(next) => cell.dateKey && onChangeDayTabText?.(cell.dateKey, next)}
                />
              </View>
            ) : null}
            {showFilesInCell ? (
              <View style={styles.dayFilesInlineList}>
                {fileItems.slice(0, 3).map((f) => (
                  <TouchableOpacity
                    key={`file-inline-${f.id}`}
                    style={styles.dayFileInlineChip}
                    onPress={() => Linking.openURL(f.url).catch(() => {})}
                  >
                    <Ionicons name="document-outline" size={11} color={theme.brand.accent} />
                    <Text style={styles.dayFileInlineChipText} numberOfLines={1}>
                      {f.name}
                    </Text>
                  </TouchableOpacity>
                ))}
                {fileItems.length > 3 ? <Text style={styles.dayFileInlineMore}>+{fileItems.length - 3} more</Text> : null}
                <TouchableOpacity
                  style={styles.dayFilesAttachBtn}
                  onPress={() => cell.dateKey && onOpenFilesManager?.(cell.dateKey)}
                  accessibilityLabel="Attach file to day"
                >
                  <Ionicons name="attach-outline" size={14} color={theme.brand.primary} />
                </TouchableOpacity>
              </View>
            ) : null}
            {showStageFilesInCell ? (
              <View style={styles.dayFilesInlineList}>
                {stageFileItems.slice(0, 3).map((f) => (
                  <TouchableOpacity
                    key={`stage-inline-${f.id}`}
                    style={styles.dayFileInlineChip}
                    onPress={() => Linking.openURL(f.url).catch(() => {})}
                  >
                    <Ionicons name="document-outline" size={11} color={theme.brand.accent} />
                    <Text style={styles.dayFileInlineChipText} numberOfLines={1}>
                      {f.name}
                    </Text>
                  </TouchableOpacity>
                ))}
                {stageFileItems.length > 3 ? <Text style={styles.dayFileInlineMore}>+{stageFileItems.length - 3} more</Text> : null}
                <TouchableOpacity
                  style={styles.dayFilesAttachBtn}
                  onPress={() => cell.dateKey && onOpenStageFiles?.(cell.dateKey)}
                  accessibilityLabel="Attach stage file to day"
                >
                  <Ionicons name="attach-outline" size={14} color={theme.brand.primary} />
                </TouchableOpacity>
              </View>
            ) : null}
            {hideEventPills ? null : (
            <ScrollView
              style={styles.dayCellEventScroll}
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              {tourDayEvents.map((ev) => (
                <View key={`td-${ev.id}`} style={[styles.tourDayPill, { backgroundColor: ev.color || BRAND }]}>
                  <Text style={styles.tourDayPillText} numberOfLines={1}>
                    {ev.sourceTourName || ev.title || '●'}
                  </Text>
                </View>
              ))}
              {visibleDayEvents.map((ev) => (
                <DraggableEventChipWeb
                  key={ev.id}
                  ev={ev}
                  styles={styles}
                  theme={theme}
                  isSelected={selectedEventId === ev.id}
                  onToggleSelect={(id) => setSelectedEventId((prev) => (prev === id ? null : id))}
                />
              ))}
            </ScrollView>
            )}
            {shareScheduleIndicatorsByDate[cell.dateKey]?.length ? (
              <ShareScheduleIndicators
                indicators={shareScheduleIndicatorsByDate[cell.dateKey]}
                theme={theme}
              />
            ) : null}
            {hideEventPills ? <View style={styles.dayCellPosterSpacer} /> : null}
            {masterTourUi && dayCard?.kind ? (
              <DayCellChipFooter
                chips={dayCard.chips}
                isDarkMode={theme.name === 'dark'}
                onChipAction={(action) => {
                  if (action === 'lodging' && cell.dateKey) onHotelChipPress?.(cell.dateKey);
                }}
              />
            ) : null}
          </View>
        </TouchableOpacity>
      </View>
      {showFloat && !hideFloat ? (
        <CalendarDayFloatToolbar
          showEventFloat={showEventFloat}
          selectedEv={selectedEv}
          styles={styles}
          theme={theme}
          allowAddEvent={allowAddEvent}
          openNew={openNew}
          goTourPlanner={goTourPlanner}
          showTourPlannerNav={showTourPlannerNav}
          openEdit={openEdit}
          openMoveToMonth={openMoveToMonth}
          deleteEvent={deleteEvent}
          deleteTourFromCalendar={deleteTourFromCalendar}
          showTextStyleControls={false}
          showExpandEditor={false}
          expandEditorIconName="expand-outline"
          expandEditorAccessibilityLabel="Expand notes editor"
          showFilesManager={sel && activeTopTab === 'FILES'}
          selectedDayTabStyle={selectedDayTabStyle}
          onChangeSelectedDayTabStyle={onChangeSelectedDayTabStyle}
          filledItems={[]}
          onOpenExpandedEditor={() => cell.dateKey && onOpenExpandedEditor?.(cell.dateKey)}
          onOpenFilesManager={() => cell.dateKey && onOpenFilesManager?.(cell.dateKey)}
          onOpenDayShare={() => {
            if (!cell.dateKey) return;
            onOpenDayShare?.(cell.dateKey);
          }}
          anchorBelow={rowIndex <= 1}
        />
      ) : null}
    </View>
  );
}

function CalendarDesktopSidebarLayout({ height, children, rail, sidebar }) {
  return (
    <View style={{ height, flexDirection: 'row', minHeight: 0, overflow: 'hidden' }}>
      <View style={{ flex: 1, minWidth: 0, overflow: 'hidden', flexDirection: 'column' }}>
        {children}
      </View>
      {rail || null}
      {sidebar}
    </View>
  );
}

function CalendarDayPanel({
  selectedDateKey,
  selectedEventId,
  setSelectedEventId,
  eventsByDate,
  tourDayMetaByDate,
  selectedTourFilterId,
  onSelectTour,
  tourColorById,
  openNew,
  openEdit,
  deleteEvent,
  deleteTourFromCalendar,
  openMoveToMonth,
  goTourPlanner,
  showTourPlannerNav,
  activeTopTab,
  activeCalendarTabKey,
  selectedDayTabText,
  selectedDayTabStyle,
  onChangeSelectedDayTabText,
  onChangeSelectedDayTabStyle,
  dayFilesByDate,
  onOpenExpandedEditor,
  onOpenFilesManager,
  onOpenDayShare,
  onOpenTourSettings,
  onOpenSharedLinks,
  tourShareSettings = null,
  selectedTourShowDateKeys = [],
  onScheduleToast,
  ownerId,
  tourId,
  allowAddEvent,
  sidebarTabProps,
  sidebarWidth = CALENDAR_SIDEBAR_WIDTH,
  sidebarWide = false,
  onToggleSidebarWide,
  onCollapsePanel,
  onTopTabPress,
  onPrevDay,
  onNextDay,
  fillMain = false,
  theme,
  styles,
}) {
  const masterTourUi = useMasterTourCalendarUi();
  const { isDarkMode } = useTheme();
  const v2Pal = palette(Boolean(isDarkMode) || theme.name === 'dark');
  const sidebarShellStyle = {
    width: fillMain ? '100%' : sidebarWidth,
    flex: fillMain ? 1 : undefined,
    flexShrink: fillMain ? 1 : 0,
    alignSelf: 'stretch',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    borderLeftWidth: fillMain ? 0 : 1,
    borderLeftColor: masterTourUi ? v2Pal.line : theme.ui.border,
    ...(Platform.OS === 'web' ? { transition: fillMain ? undefined : 'width 0.25s ease' } : {}),
  };

  if (!selectedDateKey) {
    return (
      <View style={{ ...sidebarShellStyle, alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <Text style={{ color: theme.text.tertiary, fontSize: 12, fontWeight: '600', textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.5 }}>Select a day</Text>
      </View>
    );
  }

  const parts = selectedDateKey.split('-');
  const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const dayEvents = eventsByDate[selectedDateKey] || [];
  const tourDayEvents = dayEvents.filter((e) => e.eventType === 'tour_day');
  const regularEvents = dayEvents.filter((e) => e.eventType !== 'tour_day');
  const cornerMeta = tourDayMetaByDate?.[selectedDateKey] || null;
  const canExpandTabEditor = Boolean(
    activeTopTab &&
    !sidebarTabProps?.isRiderTab &&
    !sidebarTabProps?.isStageTab &&
    !sidebarTabProps?.isDaySheetTab &&
    activeCalendarTabKey &&
    activeCalendarTabKey !== 'TOUR PARTY' &&
    activeCalendarTabKey !== 'FILES' &&
    activeCalendarTabKey !== 'LODGING'
  );
  const bg = masterTourUi ? v2Pal.surf : theme.background.card;
  const border = masterTourUi ? v2Pal.line : theme.ui.border;
  const accent = BRAND;
  const textPrimary = masterTourUi ? v2Pal.ink : theme.text.primary;
  const textSecondary = masterTourUi ? v2Pal.mute : theme.text.secondary;
  const textTertiary = masterTourUi ? v2Pal.mute : theme.text.tertiary;
  const isScheduleShowDay = Boolean(
    selectedDateKey && selectedTourShowDateKeys.includes(selectedDateKey)
  );
  const [dayScheduleExpanded, setDayScheduleExpanded] = useState(false);

  useEffect(() => {
    setDayScheduleExpanded(false);
  }, [selectedDateKey, selectedTourFilterId]);

  return (
    <View style={{ ...sidebarShellStyle, backgroundColor: masterTourUi ? v2Pal.page : theme.background.primary, flexDirection: 'column' }}>
      {/* Day header */}
      <View style={{ paddingHorizontal: 12, paddingTop: 14, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: border, gap: 8 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <CalendarDayChevrons onPrevDay={onPrevDay} onNextDay={onNextDay} theme={theme} size="sm" />
          <View style={{ flex: 1, minWidth: 0, paddingHorizontal: 2 }}>
            <Text style={{ fontSize: 10, fontWeight: '800', color: textTertiary, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>
              {date.toLocaleDateString('en-US', { weekday: 'long' })}
            </Text>
            <Text style={{ fontSize: 22, fontWeight: '800', color: textPrimary, letterSpacing: -0.5 }}>
              {date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
            </Text>
          </View>
          {fillMain ? null : (
          <TouchableOpacity
            onPress={onToggleSidebarWide}
            accessibilityLabel={sidebarWide ? 'Narrow sidebar' : 'Widen sidebar'}
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              backgroundColor: theme.background.card,
              borderWidth: 1,
              borderColor: border,
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Ionicons
              name={sidebarWide ? 'contract-outline' : 'expand-outline'}
              size={16}
              color={textSecondary}
            />
          </TouchableOpacity>
          )}
          {!fillMain && onCollapsePanel ? (
            <TouchableOpacity
              onPress={onCollapsePanel}
              accessibilityLabel="Collapse day panel"
              hitSlop={6}
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                backgroundColor: theme.background.card,
                borderWidth: 1,
                borderColor: border,
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Ionicons name="close" size={18} color={textSecondary} />
            </TouchableOpacity>
          ) : null}
        </View>
        {selectedTourFilterId && tourId && ownerId ? (
          <DayScheduleStatusChip
            dateKey={selectedDateKey}
            tourShareSettings={tourShareSettings}
            isShowDay={isScheduleShowDay}
            expanded={dayScheduleExpanded}
            onPress={() => setDayScheduleExpanded((v) => !v)}
          />
        ) : null}
      </View>

      {selectedTourFilterId && tourId && ownerId ? (
        <DayScheduleSettingsPanel
          dateKey={selectedDateKey}
          ownerId={ownerId}
          tourId={tourId}
          tourShareSettings={tourShareSettings}
          isShowDay={isScheduleShowDay}
          onToast={onScheduleToast}
          onOpenTourSettings={onOpenTourSettings}
          expanded={dayScheduleExpanded}
          onExpandedChange={setDayScheduleExpanded}
        />
      ) : null}

      {isScheduleShowDay && selectedTourFilterId ? (
        <View style={{ flexDirection: 'row', gap: 8, paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: border, alignItems: 'center' }}>
          <CalendarTabChevrons
            tabs={CALENDAR_TOP_TABS}
            activeTab={activeTopTab}
            onTabPress={onTopTabPress}
            theme={theme}
            size="sm"
          />
          {activeTopTab ? (
            <Text
              style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: '800', color: '#7c6cdd', textTransform: 'uppercase', letterSpacing: 0.4 }}
              numberOfLines={1}
            >
              {activeTopTab}
            </Text>
          ) : (
            <Text style={{ flex: 1, fontSize: 12, fontWeight: '600', color: textSecondary }} numberOfLines={1}>
              Choose a section
            </Text>
          )}
          {selectedTourFilterId ? (
            <TouchableOpacity
              onPress={() => onOpenSharedLinks?.()}
              style={{ width: 34, height: 34, borderRadius: 8, backgroundColor: theme.background.card, borderWidth: 1, borderColor: border, alignItems: 'center', justifyContent: 'center' }}
              accessibilityLabel="Shared links"
            >
              <Ionicons name="link-outline" size={15} color={accent} />
            </TouchableOpacity>
          ) : null}
          <TouchableOpacity
            onPress={() => onOpenDayShare?.(selectedDateKey)}
            style={{ width: 34, height: 34, borderRadius: 8, backgroundColor: theme.background.card, borderWidth: 1, borderColor: border, alignItems: 'center', justifyContent: 'center' }}
            accessibilityLabel="Share this day"
          >
            <Ionicons name="paper-plane-outline" size={15} color={textSecondary} />
          </TouchableOpacity>
          {selectedTourFilterId ? (
            <TouchableOpacity
              onPress={() => onOpenTourSettings?.()}
              style={{ width: 34, height: 34, borderRadius: 8, backgroundColor: theme.background.card, borderWidth: 1, borderColor: border, alignItems: 'center', justifyContent: 'center' }}
              accessibilityLabel="Tour settings"
            >
              <Ionicons name="settings-outline" size={15} color={textSecondary} />
            </TouchableOpacity>
          ) : null}
          {canExpandTabEditor ? (
            <TouchableOpacity
              onPress={() => onOpenExpandedEditor?.(selectedDateKey)}
              style={{ width: 34, height: 34, borderRadius: 8, backgroundColor: theme.background.card, borderWidth: 1, borderColor: border, alignItems: 'center', justifyContent: 'center' }}
              accessibilityLabel="Expand editor"
            >
              <Ionicons name="expand-outline" size={15} color={textSecondary} />
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {selectedTourFilterId && selectedDateKey && !isScheduleShowDay ? (
        <View style={{ paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: border }}>
          <Text style={{ fontSize: 12, lineHeight: 17, color: textSecondary, textAlign: 'center' }}>
            This day isn&apos;t on your tour schedule. Select a show day to open tour party, rider, schedule, and other tour tools.
          </Text>
        </View>
      ) : null}

      {/* Add event (no tour selected) */}
      {!selectedTourFilterId && allowAddEvent ? (
        <View style={{ paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: border }}>
          <TouchableOpacity
            onPress={openNew}
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, paddingVertical: 8, borderRadius: 8, backgroundColor: accent }}
          >
            <Ionicons name="add" size={15} color="#fff" />
            <Text style={{ fontSize: 12, fontWeight: '800', color: '#fff' }}>Add event</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <ScrollView
        style={{ flex: 1, minHeight: 0 }}
        showsVerticalScrollIndicator
        contentContainerStyle={{ paddingBottom: 24 }}
      >
        {(cornerMeta?.tours?.length > 0 || tourDayEvents.length > 0) ? (
          <View style={{ paddingHorizontal: 12, paddingTop: 10 }}>
            <Text style={{ fontSize: 10, fontWeight: '800', color: textTertiary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
              {cornerMeta?.tours?.length > 1 ? 'Tours on this day' : 'Tour'}
            </Text>
            {cornerMeta?.tours?.length > 0 ? cornerMeta.tours.map((t) => {
              const isSelected = selectedTourFilterId === t.tourId;
              const color = t.tourColor || tourColorById?.[t.tourId] || accent;
              return (
                <TouchableOpacity
                  key={t.tourId}
                  onPress={() => onSelectTour?.(t.tourId)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 6,
                    paddingVertical: 8,
                    paddingHorizontal: 10,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: isSelected ? color : border,
                    backgroundColor: isSelected ? color + '18' : theme.background.card,
                  }}
                >
                  <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: textPrimary }} numberOfLines={1}>{t.tourName}</Text>
                    {t.venueName ? (
                      <Text style={{ fontSize: 11, color: textSecondary, marginTop: 2 }} numberOfLines={1}>{t.venueName}</Text>
                    ) : null}
                  </View>
                  {isSelected ? (
                    <Ionicons name="checkmark-circle" size={16} color={color} />
                  ) : showTourPlannerNav ? (
                    <TouchableOpacity
                      onPress={(e) => {
                        e?.stopPropagation?.();
                        goTourPlanner(t.tourId);
                      }}
                      hitSlop={8}
                    >
                      <Ionicons name="map-outline" size={14} color={accent} />
                    </TouchableOpacity>
                  ) : null}
                </TouchableOpacity>
              );
            }) : tourDayEvents.map((ev) => (
              <View key={ev.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: ev.color || accent }} />
                <Text style={{ fontSize: 13, fontWeight: '700', color: textPrimary, flex: 1 }} numberOfLines={1}>{ev.title}</Text>
              </View>
            ))}
          </View>
        ) : null}

        <View style={{ paddingHorizontal: 12, paddingTop: 10 }}>
          <CalendarDayTabContent
            selectedDateKey={selectedDateKey}
            selectedTourFilterId={selectedTourFilterId}
            activeTopTab={activeTopTab}
            activeCalendarTabKey={activeCalendarTabKey}
            selectedTabDayText={selectedDayTabText}
            selectedTabDayStyle={selectedDayTabStyle}
            onChangeSelectedDayTabText={onChangeSelectedDayTabText}
            onChangeSelectedDayTabStyle={onChangeSelectedDayTabStyle}
            dayFilesByDate={dayFilesByDate}
            openExpandedTabEditor={onOpenExpandedEditor}
            openDayFilesManager={onOpenFilesManager}
            openDayShareModal={onOpenDayShare}
            theme={theme}
            styles={styles}
            sidebarFillHeight
            {...sidebarTabProps}
          />
        </View>

        {regularEvents.length > 0 ? (
          <View style={{ paddingHorizontal: 12, paddingTop: 10 }}>
            <Text style={{ fontSize: 10, fontWeight: '800', color: textTertiary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>Events</Text>
            {regularEvents.map((ev) => {
              const isSelected = ev.id === selectedEventId;
              return (
                <TouchableOpacity
                  key={ev.id}
                  onPress={() => setSelectedEventId((prev) => (prev === ev.id ? null : ev.id))}
                  style={{
                    flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, paddingHorizontal: 10,
                    borderRadius: 8, marginBottom: 4, borderLeftWidth: 3, borderLeftColor: ev.color || accent,
                    backgroundColor: isSelected ? (ev.color || accent) + '22' : theme.background.card,
                    borderWidth: 1, borderColor: isSelected ? (ev.color || accent) + '55' : border,
                  }}
                >
                  <Text style={{ flex: 1, fontSize: 13, fontWeight: '600', color: textPrimary }} numberOfLines={2}>{ev.title}</Text>
                  {isSelected ? (
                    <View style={{ flexDirection: 'row', gap: 4 }}>
                      <TouchableOpacity onPress={() => openEdit(ev)} hitSlop={6} style={{ padding: 4 }}>
                        <Ionicons name="pencil-outline" size={14} color={textSecondary} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => openMoveToMonth(ev)} hitSlop={6} style={{ padding: 4 }}>
                        <Ionicons name="calendar-outline" size={14} color={accent} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => deleteEvent(ev.id)} hitSlop={6} style={{ padding: 4 }}>
                        <Ionicons name="trash-outline" size={14} color={theme.status.error} />
                      </TouchableOpacity>
                    </View>
                  ) : null}
                </TouchableOpacity>
              );
            })}
          </View>
        ) : null}

        {regularEvents.length === 0 && tourDayEvents.length === 0 && !cornerMeta && !activeTopTab ? (
          <View style={{ paddingHorizontal: 16, paddingTop: 20, alignItems: 'center' }}>
            <Text style={{ fontSize: 12, color: textTertiary, textAlign: 'center' }}>No events on this day</Text>
          </View>
        ) : null}
      </ScrollView>
    </View>
  );
}

function CalendarWebLargeDnD({
  weekRows,
  eventsByDate,
  events,
  selectedDateKey,
  selectDate,
  onDayCellPress,
  selectedEventId,
  setSelectedEventId,
  styles,
  theme,
  uid,
  openNew,
  goTourPlanner,
  showTourPlannerNav,
  openEdit,
  openMoveToMonth,
  deleteEvent,
  deleteTourFromCalendar,
  onDragError,
  activeTopTab,
  selectedDayTabText,
  selectedDayTabStyle,
  tabTextByDate,
  tabStyleByDate,
  dayFilesByDate,
  tourDayMetaByDate,
  selectedTourFilterId,
  onSelectTour,
  tourColorById,
  onChangeSelectedDayTabText,
  onChangeSelectedDayTabStyle,
  onChangeDayTabText,
  allowAddEvent = true,
  onOpenExpandedEditor,
  onOpenFilesManager,
  onOpenStageFiles,
  onOpenDayShare,
  allowGridInlineEditAllDays = true,
  suppressCellTabEditors = false,
  activeCalendarTabKey,
  useMonthHorizontalScroll = false,
  shareScheduleIndicatorsByDate = {},
  weekStartsOn = 0,
  v2Dense = false,
  onHotelChipPress,
}) {
  const masterTourUi = useMasterTourCalendarUi();
  const weekdayLabels = weekdayLabelsForWeekStart(masterTourUi ? weekStartsOn : 0);
  const [activeDragId, setActiveDragId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragStart = useCallback((event) => {
    setActiveDragId(event.active?.id ?? null);
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
  }, []);

  const handleDragEnd = useCallback(
    async (event) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || !active) return;
      const evMatch = String(active.id).match(/^cal-evt-(.+)$/);
      const cellMatch = String(over.id).match(/^cal-cell-(.+)$/);
      if (!evMatch || !cellMatch) return;
      const eventId = evMatch[1];
      const newDateKey = cellMatch[1];
      const ev = events.find((e) => e.id === eventId);
      if (!ev || ev.date === newDateKey) return;
      try {
        await updateDoc(doc(getFirestore(), 'users', uid, 'calendarEvents', eventId), {
          date: newDateKey,
          updatedAt: serverTimestamp(),
        });
        if (ev.eventType === 'tour_day' && ev.sourceTourId != null && ev.sourceDayIndex != null) {
          await syncTourDayScheduledDate(uid, ev.sourceTourId, ev.sourceDayIndex, newDateKey);
        }
      } catch (e) {
        onDragError?.(e?.message || String(e));
      }
    },
    [events, uid, onDragError]
  );

  const activeDragEvent = useMemo(() => {
    if (!activeDragId) return null;
    const m = String(activeDragId).match(/^cal-evt-(.+)$/);
    if (!m) return null;
    return events.find((e) => e.id === m[1]) || null;
  }, [activeDragId, events]);

  const monthTable = (
    <View style={[styles.calendarTable, { flex: 1, minHeight: 0, height: '100%' }]}>
          <View style={[styles.weekRow, { flexGrow: 0, flexShrink: 0, minHeight: 28 }]}>
            {weekdayLabels.map((w) => (
              <View key={w} style={styles.columnHeader}>
                <Text style={styles.weekday}>{w}</Text>
              </View>
            ))}
          </View>
        <ScrollView
          style={{ flex: 1, minHeight: 0 }}
          nestedScrollEnabled
          scrollEnabled={Boolean(masterTourUi)}
          contentContainerStyle={masterTourUi ? { flexGrow: 1, gap: 6, paddingBottom: 12 } : { flexGrow: 1, flex: 1 }}
        >
          {weekRows.map((week, wi) => (
        <View key={`week-${wi}`} style={[styles.weekRow, masterTourUi ? { flexGrow: 1, flexShrink: 0, minHeight: v2Dense ? 120 : 136 } : { flex: 1, minHeight: 0 }]}>
              {week.map((cell, di) => (
                <DroppableDayCellWeb
                  key={`${cell.dateKey}-${wi}-${di}`}
                  cell={cell}
                  columnIndex={di}
              rowIndex={wi}
                  eventsByDate={eventsByDate}
                  selectedDateKey={selectedDateKey}
                  selectDate={selectDate}
                  onDayCellPress={onDayCellPress}
                  selectedEventId={selectedEventId}
                  setSelectedEventId={setSelectedEventId}
                  styles={styles}
                  theme={theme}
                  v2Dense={v2Dense}
                  draggingActive={Boolean(activeDragId)}
                  openNew={openNew}
                  goTourPlanner={goTourPlanner}
                  showTourPlannerNav={showTourPlannerNav}
                  openEdit={openEdit}
                  openMoveToMonth={openMoveToMonth}
                  deleteEvent={deleteEvent}
                  deleteTourFromCalendar={deleteTourFromCalendar}
                  activeTopTab={activeTopTab}
                  selectedDayTabText={selectedDayTabText}
                  selectedDayTabStyle={selectedDayTabStyle}
                  tabTextByDate={tabTextByDate}
                  tabStyleByDate={tabStyleByDate}
                  dayFilesByDate={dayFilesByDate}
                  tourDayMetaByDate={tourDayMetaByDate}
                  selectedTourFilterId={selectedTourFilterId}
                  onSelectTour={onSelectTour}
                  tourColorById={tourColorById}
                  onChangeSelectedDayTabText={onChangeSelectedDayTabText}
                  onChangeSelectedDayTabStyle={onChangeSelectedDayTabStyle}
                  onChangeDayTabText={onChangeDayTabText}
                  allowAddEvent={allowAddEvent}
                  onOpenExpandedEditor={onOpenExpandedEditor}
                  onOpenFilesManager={onOpenFilesManager}
                  onOpenStageFiles={onOpenStageFiles}
                  onOpenDayShare={onOpenDayShare}
              allowGridInlineEditAllDays={allowGridInlineEditAllDays}
              suppressCellTabEditors={suppressCellTabEditors}
              hideFloat
              shareScheduleIndicatorsByDate={shareScheduleIndicatorsByDate}
              onHotelChipPress={onHotelChipPress}
                />
              ))}
            </View>
          ))}
        </ScrollView>
    </View>
  );

  return (
    <View style={{ flex: 1, minHeight: 0, height: '100%' }}>
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <View style={[styles.card, styles.calendarCard, { flex: 1, minHeight: 0, height: '100%', flexDirection: 'column', overflow: 'visible' }]}>
        <View style={{ flex: 1, minHeight: 0 }}>
          {monthTable}
        </View>
      </View>
      <DragOverlay dropAnimation={null} zIndex={10000}>
        {activeDragEvent ? (
          <View style={[styles.dragGhost, { borderLeftColor: activeDragEvent.color || BRAND }]}>
            <Text style={styles.dragGhostText} numberOfLines={3}>
              {activeDragEvent.title}
            </Text>
          </View>
        ) : null}
      </DragOverlay>
    </DndContext>
    </View>
  );
}

/** @param {object} theme Theme tokens from `useTheme()` */
function createCalendarStyles(theme, pad, windowWidth = 1920, isDarkMode = false, updatedColorScheme = false, masterTourUi = false, v2Accent = '#2a7d5c', v2Density = 'comfortable') {
  const isDark = Boolean(isDarkMode) || theme.name === 'dark';
  const cal = getPagePaletteIfEnabled('calendar', isDarkMode, updatedColorScheme);
  const v2 = masterTourUi;
  const pal = palette(isDark);
  const v2Page = v2 ? pal.page : (cal?.pageBackground ?? theme.background.primary);
  const v2Surf = v2 ? pal.surf : theme.background.card;
  const v2Line = v2 ? pal.line : theme.ui.border;
  const v2Ink = v2 ? pal.ink : (cal?.headerTitle ?? theme.header.text);
  const v2Mute = v2 ? pal.mute : theme.text.secondary;
  const v2Empty = v2 ? pal.sunk : (cal ? (isDark ? 'rgba(255,255,255,0.08)' : '#FFFFFF') : theme.background.card);
  const v2Font = v2
    ? FONT.sans
    : (Platform.OS === 'web' ? 'VastagoGrotesk, system-ui, sans-serif' : undefined);
  const v2Mono = v2 ? FONT.mono : v2Font;
  const v2Teal = v2 ? (v2Accent || pal.teal) : (isDark ? '#3ff5a7' : 'rgb(59, 163, 120)');
  const v2TealDeep = v2 ? pal.teal : '#7c6cdd';
  const gridLine = v2 ? v2Line : (cal?.gridAccent ?? theme.ui.border);
  const isCompact = windowWidth <= BREAKPOINTS.tablet;
  const v2HeaderStack = v2 && shouldStackMasterTourHeader(windowWidth);
  const v2Dense = v2Density === 'compact';
  const useWideMonthCells = windowWidth > BREAKPOINTS.tablet && windowWidth < 1200;
  const monthCellMinWidth = useWideMonthCells ? 100 : 0;
  const chipActiveBg = isDark ? 'rgba(46, 204, 113, 0.28)' : 'rgba(39, 174, 96, 0.22)';
  const tourPickBg = isDark ? 'rgba(46, 204, 113, 0.12)' : 'rgba(39, 174, 96, 0.1)';
  const tourPickBorder = isDark ? 'rgba(46, 204, 113, 0.35)' : 'rgba(39, 174, 96, 0.3)';
  const eventRowBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const smallBtnBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const chipBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  return StyleSheet.create({
    container: {
      backgroundColor: v2Page,
      ...(v2
        ? { flexGrow: 0, flexShrink: 0 }
        : { flex: 1, minHeight: getResponsiveValue({ mobile: 400, tablet: 600, laptop: 600, desktop: 600 }) }),
    },
    centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: pad },
    muted: { color: v2Mute, fontSize: 14.5, marginBottom: 8, fontFamily: v2Font },
    toolbar: {
      flexDirection: isCompact ? 'column' : 'row',
      alignItems: isCompact ? 'flex-start' : 'center',
      paddingHorizontal: pad,
      paddingTop: isCompact ? 4 : 8,
      paddingBottom: isCompact ? 6 : 10,
      backgroundColor: v2 ? v2Surf : (cal?.pageBackground ?? theme.header.background),
      borderBottomWidth: 1,
      borderBottomColor: v2 ? v2Line : theme.header.border,
      flexWrap: isCompact ? undefined : 'wrap',
      gap: isCompact ? 0 : 8,
    },
    mobileIconActionBtn: {
      width: isCompact ? 34 : 36,
      height: isCompact ? 34 : 36,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.borderStrong,
      backgroundColor: theme.background.card,
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconBtn: { padding: 6 },
    monthTitle: {
      fontSize: v2 ? 17 : 18,
      fontWeight: v2 ? '600' : '800',
      color: v2Ink,
      letterSpacing: v2 ? -0.16 : 0,
      fontFamily: v2Font,
    },
    toolbarBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: theme.brand.accent,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 8,
    },
    toolbarBtnSecondary: {
      backgroundColor: v2Surf,
      borderWidth: 1,
      borderColor: v2Line,
    },
    toolbarBtnText: { color: theme.button.successText, fontWeight: '700', fontSize: 12 },
    toolbarBtnTextDark: { color: v2Ink, fontFamily: v2Font, fontWeight: '600' },
    toolbarLeftCluster: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      minWidth: 0,
    },
    toolbarCenterCluster: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 12,
      flexShrink: 0,
    },
    toolbarRightCluster: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 8,
      flexWrap: 'wrap',
      minWidth: 0,
    },
    v2HeaderBlock: {
      flexGrow: 0,
      flexShrink: 0,
      backgroundColor: v2Surf,
      borderBottomWidth: 1,
      borderBottomColor: v2Line,
    },
    v2Header: {
      paddingHorizontal: v2HeaderStack ? 16 : 24,
      paddingTop: 4,
      paddingBottom: v2HeaderStack ? 8 : 0,
      backgroundColor: v2Surf,
      borderBottomWidth: 0,
      flexGrow: 0,
      flexShrink: 0,
      gap: v2HeaderStack ? 8 : 0,
    },
    v2HeaderTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      position: 'relative',
      flexGrow: 0,
      flexShrink: 0,
      minWidth: 0,
    },
    v2HeaderTop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      flexWrap: 'wrap',
    },
    // Month pager (left slot)
    v2HeaderLeft: {
      position: 'relative',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 4,
      flexShrink: 0,
      zIndex: 2,
    },
    v2TourPickerKicker: {
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.8,
      textTransform: 'uppercase',
      color: v2Mute,
      fontFamily: v2Mono,
    },
    v2TourPickerNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      minWidth: 0,
      maxWidth: '100%',
    },
    v2TourName: {
      fontSize: v2HeaderStack ? 18 : 21.5,
      fontWeight: '700',
      letterSpacing: -0.4,
      color: v2Ink,
      fontFamily: v2Font,
      flexShrink: 1,
      minWidth: 0,
    },
    v2TourMeta: {
      fontSize: 12,
      letterSpacing: 0.7,
      textTransform: 'uppercase',
      color: v2Mute,
      fontFamily: v2Mono,
      maxWidth: '100%',
    },
    // Tour picker (center slot): fills the space between pager and view tabs, content centered
    v2HeaderCenter: {
      minWidth: 0,
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      zIndex: 2,
      overflow: 'hidden',
    },
    v2PagerBtn: {
      width: 28,
      height: 28,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: v2Line,
      backgroundColor: v2Surf,
      alignItems: 'center',
      justifyContent: 'center',
      ...(Platform.OS === 'web' ? { pointerEvents: 'auto' } : {}),
    },
    v2PagerGlyph: {
      fontSize: 16,
      color: v2Ink,
      fontFamily: v2Font,
      lineHeight: 18,
    },
    v2MonthLabel: {
      minWidth: v2HeaderStack ? 0 : 168,
      textAlign: 'center',
      fontSize: v2HeaderStack ? 15 : 17,
      fontWeight: '600',
      letterSpacing: -0.16,
      color: v2Ink,
      fontFamily: v2Font,
    },
    v2HeaderRight: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexWrap: 'nowrap',
      gap: 12,
      flexGrow: 0,
      flexShrink: 0,
      marginLeft: 'auto',
      zIndex: 2,
      ...(v2HeaderStack ? { width: '100%' } : {}),
    },
    v2HeaderTabsScroll: {
      width: '100%',
      flexGrow: 0,
      flexShrink: 1,
    },
    v2HeaderTabsScrollContent: {
      alignItems: 'center',
      paddingRight: 4,
    },
    v2ClaudeNavBtn: {
      paddingVertical: 8,
      paddingHorizontal: 0,
    },
    v2ClaudeNavText: {
      fontSize: 15,
      letterSpacing: 0.8,
      textTransform: 'uppercase',
      fontWeight: '600',
      color: v2Teal,
      fontFamily: v2Font,
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
      paddingBottom: 4,
    },
    v2ClaudeNavTextActive: {
      fontWeight: '700',
      color: v2Teal,
      borderBottomColor: v2Teal,
    },
    v2ClaudeNavExtra: {
      fontSize: 13.5,
      letterSpacing: 0.8,
      textTransform: 'uppercase',
      fontWeight: '500',
      color: v2Mute,
      fontFamily: v2Mono,
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
      paddingBottom: 4,
    },
    v2ViewPillsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    calViewToggle: {
      flexDirection: 'row',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: v2 ? v2Line : theme.ui.border,
      backgroundColor: v2 ? v2Page : 'transparent',
      overflow: 'hidden',
      padding: v2 ? 3 : 0,
      gap: v2 ? 2 : 0,
      flexShrink: 0,
    },
    calViewBtn: {
      paddingHorizontal: v2 ? 11 : 12,
      paddingVertical: v2 ? 5 : 6,
      backgroundColor: 'transparent',
      borderRadius: v2 ? 5 : 0,
    },
    calViewBtnActive: {
      backgroundColor: v2 ? v2Ink : theme.brand.accent,
    },
    calViewBtnText: {
      fontSize: v2 ? 11.5 : 12,
      fontWeight: v2 ? '500' : '600',
      letterSpacing: v2 ? 0.7 : 0.2,
      textTransform: v2 ? 'uppercase' : 'none',
      color: v2Mute,
      fontFamily: v2 ? v2Mono : v2Font,
    },
    calViewBtnTextActive: {
      color: v2 ? v2Page : '#fffffe',
    },
    todayBtn: {
      marginLeft: 6,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: v2Line,
      backgroundColor: v2Surf,
    },
    todayBtnActive: {
      borderColor: v2Ink,
      backgroundColor: v2Surf,
    },
    todayBtnText: {
      fontSize: 13.5,
      fontWeight: '600',
      color: v2Mute,
      fontFamily: v2Font,
    },
    todayBtnTextActive: {
      color: v2Ink,
    },
    tourDropdownBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: v2Line,
      backgroundColor: v2Surf,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: isCompact ? 6 : 8,
      minWidth: 220,
      maxWidth: 320,
      justifyContent: 'space-between',
    },
    tourDropdownText: {
      fontSize: 13.5,
      fontWeight: '600',
      color: v2Ink,
      flex: 1,
      fontFamily: v2Font,
      marginRight: 6,
      textAlign: 'center',
    },
    tourPickerFilterChip: {
      paddingVertical: 6,
      paddingHorizontal: 12,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.ui.borderStrong,
      backgroundColor: theme.background.card,
    },
    tourPickerFilterChipActive: {
      borderColor: theme.brand.accent,
      backgroundColor: isDark ? 'rgba(98,0,238,0.18)' : 'rgba(98,0,238,0.08)',
    },
    tourPickerFilterChipText: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.text.secondary,
    },
    tourPickerFilterChipTextActive: {
      color: theme.brand.accent,
      fontWeight: '700',
    },
    tourPickerSectionTitle: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginBottom: 8,
      marginTop: 4,
    },
    tourPickerModalCard: {
      maxWidth: 760,
      width: '94%',
      paddingHorizontal: 20,
      paddingTop: 18,
      paddingBottom: 14,
    },
    tourPickerModalHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 14,
      gap: 8,
    },
    tourPickerMainTabs: {
      flexDirection: 'row',
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      overflow: 'hidden',
      marginBottom: 14,
    },
    tourPickerMainTab: {
      flex: 1,
      paddingVertical: 10,
      alignItems: 'center',
      backgroundColor: theme.background.card,
    },
    tourPickerMainTabActive: {
      backgroundColor: theme.brand.accent,
    },
    tourPickerMainTabText: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.text.secondary,
    },
    tourPickerMainTabTextActive: {
      color: '#fff',
    },
    tourPickerEmptyState: {
      paddingVertical: 28,
      paddingHorizontal: 12,
      alignItems: 'center',
      gap: 10,
    },
    tourPickerModalFooter: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginTop: 14,
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: theme.ui.border,
    },
    selectedDayTourPickerBlock: {
      marginBottom: 12,
      gap: 8,
    },
    selectedDayTourPickerLabel: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 2,
    },
    selectedDayTourPickerItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 10,
      paddingHorizontal: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    selectedDayTourPickerName: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.text.primary,
    },
    selectedDayTourPickerVenue: {
      fontSize: 12,
      color: theme.text.secondary,
      marginTop: 2,
    },
    topTabsBarWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: v2Line,
      backgroundColor: v2Surf,
    },
    v2TopTabsBarWrap: {
      flexGrow: 0,
      flexShrink: 0,
      minHeight: 48,
      backgroundColor: v2Surf,
      borderBottomWidth: 1,
      borderBottomColor: v2Line,
    },
    v2TopTabsBarScroll: {
      flex: 1,
      minWidth: 0,
      width: '100%',
    },
    v2TopTabsBarContent: {
      paddingHorizontal: 24,
      paddingVertical: 10,
      gap: 22,
      alignItems: 'center',
    },
    topTabsBarScroll: {
      flex: 1,
      minWidth: 0,
    },
    topTabsBarContent: {
      paddingHorizontal: pad,
      paddingVertical: isCompact ? 4 : 8,
      gap: isCompact ? 6 : 8,
      alignItems: 'center',
    },
    v2ToolsBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingLeft: 16,
      paddingRight: 16,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: v2Line,
      backgroundColor: v2Surf,
      overflow: 'visible',
      flexGrow: 0,
      flexShrink: 0,
    },
    v2ToolsBarScroll: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingTop: 8,
      paddingBottom: 4,
      paddingRight: 12,
    },
    v2ToolsBarScroller: {
      flex: 1,
      minWidth: 0,
      overflow: 'visible',
    },
    v2ToolsChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: v2Line,
      backgroundColor: v2Page,
      flexShrink: 0,
      overflow: 'visible',
    },
    v2ToolsChatBtn: {
      position: 'relative',
      marginRight: 8,
    },
    v2ToolsExportGroup: {
      flexDirection: 'row',
      alignItems: 'stretch',
      borderWidth: 1,
      borderColor: v2Line,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: v2Page,
      flexShrink: 0,
    },
    v2ToolsExportBtn: {
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRightWidth: 1,
      borderRightColor: v2Line,
      justifyContent: 'center',
    },
    v2ToolsChipActive: {
      borderColor: v2Ink,
      backgroundColor: v2Ink,
    },
    v2ToolsChipAccent: {
      borderColor: theme.brand.accent,
      backgroundColor: isDark ? 'rgba(59,163,120,0.12)' : 'rgba(59,163,120,0.08)',
    },
    v2ToolsChipText: {
      fontSize: 10.5,
      fontWeight: '700',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: v2Ink,
      fontFamily: v2Mono,
    },
    v2ToolsChipTextAccent: {
      color: theme.brand.accent,
    },
    v2ToolsChipTextOnActive: {
      color: v2Page,
    },
    v2ToolsChatBadge: {
      position: 'absolute',
      top: -7,
      right: -8,
      minWidth: 18,
      height: 18,
      paddingHorizontal: 5,
      borderRadius: 9,
      backgroundColor: '#ef4444',
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: v2Surf,
      zIndex: 2,
    },
    v2ToolsChatBadgeText: {
      fontSize: 10,
      fontWeight: '700',
      color: '#fff',
      fontFamily: v2Mono,
    },
    v2ToolsTodayBtn: {
      flexShrink: 0,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: v2Ink,
      backgroundColor: v2Ink,
    },
    v2ToolsTodayBtnActive: {
      backgroundColor: v2Surf,
      borderColor: v2Line,
    },
    v2ToolsTodayBtnText: {
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 0.3,
      textTransform: 'uppercase',
      color: v2Page,
      fontFamily: v2Font,
    },
    v2ToolsTodayBtnTextActive: {
      color: v2Ink,
    },
    v2DisplaySidebar: {
      width: 320,
      maxWidth: '38%',
      flexShrink: 0,
      alignSelf: 'stretch',
      backgroundColor: v2Surf,
      borderRightWidth: 1,
      borderRightColor: v2Line,
      flexDirection: 'column',
      minHeight: 0,
      ...(Platform.OS === 'web' ? { boxShadow: `4px 0 24px ${isDark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.06)'}` } : {}),
    },
    v2PdfSidebar: {
      width: 360,
      maxWidth: '46%',
    },
    v2DisplaySidebarHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingTop: 14,
      paddingBottom: 10,
      borderBottomWidth: 1,
      borderBottomColor: v2Line,
      flexShrink: 0,
    },
    v2DisplaySidebarTitle: {
      fontSize: 17,
      fontWeight: '700',
      letterSpacing: -0.3,
      color: v2Ink,
      fontFamily: v2Font,
      marginBottom: 2,
    },
    v2DisplaySidebarLead: {
      fontSize: 12.5,
      lineHeight: 18,
      color: v2Mute,
      fontFamily: v2Font,
    },
    v2DisplaySidebarScroll: {
      flex: 1,
      minHeight: 0,
    },
    v2DisplaySidebarScrollContent: {
      paddingHorizontal: 16,
      paddingTop: 14,
      paddingBottom: 20,
    },
    v2DisplayModal: {
      width: '92%',
      maxWidth: 560,
      maxHeight: '88%',
      backgroundColor: v2Surf,
      borderRadius: 16,
      padding: 22,
      borderWidth: 1,
      borderColor: v2Line,
      zIndex: 1,
    },
    v2DisplayModalTitle: {
      fontSize: 22,
      fontWeight: '700',
      letterSpacing: -0.4,
      color: v2Ink,
      fontFamily: v2Font,
      marginBottom: 4,
    },
    v2DisplayModalLead: {
      fontSize: 13.5,
      lineHeight: 20,
      color: v2Mute,
      fontFamily: v2Font,
    },
    sharedLinksBarBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 12,
      paddingVertical: 8,
      marginRight: pad,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.brand.accent,
      backgroundColor: isDark ? 'rgba(59,163,120,0.12)' : 'rgba(59,163,120,0.08)',
      flexShrink: 0,
    },
    sharedLinksBarBtnText: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.brand.accent,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },
    topBarShareSettingsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: pad,
      paddingBottom: 10,
    },
    tourSettingsBarBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.brand.primary,
      backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      flexShrink: 0,
    },
    tourSettingsBarBtnText: {
      fontSize: 12.5,
      fontWeight: '800',
      color: theme.brand.primary,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },
    mobilePanelLinksRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 0,
    },
    selectedDayPanelHeaderBlock: {
      gap: 8,
      marginBottom: 4,
    },
    selectedDayPanelHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    selectedDayPanelHeaderTitle: {
      flex: 1,
      minWidth: 0,
    },
    sharedLinksPanelBtn: {
      marginRight: 0,
      marginTop: 2,
      flexShrink: 0,
    },
    selectedDayPanelStepperRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: theme.ui.border,
    },
    selectedDayPanelActiveTab: {
      flex: 1,
      fontSize: 12,
      fontWeight: '800',
      color: '#7c6cdd',
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    selectedDayPanelActiveTabMuted: {
      flex: 1,
      fontSize: 12,
      fontWeight: '600',
      color: theme.text.secondary,
    },
    riderSubtabsRow: {
      flexDirection: 'row',
      gap: 8,
      paddingHorizontal: pad,
      paddingBottom: isCompact ? 4 : 8,
      alignItems: 'center',
    },
    riderSubtabBtn: {
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    riderSubtabBtnActive: {
      borderColor: '#7c6cdd',
      backgroundColor: isDark ? 'rgba(124,108,221,0.25)' : 'rgba(124,108,221,0.12)',
    },
    riderSubtabText: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.primary,
    },
    riderSubtabTextActive: {
      color: '#7c6cdd',
    },
    riderFullHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 4,
    },
    riderShareBtn: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    riderUploadBox: {
      borderWidth: 1.5,
      borderStyle: 'dashed',
      borderColor: theme.ui.borderStrong,
      borderRadius: 10,
      paddingVertical: 16,
      paddingHorizontal: 12,
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 4,
      marginBottom: 6,
      backgroundColor: theme.background.secondary,
    },
    riderUploadTitle: {
      marginTop: 6,
      fontSize: 13,
      fontWeight: '800',
      color: theme.text.primary,
    },
    riderUploadSub: {
      marginTop: 2,
      fontSize: 11,
      color: theme.text.secondary,
    },
    riderPreviewWrap: {
      marginTop: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 10,
      overflow: 'hidden',
      backgroundColor: theme.background.secondary,
    },
    riderPreviewImage: {
      width: '100%',
      height: 280,
    },
    riderDocViewer: {
      marginTop: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 10,
      padding: 10,
      backgroundColor: theme.background.secondary,
    },
    dayShareOptionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 6,
    },
    dayShareOptionText: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.text.primary,
    },
    dayInfoSection: {
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      marginBottom: 8,
      overflow: 'hidden',
    },
    dayInfoSectionHead: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 10,
      paddingVertical: 8,
      backgroundColor: theme.background.secondary,
    },
    dayInfoSectionTitle: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.text.primary,
    },
    dayInfoSectionBody: {
      fontSize: 12,
      color: theme.text.primary,
      paddingHorizontal: 10,
      paddingVertical: 8,
      lineHeight: 18,
    },
    floatTextStyleRow: {
      marginTop: 6,
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      columnGap: 6,
      rowGap: 5,
      width: '100%',
      borderTopWidth: 1,
      borderTopColor: theme.ui.border,
      paddingTop: 6,
    },
    floatTextStyleLabel: {
      fontSize: 9,
      fontWeight: '700',
      color: theme.text.tertiary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    floatTextStyleGroup: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    floatTabColorDot: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: 'rgba(0,0,0,0.2)',
    },
    floatTabColorDotActive: {
      borderWidth: 2,
      borderColor: '#7c6cdd',
    },
    floatTabControlBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      minHeight: 26,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    floatTabControlBtnActive: {
      borderColor: theme.brand.accent,
      backgroundColor: theme.brand.accent,
    },
    floatTabControlBtnText: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.text.primary,
    },
    floatTabControlBtnTextActive: {
      color: theme.button.primaryText,
    },
    /** Legacy pills used by expanded notes quick-format actions. */
    floatTabStylePill: {
      minWidth: 20,
      paddingHorizontal: 6,
      paddingVertical: 3,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
      alignItems: 'center',
      justifyContent: 'center',
    },
    floatTabStylePillText: {
      fontSize: 10,
      fontWeight: '800',
      color: theme.text.primary,
    },
    floatFilledItemsRow: {
      marginTop: 6,
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
      justifyContent: 'center',
    },
    floatFilledItemChip: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      backgroundColor: theme.background.secondary,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    floatFilledItemText: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.text.secondary,
    },
    topTabPlainBtn: {
      paddingHorizontal: v2 ? 0 : 6,
      paddingVertical: v2 ? 2 : 2,
      marginRight: v2 ? 16 : 0,
    },
    topTabPlainText: {
      fontSize: v2 ? 11.5 : 11,
      fontWeight: v2 ? '500' : '800',
      color: v2 ? v2Mute : v2Teal,
      letterSpacing: v2 ? 1.1 : 0.4,
      textTransform: 'uppercase',
      fontFamily: v2 ? v2Mono : undefined,
      paddingBottom: v2 ? 4 : 0,
      borderBottomWidth: v2 ? 1 : 0,
      borderBottomColor: 'transparent',
    },
    topTabPlainTextActive: {
      color: v2 ? v2Ink : v2TealDeep,
      fontWeight: v2 ? '600' : '800',
      textDecorationLine: v2 ? 'none' : 'underline',
      borderBottomColor: v2 ? v2Ink : 'transparent',
    },
    topTabPlainTextLocked: {
      color: '#ff8a00',
      textDecorationLine: 'underline',
    },
    dayCellInlineTabInput: {
      marginTop: 4,
      marginBottom: 6,
      flex: 1,
      alignSelf: 'stretch',
      paddingHorizontal: 0,
      paddingVertical: 0,
      borderWidth: 0,
      backgroundColor: 'transparent',
      color: theme.input.text,
      fontSize: 12,
      minHeight: 0,
    },
    dayCellScheduleWrap: {
      marginTop: 2,
      marginBottom: 4,
      width: '100%',
      flex: 1,
      minHeight: 0,
      overflow: 'visible',
      zIndex: 2,
    },
    dayCellRichEditorWrap: {
      marginTop: 4,
      marginBottom: 6,
      width: '100%',
      flex: 1,
      minHeight: 48,
      alignSelf: 'stretch',
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 6,
      backgroundColor: theme.background.card,
      paddingHorizontal: 6,
      paddingVertical: 4,
      overflow: 'hidden',
    },
    scroll: { paddingHorizontal: pad, paddingTop: isCompact ? 8 : pad, paddingBottom: 48 },
    card: {
      backgroundColor: theme.background.card,
      borderRadius: 12,
      padding: 14,
      marginBottom: 14,
      borderWidth: 1,
      borderColor: theme.ui.border,
      boxShadow: Platform.OS === 'web' ? `0 2px 12px ${theme.ui.shadow}` : undefined,
    },
    /** Month grid sits on the warm page, not inside a white panel. */
    calendarCard: {
      overflow: 'visible',
      backgroundColor: v2 ? 'transparent' : undefined,
      borderWidth: v2 ? 0 : undefined,
      boxShadow: v2 ? 'none' : undefined,
      marginBottom: v2 ? 0 : undefined,
      paddingHorizontal: v2 ? 16 : undefined,
      paddingTop: v2 ? 8 : undefined,
      paddingBottom: v2 ? 8 : undefined,
      flex: v2 ? 1 : undefined,
      minHeight: v2 ? 0 : undefined,
    },
    calendarTable: { 
      width: '100%', 
      alignSelf: 'stretch', 
      overflow: 'visible',
      flex: v2 ? 1 : undefined,
      minHeight: v2 ? 0 : getResponsiveValue({ mobile: 400, tablet: 600, laptop: 600, desktop: 600 }),
      gap: v2 ? 6 : 0,
    },
    calendarTableWide: { minWidth: monthCellMinWidth * 7 },
    calendarScrollContent: { paddingBottom: 4 },
    weekRow: {
      flexDirection: 'row',
      width: '100%',
      alignItems: 'stretch',
      marginBottom: v2 ? 0 : 1,
      gap: v2 ? 6 : 0,
      overflow: 'visible',
      minHeight: v2 ? (v2Dense ? 120 : 136) : 0,
    },
    calendarCellWrap: {
      flex: 1,
      minHeight: 0,
      alignSelf: 'stretch',
      position: 'relative',
      overflow: 'visible',
    },
    calendarCellWrapFloat: {
      zIndex: 40,
      ...(Platform.OS === 'android' ? { elevation: 8 } : {}),
    },
    columnHeader: {
      flex: 1,
      paddingVertical: v2 ? 4 : 8,
      justifyContent: v2 ? 'flex-end' : 'center',
      alignItems: v2 ? 'flex-start' : 'center',
      paddingLeft: v2 ? 2 : 0,
    },
    weekday: {
      fontSize: v2 ? 11 : 12,
      fontWeight: v2 ? '500' : '700',
      color: v2 ? pal.faint : theme.text.tertiary,
      textAlign: v2 ? 'left' : 'center',
      textTransform: 'uppercase',
      letterSpacing: v2 ? 1.2 : 0.5,
      fontFamily: v2 ? v2Mono : v2Font,
    },
    /** Large month grid: each day is a tall droppable / tappable box with events inside. */
    dayCellLarge: {
      flex: 1,
      minHeight: v2
        ? (v2Dense ? 120 : 136)
        : (isCompact ? getResponsiveValue({ mobile: 120, tablet: 140, laptop: 160, desktop: 170 }) : 115),
      borderRadius: 10,
      borderWidth: 1,
      borderColor: gridLine,
      backgroundColor: v2 ? v2Surf : (cal ? (isDark ? 'rgba(255,255,255,0.08)' : '#FFFFFF') : theme.background.card),
      padding: v2 ? (v2Dense ? 7 : 10) : 6,
      position: 'relative',
      overflow: v2 ? 'visible' : 'hidden',
    },
    /** Top-right in day cell when SCHEDULE or a text tab is active (selected day only). */
    dayCellExpandScheduleBtn: {
      position: 'absolute',
      top: 4,
      right: 4,
      zIndex: 12,
      width: 22,
      height: 22,
      borderRadius: 11,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.elevated,
      borderWidth: 1,
      borderColor: theme.ui.border,
      ...(Platform.OS === 'android' ? { elevation: 4 } : {}),
      boxShadow: Platform.OS === 'web' ? `0 1px 6px ${theme.ui.shadow}` : undefined,
    },
    dayCellExpandScheduleBtnBottom: {
      top: undefined,
      bottom: 4,
      right: 4,
    },
    dayCellTourCorner: {
      position: 'absolute',
      top: 4,
      right: 6,
      left: 28,
      zIndex: 11,
      paddingHorizontal: 4,
      paddingVertical: 2,
      borderRadius: 6,
      gap: 2,
      alignItems: 'flex-end',
    },
    /** Flow-positioned tour info block — sits inside dayCellBody, below the day number */
    dayCellTourInfo: {
      width: '100%',
      paddingHorizontal: 2,
      paddingBottom: 4,
      gap: 2,
      flexShrink: v2 ? 0 : 1,
      minHeight: v2 ? undefined : 0,
    },
    dayCellTourCornerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      justifyContent: 'flex-end',
    },
    dayCellTourCornerDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
      flexShrink: 0,
    },
    dayCellTourCornerTour: {
      fontSize: 11,
      color: theme.text.secondary,
      textAlign: 'right',
    },
    dayCellTourCornerVenue: {
      fontSize: 11,
      fontWeight: '700',
      color: "#7c6cdd",
      textAlign: 'right',
    },
    tourDayPill: {
      borderRadius: 4,
      paddingHorizontal: 5,
      paddingVertical: 2,
      marginBottom: 2,
    },
    tourDayPillText: {
      fontSize: 10,
      fontWeight: '600',
      color: '#fff',
    },
    dayCellMuted: { backgroundColor: v2 ? 'transparent' : undefined, borderColor: v2 ? 'transparent' : undefined, opacity: 0.55 },
    dayCellEmpty: { backgroundColor: v2Empty },
    dayCellSelected: {
      backgroundColor: v2 ? hexWithAlpha(v2Teal, isDark ? 0.22 : 0.14) : (isDark ? 'rgba(46, 204, 113, 0.22)' : 'rgba(39, 174, 96, 0.2)'),
      borderColor: v2 ? v2Teal : gridLine,
      borderWidth: v2 ? 2 : 1,
      boxShadow: v2 && Platform.OS === 'web' ? `0 0 0 2px ${v2Teal}` : undefined,
    },
    dayCellDropOver: {
      backgroundColor: isDark ? 'rgba(46, 204, 113, 0.1)' : 'rgba(39, 174, 96, 0.1)',
      borderColor: theme.brand.primary,
      borderWidth: 2,
    },
    dayCellDropOverDragging: {
      backgroundColor: isDark ? 'rgba(46, 204, 113, 0.22)' : 'rgba(39, 174, 96, 0.2)',
      borderColor: theme.brand.accent,
      borderWidth: 2,
    },
    dragGhost: {
      maxWidth: 220,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 10,
      borderLeftWidth: 4,
      backgroundColor: theme.background.elevated,
      borderWidth: 1,
      borderColor: theme.ui.borderStrong,
      boxShadow: Platform.OS === 'web' ? `0 8px 24px ${theme.ui.shadowStrong}` : undefined,
      opacity: 0.95,
    },
    dragGhostText: { fontSize: 13, fontWeight: '700', color: theme.text.primary },
    dayCellFloatMenu: {
      position: 'absolute',
      left: 0,
      right: 0,
      zIndex: 50,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 4,
      paddingHorizontal: 4,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.borderStrong,
      backgroundColor: theme.background.elevated,
      boxShadow: Platform.OS === 'web' ? `0 8px 24px ${theme.ui.shadowStrong}` : undefined,
      ...(Platform.OS === 'android' ? { elevation: 12 } : {}),
    },
    dayCellFloatToolbarRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      maxWidth: '100%',
    },
    /** Icon-only hit targets for the floating toolbar. */
    floatIconBtn: {
      padding: 8,
      borderRadius: 8,
      minWidth: 40,
      minHeight: 40,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: eventRowBg,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    /** Always attach float just above the cell. */
    dayCellFloatAnchorAbove: { bottom: '100%', marginBottom: 6 },
    dayCellFloatAnchorBelow: { top: '100%', marginTop: 6 },
    dayCellEventChipActive: {
      borderColor: theme.brand.primary,
      backgroundColor: isDark ? 'rgba(46, 204, 113, 0.15)' : 'rgba(39, 174, 96, 0.12)',
    },
    eventBarBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 8,
      backgroundColor: eventRowBg,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    eventBarBtnText: { fontSize: 12, fontWeight: '700', color: theme.text.primary },
    dayCellInner: { flex: 1, minHeight: 0, width: '100%', flexDirection: 'column' },
    dayCellTouchableFill: { flex: 1, minHeight: 0, width: '100%', position: 'relative', flexDirection: 'column' },
    dayCellTopRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 6,
      width: '100%',
    },
    dayCellKind: {
      fontFamily: v2Mono,
      fontSize: 10,
      fontWeight: '500',
      letterSpacing: 1,
      textTransform: 'uppercase',
      color: v2 ? pal.faint : v2Mute,
    },
    dayCellBody: {
      flex: 1,
      minHeight: 0,
      width: '100%',
      marginTop: v2 ? (v2Dense ? 2 : 4) : 2,
      flexDirection: 'column',
    },
    dayCellPosterSpacer: {
      flex: 1,
      minHeight: 4,
    },
    dayCellNum: {
      fontSize: v2 ? (v2Dense ? 14.5 : 16.5) : 13,
      fontWeight: '600',
      letterSpacing: v2 ? -0.3 : 0,
      color: v2Ink,
      fontFamily: v2 ? v2Mono : v2Font,
    },
    dayCellNumAbs: {
      position: 'relative',
      top: 0,
      left: 0,
      zIndex: 12,
    },
    dayCellEventScroll: { flexGrow: 0, maxHeight: 120 },
    dayCellEventChip: {
      borderLeftWidth: 3,
      borderRadius: 6,
      paddingVertical: 4,
      paddingHorizontal: 6,
      borderWidth: 2,
      borderColor: 'transparent',
      marginBottom: 4,
      backgroundColor: theme.background.secondary,
    },
    dayCellEventChipRow: { flexDirection: 'row', alignItems: 'flex-start' },
    dayCellEventChipText: { flex: 1, fontSize: 12, fontWeight: '600', color: theme.text.primary },
    dayCellDragHandle: { padding: 2, ...(Platform.OS === 'web' ? { cursor: 'grab' } : {}) },
    /** Compact month grid (mobile / tablet month view). */
    compactMonthTable: { width: '100%' },
    compactWeekRow: { flexDirection: 'row', width: '100%', marginBottom: 6, gap: 6 },
    compactColumnHeader: { flex: 1, alignItems: 'flex-start', paddingVertical: 4, paddingLeft: 2 },
    compactWeekday: {
      fontSize: 11,
      fontWeight: '500',
      color: pal.faint,
      textTransform: 'uppercase',
      letterSpacing: 1.2,
      fontFamily: v2Mono,
    },
    compactDayCell: {
      flex: 1,
      minHeight: 88,
      marginHorizontal: 0,
      marginBottom: 0,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: v2Line,
      backgroundColor: v2Surf,
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      paddingTop: 6,
      paddingBottom: 4,
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    compactDayCellMuted: { backgroundColor: 'transparent', borderColor: 'transparent', opacity: 0.55 },
    compactDayCellSelected: {
      backgroundColor: v2 ? hexWithAlpha(v2Teal, isDark ? 0.22 : 0.14) : v2Surf,
      borderColor: v2 ? v2Teal : v2Ink,
      borderWidth: v2 ? 2 : 1,
      boxShadow: Platform.OS === 'web' ? `0 0 0 2px ${v2 ? v2Teal : v2Ink}` : undefined,
    },
    compactDayCellToday: {
      borderColor: theme.brand.accent,
    },
    compactDayCellEditing: {
      minHeight: 96,
      alignItems: 'stretch',
      overflow: 'visible',
      position: 'relative',
      zIndex: 20,
    },
    compactDayEditorWrap: {
      width: '100%',
      flex: 1,
      minHeight: 0,
      marginTop: 2,
      paddingHorizontal: 2,
    },
    compactDayNum: {
      fontSize: 13.5,
      fontWeight: '700',
      color: theme.text.primary,
      textAlign: 'left',
      width: '100%',
      fontFamily: v2Font,
    },
    compactDayNumSelected: { color: theme.brand.accent, fontWeight: '800' },
    compactDayNumToday: { color: theme.brand.accent, fontWeight: '800' },
    compactDayDots: { flexDirection: 'row', gap: 2, marginTop: 4, minHeight: 6, alignItems: 'center' },
    compactDayDotsSpacer: { height: 10 },
    compactDayDot: { width: 5, height: 5, borderRadius: 3 },
    selectedDayMetaRow: { marginBottom: 10, gap: 2 },
    selectedDayOffTourHint: {
      marginBottom: 12,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    selectedDayOffTourHintText: {
      fontSize: 12,
      lineHeight: 17,
      color: theme.text.secondary,
      textAlign: 'center',
    },
    selectedDayMetaTour: { fontSize: 12, fontWeight: '700', color: v2Mute },
    selectedDayMetaVenue: { fontSize: 15, fontWeight: '700', color: v2Ink },
    selectedDayEditorBlock: {
      marginBottom: 14,
      padding: 12,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: v2Line,
      backgroundColor: v2Surf,
      minWidth: 0,
      overflow: 'hidden',
    },
    selectedDayEditorBlockFill: {
      flex: 1,
      minHeight: 280,
      marginBottom: 0,
      flexDirection: 'column',
    },
    selectedDayTabEditorZone: {
      minWidth: 0,
      overflow: 'hidden',
    },
    selectedDayTabEditorZoneFill: {
      flex: 1,
      minHeight: 0,
    },
    selectedDayCopyToCard: {
      marginTop: 14,
      paddingTop: 12,
      paddingHorizontal: 10,
      paddingBottom: 10,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: v2Line,
      backgroundColor: v2Surf,
      minWidth: 0,
      overflow: 'hidden',
    },
    selectedDayEditorHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8,
      gap: 8,
    },
    selectedDayEditorLabel: {
      fontSize: 12,
      fontWeight: '800',
      color: theme.brand.accent,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      flex: 1,
    },
    selectedDayEditorActions: { flexDirection: 'row', gap: 6, flexShrink: 0 },
    selectedDayIconActionBtn: {
      width: 32,
      height: 32,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    selectedDayActionBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    selectedDayActionBtnText: { fontSize: 12, fontWeight: '700', color: theme.text.primary },
    selectedDayTabInput: {
      minHeight: 120,
      borderWidth: 0,
      backgroundColor: 'transparent',
      padding: 0,
      fontSize: 15,
      color: theme.input.text,
      textAlignVertical: 'top',
    },
    selectedDayTabInputFill: {
      flex: 1,
      minHeight: 120,
    },
    selectedDayTabInputBordered: {
      minHeight: 120,
      borderWidth: 1,
      borderColor: theme.input.border || theme.ui.border,
      borderRadius: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      backgroundColor: theme.input.background,
    },
    selectedDayEventsHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
    selectedDayEventsTitle: { fontSize: 14, fontWeight: '800', color: theme.text.primary },
    cellDayMuted: { color: theme.text.muted },
    sectionTitle: { fontSize: 16, fontWeight: '800', color: theme.text.primary, marginBottom: 10 },
    primaryBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      alignSelf: 'flex-start',
      backgroundColor: theme.button.primary,
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 8,
      marginBottom: 14,
    },
    primaryBtnDisabled: {
      opacity: 0.65,
    },
    primaryBtnText: { color: theme.button.primaryText, fontWeight: '800', fontSize: 14 },
    eventRow: {
      borderLeftWidth: 4,
      paddingLeft: 10,
      paddingVertical: 10,
      marginBottom: 8,
      backgroundColor: eventRowBg,
      borderRadius: 8,
    },
    eventTitle: { fontSize: 15, fontWeight: '700', color: theme.text.primary },
    eventSub: { fontSize: 12, color: theme.text.secondary, marginTop: 2 },
    eventNotes: { fontSize: 12, color: theme.text.tertiary, marginTop: 4 },
    link: { color: theme.text.link, fontWeight: '700', marginTop: 6, fontSize: 13 },
    eventActions: { flexDirection: 'row', gap: 8, marginTop: 8 },
    smallBtn: {
      paddingHorizontal: 10,
      paddingVertical: 6,
      backgroundColor: smallBtnBg,
      borderRadius: 6,
    },
    smallBtnText: { fontSize: 12, fontWeight: '700', color: theme.text.primary },
    modalOverlay: {
      flex: 1,
      backgroundColor: theme.background.overlay,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    modalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    modalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: theme.background.overlay,
    },
    modalCard: {
      width: '100%',
      maxWidth: 440,
      maxHeight: '90%',
      backgroundColor: theme.background.elevated,
      borderRadius: 14,
      padding: 18,
      borderWidth: 1,
      borderColor: theme.ui.border,
      zIndex: 1,
    },
    modalTitle: { fontSize: 18, fontWeight: '800', color: theme.text.primary, marginBottom: 12 },
    label: { fontSize: 12, fontWeight: '700', color: theme.text.secondary, marginTop: 8, marginBottom: 4 },
    input: {
      borderWidth: 1,
      borderColor: theme.input.border,
      borderRadius: 8,
      padding: 10,
      fontSize: 15,
      color: theme.input.text,
      backgroundColor: theme.input.background,
    },
    dateInputRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    importDatesList: {
      marginTop: 8,
      marginBottom: 10,
      maxHeight: 340,
    },
    importDateRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 6,
      borderBottomWidth: 1,
      borderBottomColor: theme.ui.border,
    },
    importDateRowBlock: {
      marginBottom: 2,
    },
    importDateMeta: { flex: 1, minWidth: 0 },
    importDateDayTitle: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.text.primary,
    },
    importDateVenue: {
      fontSize: 11,
      color: theme.text.secondary,
      marginTop: 2,
    },
    importDatePickBtn: {
      width: 126,
      minHeight: 34,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 6,
      paddingHorizontal: 8,
      paddingVertical: 6,
      backgroundColor: theme.input.background,
    },
    importDatePickBtnText: {
      fontSize: 12,
      color: theme.input.text,
      fontWeight: '700',
    },
    importDatePickBtnTextPlaceholder: {
      color: theme.text.tertiary,
      fontWeight: '500',
    },
    importDatePickerInline: {
      alignSelf: 'flex-end',
      width: 248,
      marginTop: 4,
      marginRight: 2,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      padding: 8,
      backgroundColor: theme.background.elevated,
      ...(Platform.OS === 'android' ? { elevation: 8 } : {}),
      boxShadow: Platform.OS === 'web' ? `0 6px 18px ${theme.ui.shadowStrong}` : undefined,
    },
    importPickerHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 4,
    },
    importPickerNavBtn: {
      width: 24,
      height: 24,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 6,
      backgroundColor: theme.background.card,
    },
    importPickerMonthText: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.text.primary,
    },
    importPickerWeekdayRow: { flexDirection: 'row', marginBottom: 2 },
    importPickerWeekdayCell: {
      flex: 1,
      textAlign: 'center',
      fontSize: 9,
      color: theme.text.secondary,
      fontWeight: '700',
    },
    importPickerWeekRow: { flexDirection: 'row', marginBottom: 2 },
    importPickerDayCell: {
      flex: 1,
      aspectRatio: 1,
      maxWidth: 28,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    importPickerDayCellMuted: { opacity: 0.45 },
    importPickerDayText: { fontSize: 10, color: theme.text.primary, fontWeight: '700' },
    importPickerDayTextMuted: { color: theme.text.tertiary },
    dateInput: { flex: 1 },
    timeInputInline: { width: 112, minWidth: 96, fontSize: 13 },
    datePickBtn: {
      width: 36,
      height: 36,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    inputMultiline: { minHeight: 72, textAlignVertical: 'top' },
    expandedNotesModalCard: {
      maxWidth: 760,
      width: '100%',
      alignSelf: 'center',
      overflow: 'hidden',
      flexDirection: 'column',
    },
    expandedTabEditorModalCard: {
      maxWidth: 520,
    },
    expandedNotesModalScroll: {
      flex: 1,
      minHeight: 0,
    },
    expandedNotesScrollContent: {
      paddingBottom: 8,
    },
    expandedNotesModalFooter: {
      marginTop: 4,
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: theme.ui.border,
      flexShrink: 0,
    },
    expandedNotesFooterRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: 10,
      width: '100%',
    },
    expandedNotesFooterBtn: {
      flex: 1,
      minHeight: 48,
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
    },
    expandedNotesFooterBtnPrimary: {
      backgroundColor: theme.button.primary,
    },
    expandedNotesFooterBtnSecondary: {
      backgroundColor: theme.background.secondary,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    expandedNotesFooterBtnPrimaryText: {
      color: theme.button.primaryText,
      fontWeight: '800',
      fontSize: 15,
    },
    expandedNotesFooterBtnSecondaryText: {
      color: theme.text.primary,
      fontWeight: '700',
      fontSize: 15,
    },
    expandedScheduleModalCard: {
      maxWidth: 520,
      width: '100%',
      overflow: 'hidden',
      alignSelf: 'center',
    },
    expandedScheduleFooterRow: {
      flexDirection: 'row',
      gap: 10,
      marginTop: 12,
      alignItems: 'stretch',
      width: '100%',
    },
    expandedScheduleFooterBtn: {
      flex: 1,
      minHeight: 48,
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
    },
    expandedScheduleFooterBtnPrimary: {
      backgroundColor: theme.button.primary,
    },
    expandedScheduleFooterBtnSecondary: {
      backgroundColor: theme.background.secondary,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    expandedScheduleFooterBtnPrimaryText: {
      color: theme.button.primaryText,
      fontWeight: '800',
      fontSize: 15,
    },
    expandedScheduleFooterBtnSecondaryText: {
      color: theme.text.primary,
      fontWeight: '700',
      fontSize: 15,
    },
    expandedNotesToolbar: {
      marginTop: 10,
      marginBottom: 10,
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      alignItems: 'center',
    },
    expandedNotesToolbarHint: {
      fontSize: 12,
      lineHeight: 17,
      color: theme.text.secondary,
      marginTop: 6,
      marginBottom: 4,
    },
    expandedNotesToolbarSection: {
      marginTop: 10,
      marginBottom: 4,
    },
    expandedNotesToolbarLabel: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginBottom: 6,
    },
    expandedNotesToolbarWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      alignItems: 'center',
    },
    expandedNotesToolBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    expandedNotesToolBtnText: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.text.primary,
    },
    expandedNotesColorDot: {
      width: 28,
      height: 28,
      borderRadius: 14,
      borderWidth: 2,
    },
    expandedNotesMiniStyleRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 8,
      marginBottom: 8,
    },
    expandedNotesMiniStyleLabel: {
      minWidth: 44,
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.secondary,
    },
    dayFilesListWrap: {
      marginTop: 10,
      borderTopWidth: 1,
      borderTopColor: theme.ui.border,
      paddingTop: 10,
      gap: 8,
      maxHeight: 220,
    },
    dayUploadsPanel: {
      marginTop: 10,
      borderTopWidth: 1,
      borderTopColor: theme.ui.border,
      paddingTop: 10,
      gap: 8,
    },
    daySheetEditorHeader: {
      marginBottom: 6,
    },
    daySheetToolbar: {
      marginBottom: 8,
      gap: 4,
    },
    daySheetToolbarRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      minWidth: 0,
    },
    daySheetToolbarActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 0,
    },
    daySheetToolbarStatus: {
      fontSize: 11,
      color: theme.text.tertiary,
      fontWeight: '600',
    },
    daySheetToolbarStatusCompact: {
      fontSize: 10,
    },
    daySheetIconBtn: {
      width: 32,
      height: 32,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    daySheetIconBtnActive: {
      borderColor: theme.brand.accent,
      backgroundColor: theme.brand.accent + '22',
    },
    daySheetIconBtnDisabled: {
      opacity: 0.45,
    },
    daySheetModeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 1,
      minWidth: 0,
    },
    daySheetModeBtn: {
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    daySheetModeBtnActive: {
      borderColor: theme.brand.accent,
      backgroundColor: theme.brand.accent,
    },
    daySheetModeBtnText: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.text.primary,
    },
    daySheetModeBtnTextActive: {
      color: theme.button.primaryText,
    },
    daySheetPaperWrap: {
      width: '100%',
      alignItems: 'stretch',
      marginTop: 4,
    },
    daySheetPaperWrapSidebar: {
      paddingBottom: 12,
    },
    daySheetPaper: {
      position: 'relative',
      borderWidth: 1,
      borderColor: theme.ui.borderStrong,
      backgroundColor: '#ffffff',
      width: '100%',
      maxWidth: 860,
      aspectRatio: 210 / 297,
      minHeight: 520,
      overflow: 'hidden',
    },
    daySheetBgImage: {
      ...StyleSheet.absoluteFillObject,
      opacity: 0.12,
    },
    daySheetPaperInner: {
      position: 'relative',
      zIndex: 1,
      paddingVertical: 12,
      paddingHorizontal: 24,
      gap: 10,
    },
    daySheetBannerImage: {
      width: '100%',
      height: 120,
      backgroundColor: 'transparent',
    },
    daySheetBannerPlaceholder: {
      width: '100%',
      height: 120,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    daySheetInfoBox: {
      borderWidth: 2,
      borderColor: '#000000',
      paddingVertical: 10,
      paddingHorizontal: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#ffffff',
      gap: 2,
    },
    daySheetInfoText: {
      color: '#c0392b',
      fontSize: 16,
      fontWeight: '800',
      textAlign: 'center',
    },
    daySheetScheduleBlock: {
      backgroundColor: 'transparent',
      paddingHorizontal: 4,
    },
    daySheetSchedulePreviewRow: {
      minHeight: 30,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 6,
      paddingHorizontal: 8,
    },
    daySheetSchedulePreviewItem: {
      flex: 1,
      color: '#000000',
      fontSize: 14,
      fontWeight: '700',
      textTransform: 'uppercase',
    },
    daySheetSchedulePreviewTime: {
      width: 72,
      color: '#000000',
      fontSize: 14,
      fontWeight: '700',
      textAlign: 'right',
    },
    daySheetScheduleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottomWidth: 1,
      borderBottomColor: '#000000',
      minHeight: 34,
      paddingHorizontal: 10,
      gap: 10,
    },
    daySheetScheduleItem: {
      flex: 1,
      color: '#000000',
      fontSize: 12,
      fontWeight: '700',
      textTransform: 'uppercase',
    },
    daySheetScheduleTime: {
      width: 72,
      textAlign: 'right',
      color: '#000000',
      fontSize: 12,
      fontWeight: '700',
    },
    daySheetScheduleTimeInput: {
      width: 72,
      borderWidth: 1,
      borderColor: '#000000',
      backgroundColor: '#ffffff',
      color: '#000000',
      textAlign: 'right',
      paddingHorizontal: 6,
      paddingVertical: 4,
      fontSize: 12,
      fontWeight: '700',
    },
    daySheetAftershowInput: {
      marginTop: 8,
      borderWidth: 0,
      backgroundColor: 'transparent',
      color: '#000000',
      fontSize: 13,
      fontWeight: '700',
      minHeight: 50,
      textAlignVertical: 'center',
      paddingHorizontal: 12,
      paddingVertical: 10,
      alignSelf: 'center',
      width: '92%',
    },
    daySheetAftershowPreview: {
      marginTop: 8,
      borderWidth: 0,
      backgroundColor: 'transparent',
      color: '#000000',
      fontSize: 13,
      fontWeight: '700',
      minHeight: 50,
      textAlign: 'center',
      textAlignVertical: 'center',
      paddingHorizontal: 12,
      paddingVertical: 10,
      alignSelf: 'center',
      width: '92%',
    },
    dayUploadsGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    dayUploadCard: {
      width: 140,
      minHeight: 130,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
      padding: 8,
      gap: 6,
    },
    dayUploadImageWrap: {
      width: '100%',
      height: 70,
      borderRadius: 6,
      overflow: 'hidden',
      backgroundColor: theme.background.card,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    dayUploadImage: {
      width: '100%',
      height: '100%',
    },
    dayUploadFileIconWrap: {
      width: '100%',
      height: 70,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.card,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    dayUploadFileName: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.text.primary,
      minHeight: 28,
    },
    dayUploadActions: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 6,
      marginTop: 'auto',
    },
    dayUploadDeleteBtn: {
      width: 28,
      height: 28,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    dayFileRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 6,
      borderBottomWidth: 1,
      borderBottomColor: theme.ui.border,
    },
    dayFilesInlineList: {
      marginTop: 2,
      marginBottom: 4,
      gap: 3,
    },
    /** Matches ScheduleEditor compactPlusBtn sizing / chrome. */
    dayFilesAttachBtn: {
      alignSelf: 'center',
      marginTop: 2,
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.brand.primary,
      backgroundColor: theme.background.card,
    },
    dayFileInlineChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 5,
      paddingVertical: 3,
      borderRadius: 6,
      backgroundColor: theme.background.secondary,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    dayFileInlineChipText: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.text.primary,
      flex: 1,
    },
    dayFileInlineMore: {
      fontSize: 10,
      color: theme.text.secondary,
      fontWeight: '700',
    },
    colorSwatch: { width: 28, height: 28, borderRadius: 14, borderWidth: 2, borderColor: 'transparent' },
    colorSwatchActive: { borderColor: theme.brand.primary },
    chip: {
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 999,
      backgroundColor: chipBg,
      marginRight: 8,
      marginBottom: 4,
    },
    chipActive: { backgroundColor: chipActiveBg },
    chipText: { fontSize: 11, color: theme.text.primary, maxWidth: 200 },
    tourSelectOption: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
      marginBottom: 8,
    },
    tourSelectOptionActive: {
      borderColor: theme.brand.primary,
      backgroundColor: isDark ? 'rgba(46, 204, 113, 0.18)' : 'rgba(39, 174, 96, 0.12)',
    },
    tourSelectOptionText: { color: theme.text.primary, fontSize: 13, fontWeight: '700' },
    modalActions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 8 },
    secondaryBtn: { paddingHorizontal: 14, paddingVertical: 10 },
    secondaryBtnText: { color: theme.text.primary, fontWeight: '700' },
    dangerBtn: { paddingHorizontal: 14, paddingVertical: 10 },
    dangerBtnText: { color: theme.status.error, fontWeight: '700' },
    popupButtonRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 12,
      flexWrap: 'wrap',
      marginTop: 4,
    },
    popupBtnSecondary: {
      paddingVertical: 12,
      paddingHorizontal: 18,
      borderRadius: 10,
      backgroundColor: theme.background.secondary,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    popupBtnSecondaryText: { color: theme.text.primary, fontWeight: '700', fontSize: 15 },
    popupBtnDanger: {
      paddingVertical: 12,
      paddingHorizontal: 18,
      borderRadius: 10,
      backgroundColor: theme.status.error,
    },
    popupBtnDangerText: { color: '#fff', fontWeight: '800', fontSize: 15 },
    monthPickerYearRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      marginBottom: 14,
    },
    monthPickerYearBtn: { padding: 8 },
    monthPickerYearText: { fontSize: 22, fontWeight: '800', color: theme.text.primary, minWidth: 64, textAlign: 'center' },
    monthGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      justifyContent: 'center',
    },
    monthChip: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 8,
      backgroundColor: theme.background.secondary,
      borderWidth: 1,
      borderColor: theme.ui.border,
      minWidth: 52,
      alignItems: 'center',
    },
    monthChipActive: {
      backgroundColor: isDark ? 'rgba(46, 204, 113, 0.25)' : 'rgba(39, 174, 96, 0.2)',
      borderColor: theme.brand.primary,
    },
    monthChipText: { fontSize: 12, fontWeight: '700', color: theme.text.secondary },
    monthChipTextActive: { color: theme.brand.primary },
    rangeChipRow: { flexDirection: 'row', gap: 8, marginBottom: 10 },
    rangeChip: {
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    rangeChipActive: {
      borderColor: theme.brand.primary,
      backgroundColor: isDark ? 'rgba(46, 204, 113, 0.2)' : 'rgba(39, 174, 96, 0.12)',
    },
    rangeChipText: { fontSize: 12, fontWeight: '700', color: theme.text.primary },
    pickerWeekdayRow: { flexDirection: 'row', marginBottom: 4 },
    pickerWeekdayCell: { flex: 1, textAlign: 'center', fontSize: 11, color: theme.text.secondary, fontWeight: '700' },
    pickerWeekRow: { flexDirection: 'row', marginBottom: 4 },
    pickerDayCell: {
      flex: 1,
      marginHorizontal: 1,
      height: 30,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.secondary,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    pickerDayCellMuted: { opacity: 0.5 },
    pickerDayCellInRange: { backgroundColor: isDark ? 'rgba(93, 173, 226, 0.22)' : 'rgba(52, 152, 219, 0.16)' },
    pickerDayCellEdge: { backgroundColor: theme.brand.primary, borderColor: theme.brand.primary },
    pickerDayText: { fontSize: 12, fontWeight: '700', color: theme.text.primary },
    pickerDayTextMuted: { color: theme.text.tertiary },
    pickerDayTextEdge: { color: theme.button.primaryText },
    tourPick: {
      padding: 12,
      borderRadius: 10,
      backgroundColor: tourPickBg,
      marginBottom: 8,
      borderWidth: 1,
      borderColor: tourPickBorder,
    },
    tourPickTitle: { fontWeight: '800', color: theme.text.primary, fontSize: 15 },
    tourPickSub: { fontSize: 12, color: theme.text.secondary, marginTop: 2 },
    datePickerPopupCard: {
      width: '100%',
      maxWidth: 420,
      backgroundColor: theme.background.elevated,
      borderRadius: 14,
      padding: 16,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
  });
}
